
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import API_BASE from './apiBase';
import { getVersion } from './versionService';
import { CONSENT_TYPES, initialContent } from './ConsentPage';
import '../css/App.css';
import Swal from 'sweetalert2';
import { MdRoomPreferences } from 'react-icons/md';
import { sortConsentRecords, sortIdProofValues } from './consentSortUtils';
import { TableCellsMerge } from 'lucide-react';
import { createSessionCacheKey, readSessionCache, writeSessionCache, clearAllSessionCache } from './sessionDataCache';
import { formatCompactNumber } from './numberFormatUtils';
import formatDisplayLabelShared from './formatDisplayLabel';
import maskUtils from './maskUtils';

const maskEmailUtil = maskUtils.maskEmail;
const maskPhoneUtil = maskUtils.maskPhone;
const maskDOBUtil = maskUtils.maskDOB;
const maskEmail = (email) => maskEmailUtil(email);
const maskPhone = (phone) => maskPhoneUtil(phone);
const maskDOB = (dob) => maskDOBUtil(dob);

// helpers: initials avatar and deterministic color from string
const stringToColor = (str) => {
  if (!str) return '#64748b';
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
  return `#${'00000'.substring(0, 6 - c.length)}${c}`;
};
const getInitials = (s) => {
  if (!s) return 'U';
  const t = String(s).trim();
  const parts = t.split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (parts.length === 0) return t.slice(0, 1).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + (parts[1][0] || '')).toUpperCase();
};
const Avatar = ({ value, size = 28, style = {} }) => {
  const initials = getInitials(value);
  const bg = stringToColor(String(value || initials));
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: bg, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.max(10, Math.floor(size * 0.45)), fontWeight: 700, ...style }}>
      {initials}
    </div>
  );
};

const getWelcomeName = (user) => {
  if (!user) return '';
  let name = user.full_name || '';
  if (!name && user.email) {
    const prefix = user.email.split('@')[0];
    name = prefix.replace(/[._-]/g, ' ').trim();
  }
  if (!name) return '';
  return name
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatDisplayLabel = (value) => formatDisplayLabelShared(value, 'N/A');

const GLOBAL_LANGUAGE_MAP = {
  Assamese: 'as', Bengali: 'bn', Bodo: 'brx', Dogri: 'doi', Gujarati: 'gu', Hindi: 'hi', Kannada: 'kn', Kashmiri: 'ks', Konkani: 'kok', Maithili: 'mai', Malayalam: 'ml', Manipuri: 'mni', Marathi: 'mr', Nepali: 'ne', Odia: 'or', Punjabi: 'pa', Sanskrit: 'sa', Santhali: 'sat', Sindhi: 'sd', Tamil: 'ta', Telugu: 'te', Urdu: 'ur', English: 'en'
};

const normalizeLangCode = (code) => {
  if (!code) return 'en';
  const str = String(code).trim();
  if (GLOBAL_LANGUAGE_MAP[str]) return GLOBAL_LANGUAGE_MAP[str].toLowerCase();
  const lower = str.toLowerCase();
  for (const [k, v] of Object.entries(GLOBAL_LANGUAGE_MAP)) {
    if (k.toLowerCase() === lower || v.toLowerCase() === lower) return v.toLowerCase();
  }
  if (lower.includes('-')) return lower.split('-')[0];
  if (lower.includes('_')) return lower.split('_')[0];
  return lower;
};

const getLanguageDisplayName = (raw) => {
  if (!raw) return 'English';
  const list = (Array.isArray(raw) ? raw : String(raw).split(',')).map(s => s.trim()).filter(Boolean);
  if (list.length === 0) return 'English';
  return list.map(item => {
    const lower = item.toLowerCase();
    for (const [name, code] of Object.entries(GLOBAL_LANGUAGE_MAP)) {
      if (code.toLowerCase() === lower || name.toLowerCase() === lower) return name;
    }
    return item.length <= 3 ? item.toUpperCase() : (item.charAt(0).toUpperCase() + item.slice(1));
  }).join(', ');
};

const parseConsentPayload = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string' || !value.trim().startsWith('{')) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

const CONSENT_TYPE_TRANSLATIONS = {
  hi: {
    terms_and_conditions: 'नियम और शर्तें',
    terms_conditions: 'नियम और शर्तें',
    terms_of_service: 'सेवा की शर्तें',
    privacy_policy: 'गोपनीयता नीति',
    cookie_policy: 'कुकी नीति',
    cookies: 'कुकी सहमति',
    data_sharing: 'डेटा साझाकरण',
    third_party_sharing: 'तृतीय पक्ष डेटा साझाकरण',
    marketing: 'विपणन (मार्केटिंग) सहमति',
    marketing_communications: 'विपणन संचार',
    medical_records: 'चिकित्सा रिकॉर्ड सहमति',
    health_data: 'स्वास्थ्य डेटा सहमति',
    financial_data: 'वित्तीय डेटा सहमति',
    banking: 'बैंकिंग सहमति',
    analytics: 'विश्लेषिकी डेटा',
    biometric_data: 'बायोमेट्रिक डेटा सहमति',
    location_data: 'स्थान डेटा सहमति',
    employee_consent: 'कर्मचारी सहमति',
    vendor_consent: 'विक्रेता (वेंडर) सहमति',
    customer_consent: 'ग्राहक सहमति',
    newsletter: 'न्यूज़लेटर सदस्यता',
    promotional: 'प्रचार संदेश',
    general_consent: 'सामान्य सहमति',
  },
  mr: {
    terms_and_conditions: 'अटी आणि शर्ती',
    terms_conditions: 'अटी आणि शर्ती',
    terms_of_service: 'सेवा अटी',
    privacy_policy: 'गोपनीयता धोरण',
    cookie_policy: 'कुकी धोरण',
    cookies: 'कुकी संमती',
    data_sharing: 'डेटा सामायिकरण',
    third_party_sharing: 'तृतीय पक्ष डेटा सामायिकरण',
    marketing: 'विपणन (मार्केटिंग) संमती',
    marketing_communications: 'विपणन संवाद',
    medical_records: 'वैद्यकीय नोंदी संमती',
    health_data: 'आरोग्य डेटा संमती',
    financial_data: 'आर्थिक डेटा संमती',
    banking: 'बँकिंग संमती',
    analytics: 'विश्लेषण डेटा',
    biometric_data: 'बायोमेट्रिक डेटा संमती',
    location_data: 'स्थान डेटा संमती',
    employee_consent: 'कर्मचारी संमती',
    vendor_consent: 'व्हेंडर संमती',
    customer_consent: 'ग्राहक संमती',
    newsletter: 'न्यूजलेटर सदस्यता',
    promotional: 'प्रचारात्मक संदेश',
    general_consent: 'सामान्य संमती',
  },
  gu: {
    terms_and_conditions: 'નિયમો અને શરતો',
    terms_conditions: 'નિયમો અને શરતો',
    terms_of_service: 'સેવાની શરતો',
    privacy_policy: 'ગોપનીયતા નીતિ',
    cookie_policy: 'કૂકી નીતિ',
    cookies: 'કૂકી સંમતિ',
    data_sharing: 'ડેટા શેરિંગ',
    third_party_sharing: 'તૃતીય પક્ષ ડેટા શેરિંગ',
    marketing: 'માર્કેટિંગ સંમતિ',
    marketing_communications: 'માર્કેટિંગ વાર્તાલાપ',
    medical_records: 'મેડિકલ રેકોર્ડ્સ સંમતિ',
    health_data: 'આરોગ્ય ડેટા સંમતિ',
    financial_data: 'નાણાકીય ડેટા સંમતિ',
    banking: 'બેંકિંગ સંમતિ',
    analytics: 'એનાલિટિક્સ ડેટા',
    biometric_data: 'બાયોમેટ્રિક ડેટા સંમતિ',
    location_data: 'સ્થાન ડેટા સંમતિ',
    employee_consent: 'કર્મચારી સંમતિ',
    vendor_consent: 'વેન્ડર સંમતિ',
    customer_consent: 'ગ્રાહક સંમતિ',
    newsletter: 'ન્યૂઝલેટર સબ્સ્ક્રિપ્શન',
    promotional: 'પ્રચાર સંદેશાઓ',
    general_consent: 'સામાન્ય સંમતિ',
  },
  ta: {
    terms_and_conditions: 'விதிகள் மற்றும் நிபந்தனைகள்',
    terms_conditions: 'விதிகள் மற்றும் நிபந்தனைகள்',
    terms_of_service: 'சேவை விதிகள்',
    privacy_policy: 'தனியுரிமைக் கொள்கை',
    cookie_policy: 'குக்கி கொள்கை',
    cookies: 'குக்கி ஒப்புதல்',
    data_sharing: 'தரவு பகிர்வு',
    third_party_sharing: 'மூன்றாம் தரப்பு தரவு பகிர்வு',
    marketing: 'சந்தைப்படுத்தல் ஒப்புதல்',
    marketing_communications: 'சந்தைப்படுத்தல் தகவல்தொடர்புகள்',
    medical_records: 'மருத்துவ பதிவுகள் ஒப்புதல்',
    health_data: 'சுகாதார தரவு ஒப்புதல்',
    financial_data: 'நிதித் தரவு ஒப்புதல்',
    banking: 'வங்கி ஒப்புதல்',
    analytics: 'பகுப்பாய்வு தரவு',
    biometric_data: 'பயோமெட்ரிக் தரவு ஒப்புதல்',
    location_data: 'இருப்பிடத் தரவு ஒப்புதல்',
    employee_consent: 'ஊழியர் ஒப்புதல்',
    vendor_consent: 'விற்பனையாளர் ஒப்புதல்',
    customer_consent: 'வாடிக்கையாளர் ஒப்புதல்',
    newsletter: 'செய்திமடல் சந்தா',
    general_consent: 'பொது ஒப்புதல்',
  },
  te: {
    terms_and_conditions: 'నిబంధనలు మరియు షరతులు',
    terms_conditions: 'నిబంధనలు మరియు షరతులు',
    terms_of_service: 'సేవా నిబంధనలు',
    privacy_policy: 'గోప్యతా విధానం',
    cookie_policy: 'కుకీ విధానం',
    cookies: 'కుకీ సమ్మతి',
    data_sharing: 'డేటా భాగస్వామ్యం',
    third_party_sharing: 'మూడవ పక్ష డేటా భాగస్వామ్యం',
    marketing: 'మార్కెటింగ్ సమ్మతి',
    marketing_communications: 'మార్కెటింగ్ సమాచార మార్పిడి',
    medical_records: 'వైద్య నివేదికల సమ్మతి',
    health_data: 'ఆరోగ్య డేటా సమ్మతి',
    financial_data: 'ఆర్థిక డేటా సమ్మతి',
    banking: 'బ్యాంకింగ్ సమ్మతి',
    analytics: 'విశ్లేషణ డేటా',
    biometric_data: 'బయోమెట్రిక్ డేటా సమ్మతి',
    location_data: 'ప్రాంతపు డేటా సమ్మతి',
    employee_consent: 'ఉద్యోగి సమ్మతి',
    vendor_consent: 'వెండర్ సమ్మతి',
    customer_consent: 'వినియోగదారుని సమ్మతి',
    newsletter: 'న్యూస్‌లెటర్ చందా',
    general_consent: 'సాధారణ సమ్మతి',
  },
  kn: {
    terms_and_conditions: 'ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು',
    terms_conditions: 'ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು',
    privacy_policy: 'ಗೌಪ್ಯತಾ ನೀತಿ',
    cookie_policy: 'ಕುಕಿ ನೀತಿ',
    cookies: 'ಕುಕಿ ಸಮ್ಮತಿ',
    data_sharing: 'ಡೇಟಾ ಹಂಚಿಕೆ',
    marketing: 'ಮಾರ್ಕೆಟಿಂಗ್ ಸಮ್ಮತಿ',
    medical_records: 'ವೈದ್ಯಕೀಯ ದಾಖಲೆಗಳ ಸಮ್ಮತಿ',
    health_data: 'ಆರೋಗ್ಯ ಡೇಟಾ ಸಮ್ಮತಿ',
    financial_data: 'ಹಣಕಾಸು ಡೇಟಾ ಸಮ್ಮತಿ',
    banking: 'ಬ್ಯಾಂಕಿಂಗ್ ಸಮ್ಮತಿ',
    analytics: 'ವಿಶ್ಲೇಷಣೆ ಡೇಟಾ',
  },
  ml: {
    terms_and_conditions: 'വ്യവസ്ഥകളും നിബന്ധനകളും',
    terms_conditions: 'വ്യവസ്ഥകളും നിബന്ധനകളും',
    privacy_policy: 'സ്വകാര്യതാ നയം',
    cookie_policy: 'കുക്കി നയം',
    cookies: 'കുക്കി സമ്മതം',
    data_sharing: 'ഡാറ്റ പങ്കിടൽ',
    marketing: 'മാർക്കറ്റിംഗ് സമ്മതം',
    medical_records: 'മെഡിക്കൽ റെക്കോർഡുകൾ സമ്മതം',
    financial_data: 'ധനകാര്യ ഡാറ്റ സമ്മതം',
    banking: 'ബാങ്കിംഗ് സമ്മതം',
  },
  bn: {
    terms_and_conditions: 'শর্তাবলী',
    terms_conditions: 'শর্তাবলী',
    privacy_policy: 'গোপনীয়তা নীতি',
    cookie_policy: 'কুকি নীতি',
    cookies: 'কুকি সম্মতি',
    data_sharing: 'ডাটা শেয়ারিং',
    marketing: 'মার্কেটিং সম্মতি',
    medical_records: 'চিকিৎসা সংক্রান্ত তথ্য সম্মতি',
    financial_data: 'আর্থিক তথ্য সম্মতি',
    banking: 'ব্যাংকিং সম্মতি',
  },
  pa: {
    terms_and_conditions: 'ਨਿਯਮ ਅਤੇ ਸ਼ਰਤਾਂ',
    terms_conditions: 'ਨਿਯਮ ਅਤੇ ਸ਼ਰਤਾਂ',
    privacy_policy: 'ਗੋਪਨੀਯਤਾ ਨੀਤੀ',
    cookie_policy: 'ਕੂਕੀ ਨੀਤੀ',
    cookies: 'ਕੂਕੀ ਸਹਿਮਤੀ',
    data_sharing: 'ਡੇਟਾ ਸਾਂਝਾਕਰਨ',
    marketing: 'ਮਾਰਕੀਟਿੰਗ ਸਹਿਮਤੀ',
    medical_records: 'ਮੈਡੀਕਲ ਰਿਕਾਰਡ ਸਹਿਮਤੀ',
    financial_data: 'ਵਿੱਤੀ ਡੇਟਾ ਸਹਿਮਤੀ',
    banking: 'ਬੈਂਕਿੰਗ ਸਹਿਮਤੀ',
  },
  or: {
    terms_and_conditions: 'ନିୟମ ଏବଂ ସର୍ତ୍ତାବଳୀ',
    terms_conditions: 'ନିୟମ ଏବଂ ସର୍ତ୍ତାବଳୀ',
    privacy_policy: 'ଗୋପନୀୟତା ନୀତି',
    cookie_policy: 'କୁକି ନୀତି',
    data_sharing: 'ଡାଟା ସେୟାରିଂ',
    marketing: 'ମାର୍କେଟିଂ ସମ୍ମତି',
  },
  ur: {
    terms_and_conditions: 'شرائط و ضوابط',
    terms_conditions: 'شرائط و ضوابط',
    privacy_policy: 'پرائیویسی پالیسی',
    cookie_policy: 'کوکی پالیسی',
    data_sharing: 'ڈیٹا شیئرنگ',
    marketing: 'مارکیٹنگ کی رضامندی',
  },
  as: {
    terms_and_conditions: 'নিয়ম আৰু চৰ্তসমূহ',
    terms_conditions: 'নিয়ম আৰু চৰ্তসমূহ',
    privacy_policy: 'গোপনীয়তা নীতি',
    cookie_policy: 'কুকি নীতি',
    data_sharing: 'তথ্য ভাগ-বাটোৱাৰা',
    marketing: 'মাৰ্কেটিং সন্মতি',
  }
};

const WORD_TRANSLATIONS = {
  hi: {
    terms: 'नियम',
    conditions: 'शर्तें',
    term: 'नियम',
    condition: 'शर्त',
    privacy: 'गोपनीयता',
    policy: 'नीति',
    notice: 'सूचना',
    cookie: 'कुकी',
    cookies: 'कुकीज़',
    consent: 'सहमति',
    consents: 'सहमति',
    data: 'डेटा',
    sharing: 'साझाकरण',
    share: 'साझा',
    transfer: 'हस्तांतरण',
    processing: 'प्रसंस्करण',
    third: 'तृतीय',
    party: 'पक्ष',
    marketing: 'विपणन (मार्केटिंग)',
    communication: 'संचार',
    communications: 'संचार',
    email: 'ईमेल',
    emails: 'ईमेल',
    message: 'संदेश',
    messages: 'संदेश',
    sms: 'एसएमएस',
    alert: 'अलर्ट',
    alerts: 'अलर्ट',
    promotional: 'प्रचार',
    promotion: 'प्रचार',
    medical: 'चिकित्सा',
    health: 'स्वास्थ्य',
    record: 'रिकॉर्ड',
    records: 'रिकॉर्ड',
    financial: 'वित्तीय',
    finance: 'वित्त',
    banking: 'बैंकिंग',
    bank: 'बैंक',
    payment: 'भुगतान',
    credit: 'क्रेडिट',
    account: 'खाता',
    analytics: 'विश्लेषिकी',
    usage: 'उपयोग',
    biometric: 'बायोमेट्रिक',
    biometrics: 'बायोमेट्रिक्स',
    location: 'स्थान',
    tracking: 'ट्रैकिंग',
    employee: 'कर्मचारी',
    vendor: 'विक्रेता (वेंडर)',
    customer: 'ग्राहक',
    user: 'उपयोगकर्ता',
    form: 'फॉर्म',
    verification: 'सत्यापन',
    photo: 'फोटो',
    video: 'वीडियो',
    audio: 'ऑडियो',
    call: 'कॉल',
    newsletter: 'न्यूज़लेटर',
    subscription: 'सदस्यता',
    general: 'सामान्य',
    aadhaar: 'आधार',
    pan: 'पैन',
    kyc: 'केवाईसी',
    service: 'सेवा',
    services: 'सेवाएं',
    hotel: 'होटल',
    room: 'कमरा',
    booking: 'बुकिंग',
    reservation: 'आरक्षण',
    guest: 'अतिथि',
    visitor: 'आगंतुक',
    stay: 'ठहराव',
    travel: 'यात्रा',
    flight: 'उड़ान',
    tour: 'टूर',
    transport: 'परिवहन',
    vehicle: 'वाहन',
    driver: 'ड्राइवर',
    registration: 'पंजीकरण',
    profile: 'प्रोफ़ाइल',
    personal: 'व्यक्तिगत',
    and: 'और',
    or: 'या',
    of: 'की',
    for: 'के लिए',
    with: 'के साथ',
  },
  mr: {
    terms: 'अटी',
    conditions: 'शर्ती',
    privacy: 'गोपनीयता',
    policy: 'धोरण',
    notice: 'सूचना',
    cookie: 'कुकी',
    cookies: 'कुकीज',
    consent: 'संमती',
    data: 'डेटा',
    sharing: 'सामायिकरण',
    marketing: 'विपणन',
    medical: 'वैद्यकीय',
    health: 'आरोग्य',
    record: 'नोंदी',
    records: 'नोंदी',
    financial: 'आर्थिक',
    banking: 'बँकिंग',
    employee: 'कर्मचारी',
    vendor: 'व्हेंडर',
    customer: 'ग्राहक',
    user: 'वापरकर्ता',
    form: 'फॉर्म',
    service: 'सेवा',
    and: 'आणि',
  },
  gu: {
    terms: 'નિયમો',
    conditions: 'શરતો',
    privacy: 'ગોપનીયતા',
    policy: 'નીતિ',
    notice: 'સૂચના',
    cookie: 'કૂકી',
    cookies: 'કૂકીઝ',
    consent: 'સંમતિ',
    data: 'ડેટા',
    sharing: 'શેરિંગ',
    marketing: 'માર્કેટિંગ',
    medical: 'મેડિકલ',
    health: 'આરોગ્ય',
    record: 'રેકોર્ડ',
    records: 'રેકોર્ડ્સ',
    financial: 'નાણાકીય',
    banking: 'બેંકિંગ',
    employee: 'કર્મચારી',
    vendor: 'વેન્ડર',
    customer: 'ગ્રાહક',
    user: 'વપરાશકર્તા',
    service: 'સેવા',
    and: 'અને',
  },
  ta: {
    terms: 'விதிகள்',
    conditions: 'நிபந்தனைகள்',
    privacy: 'தனியுரிமை',
    policy: 'கொள்கை',
    consent: 'ஒப்புதல்',
    data: 'தரவு',
    sharing: 'பகிர்வு',
    marketing: 'சந்தைப்படுத்தல்',
    medical: 'மருத்துவ',
    financial: 'நிதி',
    banking: 'வங்கி',
    employee: 'ஊழியர்',
    customer: 'வாடிக்கையாளர்',
    and: 'மற்றும்',
  },
  te: {
    terms: 'నిబంధనలు',
    conditions: 'షరతులు',
    privacy: 'గోప్యతా',
    policy: 'విధానం',
    consent: 'సమ్మతి',
    data: 'డేటా',
    sharing: 'భాగస్వామ్యం',
    marketing: 'మార్కెటింగ్',
    medical: 'వైద్య',
    financial: 'ఆర్థిక',
    banking: 'బ్యాంకింగ్',
    employee: 'ఉద్యోగి',
    customer: 'వినియోగదారుని',
    and: 'మరియు',
  }
};

const translateWordsFallback = (text, lang) => {
  if (!text || lang === 'en' || !WORD_TRANSLATIONS[lang]) return null;
  const words = String(text).trim().split(/[\s_-]+/);
  if (!words.length) return null;

  let hasTranslation = false;
  const translatedWords = words.map(w => {
    const cleanWord = w.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (WORD_TRANSLATIONS[lang][cleanWord]) {
      hasTranslation = true;
      return WORD_TRANSLATIONS[lang][cleanWord];
    }
    return w;
  });

  if (hasTranslation) {
    return translatedWords.join(' ');
  }
  return null;
};

const getTranslatedConsentTypeLabel = (typeKey, languageCode, itemObj = null) => {
  if (!typeKey && (!itemObj || !itemObj.title)) return '';
  const lang = normalizeLangCode(languageCode);

  const primaryKey = String(typeKey || (itemObj && (itemObj.consent_type || itemObj.type || itemObj.name)) || '').trim();

  // If English, return formatted English label
  if (lang === 'en') {
    const rawLabel = (itemObj && (itemObj.label || itemObj.name || itemObj.title)) || primaryKey;
    if (typeof rawLabel === 'string' && rawLabel.includes('(')) {
      const mainPart = rawLabel.split('(')[0].trim();
      if (mainPart) return formatDisplayLabel(mainPart);
    }
    return formatDisplayLabel(rawLabel);
  }

  // 1. If itemObj.title/label/name or primaryKey already contains non-ASCII characters (e.g. Hindi Devanagari script), return it directly!
  if (itemObj) {
    const itemLang = normalizeLangCode(itemObj.language_code || itemObj.language || itemObj.languageName || itemObj.language_name);
    const localizedLabel = itemObj.label || itemObj.localized_label || itemObj.localizedLabel || itemObj.title;
    if (itemLang === lang && localizedLabel && /[^\x00-\x7F]/.test(String(localizedLabel))) {
      // if (itemLang === lang && localizedLabel && String(localizedLabel).trim() !== formatDisplayLabel(primaryKey)) {
      return String(localizedLabel).trim();
    }
    if (itemObj.title && typeof itemObj.title === 'string' && /[^\x00-\x7F]/.test(itemObj.title)) {
      return itemObj.title.trim();
    }
    if (itemObj.label && typeof itemObj.label === 'string' && /[^\x00-\x7F]/.test(itemObj.label)) {
      return itemObj.label.trim();
    }
  }
  if (primaryKey && /[^\x00-\x7F]/.test(primaryKey)) {
    return primaryKey.trim();
  }

  // 2. Check parenthetical text in label/title (e.g. "hotel_consent (होटल से संबंधित संचार के)" or "Terms (नियम और शर्तें)")
  const rawLabelStr = (itemObj && (itemObj.label || itemObj.title || itemObj.name || itemObj.sub_category)) || primaryKey;
  if (typeof rawLabelStr === 'string' && rawLabelStr.includes('(')) {
    const match = rawLabelStr.match(/\(([^)]+)\)/);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  // 3. Check itemObj parsed metadata / data for explicit matching title/label
  if (itemObj) {
    try {
      const d = typeof itemObj.data === 'string' ? JSON.parse(itemObj.data) : (itemObj.data || (typeof itemObj.consent_metadata === 'string' ? JSON.parse(itemObj.consent_metadata) : itemObj.consent_metadata));
      if (d && (d.title || d.sub_category || d.parent_consent_label)) {
        const titleText = (d.title || d.sub_category || d.parent_consent_label).trim();
        if (titleText && /[^\x00-\x7F]/.test(titleText)) {
          return titleText;
        }
        const itemLang = String(d.language_code || d.language || itemObj.language_code || itemObj.language || '').toLowerCase();
        if (itemLang === lang && titleText) {
          return titleText;
        }
      }
    } catch (e) { }
  }

  // 4. Exact dictionary lookup in CONSENT_TYPE_TRANSLATIONS[lang]
  const keysToTry = [];
  if (primaryKey) {
    // keysToTry.push(primarykey.toLowerCase().replace(/[^a-z0-9_])/g, '_').replace(/_+/g, '_'));
    const normalizedPrimary = primaryKey.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_');
    keysToTry.push(normalizedPrimary);
    keysToTry.push(normalizedPrimary.replace(/_consent$/, ''));
    keysToTry.push(normalizedPrimary.replace(/_consent$/, 'ing'));
    keysToTry.push(formatDisplayLabel(primaryKey).toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_'));
  }
  if (itemObj && itemObj.title && typeof itemObj.title === 'string') {
    keysToTry.push(itemObj.title.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_'));
    keysToTry.push(formatDisplayLabel(itemObj.title).toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_'));
  }
  if (itemObj && itemObj.label && typeof itemObj.label === 'string') {
    keysToTry.push(itemObj.label.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_'));
    keysToTry.push(formatDisplayLabel(itemObj.label).toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_'));
  }

  if (CONSENT_TYPE_TRANSLATIONS[lang]) {
    for (const k of keysToTry) {
      if (k && CONSENT_TYPE_TRANSLATIONS[lang][k]) {
        return CONSENT_TYPE_TRANSLATIONS[lang][k];
      }
    }
  }

  // 5. Word-by-word translation fallback
  const wordFallback = translateWordsFallback(primaryKey, lang) || (itemObj && itemObj.title ? translateWordsFallback(itemObj.title, lang) : null);
  if (wordFallback) {
    return wordFallback;
  }

  // 6. Fallback to formatted English label
  return formatDisplayLabel(primaryKey);
};

const normalizeConsentTypeKey = (value) => String(value || '')
  .trim()
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .toLowerCase();

const optionMatchesConsentType = (optionValue, targetValue) => {
  const optionKey = normalizeConsentTypeKey(optionValue);
  const targetKey = normalizeConsentTypeKey(targetValue);
  return !!optionKey && !!targetKey && optionKey === targetKey;
};

const findConsentTypeOption = (options, value) => {
  const target = normalizeConsentTypeKey(value);
  if (!Array.isArray(options) || !target) return null;
  return options.find(item => {
    const optionValue = item?.consent_type || item?.type || item?.name || item?.id || '';
    return normalizeConsentTypeKey(optionValue) === target;
  }) || null;
};

const getConsentTypeOptionLabel = (item, languageCode) => {
  const lang = normalizeLangCode(languageCode);
  const typeValue = item?.consent_type || item?.type || item?.name || item?.id || '';
  const payload = parseConsentPayload(item?.data) || parseConsentPayload(item?.consent_metadata);
  const itemLanguage = normalizeLangCode(
    item?.language_code || item?.language || item?.language_name ||
    payload?.language_code || payload?.language || payload?.language_name
  );
  const localizedLabel = item?.label || item?.localized_label || item?.localizedLabel ||
    item?.title || payload?.title || payload?.label || payload?.parent_consent_label;

  if (localizedLabel && itemLanguage === lang) return String(localizedLabel).trim();
  if (localizedLabel && /[^\x00-\x7F]/.test(String(localizedLabel))) return String(localizedLabel).trim();
  return getTranslatedConsentTypeLabel(typeValue, lang, {
    ...item,
    label: localizedLabel || typeValue,
  });
};

const normalizeConsentOptionIdentity = (item) => {
  const raw = item?.consent_type || item?.type || item?.name || item?.id || '';
  return String(raw).trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
};

const dedupeConsentTypeOptions = (items, selectedLanguage) => {
  const selectedLang = normalizeLangCode(selectedLanguage || 'en');
  const deduped = new Map();

  for (const item of Array.isArray(items) ? items : []) {
    const value = item?.consent_type || item?.type || item?.name || item?.id || '';
    const normalized = normalizeConsentOptionIdentity({ consent_type: value });
    if (!normalized) continue;

    const existing = deduped.get(normalized);
    const incomingLang = normalizeLangCode(item?.language_code || item?.language || item?.language_name || 'en');
    const currentLang = normalizeLangCode(existing?.language_code || existing?.language || existing?.language_name || 'en');
    const incomingIsTarget = incomingLang === selectedLang;
    const currentIsTarget = currentLang === selectedLang;
    const incomingLabel = String(item?.label || item?.title || item?.localized_label || item?.localizedLabel || value || '').trim();
    const currentLabel = String(existing?.label || existing?.title || existing?.localized_label || existing?.localizedLabel || existing?.consent_type || '').trim();

    const shouldReplace = !existing ||
      (incomingIsTarget && !currentIsTarget) ||
      (!incomingIsTarget && currentIsTarget ? false : (incomingLabel.length > currentLabel.length && !currentIsTarget));

    if (!existing || shouldReplace) {
      deduped.set(normalized, { ...item, consent_type: value, type: value, name: value, label: incomingLabel || value });
    }
  }

  // return Array.from(deduped.values());
  const uniqueByLabel = new Map();
  for (const item of deduped.values()) {
    const label = getConsentTypeOptionLabel(item, selectedLang)
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
    if (!label) continue;
    const existing = uniqueByLabel.get(label);
    const itemLang = normalizeLangCode(item?.language_code || item?.language || item?.language_name || 'en');
    const existingLang = normalizeLangCode(existing?.language_code || existing?.language || existing?.language_name || 'en');
    if (!existing || (itemLang === selectedLang && existingLang !== selectedLang)) {
      uniqueByLabel.set(label, item);
    }
  }
  return Array.from(uniqueByLabel.values());
};

const mapSubConsentOptions = (rows, selectedLanguage) => {
  const language = normalizeLangCode(selectedLanguage || 'en');
  const optionsById = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    let metadata = parseConsentPayload(row?.data) || row?.parsed || {};
    if (typeof metadata === 'string') metadata = parseConsentPayload(metadata) || {};
    const variant = metadata?.kind === 'multilingual_consent' || metadata?.is_multilingual_variant || row?.parent_sub_consent_id != null;
    const rawLanguage = row?.language_code || metadata?.language_code || metadata?.language || metadata?.language_name || '';
    const rowLanguage = normalizeLangCode(rawLanguage || 'en');
    if (rowLanguage !== language) continue;
    const baseId = row?.parent_sub_consent_id || metadata?.parent_sub_consent_id || metadata?.parentSubConsentId || row?.sub_consent_id || row?.id;
    if (baseId == null) continue;
    const key = `${String(baseId)}|${rowLanguage}`;
    const existing = optionsById.get(key);
    if (existing && !(variant && !existing.is_multilingual_variant)) continue;
    let message = row?.message || row?.consent_message || metadata?.consent_message || '';
    const parsedMessage = parseConsentPayload(message);
    if (parsedMessage) message = parsedMessage.consent_message || parsedMessage.content || message;
    optionsById.set(key, {
      id: baseId,
      sub_consent_id: baseId,
      variant_id: variant ? row.id : null,
      consent_id: row?.consent_id || row?.parent_consent_id || metadata?.parent_consent_id || metadata?.consent_id || null,
      parent_consent_id: row?.parent_consent_id || row?.consent_id || metadata?.parent_consent_id || metadata?.consent_id || null,
      parent_sub_consent_id: variant ? baseId : null,
      consent_type: row?.consent_type || metadata?.parent_consent_type || metadata?.parent_type || row?.name || '',
      label: metadata?.title || metadata?.sub_consent_name || row?.sub_category || row?.name || String(baseId),
      message,
      language_code: rowLanguage || language,
      language_name: row?.language_name || metadata?.language_name || metadata?.language || language,
      version: row?.version || metadata?.version || metadata?.consent_version || '',
      status: row?.status || metadata?.status || 'under_process',
      is_multilingual_variant: variant,
      raw: row,
    });
  }
  return Array.from(optionsById.values());
};

const getTranslatedSubConsentLabel = (subObj, languageCode) => {
  if (!subObj) return '';
  const lang = normalizeLangCode(languageCode);

  const rawLabel = subObj.label || subObj.sub_category || subObj.name || String(subObj.id || '');

  // If English, return English portion if parenthetical
  if (lang === 'en') {
    if (typeof rawLabel === 'string' && rawLabel.includes('(')) {
      const mainPart = rawLabel.split('(')[0].trim();
      if (mainPart) return mainPart;
    }
    return rawLabel;
  }

  // 1. If label contains parenthetical text e.g., "Terms and Conditions (नियम और शर्तें)"
  if (typeof rawLabel === 'string' && rawLabel.includes('(')) {
    const match = rawLabel.match(/\(([^)]+)\)/);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  // 2. Check parsed data JSON for matching language
  try {
    const d = typeof subObj.data === 'string' ? JSON.parse(subObj.data) : subObj.data;
    if (d && d.title) {
      const dLang = String(d.language_code || d.language || '').toLowerCase();
      if (!dLang || dLang === lang || /[^\x00-\x7F]/.test(d.title)) {
        return d.title;
      }
    }
  } catch (e) { }

  // 3. Check translation dictionary
  const keysToTry = [
    String(rawLabel).trim().toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_'),
    formatDisplayLabel(rawLabel).toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_')
  ];

  if (CONSENT_TYPE_TRANSLATIONS[lang]) {
    for (const k of keysToTry) {
      if (k && CONSENT_TYPE_TRANSLATIONS[lang][k]) {
        return CONSENT_TYPE_TRANSLATIONS[lang][k];
      }
    }
  }

  // 4. Word-by-word translation fallback
  const wordFallback = translateWordsFallback(rawLabel, lang);
  if (wordFallback) {
    return wordFallback;
  }

  // 5. If rawLabel contains non-ASCII characters, return it
  if (typeof rawLabel === 'string' && /[^\x00-\x7F]/.test(rawLabel)) {
    return rawLabel.trim();
  }

  return rawLabel;
};

function UserPanel(props) {
  const { user, initialTab, initialRoleFilter, onMenu } = props || {};
  const userPanelOpenedAt = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const [selectedUploadSubCategory, setSelectedUploadSubCategory] = useState('');
  const [selectedUploadConsentType, setSelectedUploadConsentType] = useState('');
  const [selectedUploadConsentMessage, setSelectedUploadConsentMessage] = useState('');
  // const [selectUploadSubCategory,setSelectedUploadSubCategory] = useState('');
  const [selectedUploadLanguage, setSelectedUploadLanguage] = useState('en');
  // measured header offset used to vertically adjust modal so header doesn't cut it
  const [modalHeaderOffset, setModalHeaderOffset] = useState(0);
  const [showSampleCSV, setShowSampleCSV] = useState(false);
  const [showCSVConfirm, setShowCSVConfirm] = useState(false);
  const [pendingCSVFile, setPendingCSVFile] = useState(null);
  const [csvUploadMessage, setCsvUploadMessage] = useState(null);
  const [csvUploadError, setCsvUploadError] = useState(null);
  const [csvTransferActive, setCsvTransferActive] = useState(false);
  const [csvUploadProgress, setCsvUploadProgress] = useState(0);
  const [csvLiveStats, setCsvLiveStats] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  // responsive helper: track mobile/tablet viewport so components render responsively
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 900 : false);

  useEffect(() => {
    if (!csvTransferActive || typeof window === 'undefined') return undefined;
    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = 'Your CSV is still uploading. Please keep this page open.';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [csvTransferActive]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    // initialize
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Ensure modals/drawers are closed when this component mounts or when tab changes
  useEffect(() => {
    setRevokeModalOpen(false);
    setSelectedConsent(null);
  }, [initialTab]);

  const sampleCSVHeaders = [
    'username', 'category(Adult/Minor)', 'email', 'phone', 'address', 'dob', 'group', 'location', 'department', 'parent_Name', 'parent_Email', 'parent_Phone', 'data_Type_Consent'
  ];
  const sampleCSVData = [
    ['# INSTRUCTIONS', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['# 1. Group must be one of: Employees | Vendors & Suppliers | Partners | Customers  (if missing or invalid it defaults to employee)', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['# 2. Category: "Adult" or "Minor" (defaults to Adult). For Minor, DOB and parent details (parentName, parentEmail, parentPhone) are REQUIRED.', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['# 3. For Adult, DOB is optional. If DOB is provided for Adult, age must be 18 or above (minor DOB not allowed for Adult).', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['# 4. data_Type_Consent: Enter comma-separated data types (e.g. Aadhaar, PAN, Passport, Bank AC)', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['# 5.John Doe', 'Adult', 'john@example.com', '9876543210', '123 Main St', '1995-05-15', 'Employees', 'Bengaluru', 'HR', '', '', '', 'Aadhaar,PAN'],
    ['# 6.Jane Minor', 'Minor', 'minor@example.com', '9876543211', '456 Park Ave', '2012-08-20', 'Vendors & Suppliers', 'Mumbai', 'Sales', 'Robert Minor', 'robert@example.com', '9876543212', 'Passport']
  ];

  const getSampleCSV = () => {

    return sampleCSVHeaders.join(',') + '\n' + sampleCSVData.map(row => row.join(',')).join('\n');
  };
  const downloadSampleCSV = () => {
    const csv = getSampleCSV();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_consent_upload.csv';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
  };
  const GuideModal = ({ open, onClose, steps }) => {
    useEffect(() => {
      if (!open) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }, [open]);

    if (!open) return null;

    return ReactDOM.createPortal(
      <div
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        style={{
          position: 'fixed', inset: 0, zIndex: 2147483647,
          background: 'rgba(15,23,42,0.55)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: '32px 16px', overflowY: 'auto',
        }}
      >
        <div style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640,
          border: '1px solid #e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>📖 Page Guide</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: '#94a3b8', fontSize: 18, lineHeight: 1 }}>✕</button>
          </div>
          <div style={{ padding: '18px 20px', maxHeight: '75vh', overflowY: 'auto' }}>
            {steps.map((s, i) => (
              <div key={i} style={{
                display: 'flex', gap: 12, padding: '12px 0',
                borderBottom: i < steps.length - 1 ? '1px solid #f1f5f9' : 'none',
              }}>
                <div style={{
                  flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                  background: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: 12,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{i + 1}</div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>{s.title}</div>
                  <div style={{ fontSize: 12.5, color: '#4b5563', lineHeight: 1.5 }}>{s.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>,
      document.body
    );
  };
  const SampleCSVModal = ({ visible, onClose, downloadSampleCSV, csvFileInputRef, modalHeaderOffset }) => {
    useEffect(() => {
      if (!visible) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }, [visible]);

    if (!visible) return null;

    const overlayStyle = {
      position: 'fixed', left: 0, top: 0, right: 0, bottom: 0,
      background: 'rgba(2,6,23,0.45)', backdropFilter: 'blur(4px)', zIndex: 2147483647,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pointerEvents: 'auto', padding: 12
    };
    const modalStyle = {
      // place modal below header so its top controls are never hidden
      marginTop: Math.max(12, Math.round(modalHeaderOffset || 0) + 12),
      position: 'relative',
      background: '#fff', padding: 22, borderRadius: 12, maxWidth: 720, width: '92vw',
      boxShadow: '0 28px 68px rgba(2,6,23,0.22)', maxHeight: `calc(100vh - ${Math.max(120, (modalHeaderOffset || 0) + 48)}px)`, overflowY: 'auto', transition: 'transform 220ms cubic-bezier(.2,.9,.2,1),opacity 180ms'
    };

    const content = (
      <div className="modal-overlay" style={overlayStyle} onClick={onClose}>
        <div className="modal" role="dialog" aria-modal="true" style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #eef2f7' }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Bulk Consent CSV</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>Download the sample CSV, fill it with your data, then upload using the button below.</div>
            </div>
            <button onClick={onClose} aria-label="Close modal" style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b', padding: 6 }}>✖</button>
          </div>

          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: 13, color: '#475569', background: '#f8fafc', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', textAlign: 'center', maxWidth: 640 }}>
              The sample preview is hidden for a compact upload flow. Use the buttons below to download the template or upload your completed CSV.
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexDirection: (isMobile ? 'column' : 'row'), width: '100%' }}>
              <button onClick={() => {
                try {
                  // Close the modal first so the dialog does not sit behind it
                  try { onClose && onClose(); } catch (e) { }
                  // small delay to allow modal to unmount before opening dialog
                  setTimeout(() => {
                    try {
                      // const html =`
                      const popupHtml = `
                        <div style="text-align:left">
                            <h3>CSV Format</h3>
                            <ul style="text-align:left;margin-bottom:12px">
                                <li><strong>Required fields:</strong> username, category, email, phone, dob, address,location, department group, preferred_languages, Data_Type_consent</li>
                                <li><strong>Phone:</strong> Must be 10 digits (example: 9876543214)</li>
                                <li><strong>Group:</strong> Must be one of <b>Employees | Vendors & Suppliers | Partners| Customer </b>. If missing or invalid, it defaults to <b>employee</b>.</li>
                                <li><strong>Minor rule:</strong> If DOB indicates age under 18, then <b>parent_Name, parent_Email, parent_Phone</b> are required.</li>
                                <li>Rows with invalid phone numbers will be skipped and you will be prompted before upload.</li>
                            </ul>

                            <p>Click <b>"Download Sample CSV"</b> to get the template file.</p>
                        </div>
                        `;

                      const printHtml = `
                        <div style="text-align:left">
                            <h3>CSV Format</h3>

                            <ul style="text-align:left;margin-bottom:12px">
                                <li><strong>Required fields:</strong> username, category, email, phone, dob, address,location, department group, preferred_languages, Data_Type_consent</li>
                                <li><strong>Phone:</strong> Must be 10 digits (example: 9876543214)</li>
                                <li><strong>Group:</strong> Must be one of <b>Employees | Vendors & Suppliers | Partners| Customers </b>. If missing or invalid, it defaults to <b>employee</b>.</li>
                                <li><strong>Minor rule:</strong> If DOB indicates age under 18, then <b>parentName, parentEmail, parentPhone</b> are required.</li>
                                <li>Rows with invalid phone numbers will be skipped and you will be prompted before upload.</li>
                            </ul>

                            <p>Click <b>"Download Sample CSV"</b> to get the template file.</p>

                            <h4 style="margin-top:14px">CSV Columns (name & description)</h4>
                            <div style="overflow:auto;white-space:nowrap;margin-top:8px">
                              <table style="min-width:1000px;border-collapse:collapse;font-size:13px">
                                <thead>
                                  <tr>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6;width:35%">Field</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">Description / Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">username</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Full name of the user.</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">email</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">User email address (unique identifier).</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">phone</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">10 digit phone number (digits only).</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">address</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Postal address of the user.</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">dob</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Date of birth (prefer dd/mm/yyyy). Used to detect minors.</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">type</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Consent type (e.g. general, bank, patient).</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">consent</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Consent message/content (HTML is allowed).</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">entity</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Entity category: employee / vendor / partner / customer. Defaults to employee.</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">parentName</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Parent / guardian name (required if minor).</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">parentEmail</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Parent / guardian email (required if minor).</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">parentPhone</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Parent / guardian phone (required if minor).</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">preferredLanguages</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Preferred languages (comma-separated, e.g. English, Hindi).</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">locality</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Locality / city (used for location-specific consents). Use one of the dropdown values when uploading.</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">department</td><td style="padding:8px;border-bottom:1px solid #f2f2f２">Department name (e.g. Sales, HR). Values should match departments in the system.</td></tr>
                                  <tr><td style="padding:8px;border-bottom:1px solid #f2f2f2">data_Type_Consent</td><td style="padding:8px;border-bottom:1px solid #f2f2f2">Data types for consent (comma-separated, e.g. Aadhaar, PAN, Passport, Bank AC).</td></tr>
                                </tbody>
                              </table>
                            </div>
                            <h4 style="margin-top:14px">Sample CSV (header + one example row)</h4>
                            <div style="overflow:auto;white-space:nowrap;margin-top:8px">
                              <table style="min-width:1000px;border-collapse:collapse;font-size:13px">
                                <thead>
                                  <tr>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">username</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">email</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">phone</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">address</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">dob</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">group</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">parentName</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">parentEmail</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">parentPhone</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">preferredLanguages</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">location</th>
                                    <th style="text-align:left;padding:8px;border-bottom:1px solid #e6e6e6">department</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">John Doe</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">john@example.com</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">9876543210</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">123 Main St, City</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">01/01/1990</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">general</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">I consent to processing of my data.</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">employee</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2"></td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2"></td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2"></td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">English,Hindi</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">Mumbai</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">Sales</td>
                                  </tr>
                                  <tr>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">Jane Minor</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">jane.minor@example.com</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">9876501234</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">45 Oak Lane, Town</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">01/01/2010</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">general</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">Consent given by parent/guardian.</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">employee</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">Suresh Parent</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">suresh.parent@example.com</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">9876512345</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">English</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">Kolkata</td>
                                    <td style="padding:8px;border-bottom:1px solid #f2f2f2">Support</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                        </div>
                        `;

                      Swal.fire({ title: 'Bulk CSV instructions', html: popupHtml, icon: 'info', width: 700, showCancelButton: true, confirmButtonText: 'Download PDF', cancelButtonText: 'Cancel', reverseButtons: true }).then((res) => {
                        if (res && res.isConfirmed) {
                          try {
                            const w = window.open('', '_blank');

                            const content = `<!doctype html><html><head><meta charset="utf-8"><title>CSV Instructions</title><style>body{font-family:Arial,Helvetica,sans-serif;padding:24px;color:#111}h3{margin-top:0}ul{line-height:1.5}table{border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:left}</style></head><body>${printHtml}</body></html>`;
                            w.document.write(content);
                            w.document.close();
                            // give the new window a moment to render before printing
                            setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* ignore */ } }, 250);
                          } catch (e) {
                            try { alert('Unable to open printable view. See README for CSV instructions.'); } catch (_) { }
                          }
                        }
                      });
                    } catch (e) { try { alert('See README for CSV instructions'); } catch (_) { } }
                  }, 50);
                } catch (e) { try { alert('See README for CSV instructions'); } catch (_) { } }
              }} className="btn" style={{ background: '#fff', color: '#2563eb', border: '1px solid #2563eb', padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14 }}>❓ Help</button>

              <button onClick={downloadSampleCSV} className="btn btn-primary" style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14, width: isMobile ? '100%' : 'auto' }}>📥 Download Sample CSV</button>
              <button onClick={() => { onClose(); csvFileInputRef.current && csvFileInputRef.current.click(); }} className="btn" style={{ background: '#fff', color: '#2563eb', border: '1px solid #2563eb', padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14, width: isMobile ? '100%' : 'auto' }}>📤 Upload CSV File</button>
              <button onClick={onClose} className="btn" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14, width: isMobile ? '100%' : 'auto' }}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    );

    return ReactDOM.createPortal(content, document.body);
  };

  const UserLogsContent = () => {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);

    useEffect(() => {
      let mounted = true;
      const fetch = async () => {
        setLoading(true);
        try {
          // include tenant_id to ensure only current-tenant records are returned
          // const tenantId = (user && (user.tenant_id || user.tenantId || user.tenant)) ? (user.tenant_id || user.tenantId || user.tenant):  null;
          // const params = {limit :5000};
          const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
          let tenantId = (user && (user.tenant_id || user.tenantId || user.tenant)) ? (user.tenant_id || user.tenantId || user.tenant) : null;
          if (tenantId === null && token) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              tenantId = payload.tenant_id || payload.tenant || payload.tid || null;
            } catch (_) { }
          }
          const params = { limit: 500 };
          if (tenantId !== null) params.tenant_id = tenantId;
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const res = await axios.get(`${API_BASE}/user/consent`, { params, headers });
          // API returns { consents: [...], total: N } or older shape rows/array
          const data = res.data && (res.data.consents || res.data.rows || Array.isArray(res.data) && res.data) ? (res.data.consents || res.data.rows || res.data) : [];
          if (!mounted) return;
          setRows(Array.isArray(data) ? data : []);
        } catch (e) {
          setRows([]);
        } finally {
          if (mounted) setLoading(false);
        }
      };
      fetch();
      return () => { mounted = false; };
    }, []);

    if (loading) return <div style={{ padding: 24, textAlign: 'center' }}>Loading…</div>;

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #eef2f7' }}>
              <th style={{ padding: '8px 12px' }}>Username</th>
              <th style={{ padding: '8px 12px' }}>Email</th>
              <th style={{ padding: '8px 12px' }}>Consent Type</th>
              <th style={{ padding: '8px 12px' }}>Status</th>
              <th style={{ padding: '8px 12px' }}>Browser</th>
              <th style={{ padding: '8px 12px' }}>IP</th>
              <th style={{ padding: '8px 12px' }}>Expiry Count</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '8px 12px' }}>{r.username || r.user_name || '-'}</td>
                <td style={{ padding: '8px 12px' }}>{maskEmail(r.email || r.responder_email) || '-'}</td>
                <td style={{ padding: '8px 12px' }}>{r.type || r.consent_type || '-'}</td>
                <td style={{ padding: '8px 12px' }}>{r.status || r.user_action || r.response || '-'}</td>
                <td style={{ padding: '8px 12px' }}>{r.browser || (r.user_agent ? (String(r.user_agent).split(' ')[0]) : '-')}</td>
                <td style={{ padding: '8px 12px' }}>{r.ip_address || r.ip || r.client_ip || '-'}</td>
                <td style={{ padding: '8px 12px' }}>{(r.reminder_count !== undefined && r.reminder_count !== null) ? r.reminder_count : (r.expiry || 0)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>No logs found.</td></tr>}
          </tbody>
        </table>
      </div>
    );
  };

  const stripHtml = (html) => {
    if (!html) return '';
    try { return html.replace(/<[^>]*>?/gm, '').replace(/\s+/g, ' ').trim(); } catch (e) { return String(html); }
  };

  const getConsentHeading = (html) => {
    if (!html) return '';
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html || '', 'text/html');
      // prefer first <strong> or <b> or heading tags
      const strong = doc.querySelector('strong, b, h1, h2, h3, h4, h5, h6');
      if (strong && strong.textContent && strong.textContent.trim()) return strong.textContent.trim();
      // fallback: first paragraph
      const p = doc.querySelector('p');
      if (p && p.textContent && p.textContent.trim()) {
        const txt = p.textContent.trim();
        return txt.length > 60 ? txt.slice(0, 60).trim() + '…' : txt;
      }
      // last fallback: stripped text truncated
      const s = stripHtml(html);
      if (!s) return '';
      if (s.length <= 60) return s;
      // try to cut at first sentence boundary
      const m = s.match(/(.{40,60}?)[\.\!\?]\s/);
      if (m && m[1]) return m[1].trim() + '…';
      return s.slice(0, 60).trim() + '…';
    } catch (e) {
      return stripHtml(html).slice(0, 60);
    }
  };

  const getConsent3Words = (html) => {
    if (!html) return 'Consent Document';
    const clean = stripHtml(html).trim();
    if (!clean) return 'Consent Document';
    const words = clean.split(/\s+/).filter(Boolean);
    if (words.length <= 3) return words.join(' ');
    return words.slice(0, 3).join(' ') + '...';
  };

  const sanitize = (html) => {
    if (!html) return '';
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html || '', 'text/html');
      const allowed = new Set(['B', 'I', 'U', 'STRONG', 'EM', 'SPAN', 'P', 'BR', 'UL', 'OL', 'LI', 'A']);
      const walk = (node) => {
        const children = Array.from(node.childNodes);
        for (const child of children) {
          if (child.nodeType === Node.ELEMENT_NODE) {
            if (!allowed.has(child.tagName)) {
              const frag = document.createDocumentFragment();
              while (child.firstChild) frag.appendChild(child.firstChild);
              child.replaceWith(frag);
            } else {
              // sanitize attributes: allow href on <a> and harmless style color
              for (const attr of Array.from(child.attributes || [])) {
                const name = attr.name.toLowerCase();
                const val = attr.value || '';
                if (name === 'href') {
                  // only allow safe protocols
                  if (!/^\s*(https?:|mailto:|\/)/i.test(val)) child.removeAttribute('href');
                } else if (name === 'style') {
                  const safe = val.split(';').map(s => s.trim()).filter(Boolean).map(pair => {
                    const [k, v] = pair.split(':').map(t => t.trim());
                    if (!k || !v) return null;
                    if (k.toLowerCase() !== 'color') return null;
                    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v) || /^rgb\(/i.test(v) || /^rgba\(/i.test(v)) return `${k}: ${v}`;
                    return null;
                  }).filter(Boolean).join(';');
                  if (safe) child.setAttribute('style', safe); else child.removeAttribute('style');
                } else {
                  // remove any other attribute to be safe
                  child.removeAttribute(attr.name);
                }
              }
              walk(child);
            }
          } else if (child.nodeType === Node.TEXT_NODE) {
            // keep text nodes
          } else {
            child.remove();
          }
        }
      };
      walk(doc.body);
      return doc.body.innerHTML;
    } catch (e) {
      return '';
    }
  };

  const decodeHtmlEntities = (str) => {
    if (!str && str !== '') return '';
    try {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = String(str);
      return textarea.value;
    } catch (e) {
      return String(str || '');
    }
  };

  // Helper to extract a useful error message from axios errors or generic errors
  const extractErrorMsg = (err) => {
    try {
      if (!err) return 'Unknown error';
      const resp = err.response || err;
      if (resp && resp.data) {
        const d = resp.data;
        if (typeof d === 'string' && d.trim()) return d;
        if (d && (d.detail || d.message)) return d.detail || d.message;
        try { return JSON.stringify(d, Object.getOwnPropertyNames(d)); } catch (e) { }
      }
      // axios may expose raw XHR response text on err.request
      try {
        const req = err.request || null;
        if (req && req.responseText) {
          const rt = req.responseText;
          if (rt && rt.trim()) {
            try { const parsed = JSON.parse(rt); if (parsed && (parsed.detail || parsed.message)) return parsed.detail || parsed.message; } catch (e) { }
            return rt;
          }
        }
      } catch (e) { }
      if (err && typeof err === 'object') {
        try { return JSON.stringify(err, Object.getOwnPropertyNames(err)); } catch (e) { }
      }
      if (err.message) return err.message;
      return String(err);
    } catch (e) { return 'Unknown error'; }
  };

  const UploadConfirmModal = ({ visible, onClose, file, typesList, initialMessages, onSubmitUpload, modalHeaderOffset }) => {
    const [uploadConsentVariants, setUploadConsentVariants] = useState([]);
    const [languageTypeOptions, setLanguageTypeOptions] = useState(null);
    const [loadingLanguageTypes, setLoadingLanguageTypes] = useState(false);

    const readVariant = (item) => {
      try {
        const raw = item?.consent_metadata || item?.data || item?.message;
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (e) {
        return {};
      }
    };

    const uploadItemLanguage = (item) => {
      const meta = readVariant(item);
      const rawLanguage = meta.language_code || meta.languageCode || meta.language || meta.lang ||
        meta.language_name || meta.languageName || item?.language_code || item?.language_name || item?.language || '';
      return rawLanguage ? normalizeLangCode(rawLanguage) : '';
    };

    const normalizeUploadType = (value) => String(value || '')
      .trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').toLowerCase();

    const matchesLanguageScope = (item, targetLanguage) => {
      const targetLang = normalizeLangCode(targetLanguage || 'en');
      const itemLanguage = uploadItemLanguage(item) || 'en';
      return itemLanguage === targetLang;
    };

    const uploadTypeMatches = (item, targetType) => {
      const meta = readVariant(item);
      const itemTypes = [
        item?.consent_type,
        item?.type,
        item?.name,
        meta.parent_consent_type,
        meta.parent_type,
      ].filter(Boolean).map(normalizeUploadType);
      return itemTypes.includes(normalizeUploadType(targetType));
    };

    const getUploadTypeLabel = (type, language) => {
      const targetType = normalizeUploadType(type);
      const targetLanguage = normalizeLangCode(language);
      const matchingVariant = (uploadConsentVariants || []).find(item => {
        const itemLanguage = uploadItemLanguage(item);
        return uploadTypeMatches(item, targetType) && itemLanguage === targetLanguage;
      });
      if (matchingVariant) {
        const meta = readVariant(matchingVariant);
        const label = meta.label || meta.title || meta.parent_consent_label || matchingVariant.label || matchingVariant.title;
        if (label) return String(label).trim();
        if (targetLanguage !== 'en' && /[^\x00-\x7F]/.test(String(matchingVariant.consent_type || ''))) {
          return String(matchingVariant.consent_type).trim();
        }
      }
      return getTranslatedConsentTypeLabel(type, language);
    };

    const lookupBaseMessage = (type) => {
      if (!initialMessages) return '';
      const target = normalizeUploadType(type);
      const key = Object.keys(initialMessages).find(k => normalizeUploadType(k) === target);
      return key ? initialMessages[key] : '';
    };

    const getUploadMessage = (item) => {
      const metadata = readVariant(item);
      const candidates = [
        metadata.consent_message,
        metadata.content,
        item?.consent_message,
        item?.message,
      ];
      for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) {
          const parsed = parseConsentPayload(candidate);
          if (parsed) {
            const parsedMessage = parsed.consent_message || parsed.content || parsed.message;
            if (typeof parsedMessage === 'string' && parsedMessage.trim()) return parsedMessage;
          }
          return candidate;
        }
      }
      return '';
    };

    const resolveUploadMessage = (type, language, subRows = subConsents, selectedSubId = selectedUploadSubCategory) => {
      const targetType = normalizeUploadType(type);
      const targetLanguage = normalizeLangCode(language);
      if (selectedSubId !== null && selectedSubId !== undefined && selectedSubId !== '') {
        const selectedSub = (subRows || []).find(item => String(item.id) === String(selectedSubId));
        if (selectedSub) {
          const metadata = readVariant(selectedSub);
          const messageLanguage = metadata.language_code || metadata.language || selectedSub.language_code;
          if (!messageLanguage || normalizeLangCode(messageLanguage) === targetLanguage) {
            const message = getUploadMessage(selectedSub);
            if (message) return message;
          }
        }
      }

      const typeOption = (languageTypeOptions || []).find(item => {
        const itemLanguage = uploadItemLanguage(item) || 'en';
        const itemTypes = [
          item?.consent_type,
          item?.type,
          item?.name,
          item?.parent_consent_type,
          item?.parent_type,
        ].filter(Boolean);
        return itemLanguage === targetLanguage &&
          itemTypes.some(itemType => normalizeUploadType(itemType) === targetType);
      });
      if (typeOption) {
        const message = getUploadMessage(typeOption);
        if (message) return message;
      }

      const variant = (uploadConsentVariants || []).find(item => {
        const itemLanguage = uploadItemLanguage(item);
        return uploadTypeMatches(item, targetType) && itemLanguage === targetLanguage;
      });
      if (variant) {
        const message = getUploadMessage(variant);
        if (message) return message;
      }
      return lookupBaseMessage(type);
    };

    const resolvedUploadConsentMessage = selectedUploadConsentType
      ? resolveUploadMessage(selectedUploadConsentType, selectedUploadLanguage, subConsents, selectedUploadSubCategory)
      : selectedUploadConsentMessage;

    useEffect(() => {
      if (!visible) return;
      let mounted = true;
      const loadUploadVariants = async () => {
        try {
          const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
          const params = { include_variants: true };
          if (tenant_id) params.tenant_id = tenant_id;
          const res = await axios.get(`${API_BASE}/manager/new-consents`, { params });
          const rows = res?.data?.data || res?.data?.new_consents || [];
          const latestUploadStatus = new Map();
          (Array.isArray(rows) ? rows : []).forEach(r => {
            const ct = String(r.consent_type || r.type || r.name || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
            if (ct && (r.parent_id == null || r.parent_id === 0) && !latestUploadStatus.has(ct)) {
              latestUploadStatus.set(ct, String(r.status || '').trim().toLowerCase());
            }
          });
          const acceptedRows = (Array.isArray(rows) ? rows : []).filter(r => {
            const st = String(r.status || '').trim().toLowerCase();
            const ct = String(r.consent_type || r.type || r.name || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
            const isActiveOrAccepted = ['accepted', 'active', 'approved', 'granted', 'agree', 'agreed', 'yes'].includes(st);
            const isNotDisabled = !['inactive', 'rejected', 'declined', 'deleted'].includes(latestUploadStatus.get(ct));
            return isActiveOrAccepted && isNotDisabled;
          });
          if (mounted) setUploadConsentVariants(acceptedRows);
        } catch (e) {
          if (mounted) setUploadConsentVariants([]);
        }
      };
      loadUploadVariants();
      return () => { mounted = false; };
    }, [visible]);

    useEffect(() => {
      if (!visible) return;
      let mounted = true;
      const loadLanguageTypes = async () => {
        if (mounted) setLoadingLanguageTypes(true);
        try {
          const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
          if (!tenant_id) {
            if (mounted) setLanguageTypeOptions([]);
            return;
          }
          const res = await axios.get(`${API_BASE}/user/consent/types-by-language`, {
            params: { tenant_id: String(tenant_id), language: selectedUploadLanguage || 'en' },
          });
          const rows = Array.isArray(res?.data?.types) ? res.data.types : [];
          if (mounted) setLanguageTypeOptions(rows);
        } catch (e) {
          // Use the variant response below when the language endpoint is unavailable.
          if (mounted) setLanguageTypeOptions(null);
        } finally {
          if (mounted) setLoadingLanguageTypes(false);
        }
      };
      setLanguageTypeOptions(null);
      loadLanguageTypes();
      return () => { mounted = false; };
    }, [visible, selectedUploadLanguage]);

    useEffect(() => {
      if (!visible || loadingLanguageTypes || languageTypeOptions === null || !selectedUploadConsentType) return;
      const validTypes = Array.isArray(languageTypeOptions) ? languageTypeOptions : [];
      const selectedTypeExists = validTypes.some(item => {
        const value = item?.consent_type || item?.type || item?.name || item?.id;
        return optionMatchesConsentType(value, selectedUploadConsentType);
      }) || (Array.isArray(typesList) ? typesList.some(type => optionMatchesConsentType(type, selectedUploadConsentType)) : false);
      if (!selectedTypeExists) {
        setSelectedUploadConsentType('');
        setSelectedUploadConsentMessage('');
        setSelectedUploadSubCategory('');
        setSubConsents([]);
        setShowSubConsent(false);
      }
    }, [visible, loadingLanguageTypes, languageTypeOptions, selectedUploadConsentType, typesList]);

    if (!visible) return null;
    const normalizedUploadLanguage = normalizeLangCode(selectedUploadLanguage || 'en');
    const primaryLanguageOptions = Array.isArray(languageTypeOptions)
      ? languageTypeOptions.filter(item => matchesLanguageScope(item, normalizedUploadLanguage)).map(item => item?.consent_type || item?.type || item?.name || item?.id).filter(Boolean)
      : [];
    const fallbackTypeOptions = (Array.isArray(typesList) ? typesList : []).filter(type => {
      if (!type) return false;
      const matchesType = (uploadConsentVariants || []).some(item => uploadTypeMatches(item, type));
      if (!matchesType) return false;
      return (uploadConsentVariants || []).some(item =>
        uploadTypeMatches(item, type) && matchesLanguageScope(item, normalizedUploadLanguage)
      );
    });
    const languageFilteredTypes = primaryLanguageOptions.length
      ? primaryLanguageOptions
      : fallbackTypeOptions;
    const uploadTypeOptions = Array.from(new Set(languageFilteredTypes));
    const overlayStyle = {
      position: 'fixed', left: 0, top: 0, right: 0, bottom: 0,
      background: 'rgba(2,6,23,0.45)', backdropFilter: 'blur(4px)', zIndex: 2147483647,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pointerEvents: 'auto', padding: 12
    };
    const modalStyle = {
      marginTop: Math.max(12, Math.round(modalHeaderOffset || 0) + 12),
      position: 'relative',
      background: '#fff', padding: 20, borderRadius: 12, width: 'auto', maxWidth: 720, boxShadow: '0 18px 48px rgba(2,6,23,0.18)', maxHeight: `calc(100vh - ${Math.max(120, (modalHeaderOffset || 0) + 48)}px)`, overflowY: 'auto'
    };

    const handleTypeChange = (ev) => {
      const v = ev.target.value;

      setSelectedUploadConsentType(v);
      setSelectedUploadConsentMessage(resolveUploadMessage(v, selectedUploadLanguage, subConsents, ''));
      try { setSelectedUploadSubCategory(''); setSubConsents([]); setShowSubConsent(false); } catch (e) { }
      // fetch tenant-scoped sub-consents for this consent type
      (async () => {
        try {
          const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
          const params = { params: { consent_type: v, language: languageCodeFor(selectedUploadLanguage || 'en') } };
          if (tenant_id) params.params.tenant_id = tenant_id;
          const selectedTypeOption = (languageTypeOptions || []).find(item => String(item?.consent_type || item?.type || item?.name || item?.id) === String(v));
          const parentConsentId = selectedTypeOption?.consent_id || selectedTypeOption?.parent_consent_id || selectedTypeOption?.id;
          if (parentConsentId != null) params.params.parent_consent_id = parentConsentId;
          setLoadingSubConsents(true);
          const res = await axios.get(`${API_BASE}/manager/sub-category-consents`, params);
          const rows = res && res.data && (res.data.rows || res.data.data || res.data) ? (res.data.rows || res.data.data || res.data) : [];
          const mapped = mapSubConsentOptions(rows, selectedUploadLanguage);
          setSubConsents(mapped);
          setShowSubConsent((mapped || []).length > 0);
          // if language selected, try to auto-pick variant message
          if (mapped && mapped.length && selectedUploadLanguage) {
            setSelectedUploadConsentMessage(resolveUploadMessage(v, selectedUploadLanguage, mapped, ''));
          }
        } catch (e) {
          setSubConsents([]);
          setShowSubConsent(false);
        } finally { setLoadingSubConsents(false); }
      })();
    };

    const handleUploadSubChange = (ev) => {
      const v = ev.target.value;
      setSelectedUploadSubCategory(v);
      try {

        setSelectedUploadConsentMessage(resolveUploadMessage(selectedUploadConsentType, selectedUploadLanguage, subConsents, v));

      } catch (e) { }
    };

    const selectedUploadSubConsent = (subConsents || []).find(item => String(item.id) === String(selectedUploadSubCategory));

    const handleUploadLanguageChange = (ev) => {
      const lang = ev.target.value;
      const currentType = selectedUploadConsentType;
      const validOptions = Array.isArray(languageTypeOptions) ? languageTypeOptions : [];
      const keepType = !!currentType && (
        validOptions.some(item => {
          const candidate = item?.consent_type || item?.type || item?.name || item?.id || '';
          return optionMatchesConsentType(candidate, currentType);
        }) || (Array.isArray(typesList) ? typesList.some(type => optionMatchesConsentType(type, currentType)) : false)
      );
      setSelectedUploadLanguage(lang);
      setSelectedUploadConsentType(keepType ? currentType : '');
      setSelectedUploadConsentMessage(keepType ? resolveUploadMessage(currentType, lang, subConsents, '') : '');
      setSelectedUploadSubCategory('');
      setSubConsents([]);
      setShowSubConsent(false);
    };

    return ReactDOM.createPortal(
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Confirm CSV Upload</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>Select the consent type to apply to uploaded rows and review its message.</div>
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{file && file.name}</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Language</label>
            <select value={selectedUploadLanguage} onChange={handleUploadLanguageChange} style={{ padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
              {effectiveLanguageOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              {!effectiveLanguageOptions.length && <option value="" disabled>{tenantLanguagesLoading ? 'Loading tenant languages…' : 'No consent languages configured for this tenant'}</option>}
            </select>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Consent Type</label>
            <select value={selectedUploadConsentType} onChange={handleTypeChange} disabled={loadingLanguageTypes} style={{ padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <option value="">-- Select consent type --</option>
              {loadingLanguageTypes ? <option value="" disabled>Loading consent types…</option> : uploadTypeOptions.map(t => (
                <option key={t} value={t}>{getUploadTypeLabel(t, selectedUploadLanguage || 'en')}</option>
              ))}
            </select>
            {/* Editing of raw consent message is hidden in bulk CSV upload flow. */}

            {showSubConsent ? (
              <>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Sub Consent (Optional)</label>
                {loadingSubConsents ? (
                  <div style={{ padding: 10, color: '#64748b' }}>Loading sub-consents…</div>
                ) : (
                  <select value={selectedUploadSubCategory} onChange={handleUploadSubChange} style={{ padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <option value="">-- Select sub consent --</option>
                    {(subConsents || []).map(s => (
                      <option key={s.id} value={s.id}>{getTranslatedSubConsentLabel(s, selectedUploadLanguage || 'en')}</option>
                    ))}
                  </select>
                )}
              </>
            ) : null}
            {selectedUploadConsentType && !loadingSubConsents && !showSubConsent ? (
              <div style={{ padding: '9px 11px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: 12 }}>
                No sub-consents are available for this consent type.
              </div>
            ) : null}
            {selectedUploadConsentType ? (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, padding: 10, borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700 }}>CONSENT TYPE</div>
                  <div style={{ color: '#1e3a8a', fontSize: 13, fontWeight: 700, overflowWrap: 'anywhere' }}>{getUploadTypeLabel(selectedUploadConsentType, selectedUploadLanguage || 'en')}</div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700 }}>SUB-CONSENT</div>
                  <div style={{ color: '#1e3a8a', fontSize: 13, fontWeight: 700, overflowWrap: 'anywhere' }}>
                    {selectedUploadSubConsent ? getTranslatedSubConsentLabel(selectedUploadSubConsent, selectedUploadLanguage || 'en') : (showSubConsent ? 'Select a sub-consent' : 'Not applicable')}
                  </div>
                </div>
              </div>
            ) : null}


            <label style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Consent Message (preview)</label>
            <div style={{ background: '#fff', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', minHeight: 80, maxHeight: 240, overflowY: 'auto' }}>
              {resolvedUploadConsentMessage ? (
                <div dangerouslySetInnerHTML={{ __html: sanitize(resolvedUploadConsentMessage) }} />
              ) : (
                <div style={{ color: '#94a3b8' }}>No preview available</div>
              )}
            </div>


            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6, flexDirection: (isMobile ? 'column-reverse' : 'row'), width: '100%' }}>
              <div style={{ display: 'flex', flex: 1 }} />
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }} style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: 8, width: isMobile ? '100%' : 'auto' }}>Cancel</button>
              {
                // Prevent double-submit: ignore subsequent clicks and close modal immediately
              }
              <button type="button" onClick={(e) => {
                try {
                  e.preventDefault(); e.stopPropagation();
                  // mark clicked to prevent duplicates (ref stored on DOM element)
                  const btn = e.currentTarget;
                  if (btn.getAttribute('data-submitted') === '1') return;
                  btn.setAttribute('data-submitted', '1');
                  // Close the modal immediately so the consent-type page is removed
                  try { onClose && onClose(); } catch (err) { }
                  // Give a tiny delay to allow modal to unmount before starting upload
                  setTimeout(() => {
                    try {
                      const selectedType = (languageTypeOptions || []).find(item => String(item?.consent_type || item?.type || item?.name || item?.id) === String(selectedUploadConsentType));
                      onSubmitUpload({
                        file,
                        consent_type: selectedType?.parent_consent_type || selectedType?.consent_type || selectedUploadConsentType,
                        consent_message: resolvedUploadConsentMessage,
                        consent_id: selectedType?.consent_id || selectedType?.parent_consent_id || selectedType?.id,
                        sub_consent_id: selectedUploadSubCategory || undefined,
                        version: selectedType?.consent_version || selectedType?.version,
                        language: selectedUploadLanguage,
                      });
                    } catch (err) { console.warn('onSubmitUpload failed', err); }
                  }, 80);
                } catch (err) {
                  console.warn('Upload button handler error', err);
                }
              }} style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, width: isMobile ? '100%' : 'auto' }}>Upload & Submit</button>
            </div>
          </div>
        </div>
      </div>
      , document.body);
  };

  // keep modal offset updated so the modal is visually centered despite sticky header
  useEffect(() => {
    // if (!showSampleCSV ) return;
    if (!showSampleCSV && !showCSVConfirm) return;
    const measure = () => {
      try {
        // prefer global base header used by BasePage, fallback to page-header
        const hdr = document.querySelector('.base-header') || document.querySelector('.page-header') || document.querySelector('header');
        const h = hdr ? Math.max(0, Math.ceil(hdr.getBoundingClientRect().height)) : 0;
        setModalHeaderOffset(h);
      } catch (e) { setModalHeaderOffset(0); }
    };
    measure();
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    // also re-measure after a frame to allow layout to settle
    const raf = requestAnimationFrame(measure);
    return () => { try { cancelAnimationFrame(raf); } catch (e) { } window.removeEventListener('resize', onResize); };
  }, [showSampleCSV, showCSVConfirm]);

  // initialize consent types/messages from imported CONSENT_TYPES and initialContent
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        if ((!types || types.length === 0) && CONSENT_TYPES && CONSENT_TYPES.length) {
          const keys = CONSENT_TYPES.map(t => t.key).sort((a, b) => a.toString().localeCompare(b.toString(), undefined, { sensitivity: 'base' }));
          if (mounted) setTypes(keys);
        }
        // fetch server-side stored messages and merge with defaults
        try {
          const tenant_id = Number(localStorage.getItem('tenant_id')) || null;
          const params = tenant_id ? { params: { tenant_id }, skipLoading: true } : { skipLoading: true };
          const res = await axios.get(`${API_BASE}/manager/new-consents`, params);
          const rows = res.data && (res.data.data || res.data.new_consents || res.data.new_consent || res.data) ? (res.data.data || res.data.new_consents || res.data.new_consent || res.data) : [];
          const latestStatusMap = new Map();
          (Array.isArray(rows) ? rows : []).forEach(r => {
            const ct = String(r.consent_type || r.type || r.key || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
            if (ct && (r.parent_id == null || r.parent_id === 0) && !latestStatusMap.has(ct)) {
              latestStatusMap.set(ct, String(r.status || '').trim().toLowerCase());
            }
          });
          const map = {};
          for (const r of (rows || [])) {
            const st = String(r.status || '').trim().toLowerCase();
            const normKey = (r.consent_type || r.type || r.key || '').toString().trim().toLowerCase().replace(/[\s-]+/g, '_');
            const isActiveOrAccepted = ['accepted', 'active', 'approved', 'granted', 'agree', 'agreed', 'yes'].includes(st);
            const isNotDisabled = !['inactive', 'rejected', 'declined', 'deleted'].includes(latestStatusMap.get(normKey));
            if (!isActiveOrAccepted || !isNotDisabled) continue;
            const key = (r.consent_type || r.type || r.key || '').toString();
            const msg = r.consent_message || r.message || r.consent || '';
            if (key) map[key] = msg;
          }
          // fallback to initialContent for missing types
          const merged = { ...(initialContent || {}), ...map };
          if (mounted) setTypeMessages(merged);
          // Only merge accepted server-created types
          const fetchedKeys = Object.keys(map || {});
          if (fetchedKeys.length) {
            if (mounted) setTypes(Array.from(new Set(fetchedKeys)));
          }
        } catch (e) {
          // fallback to local initial content
          if (mounted && (!typeMessages || Object.keys(typeMessages).length === 0)) setTypeMessages({ ...initialContent });
        }
      } catch (e) { }
    };
    init();
    const handler = (ev) => {
      // refresh messages when consent templates are updated elsewhere
      try {
        const tenant_id = (ev && ev.detail && ev.detail.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
        (async () => {
          try {
            const params = tenant_id ? { params: { tenant_id }, skipLoading: true } : { skipLoading: true };
            const res = await axios.get(`${API_BASE}/manager/new-consents`, params);
            const rows = res.data && (res.data.data || res.data.new_consents || res.data.new_consent || res.data) ? (res.data.data || res.data.new_consents || res.data.new_consent || res.data) : [];
            const latestStatusMap = new Map();
            (Array.isArray(rows) ? rows : []).forEach(r => {
              const ct = String(r.consent_type || r.type || r.key || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
              if (ct && (r.parent_id == null || r.parent_id === 0) && !latestStatusMap.has(ct)) {
                latestStatusMap.set(ct, String(r.status || '').trim().toLowerCase());
              }
            });
            const map = {};
            for (const r of (rows || [])) {
              const normKey = (r.consent_type || r.type || r.key || '').toString().trim().toLowerCase().replace(/[\s-]+/g, '_');
              const st = String(r.status || '').trim().toLowerCase();
              const isActiveOrAccepted = ['accepted', 'active', 'approved', 'granted', 'agree', 'agreed', 'yes'].includes(st);
              const isNotDisabled = !['inactive', 'rejected', 'declined', 'deleted'].includes(latestStatusMap.get(normKey));
              if (!isActiveOrAccepted || !isNotDisabled) continue;
              const key = (r.consent_type || r.type || r.key || '').toString();
              const msg = r.consent_message || r.message || r.consent || '';
              if (key) map[key] = msg;
            }
            const merged = { ...(initialContent || {}), ...map };
            setTypeMessages(merged);
          } catch (e) { console.warn('Failed refreshing consent templates', e); }
        })();
      } catch (e) { }
    };
    window.addEventListener('consentTemplateUpdated', handler);
    return () => { mounted = false; window.removeEventListener('consentTemplateUpdated', handler); };
  }, []);
  const [tab, setTab] = useState(initialTab || 'consent');
  const [roleFilter, setRoleFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [expiringFilter, setExpiringFilter] = useState(false);
  const [expiringDays, setExpiringDays] = useState(7);
  const [typeFilter, setTypeFilter] = useState(null);
  const [dedupeMode, setDedupeMode] = useState(false);

  useEffect(() => {
    // initialRoleFilter may be a simple role string, or an object like { role: 'Employee', status: 'accepted' }
    console.debug('[UserPanel] initialRoleFilter changed ->', initialRoleFilter);
    if (!initialRoleFilter) {
      setRoleFilter(null);
      setStatusFilter(null);
      setExpiringFilter(false);
      setExpiringDays(7);
      setDedupeMode(false);
      return;
    }
    if (typeof initialRoleFilter === 'object') {
      setRoleFilter(initialRoleFilter.role || null);
      setStatusFilter(initialRoleFilter.status || (initialRoleFilter.expiredOnly ? 'expired' : null));
      setExpiringFilter(Boolean(initialRoleFilter.expiringSoon));
      setExpiringDays(initialRoleFilter.days ? Number(initialRoleFilter.days) : 7);
      setTypeFilter(initialRoleFilter.type || initialRoleFilter.consent_type || null);
      setDedupeMode(Boolean(initialRoleFilter.dedupe));
    } else {
      setRoleFilter(initialRoleFilter || null);
      setStatusFilter(null);
      setExpiringFilter(false);
      setExpiringDays(7);
      setTypeFilter(null);
      setDedupeMode(false);
    }
  }, [initialRoleFilter]);
  const [isSlidingPageOpen, setSlidingPageOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isRevokeModalOpen, setRevokeModalOpen] = useState(false);
  const [pendingReconsentId, setPendingReconsentId] = useState(null);
  const [templateStatusMap, setTemplateStatusMap] = useState({});

  const getCanonicalTypeKeys = useCallback((val) => {
    if (!val) return [];
    const raw = String(val).trim().toLowerCase();
    const s1 = raw.replace(/[_\s-]+/g, ' ').trim();
    const s2 = raw.replace(/[_\s-]+/g, '_').trim();
    const withoutConsent = s1.replace(/\s+consent$/i, '').trim();
    const withoutConsentUnderscore = s2.replace(/_consent$/i, '').trim();
    return Array.from(new Set([raw, s1, s2, withoutConsent, withoutConsentUnderscore].filter(Boolean)));
  }, []);

  const isTemplateUnderProcess = useCallback((rec) => {
    if (!rec) return false;
    const recStatus = String(rec.template_status || rec.templateStatus || '').trim().toLowerCase();
    if (recStatus === 'under_process') return true;
    if (recStatus === 'accepted') return false;

    const rawType = (rec.type || rec.consent_type || '').toString().trim();
    const keys = getCanonicalTypeKeys(rawType);
    for (const k of keys) {
      if (templateStatusMap[k] === 'under_process') return true;
      if (templateStatusMap[k] === 'accepted') return false;
    }

    const rowStatus = String(rec.status || '').trim().toLowerCase();
    const isReconsentRec = /reconsent|suspend|supersed/i.test(rowStatus);
    if (isReconsentRec) {
      return true;
    }
    return false;
  }, [templateStatusMap, getCanonicalTypeKeys]);

  const refreshTemplateStatuses = useCallback(async () => {
    try {
      const tid = Number(user?.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
      if (!tid) return;
      const res = await axios.get(`${API_BASE}/manager/new-consents`, { params: { tenant_id: tid }, skipLoading: true });
      const rows = res.data?.data || [];
      const statusObj = {};
      rows.forEach(d => {
        const rawType = (d.consent_type || d.type || d.consent_type_name || '').toString().trim();
        const status = String(d.status || '').trim().toLowerCase();
        if (rawType && (d.parent_id == null || d.parent_id === 0)) {
          const keys = getCanonicalTypeKeys(rawType);
          keys.forEach(k => {
            statusObj[k] = status;
          });
        }
      });
      setTemplateStatusMap(prev => ({ ...prev, ...statusObj }));
    } catch (e) {
      // ignore
    }
  }, [user?.tenant_id, getCanonicalTypeKeys]);

  useEffect(() => {
    refreshTemplateStatuses();
  }, [refreshTemplateStatuses]);

  const _revokeStorageKey = (() => {
    try {
      const tid = (typeof user !== 'undefined' && user && user.tenant_id) ? user.tenant_id : (localStorage.getItem('tenant_id') || '0');
      return `userpanel_revoke_requested_${tid}`;
    } catch (e) { return 'userpanel_revoke_requested_0'; }
  });
  const [revokeRequestedIds, setRevokeRequestedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(_revokeStorageKey());
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr.map(String) : []);
    } catch (e) { return new Set(); }
  });
  const persistRevokeRequestedIds = (setObj) => {
    try { localStorage.setItem(_revokeStorageKey(), JSON.stringify(Array.from(setObj))); } catch (e) { }
  };

  const _reconsentStorageKey = (() => {
    try {
      const tid = (typeof user !== 'undefined' && user && user.tenant_id) ? user.tenant_id : (localStorage.getItem('tenant_id') || '0');
      return `userpanel_reconsent_submitted_${tid}`;
    } catch (e) { return 'userpanel_reconsent_submitted_0'; }
  });
  const [reconsentSubmittedIds, setReconsentSubmittedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(_reconsentStorageKey());
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr.map(String) : []);
    } catch (e) { return new Set(); }
  });
  const persistReconsentSubmittedIds = (setObj) => {
    try { localStorage.setItem(_reconsentStorageKey(), JSON.stringify(Array.from(setObj))); } catch (e) { }
  };

  const consentCacheTenantId = Number(user?.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
  const initialConsentCacheKey = createSessionCacheKey('userpanel-consents', user, {
    tenant_id: consentCacheTenantId,
    dashboard: true,
    page: 1,
    page_size: 10,
  });
  const [initialConsentCache] = useState(() => initialRoleFilter ? null : readSessionCache(initialConsentCacheKey));
  const [consents, setConsents] = useState(() => initialConsentCache?.consents || []);
  const [localizedConsentTypeLabels, setLocalizedConsentTypeLabels] = useState({});
  const [serverTotal, setServerTotal] = useState(() => initialConsentCache?.total ?? null);
  const [serverStats, setServerStats] = useState(() => initialConsentCache?.stats || null);
  const [consentDataTimingMs, setConsentDataTimingMs] = useState(null);
  const [consentPageReadyMs, setConsentPageReadyMs] = useState(null);
  const firstConsentDataShown = useRef(false);
  // Debug: log when consents or roleFilter change to help diagnose filtering
  useEffect(() => {
    try {
      console.debug('[UserPanel] consents loaded:', (consents || []).length, 'roleFilter:', roleFilter, 'statusFilter:', statusFilter);
      if (roleFilter && consents && consents.length) {
        const sample = consents.slice(0, 6).map(c => ({ id: c.id, candidate: String((c.role || c.user_role || c.type || c.consent_type || c.category || c.template || c.entity || '') || '').toLowerCase() }));
        console.debug('[UserPanel] sample candidates:', sample);
      }
    } catch (e) { }
  }, [consents, roleFilter, statusFilter]);

  useEffect(() => {
    let active = true;
    const tenantId = Number(user?.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
    const languages = Array.from(new Set((consents || [])
      .map(record => normalizeLangCode(record.consent_language || record.language || record.preferred_languages || record.preferredLanguages || 'en'))
      .filter(language => language !== 'en')));

    if (!tenantId || languages.length === 0) {
      setLocalizedConsentTypeLabels({});
      return () => { active = false; };
    }

    const loadLocalizedTypeLabels = async () => {
      const labelsByLanguage = {};
      await Promise.all(languages.map(async language => {
        try {
          const response = await axios.get(`${API_BASE}/user/consent/types-by-language`, {
            params: { tenant_id: String(tenantId), language },
            skipLoading: true,
          });
          const localizedTypes = Array.isArray(response.data?.types) ? response.data.types : [];
          labelsByLanguage[language] = {};
          localizedTypes.forEach(item => {
            const type = item.consent_type || item.type || item.name || '';
            const label = item.label || item.title || item.localized_label || item.localizedLabel;
            if (type && label && normalizeLangCode(item.language_code || language) === language) {
              labelsByLanguage[language][normalizeConsentTypeKey(type)] = String(label).trim();
            }
          });
        } catch (error) {
          console.warn(`Failed to load ${language} consent type labels`, error);
        }
      }));

      if (active) setLocalizedConsentTypeLabels(labelsByLanguage);
    };

    loadLocalizedTypeLabels();
    return () => { active = false; };
  }, [consents, user?.tenant_id]);

  const [saved, setSaved] = useState(false);
  const [types, setTypes] = useState([]);
  const [typeMessages, setTypeMessages] = useState({});
  const [dataTypes, setDataTypes] = useState([]);
  const [consentOptions, setConsentOptions] = useState(null);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsConsent, setDetailsConsent] = useState(null);
  const [detailsCopied, setDetailsCopied] = useState(false);
  const [consentScrollPct, setConsentScrollPct] = useState(0);
  // selection for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [moreMenuOpenId, setMoreMenuOpenId] = useState(null);
  const [moreLoading, setMoreLoading] = useState({ id: null, type: null });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter, statusFilter, typeFilter, dateFilter, expiringFilter, expiringDays, debouncedSearchQuery]);

  const openConsentLogsModal = async (record) => {
    if (!record || !record.id) return;
    try {
      Swal.fire({
        title: 'Loading Activity Logs...',
        html: '<div style="padding:16px;color:#64748b;font-size:13.5px;">Fetching complete activity history for this consent...</div>',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const response = await axios.get(`${API_BASE}/user/consent/${record.id}/activity`, {
        params: { limit: 100 },
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });

      let history = Array.isArray(response.data?.logs) ? response.data.logs : [];
      const encId = response.data?.encrypted_id || response.data?.public_id || record.public_id || (record.id ? `CNS-${String(record.id).padStart(8, '0')}` : '—');

      const escapeHtml = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

      const formatFriendlyDetails = (detailsRaw) => {
        if (!detailsRaw && detailsRaw !== 0) return '<span style="color:#94a3b8">—</span>';
        let data = detailsRaw;
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch { return escapeHtml(data); }
        }
        if (typeof data !== 'object' || Array.isArray(data)) return escapeHtml(String(data));

        let consentMsg = data.consent_message || data.message || data.consent || data.data_preview;
        if (consentMsg && typeof consentMsg === 'string') {
          consentMsg = consentMsg.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        }

        const pills = [];

        // Channel (Email / WhatsApp / Portal)
        if (data.channel) {
          const ch = String(data.channel);
          const isWa = ch.toLowerCase().includes('whatsapp');
          pills.push(`<span style="display:inline-flex;align-items:center;gap:4px;margin:2px 4px 2px 0;padding:2px 8px;background:${isWa ? '#f0fdf4' : '#eff6ff'};color:${isWa ? '#15803d' : '#1d4ed8'};border:1px solid ${isWa ? '#bbf7d0' : '#bfdbfe'};border-radius:4px;font-size:11.5px;font-weight:600;">${isWa ? '💬' : '✉️'} Channel: ${escapeHtml(ch)}</span>`);
        }

        // IP Address
        const ip = data.ip_address || data.ip || data.responder_ip;
        if (ip && ip !== '—' && ip !== '-') {
          pills.push(`<span style="display:inline-flex;align-items:center;gap:4px;margin:2px 4px 2px 0;padding:2px 8px;background:#f0f9ff;color:#0369a1;border:1px solid #bae6fd;border-radius:4px;font-size:11.5px;font-weight:600;font-family:monospace;">🌐 IP: ${escapeHtml(ip)}</span>`);
        }

        // Browser / Device
        const browser = data.browser || data.responder_browser;
        if (browser && browser !== '—' && browser !== '-') {
          pills.push(`<span style="display:inline-flex;align-items:center;gap:4px;margin:2px 4px 2px 0;padding:2px 8px;background:#f8fafc;color:#334155;border:1px solid #cbd5e1;border-radius:4px;font-size:11.5px;font-weight:500;">💻 Browser: ${escapeHtml(browser)}</span>`);
        }

        // Reminder Cycle
        if (data.reminder_number) {
          pills.push(`<span style="display:inline-flex;align-items:center;gap:4px;margin:2px 4px 2px 0;padding:2px 8px;background:#fef3c7;color:#92400e;border:1px solid #fde68a;border-radius:4px;font-size:11.5px;font-weight:600;">⏰ Cycle: ${escapeHtml(data.reminder_number)} of 3</span>`);
        }

        // Status / Response
        const respOrStatus = data.response || data.status;
        if (respOrStatus) {
          const lower = String(respOrStatus).toLowerCase();
          const isAcc = lower.includes('accept');
          const isDec = lower.includes('declin') || lower.includes('reject');
          const isRev = lower.includes('revok');
          const bg = isAcc ? '#dcfce7' : isDec ? '#fee2e2' : isRev ? '#ffedd5' : '#f1f5f9';
          const col = isAcc ? '#166534' : isDec ? '#991b1b' : isRev ? '#c2410c' : '#475569';
          pills.push(`<span style="display:inline-flex;align-items:center;gap:4px;margin:2px 4px 2px 0;padding:2px 8px;background:${bg};color:${col};border:1px solid #e2e8f0;border-radius:4px;font-size:11.5px;font-weight:600;">Status: ${escapeHtml(respOrStatus)}</span>`);
        }

        // Other metadata entries
        const skipKeys = new Set([
          'consent_message', 'message', 'consent', 'data_preview', 'body', 'request_details',
          'channel', 'ip_address', 'ip', 'responder_ip', 'browser', 'responder_browser', 'user_agent', 'responder_user_agent',
          'reminder_number', 'response', 'status', 'consent_id', 'consent_record_id'
        ]);

        const otherEntries = Object.entries(data).filter(([k, v]) => !skipKeys.has(k) && v !== null && v !== undefined && v !== '');
        for (const [key, val] of otherEntries) {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          const valStr = typeof val === 'object' ? JSON.stringify(val) : String(val);
          pills.push(`<span style="display:inline-block;margin:2px 4px 2px 0;padding:2px 7px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;font-size:11.5px;color:#475569;"><b>${escapeHtml(label)}:</b> ${escapeHtml(valStr)}</span>`);
        }

        let outHtml = pills.join(' ');

        if (consentMsg) {
          outHtml += `
            <div style="margin-top:6px;padding:7px 10px;background:#f8fafc;border-left:3px solid #3b82f6;border-radius:4px;font-size:11.5px;color:#1e293b;line-height:1.45;word-break:break-word;">
              <span style="font-weight:700;color:#1e40af;font-size:10.5px;text-transform:uppercase;letter-spacing:0.5px;display:block;margin-bottom:2px;">📋 Consent Message / Notice:</span>
              ${escapeHtml(consentMsg.length > 250 ? consentMsg.slice(0, 250) + '…' : consentMsg)}
            </div>
          `;
        }

        return outHtml || '<span style="color:#94a3b8">—</span>';
      };

      const renderActionBadge = (actionStr) => {
        const act = String(actionStr || 'Activity').trim();
        const lower = act.toLowerCase();
        if (lower.includes('version change')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#f5f3ff;color:#6d28d9;border:1px solid #ddd6fe;border-radius:6px;font-size:12px;font-weight:600;">🏷️ Version Changed</span>`;
        }
        if (lower.includes('reconsent')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#eef2ff;color:#4338ca;border:1px solid #c7d2fe;border-radius:6px;font-size:12px;font-weight:600;">🔄 Reconsent Requested</span>`;
        }
        if (lower.includes('reminder')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;border-radius:6px;font-size:12px;font-weight:600;">⏰ Reminder Sent</span>`;
        }
        if (lower.includes('status change')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#f0f9ff;color:#0369a1;border:1px solid #bae6fd;border-radius:6px;font-size:12px;font-weight:600;">🔄 Status Changed</span>`;
        }
        if (lower.includes('creat') || lower.includes('initial')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#f0fdf4;color:#15803d;border:1px solid #bbf7d0;border-radius:6px;font-size:12px;font-weight:600;">✓ Consent Created</span>`;
        }
        if (lower.includes('active') || lower.includes('accepted') || lower.includes('grant')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#dcfce7;color:#166534;border:1px solid #86efac;border-radius:6px;font-size:12px;font-weight:600;">✓ Consent Accepted</span>`;
        }
        if (lower.includes('revok')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;border-radius:6px;font-size:12px;font-weight:600;">⛔ Consent Revoked</span>`;
        }
        if (lower.includes('declin') || lower.includes('reject')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#fef2f2;color:#b91c1c;border:1px solid #fecaca;border-radius:6px;font-size:12px;font-weight:600;">✕ Consent Declined</span>`;
        }
        if (lower.includes('updat') || lower.includes('edit')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#fefce8;color:#a16207;border:1px solid #fef08a;border-radius:6px;font-size:12px;font-weight:600;">✏️ Consent Updated</span>`;
        }
        if (lower.includes('sent') || lower.includes('email') || lower.includes('mail')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;border-radius:6px;font-size:12px;font-weight:600;">✉️ Consent Sent</span>`;
        }
        if (lower.includes('whatsapp')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#f0fdf4;color:#15803d;border:1px solid #bbf7d0;border-radius:6px;font-size:12px;font-weight:600;">💬 WhatsApp Message</span>`;
        }
        if (lower.includes('cancel')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#f1f5f9;color:#475569;border:1px solid #cbd5e1;border-radius:6px;font-size:12px;font-weight:600;">🚫 Cancelled</span>`;
        }
        if (lower.includes('current')) {
          return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#f8fafc;color:#475569;border:1px solid #cbd5e1;border-radius:6px;font-size:12px;font-weight:600;">ℹ️ Current Status</span>`;
        }
        return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;background:#f1f5f9;color:#334155;border:1px solid #cbd5e1;border-radius:6px;font-size:12px;font-weight:600;">📋 ${escapeHtml(act)}</span>`;
      };

      const formatTimestampIST = (istStr, isoStr) => {
        if (istStr) return escapeHtml(istStr);
        if (!isoStr) return '—';
        try {
          const d = parseAsUTC(isoStr) || new Date(isoStr);
          return new Intl.DateTimeFormat('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour12: true,
            hour: '2-digit',
            minute: '2-digit'
          }).format(d);
        } catch {
          return escapeHtml(isoStr);
        }
      };

      const rowsHtml = history.length === 0
        ? `<tr><td colspan="4" style="text-align:center;padding:36px;color:#64748b;font-size:13.5px;"><div style="font-size:26px;margin-bottom:8px;">📋</div>No activity logs recorded yet for Consent Ref #${escapeHtml(encId)}.</td></tr>`
        : history.map((entry, idx) => {
          const timeFormatted = formatTimestampIST(entry.timestamp_ist, entry.timestamp);
          const actionBadge = renderActionBadge(entry.action);
          const role = entry.actor_role || (entry.action === 'Current consent status' ? 'Record' : (entry.action && entry.action.toLowerCase().includes('accept') ? 'End User' : 'Admin'));
          const email = entry.actor_email || (entry.action === 'Current consent status' ? 'System Record' : (record.email || 'System'));
          const detailsFormatted = formatFriendlyDetails(entry.details);

          return `
              <tr style="background:${idx % 2 === 0 ? '#ffffff' : '#f8fafc'}; transition: background 0.15s ease;">
                <td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;white-space:nowrap;color:#475569;font-size:12px;font-weight:500;">
                  ${timeFormatted}
                </td>
                <td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;white-space:nowrap;">
                  ${actionBadge}
                </td>
                <td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;color:#0f172a;font-size:12px;">
                  <div style="font-weight:700;font-size:11px;color:#475569;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:2px;">${escapeHtml(role)}</div>
                  <div style="font-family:monospace;font-size:12px;color:#0f172a;word-break:break-all;">${escapeHtml(email)}</div>
                </td>
                <td style="padding:10px 14px;border-bottom:1px solid #e2e8f0;color:#334155;font-size:12px;line-height:1.45;">
                  ${detailsFormatted}
                </td>
              </tr>
            `;
        }).join('');

      const personName = record.username || record.Name || record.name || 'User';
      const personEmail = record.email || '—';
      const consentType = record.type || record.consent_type || 'General';
      const statusText = record.status || 'Pending';

      const modalHtml = `
        <div style="text-align:left; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
          <!-- Summary Header Card -->
          <div style="background:#f1f5f9; border:1px solid #e2e8f0; border-radius:10px; padding:12px 16px; margin-bottom:16px; display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:12px;">
            <div style="display:flex; align-items:center; gap:10px;">
              <div style="width:38px; height:38px; border-radius:50%; background:#e0e7ff; color:#3730a3; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:16px;">
                👤
              </div>
              <div>
                <div style="font-weight:700; color:#0f172a; font-size:15px;">${escapeHtml(personName)}</div>
                <div style="font-size:12px; color:#64748b;">${escapeHtml(personEmail)}</div>
              </div>
            </div>

            <div style="display:flex; align-items:center; gap:12px;">
              <div style="text-align:right;">
                <div style="font-size:11px; text-transform:uppercase; color:#64748b; font-weight:600; letter-spacing:0.5px;">Consent Type</div>
                <div style="font-size:13px; font-weight:700; color:#1e293b;">${escapeHtml(consentType)}</div>
              </div>
              <div style="text-align:right;">
                <div style="font-size:11px; text-transform:uppercase; color:#64748b; font-weight:600; letter-spacing:0.5px;">Status</div>
                <div style="font-size:13px; font-weight:700; color:#0f172a;">${escapeHtml(statusText)}</div>
              </div>
              <div style="background:#e0f2fe; color:#0369a1; border:1px solid #bae6fd; padding:4px 10px; border-radius:6px; font-size:12px; font-weight:700; font-family:monospace;">
                🔒 Ref: ${escapeHtml(encId)}
              </div>
            </div>
          </div>

          <!-- Logs Table Container -->
          <div style="max-height:58vh; overflow-x:auto; overflow-y:auto; border:1px solid #cbd5e1; border-radius:8px; box-shadow:0 1px 3px rgba(0,0,0,0.05);">
            <table style="width:100%; min-width:820px; border-collapse:collapse; text-align:left; table-layout:auto;">
              <thead>
                <tr style="background:#f8fafc; position:sticky; top:0; z-index:2; border-bottom:2px solid #cbd5e1;">
                  <th style="padding:10px 14px; font-size:11.5px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:0.5px; width:160px;">Date & Time (IST)</th>
                  <th style="padding:10px 14px; font-size:11.5px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:0.5px; width:170px;">Activity Event</th>
                  <th style="padding:10px 14px; font-size:11.5px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:0.5px; width:200px;">Triggered By</th>
                  <th style="padding:10px 14px; font-size:11.5px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:0.5px;">Activity Details</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
          </div>

          <div style="display:flex; justify-content:space-between; align-items:center; margin-top:12px; padding:0 4px; font-size:12px; color:#64748b;">
            <span>Showing ${history.length} recorded activity event(s) for Consent Ref #${escapeHtml(encId)}</span>
            <span style="font-weight:500;">Time zone: Indian Standard Time (IST)</span>
          </div>
        </div>
      `;

      await Swal.fire({
        title: `Activity Logs — Consent Ref #${encId}`,
        html: modalHtml,
        width: 980,
        showCloseButton: true,
        showConfirmButton: true,
        confirmButtonText: 'Close',
        confirmButtonColor: '#2563eb'
      });
    } catch (err) {
      console.error('Failed to load activity logs', err);
      Swal.fire({
        icon: 'error',
        title: 'Unable to Load Logs',
        text: 'Failed to retrieve activity logs for this consent record. Please try again later.'
      });
    }
  };

  const handleMoreAction = async (type, rec) => {
    try {
      if (type === 'logs') {
        setMoreMenuOpenId(null);
        await openConsentLogsModal(rec);
        return;
      }
      setMoreLoading({ id: rec.id, type });
      // show loading for a short moment to indicate action
      await new Promise(r => setTimeout(r, 1200));
      if (type === 'whatsapp') await handleWhatsApp(rec);
      if (type === 'mail') await handleMail(rec);
      if (type === 'privacy') await handlePrivacyNotice(rec);
    } catch (e) {
      console.error('More action error', e);
    } finally {
      setMoreLoading({ id: null, type: null });
      setMoreMenuOpenId(null);
    }
  };

  useEffect(() => {
    if (moreMenuOpenId === null) return;
    const handler = () => setMoreMenuOpenId(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [moreMenuOpenId]);
  // const [cookieEvents, setCookieEvents] = useState([]);

  // Toggle to show/hide the Data column in the consents table
  const showDataColumn = false;

  const [userForm, setUserForm] = useState({
    username: '',
    ageCategory: 'adult',
    email: '',
    phone: '',
    address: '',
    dob: '',
    type: '',
    consentId: null,
    entity: '',
    consent: '',
    appName: '',
    userAction: '',
    data: '',
    expiry: '',
    status: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    _guardianType: 'parent',
    idProofType: [],
    idProofFileName: '',
    dataTypeConsent: [],
    locality: '',
    department: '',
    subCategory: '',
    language: '',
  });
  const [departments, setDepartments] = useState([]);
  const [subConsents, setSubConsents] = useState([]);
  const [loadingSubConsents, setLoadingSubConsents] = useState(false);
  const [showSubConsent, setShowSubConsent] = useState(false);
  const [consentTypesByLanguage, setConsentTypesByLanguage] = useState([]);
  const [loadingLanguageTypes, setLoadingLanguageTypes] = useState(false);
  const [tenantLanguageCodes, setTenantLanguageCodes] = useState([]);
  const [tenantLanguagesLoading, setTenantLanguagesLoading] = useState(true);

  useEffect(() => {
    // Fetch sub-consents for the currently selected `type` when Record Consent drawer opens
    let mounted = true;
    const fetchSubConsents = async () => {
      if (!isSlidingPageOpen) return;
      const rawType = userForm.type;
      if (!rawType) {
        setSubConsents([]);
        setShowSubConsent(false);
        return;
      }
      const selectedTypeMeta = findConsentTypeOption(consentTypesByLanguage, rawType);
      const typeForQuery = selectedTypeMeta
        ? (selectedTypeMeta.consent_type || selectedTypeMeta.type || selectedTypeMeta.name || rawType)
        : rawType;
      const parentConsentIdForQuery = selectedTypeMeta?.consent_id ?? selectedTypeMeta?.parent_consent_id ?? selectedTypeMeta?.id ?? userForm.consentId ?? null;

      setLoadingSubConsents(true);
      try {
        const tenant_id = (user && (user.tenant_id || user.tenantId || user.tenant)) ? (user.tenant_id || user.tenantId || user.tenant) : (Number(localStorage.getItem('tenant_id')) || null);
        const params = {};
        if (tenant_id) params.tenant_id = tenant_id;
        params.consent_type = typeForQuery;
        if (parentConsentIdForQuery != null) params.parent_consent_id = parentConsentIdForQuery;
        params.language = languageCodeFor(userForm.language || 'en');
        const res = await axios.get(`${API_BASE}/manager/sub-category-consents`, { params });
        let rows = res.data && (res.data.rows || res.data) ? (res.data.rows || res.data) : [];
        if (!Array.isArray(rows) || rows.length === 0) {
          const fallback = await axios.get(`${API_BASE}/manager/sub-category-consents`, {
            params: {
              ...(tenant_id ? { tenant_id } : {}),
              consent_type: typeForQuery,
              ...(parentConsentIdForQuery != null ? { parent_consent_id: parentConsentIdForQuery } : {}),
              language: languageCodeFor(userForm.language || 'en'),
            },
          });
          const fallbackRows = fallback.data && (fallback.data.rows || fallback.data) ? (fallback.data.rows || fallback.data) : [];
          const normalizeLookup = value => String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
          const wanted = normalizeLookup(typeForQuery);
          const wantedLanguage = languageCodeFor(userForm.language || 'en').toLowerCase();
          rows = (Array.isArray(fallbackRows) ? fallbackRows : []).filter(row => {
            let meta = row.data || {};
            try { if (typeof meta === 'string') meta = JSON.parse(meta); } catch (e) { meta = {}; }
            const candidates = [
              meta.parent_consent_type, meta.parent_type, meta.parentType,
              meta.parentConsentType, row.name, row.sub_category,
            ].filter(Boolean).map(normalizeLookup);
            if (!candidates.includes(wanted)) return false;
            const rowLanguage = languageCodeFor(meta.language_code || meta.language || meta.lang || meta.language_name || meta.languageName || meta.languageCode || '').toLowerCase();
            return (rowLanguage || 'en') === wantedLanguage;
          });
        }
        const mapped = mapSubConsentOptions(rows, userForm.language);
        if (mounted) setSubConsents(mapped);
        const has = mapped && mapped.length > 0;
        if (mounted) setShowSubConsent(has);
      } catch (e) {
        console.warn('Failed to fetch sub-consents', e);
        if (mounted) { setSubConsents([]); setShowSubConsent(false); }
      } finally {
        if (mounted) setLoadingSubConsents(false);
      }
    };
    fetchSubConsents();
    return () => { mounted = false; };
  }, [isSlidingPageOpen, userForm.type, userForm.language, userForm.consentId]);

  // When subCategory or language changes in the record form, attempt to pick
  // the matching multilingual variant (if available) and prefill the consent
  // content shown to the user. This avoids manual translation — we use stored
  // language variants as-is.
  useEffect(() => {
    try {
      const subId = userForm.subCategory;
      const lang = userForm.language || 'en';
      if (!subId) return;
      const findVariantMessage = (id, language) => {
        try {
          // first try direct match by id
          const primary = (subConsents || []).find(s => String(s.id) === String(id));
          if (primary) {
            const raw = primary.message;
            if (raw && typeof raw === 'string') {
              try {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.kind === 'multilingual_consent') {
                  if (!language || parsed.language_code === language) return parsed.consent_message || '';
                }
              } catch (e) { /* not JSON, fallback below */ }
            }
            // fallback to raw message
            if (primary.message) return primary.message;
          }
          // otherwise search all subConsents for a multilingual variant matching language
          for (const s of (subConsents || [])) {
            try {
              const raw = s.message;
              if (!raw) continue;
              const parsed = (typeof raw === 'string') ? JSON.parse(raw) : raw;
              if (parsed && parsed.kind === 'multilingual_consent' && parsed.language_code === language) return parsed.consent_message || '';
            } catch (e) { continue; }
          }
        } catch (e) { /* ignore */ }
        return null;
      };
      const msg = findVariantMessage(subId, lang);
      if (msg) {
        setUserForm(prev => ({ ...prev, consent: msg }));
      }
    } catch (e) { /* ignore */ }
  }, [userForm.subCategory, userForm.language, subConsents]);
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        // const res = await axios.get(`${API_BASE}/manager/departments`);
        const tenant_id = (user && (user.tenant_id || user.tenantId || user.tenant)) ? (user.tenant_id || user.tenantId || user.tenant) : (Number(localStorage.getItem('tenant_id')) || null);
        const params = tenant_id ? { params: { tenant_id }, skipLoading: true } : { skipLoading: true };
        const res = await axios.get(`${API_BASE}/manager/departments`, params);
        const rows = Array.isArray(res?.data?.departments)
          ? res.data.departments
          : (Array.isArray(res?.data) ? res.data : []);
        const mapped = (rows || []).map(r => ({ id: r.id || r.department_id || r.id, name: r.name || r.department || r.department_name || r.title || String(r) }));
        mapped.sort((a, b) => (a.name || '').toString().localeCompare((b.name || '').toString()));
        setDepartments(mapped);
      } catch (e) {
        console.warn('Failed to fetch departments', e);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch language-filtered consent types when language changes
  useEffect(() => {
    const fetchLanguageTypes = async () => {
      try {
        const lang = userForm.language || 'en';
        if (!lang) return;

        const tenant_id = (user && (user.tenant_id || user.tenantId || user.tenant))
          ? (user.tenant_id || user.tenantId || user.tenant)
          : (Number(localStorage.getItem('tenant_id')) || null);

        if (!tenant_id) {
          setConsentTypesByLanguage([]);
          return;
        }
        setLoadingLanguageTypes(true);
        try {
          // Ensure params are sent as strings to avoid 422 due to unexpected types
          const params = { tenant_id: String(tenant_id), language: String(lang) };
          const res = await axios.get(`${API_BASE}/user/consent/types-by-language`, { params });
          // const types = (res.data && res.data.types) ? res.data.types : [];
          let types = (res.data && res.data.types) ? res.data.types : [];

          // Language titles are maintained in sub_category_consent. Merge them
          // here because the type endpoint may only return the canonical key.
          try {
            const subRes = await axios.get(`${API_BASE}/manager/sub-category-consents`, {
              params: { tenant_id, language: String(lang) },
            });
            const subRows = Array.isArray(subRes?.data?.rows)
              ? subRes.data.rows
              : (Array.isArray(subRes?.data) ? subRes.data : []);
            const localizedByType = {};
            for (const row of subRows) {
              let parsed = {};
              try {
                parsed = typeof row.data === 'string' ? JSON.parse(row.data) : (row.data || {});
              } catch (e) { parsed = {}; }
              const parent = parsed.parent_consent_type || row.name || '';
              const parentKey = String(parent).trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
              if (!parentKey) continue;
              const title = parsed.title || parsed.localized_label || parsed.label ||
                (typeof row.sub_category === 'string' && row.sub_category.includes('(')
                  ? row.sub_category.slice(row.sub_category.lastIndexOf('(') + 1).replace(/\)\s*$/, '').trim()
                  : '');
              if (title) localizedByType[parentKey] = title;
            }
            if (Object.keys(localizedByType).length > 0) {
              types = types.map(item => {
                const value = item.consent_type || item.type || item.name || '';
                const key = String(value).trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                const label = localizedByType[key] || localizedByType[key.replace(/_consent$/, '')];
                return label && !(item.label || item.title || item.localized_label || item.localizedLabel)
                  ? { ...item, label, title: label, language_code: lang }
                  : item;
              });
            }
          } catch (e) {
            console.warn('Failed to merge localized consent type titles', e);
          }
          // Merge the language-scoped list with the tenant's complete consent set to
          // keep the form complete while still honoring the currently selected language.
          const uniqueTypes = [];
          // const seenTypeKeys = new Set();
          const addTypeItem = (item) => {
            if (!item) return;
            const value = item.consent_type || item.type || item.name || item.id || '';
            const label = item.label || item.title || item.localized_label || item.localizedLabel || '';
            const normalizedValue = String(value).trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
            const normalizedLabel = String(label).trim().toLowerCase().replace(/\s+/g, '_');
            const rawValue = String(value).trim().toLowerCase();
            const key = `${normalizedValue || rawValue}|${normalizedLabel || rawValue}`;
            // if (!key || seenTypekeys.has(key)) return;
            // seenTypekeys.add(key);
            if (!key) return;
            const existingIndex = uniqueTypes.findIndex(existingItem => {
              const existingValue = existingItem?.consent_type || existingItem?.type || existingItem?.name || existingItem?.id || '';
              const existingKey = String(existingValue).trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
              return existingKey === (normalizedValue || rawValue);
            });
            if (existingIndex >= 0) {
              const existingItem = uniqueTypes[existingIndex];
              const incomingLang = normalizeLangCode(item?.language_code || item?.language || item?.language_name || 'en');
              const existingLang = normalizeLangCode(existingItem?.language_code || existingItem?.language || existingItem?.language_name || 'en');
              const incomingMatches = incomingLang === normalizeLangCode(lang);
              const existingMatches = existingLang === normalizeLangCode(lang);
              if (incomingMatches && !existingMatches) {
                uniqueTypes[existingIndex] = { ...item, consent_type: value, type: value, name: value, label: label || value };
              }
              return;
            }
            uniqueTypes.push(item);
          };

          for (const item of types) addTypeItem(item);

          const latestStatusByType = new Map();
          try {
            const allRes = await axios.get(`${API_BASE}/manager/new-consents`, {
              params: { tenant_id, include_variants: true, limit: 500 },
            });
            const allRows = allRes?.data?.data || allRes?.data?.new_consents || allRes?.data?.new_consent || [];
            const normalizedRequestedLang = normalizeLangCode(String(lang));
            const rowsById = new Map(
              (Array.isArray(allRows) ? allRows : [])
                .filter(row => row?.id != null)
                .map(row => [String(row.id), row])
            );
            // Track the latest status of each consent type
            for (const row of Array.isArray(allRows) ? allRows : []) {
              const ct = String(row.consent_type || row.type || row.name || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
              if (ct && (row.parent_id == null || row.parent_id === 0) && !latestStatusByType.has(ct)) {
                latestStatusByType.set(ct, String(row.status || '').trim().toLowerCase());
              }
            }

            for (const row of Array.isArray(allRows) ? allRows : []) {
              const rowStatus = String(row.status || '').trim().toLowerCase();
              // if (rowStatus !== 'accepted') continue;
              if (!['accepted', 'active', 'approved', 'granted', 'agree', 'agreed', 'yes'].includes(rowStatus)) continue;
              let rowMetadata = row.consent_metadata || row.data || {};
              try { if (typeof rowMetadata === 'string') rowMetadata = JSON.parse(rowMetadata); } catch (e) { rowMetadata = {}; }
              if (!rowMetadata || typeof rowMetadata !== 'object') rowMetadata = {};
              const parentRow = row.parent_id != null ? rowsById.get(String(row.parent_id)) : null;
              const canonicalType = rowMetadata.parent_consent_type || rowMetadata.parent_type || rowMetadata.parentType ||
                parentRow?.consent_type || parentRow?.type || parentRow?.name || '';
              const isVariant = rowMetadata.kind === 'multilingual_consent' || rowMetadata.is_multilingual_variant || row.parent_id != null;
              const rowType = row.consent_type || row.type || row.name || row.key || row.consentType || '';
              if (isVariant && !canonicalType) continue;
              const ct = isVariant ? canonicalType : rowType;
              if (!ct) continue;
              const normCt = String(ct).trim().toLowerCase().replace(/[\s-]+/g, '_');
              // if (latestStatusByType.get(normCt) !== 'accepted') continue;
              const ctStatus = latestStatusByType.get(normCt);
              if (['inactive', 'rejected', 'declined', 'deleted'].includes(ctStatus)) continue;
              const rawLang = row.language_code || row.language || row.lang || row.language_name || row.languageName ||
                rowMetadata.language_code || rowMetadata.language || rowMetadata.lang || rowMetadata.language_name || '';
              const langCode = normalizeLangCode(rawLang || 'en');
              if (langCode !== normalizedRequestedLang) continue;
              const label = row.label || row.title || row.localized_label || row.localizedLabel || ct;
              addTypeItem({
                ...row,
                consent_type: ct,
                type: ct,
                name: ct,
                label,
                title: label,
                language_code: langCode,
                ...(isVariant && canonicalType ? {
                  consent_id: row.parent_id || rowMetadata.parent_consent_id || rowMetadata.parentConsentId || parentRow?.id,
                  parent_consent_id: row.parent_id || rowMetadata.parent_consent_id || rowMetadata.parentConsentId || parentRow?.id,
                } : {}),
              });
            }
          } catch (e) {
            console.warn('Failed to merge complete consent type list for record form', e);
          }

          const finalFilteredTypes = uniqueTypes.filter(item => {
            const val = item?.consent_type || item?.type || item?.name || '';
            const normVal = String(val).trim().toLowerCase().replace(/[\s-]+/g, '_');
            const latestSt = latestStatusByType.get(normVal);
            return !['inactive', 'rejected', 'declined', 'deleted'].includes(latestSt);
          });

          setConsentTypesByLanguage(dedupeConsentTypeOptions(finalFilteredTypes, lang));

        } catch (err) {
          // log full error for debugging (includes FastAPI validation details)
          console.error('Failed to fetch language-filtered consent types', err, err && err.response && err.response.data);
          // If backend returns 422, fallback to fetching manager/new-consents and filter client-side
          const status = err && err.response && err.response.status;
          if (!err.response || status === 404 || status === 422 || status >= 500) {
            try {
              const params = { tenant_id, include_variants: true };
              const res2 = await axios.get(`${API_BASE}/manager/new-consents`, { params });
              const rows = res2.data && (res2.data.data || res2.data.new_consents || res2.data.new_consent || res2.data) ? (res2.data.data || res2.data.new_consents || res2.data.new_consent || res2.data) : [];
              const filtered = [];
              const filteredKeys = new Set();
              const latestFallbackStatus = new Map();
              (Array.isArray(rows) ? rows : []).forEach(r => {
                const ct = String(r.consent_type || r.type || r.name || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
                if (ct && (r.parent_id == null || r.parent_id === 0) && !latestFallbackStatus.has(ct)) {
                  latestFallbackStatus.set(ct, String(r.status || '').trim().toLowerCase());
                }
              });
              for (const r of (rows || [])) {
                try {
                  const consentStatus = String(r.status || '').trim().toLowerCase();
                  // if (consentStatus !== 'accepted') continue;
                  if (!['accepted', 'active', 'approved', 'granted', 'agree', 'agreed', 'yes'].includes(consentStatus)) continue;
                  const ct = r.consent_type || r.type || r.key || r.consentType || '';
                  const typeKey = String(ct).trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                  if (!typeKey || filteredKeys.has(typeKey)) continue;

                  // if (latestFallbackStatus.get(typeKey) !== 'accepted') continue;
                  const fallbackSt = latestFallbackStatus.get(typeKey);
                  if (['inactive', 'rejected', 'declined', 'deleted'].includes(fallbackSt)) continue;
                  // try language field directly
                  const lc = (r.language_code || r.language || (r.consent_metadata && (typeof r.consent_metadata === 'object' ? r.consent_metadata.language_code : (typeof r.consent_metadata === 'string' && r.consent_metadata.includes('language_code') ? null : null))) || '').toString().toLowerCase();
                  // try parsing consent_message from JSON columns
                  let msg = r.consent_message || r.message || r.consent || '';
                  try {
                    if (typeof msg === 'string' && msg.trim().startsWith('{')) {
                      const pj = JSON.parse(msg);
                      if (pj && pj.consent_message) msg = pj.consent_message;
                    }
                  } catch (e) { }
                  // If explicit language code matches requested language, include
                  if ((lc && lc === lang.toString().toLowerCase())) {
                    filtered.push({ consent_type: ct, consent_message: msg, language_code: lc });
                    filteredKeys.add(typeKey);
                    continue;
                  }
                  // otherwise, check consent_metadata/data JSON for multilingual wrapper
                  try {
                    const meta = r.consent_metadata || r.data || null;
                    if (meta) {
                      let mj = null;
                      if (typeof meta === 'string' && meta.trim().startsWith('{')) mj = JSON.parse(meta);
                      else if (typeof meta === 'object') mj = meta;
                      if (mj && mj.kind === 'multilingual_consent' && mj.language_code && mj.language_code.toString().toLowerCase() === lang.toString().toLowerCase()) {
                        filtered.push({ consent_type: ct, consent_message: mj.consent_message || msg, language_code: mj.language_code });
                        filteredKeys.add(typeKey);
                        continue;
                      }
                    }
                  } catch (e) { }
                } catch (e) { /* ignore row parse errors */ }
              }
              if (filtered.length) setConsentTypesByLanguage(dedupeConsentTypeOptions(filtered, lang));
              else setConsentTypesByLanguage([]);
            } catch (e) {
              console.error('Fallback fetch for consent types failed', e);
              setConsentTypesByLanguage([]);
            }
          } else {
            setConsentTypesByLanguage([]);
          }
        }
      } catch (e) {
        console.warn('Failed to fetch language-filtered consent types', e);
        setConsentTypesByLanguage([]);
      } finally {
        setLoadingLanguageTypes(false);
      }
    };

    if (isSlidingPageOpen) {
      fetchLanguageTypes();
    }
  }, [userForm.language, isSlidingPageOpen]);

  const [idProofFile, setIdProofFile] = useState(null);
  const [useParentContact, setUseParentContact] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [revokeForm, setRevokeForm] = useState({
    reason: '',
    comments: '',
    attachments: [],
  });
  const [revokeOptions, setRevokeOptions] = useState({
    email: false,
    whatsapp: false,
    dob: false,
    all: false,
  });
  const [isCommentsOpen, setCommentsOpen] = useState(false);
  const [commentsRecord, setCommentsRecord] = useState(null);
  const [mailLoading, setMailLoading] = useState({});

  // Privacy Notice send modal state
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [privacyModalConsent, setPrivacyModalConsent] = useState(null);
  const [privacyModalNotice, setPrivacyModalNotice] = useState(null);
  const [privacyMessage, setPrivacyMessage] = useState('');
  const [privacyFormat, setPrivacyFormat] = useState('text');
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [privacyAutoTranslate, setPrivacyAutoTranslate] = useState(true);
  const [privacyTargetLanguage, setPrivacyTargetLanguage] = useState('');
  const [privacyAttachments, setPrivacyAttachments] = useState([]);
  const [privacyGenerating, setPrivacyGenerating] = useState(false);
  const [privacyStatusMessage, setPrivacyStatusMessage] = useState('');
  const privacyAttachmentRef = useRef(null);

  // missing refs/state restored (used throughout the component)
  const firstInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const csvFileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const detailsPollRef = useRef(null);
  const [isParentModalOpen, setParentModalOpen] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // const showLegend = true;
  const [showLegend, setShowLegend] = useState(false);

  const previewPrivacyAttachment = (idx) => {
    try {
      const f = privacyAttachments[idx];
      if (!f) return;
      const url = URL.createObjectURL(f);
      window.open(url, '_blank');
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { } }, 10000);
    } catch (e) { console.warn('Preview failed', e); }
  };

  const sendAuditLog = async (action, targetEmail, details) => {
    try {
      const raw = localStorage.getItem('user');
      let actor = null;
      let actorId = null;
      try {
        if (raw) {
          const parsed = JSON.parse(raw);
          actor = parsed.email || parsed.user_email || parsed.username || null;
          actorId = parsed.id || parsed.user_id || null;
        }
      } catch (e) { }
      const payload = {
        tenant_id: user?.tenant_id || null,
        user_id: null,
        action: action,
        target_email: targetEmail || null,
        actor_email: actor || null,
        actor_id: actorId || null,
        details: details || `${action} target=${targetEmail} actor=${actor || ''}`
      };
      await axios.post(`${API_BASE}/manager/logs`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
    } catch (e) {
      // ignore logging failures
    }
  };
  const handleCSVInputChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 150 * 1024 * 1024) {
      await Swal.fire({
        title: 'CSV file is too large',
        text: 'Please upload a CSV file that is 150 MB or smaller.',
        icon: 'error'
      });
      try { if (e.target) e.target.value = null; } catch (_) { }
      setPendingCSVFile(null);
      setShowCSVConfirm(false);
      setCsvUploadMessage(null);
      setCsvUploadError(null);
      return;
    }
    setSelectedUploadLanguage('en');
    setSelectedUploadSubCategory('');
    let detectedType = '';
    let detectedMsg = '';
    if (f.size > 25 * 1024 * 1024) {
      setPendingCSVFile(f);
      setShowCSVConfirm(true);
      setCsvUploadMessage(null);
      setCsvUploadError(null);
      return;
    }

    try {
      const text = await f.text();
      // split into lines, preserve comment lines so we can skip them when searching for header
      const rawLines = text.split(/\r?\n/);
      // find header line: skip leading comment/blank lines
      let headerLineIndex = -1;
      for (let i = 0; i < rawLines.length; i++) {
        const l = rawLines[i].trim();
        if (!l) continue;
        if (l.startsWith('#')) continue;
        headerLineIndex = i; break;
      }
      if (headerLineIndex === -1) {
        setCsvUploadError('CSV appears empty or only contains comments');
        try { if (e && e.target) e.target.value = null; } catch (_) { }
        return;
      }

      // rows: slice from the detected header line so subsequent indexing is simple
      const rows = rawLines.slice(headerLineIndex);

      const parseCSVLine = (line) => {
        const res = []; let cur = '', inQ = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') { inQ = !inQ; continue; }
          if (ch === ',' && !inQ) { res.push(cur.trim()); cur = ''; continue; }
          cur += ch;
        }
        res.push(cur.trim()); return res;
      };

      const headers = parseCSVLine(rows[0]).map(h => h.toLowerCase().trim());
      // Attempt to auto-detect a single "type" value from CSV headers/rows
      try {
        const norm = (s) => String(s || '').toLowerCase().replace(/[^0-9a-z]+/g, ' ').replace(/\s+/g, ' ').trim();
        const findTypeIdx = () => {
          for (let i = 0; i < headers.length; i++) {
            const h = headers[i] || '';
            const nh = norm(h);
            if (nh === 'type' || nh === 'consent type' || nh === 'consenttype' || nh === 'consent_type') return i;
          }
          return -1;
        };
        const tidx = findTypeIdx();
        if (tidx !== -1) {
          const vals = new Set();
          for (let i = 1; i < rows.length; i++) {
            if (!rows[i].trim()) continue;
            const cols = parseCSVLine(rows[i]);
            const v = (cols[tidx] || '').trim();
            if (v) vals.add(v);
          }
          if (vals.size === 1) detectedType = Array.from(vals)[0];
        }
        if (detectedType && Array.isArray(types) && types.length) {
          const match = types.find(t => String(t || '').toLowerCase() === String(detectedType || '').toLowerCase());
          if (match) detectedType = match;
        }
        if (detectedType && typeMessages) {
          const nk = (detectedType || '').toString().toLowerCase().replace(/[^0-9a-z]+/g, ' ').replace(/\s+/g, ' ').trim();
          for (const k of Object.keys(typeMessages || {})) {
            const kk = (k || '').toString().toLowerCase().replace(/[^0-9a-z]+/g, ' ').replace(/\s+/g, ' ').trim();
            if (kk === nk) { detectedMsg = typeMessages[k] || ''; break; }
          }
        }
      } catch (e) { console.warn('CSV type auto-detect failed', e); }
      const VALID_ENTITIES = ['employee', 'vendor', 'supplier', 'partner', 'customer', 'employees', 'vendors', 'suppliers', 'partners', 'customers'];
      const validationErrors = [];
      const validationErrorItems = [];
      const phoneWarnings = [];
      const phoneWarningItems = [];
      const deptWarnings = [];

      for (let i = 1; i < rows.length; i++) {
        if (!rows[i].trim()) continue;
        const cols = parseCSVLine(rows[i]);
        // Skip instruction/comment rows where first non-empty cell starts with '#'
        try {
          const firstNonEmpty = cols.find(c => c !== undefined && c !== null && String(c).trim() !== '');
          if (firstNonEmpty && String(firstNonEmpty).trim().startsWith('#')) continue;
        } catch (e) { }
        const rowNum = i + 1;

        const getVal = (name, aliases = []) => {
          const candidateNames = [name, ...aliases].map(v => String(v || '').toLowerCase().replace(/[\s_]/g, ''));
          const foundIdx = headers.findIndex(h => {
            const normalizedHeader = String(h || '').toLowerCase().replace(/[\s_]/g, '');
            return candidateNames.includes(normalizedHeader);
          });
          if (foundIdx === -1) return '';
          return (cols[foundIdx] || '').trim();
        };

        const email = getVal('email') || getVal('parentemail');
        if (!email) {
          const errMsg = `Row ${rowNum}: Missing required email field.`;
          validationErrors.push(errMsg);
          validationErrorItems.push({
            row: rowNum,
            username: getVal('username') || getVal('name') || 'Unknown',
            category: 'Missing Email',
            field: 'email',
            value: '(empty)',
            message: 'Email address field is missing or empty.',
            solution: 'Provide a valid user email address.',
          });
        }

        const username = getVal('username') || getVal('name');
        if (!username) {
          const errMsg = `Row ${rowNum}: Missing required username/name field.`;
          validationErrors.push(errMsg);
          validationErrorItems.push({
            row: rowNum,
            username: email || 'Unknown',
            category: 'Missing Name',
            field: 'username',
            value: '(empty)',
            message: 'Username / Name field is required.',
            solution: 'Enter the user’s full name or username.',
          });
        }

        const entity = getVal('entity', ['group', 'groupName', 'group_name', 'entityType', 'entity_type', 'user_group']);
        if (!entity) {
          const errMsg = `Row ${rowNum}: Missing required group/entity field.`;
          validationErrors.push(errMsg);
          validationErrorItems.push({
            row: rowNum,
            username: username || email || 'Unknown',
            category: 'Missing Group',
            field: 'entity',
            value: '(empty)',
            message: 'Group/Entity field is required.',
            solution: 'Enter an entity group (e.g. employee, vendor, partner, customer).',
          });
        } else {
          const normalizedEntity = entity.toLowerCase();
          const aliasMap = {
            supplier: 'vendor',
            suppliers: 'vendor',
            customer: 'customer',
            customers: 'customer',
            partner: 'partner',
            partners: 'partner',
            employee: 'employee',
            employees: 'employee',
          };
          const canonicalEntity = aliasMap[normalizedEntity] || normalizedEntity;
          if (!VALID_ENTITIES.includes(normalizedEntity) && !VALID_ENTITIES.includes(canonicalEntity)) {
            console.warn(`Row ${rowNum}: entity "${entity}" is invalid — will default to employee`);
          }
        }

        const rawCategory = getVal('category', ['agecategory', 'age_category', 'minoradult', 'usercategory', 'agegroup']);
        const parentName = getVal('parentname');
        const parentEmail = getVal('parentemail');
        const parentPhone = getVal('parentphone');
        const hasParentFields = Boolean(parentName || parentEmail || parentPhone);

        let rowCategory = 'adult';
        if (rawCategory) {
          const normCat = rawCategory.toLowerCase().trim();
          if (normCat === 'minor' || normCat === 'under 18' || normCat === 'child') {
            rowCategory = 'minor';
          } else {
            rowCategory = 'adult';
          }
        } else if (hasParentFields) {
          rowCategory = 'minor';
        }

        const dob = getVal('dob');
        let parsedAge = null;
        if (dob) {
          const birth = new Date(dob);
          if (!isNaN(birth.getTime())) {
            const today = new Date();
            let age = today.getFullYear() - birth.getFullYear();
            const m = today.getMonth() - birth.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
            parsedAge = age;
          }
        }

        if (rowCategory === 'minor') {
          // Minor MUST have DOB
          if (!dob) {
            const errMsg = `Row ${rowNum} ("${username || 'unknown'}"): Minor requires date of birth (DOB).`;
            validationErrors.push(errMsg);
            validationErrorItems.push({
              row: rowNum,
              username: username || email || 'Unknown',
              category: 'Missing Minor DOB',
              field: 'dob',
              value: '(empty)',
              message: 'Minor consent records require a date of birth.',
              solution: 'Provide a valid date of birth (under 18) for the minor.',
            });
          } else if (parsedAge !== null && parsedAge >= 18) {
            const errMsg = `Row ${rowNum} ("${username || 'unknown'}"): Marked as Minor but DOB indicates age 18 or above (age ${parsedAge}).`;
            validationErrors.push(errMsg);
            validationErrorItems.push({
              row: rowNum,
              username: username || email || 'Unknown',
              category: 'Age Conflict',
              field: 'dob',
              value: `${dob} (Age ${parsedAge})`,
              message: 'Marked as Minor, but date of birth indicates age 18 or older (Adult).',
              solution: 'Change category to Adult or provide a minor date of birth.',
            });
          }

          // Minor MUST have guardian details
          const missingParent = [];
          if (!parentName) missingParent.push('parentName');
          if (!parentEmail) missingParent.push('parentEmail');
          if (!parentPhone) missingParent.push('parentPhone');

          if (missingParent.length > 0) {
            const errMsg = `Row ${rowNum} ("${username || 'unknown'}"): Minor requires guardian details: ${missingParent.join(', ')}.`;
            validationErrors.push(errMsg);
            validationErrorItems.push({
              row: rowNum,
              username: username || email || 'Unknown',
              category: 'Guardian Consent',
              field: missingParent.join(', '),
              value: parsedAge !== null ? `Age ${parsedAge}` : 'Minor',
              message: `User is marked as Minor and requires guardian details: ${missingParent.join(', ')}.`,
              solution: 'Add parentName, parentEmail, and parentPhone for minor users.',
            });
          }
        } else {
          // Adult: DOB is optional, but if provided, age MUST NOT be under 18
          if (dob && parsedAge !== null && parsedAge < 18) {
            const errMsg = `Row ${rowNum} ("${username || 'unknown'}"): Adult cannot have date of birth under 18 (age ${parsedAge}).`;
            validationErrors.push(errMsg);
            validationErrorItems.push({
              row: rowNum,
              username: username || email || 'Unknown',
              category: 'Age Conflict',
              field: 'dob',
              value: `${dob} (Age ${parsedAge})`,
              message: `User is marked as Adult, but date of birth indicates age under 18 (${parsedAge} years).`,
              solution: 'Change category to Minor with guardian details, or provide an adult date of birth.',
            });
          }
        }

        const validateIndianPhone = (number, label, rNum) => {
          if (!number) return null; // empty allowed
          let cleaned = number.replace(/\s|-/g, '');
          if (cleaned.startsWith('+91')) cleaned = cleaned.slice(3);
          else if (cleaned.startsWith('91') && cleaned.length === 12) cleaned = cleaned.slice(2);
          if (!/^\d+$/.test(cleaned))
            return `Row ${rNum}: ${label} "${number}" contains non-numeric characters.`;
          if (cleaned.length !== 10)
            return `Row ${rNum}: ${label} "${number}" must be exactly 10 digits (got ${cleaned.length}).`;
          if (!['6', '7', '8', '9'].includes(cleaned[0]))
            return `Row ${rNum}: ${label} "${number}" must start with 6, 7, 8, or 9.`;
          return null;
        };

        const phoneVal = getVal('phone');
        const parentPhoneVal = getVal('parentphone');

        const userPhoneErr = validateIndianPhone(phoneVal, 'phone', rowNum);
        const parentPhoneErr = validateIndianPhone(parentPhoneVal, 'parentPhone', rowNum);

        if (userPhoneErr) {
          phoneWarnings.push(userPhoneErr);
          phoneWarningItems.push({
            row: rowNum,
            username: username || email || 'Unknown',
            field: 'phone',
            value: phoneVal,
            message: userPhoneErr,
            solution: 'Must be 10 digits starting with 6, 7, 8, or 9.',
          });
        }
        if (parentPhoneErr) {
          phoneWarnings.push(parentPhoneErr);
          phoneWarningItems.push({
            row: rowNum,
            username: username || email || 'Unknown',
            field: 'parentPhone',
            value: parentPhoneVal,
            message: parentPhoneErr,
            solution: 'Parent phone must be 10 digits starting with 6, 7, 8, or 9.',
          });
        }

        // ── Locality / Location validation: must match known Indian localities ──
        const localityVal = getVal('locality') || getVal('location');
        if (localityVal) {
          const allowedLocs = Array.isArray(effectiveLocations) ? effectiveLocations.map(l => String(l || '').toLowerCase().trim()) : [];
          if (allowedLocs.length > 0 && !allowedLocs.includes((localityVal || '').toLowerCase().trim())) {
            const errMsg = `Row ${rowNum} ("${getVal('username') || 'unknown'}"): locality "${localityVal}" is not a valid location.`;
            validationErrors.push(errMsg);
            validationErrorItems.push({
              row: rowNum,
              username: username || email || 'Unknown',
              category: 'Invalid Location',
              field: 'locality',
              value: localityVal,
              message: `Locality "${localityVal}" is not recognized by the system.`,
              solution: allowedLocs.length ? `Valid locations: ${allowedLocs.slice(0, 4).join(', ')}${allowedLocs.length > 4 ? ', etc.' : ''}` : 'Provide a registered location.',
            });
          }
        }

        // ── Department validation: must match departments fetched from server when available ──
        const deptVal = getVal('department');
        if (deptVal) {
          if (departments && departments.length > 0) {
            const allowedDeps = departments.map(d => (d.name || '').toString().toLowerCase().trim());
            if (!allowedDeps.includes((deptVal || '').toLowerCase().trim())) {
              const errMsg = `Row ${rowNum} ("${getVal('username') || 'unknown'}"): department "${deptVal}" is not recognized by the system.`;
              validationErrors.push(errMsg);
              validationErrorItems.push({
                row: rowNum,
                username: username || email || 'Unknown',
                category: 'Invalid Department',
                field: 'department',
                value: deptVal,
                message: `Department "${deptVal}" is not recognized by the system.`,
                solution: departments.length ? `Valid departments: ${departments.map(d => d.name).slice(0, 4).join(', ')}${departments.length > 4 ? ', etc.' : ''}` : 'Provide a registered department.',
              });
            }
          } else {
            deptWarnings.push(`Row ${rowNum}: department "${deptVal}" could not be validated (server departments not loaded).`);
          }
        }
      }

      const escapeHtml = (s) => (s || '').toString().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

      // ── Handle CSV Validation Errors with Grouped Counts and Clean Table (No Red Screen) ──
      if (validationErrors.length) {
        // Prevent background red error banner from covering the screen
        setCsvUploadError(null);
        setCsvUploadMessage(null);

        const totalErrors = validationErrorItems.length;
        const uniqueRows = Array.from(new Set(validationErrorItems.map(item => item.row))).sort((a, b) => a - b);
        const affectedRowsCount = uniqueRows.length;

        // Group counts by error category
        const categoryGroups = {};
        validationErrorItems.forEach(item => {
          if (!categoryGroups[item.category]) {
            categoryGroups[item.category] = { count: 0, solution: item.solution, rows: [] };
          }
          categoryGroups[item.category].count += 1;
          categoryGroups[item.category].rows.push(item.row);
        });

        // Summary cards for each error category
        const categoryCardsHtml = Object.entries(categoryGroups).map(([cat, info]) => {
          let badgeBg = '#fee2e2';
          let badgeColor = '#991b1b';
          let borderCol = '#fecaca';
          if (cat.includes('Department')) { badgeBg = '#fef3c7'; badgeColor = '#92400e'; borderCol = '#fde68a'; }
          else if (cat.includes('Location') || cat.includes('Locality')) { badgeBg = '#e0f2fe'; badgeColor = '#0369a1'; borderCol = '#bae6fd'; }
          else if (cat.includes('Email') || cat.includes('Name')) { badgeBg = '#fee2e2'; badgeColor = '#b91c1c'; borderCol = '#fecaca'; }

          return `
            <div style="background:#ffffff;border:1px solid ${borderCol};border-radius:8px;padding:10px 12px;text-align:left;box-shadow:0 1px 2px rgba(0,0,0,0.04);">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
                <span style="font-size:12.5px;font-weight:700;color:#0f172a;">${escapeHtml(cat)}</span>
                <span style="font-size:11px;font-weight:700;padding:2px 8px;border-radius:10px;background:${badgeBg};color:${badgeColor};">${info.count} row${info.count > 1 ? 's' : ''}</span>
              </div>
              <div style="font-size:11px;color:#475569;line-height:1.4;">${escapeHtml(info.solution)}</div>
            </div>
          `;
        }).join('');

        // Clean, compact table rows (internal scrollable container, avoids page scroll)
        const rowsHtml = validationErrorItems.map((item, idx) => {
          const isEven = idx % 2 === 0;
          return `
            <tr style="background:${isEven ? '#ffffff' : '#f8fafc'};border-bottom:1px solid #f1f5f9;">
              <td style="padding:6px 10px;font-weight:700;color:#2563eb;white-space:nowrap;">Row ${item.row}</td>
              <td style="padding:6px 10px;color:#0f172a;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:110px;" title="${escapeHtml(item.username)}">${escapeHtml(item.username)}</td>
              <td style="padding:6px 10px;white-space:nowrap;">
                <span style="font-size:10.5px;font-weight:600;padding:2px 7px;border-radius:6px;background:#f1f5f9;color:#334155;border:1px solid #e2e8f0;">
                  ${escapeHtml(item.category)}
                </span>
              </td>
              <td style="padding:6px 10px;color:#334155;">
                <div style="font-weight:600;color:#991b1b;font-size:11.5px;">${escapeHtml(item.message)}</div>
                <div style="font-size:11px;color:#64748b;margin-top:2px;">Fix: ${escapeHtml(item.solution)}</div>
              </td>
            </tr>
          `;
        }).join('');

        // Plain text log for copying or downloading
        const textLog = [
          `CSV VALIDATION ERROR REPORT`,
          `Generated: ${new Date().toLocaleString()}`,
          `Total Issues: ${totalErrors} found across ${affectedRowsCount} rows`,
          `Affected Rows: ${uniqueRows.join(', ')}`,
          `----------------------------------------------------------------------`,
          ...validationErrorItems.map(i => `Row ${i.row} [${i.username}] ${i.category}: ${i.message} | Resolution: ${i.solution}`),
        ].join('\n');

        const html = `
          <div style="text-align:left;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1e293b;">
            <!-- Header Summary Banner -->
            <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px;padding:12px 14px;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;margin-bottom:12px;">
              <div style="display:flex;align-items:center;gap:10px;">
                <span style="font-size:22px;">⚠️</span>
                <div>
                  <div style="font-size:14.5px;font-weight:700;color:#991b1b;">
                    ${totalErrors} Issue${totalErrors > 1 ? 's' : ''} Detected in CSV (${affectedRowsCount} Row${affectedRowsCount > 1 ? 's' : ''} Affected)
                  </div>
                  <div style="font-size:12px;color:#7f1d1d;margin-top:2px;">
                    Review category counts below to fix your CSV file, or proceed to upload only the valid rows.
                  </div>
                </div>
              </div>
              <div style="font-size:11.5px;font-weight:700;color:#991b1b;background:#ffffff;padding:4px 10px;border-radius:12px;border:1px solid #fca5a5;">
                Rows: ${uniqueRows.slice(0, 8).join(', ')}${uniqueRows.length > 8 ? '...' : ''}
              </div>
            </div>

            <!-- Categories Breakdown Cards -->
            <div style="display:grid;grid-template-columns:repeat(auto-fit, minmax(220px, 1fr));gap:8px;margin-bottom:12px;">
              ${categoryCardsHtml}
            </div>

            <!-- Bounded Internal Scrollable Table (Strict 240px max-height) -->
            <div style="border:1px solid #cbd5e1;border-radius:8px;overflow:hidden;background:#ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.05);">
              <div style="background:#f8fafc;padding:7px 12px;border-bottom:1px solid #e2e8f0;display:flex;justify-content:space-between;align-items:center;">
                <span style="font-size:11.5px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.04em;">
                  Row-by-Row Issue Details (${validationErrorItems.length} items)
                </span>
                <span style="font-size:11px;color:#64748b;">
                  Scroll table to view all rows
                </span>
              </div>
              <div style="max-height:240px;overflow-y:auto;">
                <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
                  <thead style="position:sticky;top:0;background:#f1f5f9;z-index:2;">
                    <tr>
                      <th style="padding:6px 10px;text-align:left;font-weight:700;color:#334155;width:65px;border-bottom:1px solid #cbd5e1;">Row</th>
                      <th style="padding:6px 10px;text-align:left;font-weight:700;color:#334155;width:110px;border-bottom:1px solid #cbd5e1;">User</th>
                      <th style="padding:6px 10px;text-align:left;font-weight:700;color:#334155;width:125px;border-bottom:1px solid #cbd5e1;">Category</th>
                      <th style="padding:6px 10px;text-align:left;font-weight:700;color:#334155;border-bottom:1px solid #cbd5e1;">Issue & Fix Suggestion</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${rowsHtml}
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Bottom Tools Bar -->
            <div style="margin-top:10px;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;">
              <span style="font-size:11.5px;color:#64748b;">
                Invalid rows will be skipped if you click Proceed.
              </span>
              <div style="display:flex;gap:6px;">
                <button id="swal-btn-copy-errors" type="button" style="padding:5px 12px;border-radius:6px;border:1px solid #cbd5e1;background:#f8fafc;color:#334155;font-size:11.5px;font-weight:600;cursor:pointer;">
                  📋 Copy Error List
                </button>
                <button id="swal-btn-download-errors" type="button" style="padding:5px 12px;border-radius:6px;border:1px solid #cbd5e1;background:#f8fafc;color:#334155;font-size:11.5px;font-weight:600;cursor:pointer;">
                  💾 Download Report (.txt)
                </button>
              </div>
            </div>
          </div>
        `;

        try {
          const result = await Swal.fire({
            title: 'CSV Validation Issues Found',
            html,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Proceed (Skip Invalid Rows)',
            cancelButtonText: 'Cancel & Fix CSV',
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#64748b',
            width: 820,
            target: document.body,
            customClass: {
              container: 'swal2-always-top-layer',
              popup: 'swal2-always-top-popup',
            },
            didOpen: (popup) => {
              popup.style.zIndex = '2147483647';
              if (popup.parentElement) {
                popup.parentElement.style.zIndex = '2147483647';
              }
              const copyBtn = popup.querySelector('#swal-btn-copy-errors');
              if (copyBtn) {
                copyBtn.onclick = () => {
                  try {
                    navigator.clipboard.writeText(textLog);
                    copyBtn.textContent = '✓ Copied!';
                    setTimeout(() => { copyBtn.textContent = '📋 Copy Error List'; }, 2000);
                  } catch (_) { }
                };
              }
              const dlBtn = popup.querySelector('#swal-btn-download-errors');
              if (dlBtn) {
                dlBtn.onclick = () => {
                  try {
                    const blob = new Blob([textLog], { type: 'text/plain;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `csv-validation-errors-${Date.now()}.txt`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch (_) { }
                };
              }
            },
          });

          if (!result || !result.isConfirmed) {
            try { if (csvFileInputRef && csvFileInputRef.current) csvFileInputRef.current.value = null; } catch (_) { }
            try { setPendingCSVFile(null); setShowCSVConfirm(false); setCsvUploadError(null); setCsvUploadMessage(null); } catch (_) { }
            return;
          }
          // If confirmed, proceed with upload flow (backend will skip or handle invalid rows)
        } catch (swalErr) {
          try { if (csvFileInputRef && csvFileInputRef.current) csvFileInputRef.current.value = null; } catch (_) { }
          try { setPendingCSVFile(null); setShowCSVConfirm(false); setCsvUploadError(null); setCsvUploadMessage(null); } catch (_) { }
          return;
        }
      }

      // ── Handle Phone Warnings with Clean Format (No Red Screen) ──
      if (phoneWarnings.length) {
        setCsvUploadError(null);
        const uniquePhoneRows = Array.from(new Set(phoneWarningItems.map(p => p.row))).sort((a, b) => a - b);
        const rowsHtml = phoneWarningItems.map((item, idx) => {
          const isEven = idx % 2 === 0;
          return `
            <tr style="background:${isEven ? '#ffffff' : '#f8fafc'};border-bottom:1px solid #f1f5f9;">
              <td style="padding:6px 10px;font-weight:700;color:#2563eb;white-space:nowrap;">Row ${item.row}</td>
              <td style="padding:6px 10px;color:#0f172a;font-weight:600;white-space:nowrap;">${escapeHtml(item.username)}</td>
              <td style="padding:6px 10px;color:#991b1b;font-family:monospace;font-size:11px;">"${escapeHtml(item.value || '(empty)')}"</td>
              <td style="padding:6px 10px;color:#475569;font-size:11px;">${escapeHtml(item.solution)}</td>
            </tr>
          `;
        }).join('');

        const phoneHtml = `
          <div style="text-align:left;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1e293b;">
            <div style="padding:10px 14px;background:#fffbeb;border:1px solid #fde68a;border-radius:8px;margin-bottom:12px;display:flex;align-items:center;gap:10px;">
              <span style="font-size:20px;">📱</span>
              <div>
                <div style="font-size:14px;font-weight:700;color:#92400e;">
                  ${phoneWarnings.length} Invalid Phone Number${phoneWarnings.length > 1 ? 's' : ''} Detected (${uniquePhoneRows.length} Rows)
                </div>
                <div style="font-size:12px;color:#b45309;margin-top:2px;">
                  These rows will be skipped during processing. You can proceed with the rest of the CSV data.
                </div>
              </div>
            </div>

            <div style="border:1px solid #cbd5e1;border-radius:8px;overflow:hidden;max-height:220px;overflow-y:auto;background:#ffffff;">
              <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
                <thead style="position:sticky;top:0;background:#f1f5f9;z-index:2;">
                  <tr>
                    <th style="padding:6px 10px;text-align:left;font-weight:700;color:#334155;width:65px;border-bottom:1px solid #cbd5e1;">Row</th>
                    <th style="padding:6px 10px;text-align:left;font-weight:700;color:#334155;width:110px;border-bottom:1px solid #cbd5e1;">User</th>
                    <th style="padding:6px 10px;text-align:left;font-weight:700;color:#334155;width:130px;border-bottom:1px solid #cbd5e1;">Provided Value</th>
                    <th style="padding:6px 10px;text-align:left;font-weight:700;color:#334155;border-bottom:1px solid #cbd5e1;">Requirement</th>
                  </tr>
                </thead>
                <tbody>
                  ${rowsHtml}
                </tbody>
              </table>
            </div>
          </div>
        `;

        try {
          const result = await Swal.fire({
            title: 'Phone Number Warnings',
            html: phoneHtml,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Proceed (Skip Invalid Phones)',
            cancelButtonText: 'Cancel & Fix CSV',
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#64748b',
            width: 780,
            target: document.body,
            customClass: {
              container: 'swal2-always-top-layer',
              popup: 'swal2-always-top-popup',
            },
            didOpen: (popup) => {
              popup.style.zIndex = '2147483647';
              if (popup.parentElement) popup.parentElement.style.zIndex = '2147483647';
            },
          });

          if (!result || !result.isConfirmed) {
            try { if (csvFileInputRef && csvFileInputRef.current) csvFileInputRef.current.value = null; } catch (_) { }
            try { setPendingCSVFile(null); setShowCSVConfirm(false); setCsvUploadError(null); setCsvUploadMessage(null); } catch (_) { }
            return;
          }
        } catch (swalErr) {
          try { if (csvFileInputRef && csvFileInputRef.current) csvFileInputRef.current.value = null; } catch (_) { }
          try { setPendingCSVFile(null); setShowCSVConfirm(false); setCsvUploadError(null); setCsvUploadMessage(null); } catch (_) { }
          return;
        }
      }

      // end code shubhi
    } catch (validationErr) {
      console.warn('CSV pre-validation error', validationErr);
    }
    // Instead of uploading immediately, open confirm modal so user can pick consent type/message
    try {
      setPendingCSVFile(f);
      // setSelectedUploadConsentType('');
      // setSelectedUploadConsentMessage('');
      setSelectedUploadConsentType(detectedType || '');
      setSelectedUploadConsentMessage(detectedMsg || '');
      setShowCSVConfirm(true);
      setCsvUploadMessage(null);
      setCsvUploadError(null);
    } catch (e) {
      console.warn('Failed preparing CSV upload confirmation', e);
    }
    return;
  };

  const performCSVUpload = async ({ file, consent_type, consent_message, consent_id, sub_consent_id, version, language }) => {
    if (!file) return;
    if (file.size > 150 * 1024 * 1024) {
      await Swal.fire({
        title: 'CSV file is too large',
        text: 'Please upload a CSV file that is 150 MB or smaller.',
        icon: 'error'
      });
      setCsvUploadMessage(null);
      setCsvUploadError(null);
      setPendingCSVFile(null);
      setShowCSVConfirm(false);
      return;
    }
    const refreshAllConsentRecords = async () => {
      setCurrentPage(1);
      setRoleFilter(null);
      setStatusFilter(null);
      setExpiringFilter(false);
      setTypeFilter(null);
      setDateFilter('');
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setSelectedIds([]);
      try { clearAllSessionCache(); } catch (e) { }
      await fetchConsents({ page: 1, unfiltered: true });
    };
    setCsvUploadMessage('Uploading...');
    setCsvUploadError(null);
    setCsvUploadProgress(0);
    const fd = new FormData();
    // fd.append('file',file);
    // Normalize common header names so backend detects fields like id_proof_type
    let uploadFile = file;
    try {
      if (file.size <= 25 * 1024 * 1024) {
        const text = await file.text();
        const rawLines = text.split(/\r?\n/);
        // find header line: skip leading blanks/comments
        let headerLineIndex = -1;
        for (let i = 0; i < rawLines.length; i++) {
          const l = rawLines[i].trim();
          if (!l) continue;
          if (l.startsWith('#')) continue;
          headerLineIndex = i; break;
        }
        if (headerLineIndex !== -1) {
          const parseCSVLine = (line) => {
            const res = []; let cur = '', inQ = false;
            for (let i = 0; i < line.length; i++) {
              const ch = line[i];
              if (ch === '"') { inQ = !inQ; continue; }
              if (ch === ',' && !inQ) { res.push(cur.trim()); cur = ''; continue; }
              cur += ch;
            }
            res.push(cur.trim()); return res;
          };
          const toSnake = (s) => {
            try { return String(s || '').replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[^0-9a-zA-Z_]+/g, '_').replace(/__+/g, '_').replace(/^_|_$/g, '').toLowerCase(); } catch (e) { return String(s || '').toLowerCase(); }
          };
          const headerParts = parseCSVLine(rawLines[headerLineIndex]);
          const map = {
            category: 'age_category',
            agecategory: 'age_category',
            age_category: 'age_category',
            minoradult: 'age_category',
            idprooftype: 'id_proof_type',
            idprooffilename: 'id_proof_file',
            idprooffile: 'id_proof_file',
            language: 'preferred_languages',
            lang: 'preferred_languages',
            preferredlanguages: 'preferred_languages',
            datatypedeconsent: 'data_type_consent',
            datatypeconsent: 'data_type_consent',
            datatypetypeconsent: 'data_type_consent',
            datatypetconsent: 'data_type_consent',
            parentname: 'parent_name',
            parentemail: 'parent_email',
            parentphone: 'parent_phone',
            locality: 'location',
            localityname: 'location',
            locationname: 'location',
            city: 'location',
            district: 'location',
            loc: 'location',
            branch: 'location',
            departmentname: 'department',
            deptname: 'department',
            dept: 'department',
            department_name: 'department',
          };
          const newHeaders = headerParts.map(h => {
            const sn = toSnake(h || '');
            const key = sn.replace(/[^0-9a-z_]/g, '');
            if (map[key]) return map[key];
            return sn;
          });
          rawLines[headerLineIndex] = newHeaders.map(h => (h || '')).join(',');
          const newContent = rawLines.join('\n');
          try {
            uploadFile = new File([newContent], file.name, { type: file.type });
          } catch (fileErr) {
            // some environments may not support File constructor; fallback to Blob
            uploadFile = new Blob([newContent], { type: file.type });
          }
        }
      }
    } catch (e) {
      console.warn('CSV header normalization failed', e);
      uploadFile = file;
    }
    fd.append('file', uploadFile, uploadFile.name || file.name);
    if (user?.tenant_id) fd.append('tenant_id', user.tenant_id);
    if (consent_type) fd.append('consent_type', consent_type);
    if (consent_message) fd.append('consent_message', consent_message);
    if (language) fd.append('language', language);
    if (consent_id) fd.append('consent_id', String(consent_id));
    if (sub_consent_id) fd.append('sub_consent_id', String(sub_consent_id));
    if (version) fd.append('version', String(version));
    const endpoint = (API_BASE || '').toString().trim() ? `${API_BASE}/user/consent/upload` : '/user/consent/upload';
    let chunkUploadId = null;
    setCsvTransferActive(true);
    const authToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    const authHeaders = {
      Accept: 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    };
    try {
      let res;
      try {
        const pingRes = await axios.get(`${API_BASE}/user/status`, { timeout: 5000, headers: authHeaders });
        console.log('CSV upload: backend status ping:', pingRes && pingRes.data);
      } catch (pingErr) {
        console.warn('CSV upload: backend status ping failed', pingErr && pingErr.message);
      }
      if (file.size > 25 * 1024 * 1024) {
        const chunkSize = 8 * 1024 * 1024;
        const totalChunks = Math.ceil(file.size / chunkSize);
        const startForm = new FormData();
        startForm.append('filename', file.name);
        startForm.append('file_size_bytes', String(file.size));
        ['tenant_id', 'consent_type', 'consent_message', 'language', 'consent_id', 'sub_consent_id', 'version'].forEach((key) => {
          const value = fd.get(key);
          if (value !== null && value !== '') startForm.append(key, String(value));
        });
        const startResponse = await axios.post(`${endpoint}/chunks/start`, startForm, {
          timeout: 30000,
          headers: authHeaders,
        });
        chunkUploadId = startResponse?.data?.upload_id;
        if (!chunkUploadId) throw new Error('Upload service did not return an upload ID');
        setCsvUploadMessage('Uploading CSV in chunks...');
        for (let index = 0; index < totalChunks; index += 1) {
          const chunk = file.slice(index * chunkSize, Math.min((index + 1) * chunkSize, file.size));
          const chunkForm = new FormData();
          chunkForm.append('file', chunk, `${file.name}.part`);
          for (let retry = 0; ; retry += 1) {
            try {
              await axios.post(`${endpoint}/chunks/${chunkUploadId}/${index}`, chunkForm, {
                timeout: 120000,
                headers: authHeaders,
                onUploadProgress: (event) => {
                  const sent = index * chunkSize + Math.min(event.loaded || 0, chunk.size);
                  setCsvUploadProgress(Math.min(99, Math.floor((sent / file.size) * 100)));
                },
              });
              break;
            } catch (chunkError) {
              const status = chunkError?.response?.status;
              if ((status && status < 500) || retry >= 2) throw chunkError;
              setCsvUploadMessage(`Retrying chunk ${index + 1} of ${totalChunks}...`);
              await new Promise(resolve => setTimeout(resolve, 1000 * (retry + 1)));
            }
          }
          setCsvUploadMessage(`Uploading chunk ${index + 1} of ${totalChunks}...`);
          setCsvUploadProgress(Math.min(99, Math.floor(((index + 1) * chunkSize / file.size) * 100)));
        }

        const completeForm = new FormData();
        completeForm.append('total_chunks', String(totalChunks));
        setCsvUploadMessage('Finalizing CSV upload...');
        res = await axios.post(`${endpoint}/chunks/${chunkUploadId}/complete`, completeForm, {
          timeout: 120000,
          headers: authHeaders,
        });
        setCsvUploadProgress(100);
        setCsvUploadMessage('Chunks uploaded. Starting batch processing...');
      } else {
        for (const entry of fd.entries()) {
          const k = entry[0]; const v = entry[1];
          if (v && v.name) console.log('FormData entry:', k, '=', v.name);
          else console.log('FormData entry:', k, '=', v);
        }
        res = await axios.post(endpoint, fd, {
          timeout: 12000000,
          headers: authHeaders,
          onUploadProgress: (event) => {
            if (event.total) setCsvUploadProgress(Math.floor((event.loaded / event.total) * 100));
          },
        });
      }
      console.log('CSV upload: response status=', res && res.status, 'data=', res && res.data);
      if (res?.data?.accepted && res.data.job_id) {
        const jobId = res.data.job_id;
        setCsvTransferActive(true);
        setCsvUploadProgress(0);
        setCsvUploadMessage('Queued for batch processing...');
        setCsvLiveStats({
          total: 0,
          processed: 0,
          successful: 0,
          failed: 0,
          duplicates: 0,
          remaining: 0,
          percent: 0,
        });
        setCsvUploadError(null);
        let jobIssueSummary = '';
        let terminalStatusReceived = false;
        let missingStatusResponses = 0;
        let jobStatusUnavailable = false;
        const apiBase = String(API_BASE || '').replace(/\/+$/, '');
        const primaryStatusUrl = `${apiBase}/bulk/jobs/${jobId}`;
        const fallbackBase = apiBase.replace(/\/api$/, '');
        const fallbackStatusUrl = fallbackBase !== apiBase
          ? `${fallbackBase}/bulk/jobs/${jobId}`
          : null;
        let activeStatusUrl = primaryStatusUrl;
        const normalizeJobErrors = (input) => {
          if (Array.isArray(input)) return input;
          if (!input) return [];
          if (typeof input === 'string') {
            try {
              const parsed = JSON.parse(input);
              return Array.isArray(parsed) ? parsed : [{ error: parsed }];
            } catch (e) {
              return [{ error: input }];
            }
          }
          if (typeof input === 'object') return [input];
          return [];
        };
        for (let attempt = 0; attempt < 600; attempt += 1) {
          await new Promise(resolve => setTimeout(resolve, 1500));
          try {
            let statusRes;
            try {
              statusRes = await axios.get(activeStatusUrl);
            } catch (statusRequestError) {
              if (
                statusRequestError?.response?.status === 404
                && activeStatusUrl === primaryStatusUrl
                && fallbackStatusUrl
              ) {
                statusRes = await axios.get(fallbackStatusUrl);
                activeStatusUrl = fallbackStatusUrl;
              } else {
                throw statusRequestError;
              }
            }
            const status = String(statusRes?.data?.status || 'processing').toLowerCase();
            const errors = normalizeJobErrors(statusRes?.data?.errors);
            const duplicateErrors = errors.filter((error) => error && typeof error === 'object' && error.duplicate);
            const duplicateDetails = duplicateErrors
              .map((error) => `Row ${error.row}: ${error.email} already exists for the same Consent Type "${error.consent_type}" and Group "${error.group}".`)
              .join('\n');
            const errorText = errors
              .filter((error) => !(error && typeof error === 'object' && error.duplicate))
              .map((er) => {
                if (!er) return '';
                if (typeof er === 'string') return er;
                if (er && er.error) return `${er.row ? `Row ${er.row}: ` : ''}${er.error}`;
                return JSON.stringify(er);
              })
              .filter(Boolean)
              .slice(0, 8)
              .join('\n');

            const total = Number(statusRes?.data?.total_records || 0);
            const processed = Number(statusRes?.data?.processed_rows || 0);
            const inserted = Number(statusRes?.data?.inserted !== undefined ? statusRes?.data?.inserted : (statusRes?.data?.successful_rows || 0));
            const failed = Number(statusRes?.data?.failed_rows !== undefined ? statusRes?.data?.failed_rows : (statusRes?.data?.failed || 0));
            const duplicates = Number(statusRes?.data?.duplicates || duplicateErrors.length || 0);
            const remaining = Number(statusRes?.data?.remaining_records !== undefined ? statusRes?.data?.remaining_records : Math.max(0, total - processed));

            let pct = Number(statusRes?.data?.progress_percent);
            if (isNaN(pct) || pct === null || pct === undefined) {
              if (total > 0) pct = Math.min(100, Math.max(0, Math.round((processed / total) * 100)));
              else pct = (status === 'done' || status === 'completed') ? 100 : 0;
            }
            pct = Math.min(100, Math.max(0, pct));
            setCsvUploadProgress(pct);
            setCsvLiveStats({
              total,
              processed,
              successful: inserted,
              failed,
              duplicates,
              remaining,
              percent: pct,
            });

            if (status === 'queued') {
              setCsvUploadMessage('Queued for batch processing...');
            } else if (status === 'processing') {
              setCsvUploadMessage(total > 0
                ? `Processing batch: ${processed.toLocaleString()} of ${total.toLocaleString()} rows (${pct}%)...`
                : 'Processing batches in background...');
            } else if (status === 'done' || status === 'completed') {
              terminalStatusReceived = true;
              setCsvUploadProgress(100);
              setCsvUploadMessage('Completed');
              setCsvLiveStats(prev => ({
                ...(prev || {}),
                total: total || processed,
                processed: total || processed,
                successful: inserted,
                failed: 0,
                duplicates,
                remaining: 0,
                percent: 100,
              }));
              try {
                await Swal.fire({
                  title: duplicates > 0 ? 'Batch Upload Completed (Duplicates Found)' : 'CSV Upload Completed',
                  html: `
                    <div style="text-align:left;font-size:13px;line-height:1.7;">
                      <p style="margin-bottom:10px;font-weight:700;color:#0f172a;">CSV processing completed successfully:</p>
                      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px;">
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span>Total Records:</span><b style="color:#0f172a;">${(total || processed).toLocaleString()}</b></div>
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span style="color:#16a34a;">Successfully Processed:</span><b style="color:#16a34a;">${inserted.toLocaleString()}</b></div>
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span style="color:#d97706;">Duplicate Records:</span><b style="color:#d97706;">${duplicates.toLocaleString()}</b></div>
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span style="color:#64748b;">Remaining Records:</span><b style="color:#64748b;">0</b></div>
                      </div>
                      ${duplicates > 0 ? '<div style="margin-top:10px;padding:8px 10px;background:#fef3c7;border-radius:6px;color:#92400e;font-size:12px;">Duplicate records were identified and preserved without creating multiple active consents.</div>' : ''}
                    </div>
                  `,
                  icon: duplicates > 0 ? 'warning' : 'success',
                  confirmButtonText: 'OK',
                  confirmButtonColor: '#2563eb',
                });
              } catch (alertError) {
                console.error('Failed to show CSV upload result alert', alertError);
              }
              try {
                await refreshAllConsentRecords();
              } catch (refreshError) {
                console.error('Failed to refresh consents after CSV upload', refreshError);
              }
              break;
            } else if (status === 'partial') {
              terminalStatusReceived = true;
              const resultSummary = `Total Records: ${total}\nSuccessfully Processed: ${inserted}\nDuplicate Records: ${duplicates}\nFailed Records: ${failed}`;
              jobIssueSummary = [resultSummary, duplicateDetails, errorText].filter(Boolean).join('\n\n');
              setCsvUploadMessage('Partially Completed');
              setCsvUploadError(jobIssueSummary);
              try {
                await Swal.fire({
                  title: 'CSV Upload Partially Completed',
                  html: `
                    <div style="text-align:left;font-size:13px;line-height:1.7;">
                      <p style="margin-bottom:10px;font-weight:700;color:#92400e;">Batch processing completed with some failed rows:</p>
                      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px;">
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span>Total Records:</span><b style="color:#0f172a;">${total.toLocaleString()}</b></div>
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span style="color:#16a34a;">Successfully Processed:</span><b style="color:#16a34a;">${inserted.toLocaleString()}</b></div>
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span style="color:#dc2626;">Failed / Rejected:</span><b style="color:#dc2626;">${failed.toLocaleString()}</b></div>
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span style="color:#d97706;">Duplicate Records:</span><b style="color:#d97706;">${duplicates.toLocaleString()}</b></div>
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span style="color:#64748b;">Remaining Records:</span><b style="color:#64748b;">${remaining.toLocaleString()}</b></div>
                      </div>
                      ${duplicateDetails ? `<div style="margin-top:10px;padding:8px;background:#fef2f2;border-radius:6px;max-height:100px;overflow:auto;font-size:11px;font-family:monospace;white-space:pre-wrap;color:#991b1b;">${duplicateDetails}</div>` : ''}
                      ${errorText ? `<div style="margin-top:10px;padding:8px;background:#fef2f2;border-radius:6px;max-height:100px;overflow:auto;font-size:11px;font-family:monospace;white-space:pre-wrap;color:#991b1b;">${errorText}</div>` : ''}
                      <p style="margin-top:10px;font-size:12px;color:#64748b;">You can review detailed rejection reasons and retry failed records under <b>User Logs &gt; CSV Status</b>.</p>
                    </div>
                  `,
                  icon: 'warning',
                  width: 620,
                  confirmButtonText: 'OK',
                  confirmButtonColor: '#2563eb',
                });
              } catch (alertError) {
                console.error('Failed to show CSV duplicate summary alert', alertError);
              }
              try {
                await refreshAllConsentRecords();
              } catch (refreshError) {
                console.error('Failed to refresh consents after CSV upload', refreshError);
              }
              break;
            } else if (status === 'failed') {
              terminalStatusReceived = true;
              jobIssueSummary = errorText || 'Upload failed. Retry the job after reviewing the error details.';
              setCsvUploadMessage(null);
              setCsvUploadError(jobIssueSummary);
              try {
                await Swal.fire({
                  title: 'CSV Upload Failed',
                  html: `
                    <div style="text-align:left;font-size:13px;line-height:1.7;">
                      <p style="margin-bottom:10px;font-weight:700;color:#dc2626;">Batch processing failed:</p>
                      <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:12px;">
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span>Total Records:</span><b style="color:#0f172a;">${total.toLocaleString()}</b></div>
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span style="color:#16a34a;">Successfully Processed:</span><b style="color:#16a34a;">${inserted.toLocaleString()}</b></div>
                        <div style="display:flex;justify-content:space-between;padding:3px 0;"><span style="color:#dc2626;">Failed:</span><b style="color:#dc2626;">${failed.toLocaleString()}</b></div>
                      </div>
                      <div style="margin-top:10px;padding:8px;background:#fef2f2;border-radius:6px;max-height:120px;overflow:auto;font-size:11px;font-family:monospace;white-space:pre-wrap;color:#991b1b;">${jobIssueSummary}</div>
                      <p style="margin-top:10px;font-size:12px;color:#64748b;">You can review detailed rejection reasons and retry failed records under <b>User Logs &gt; CSV Status</b>.</p>
                    </div>
                  `,
                  icon: 'error',
                  width: 620,
                  confirmButtonText: 'OK',
                  confirmButtonColor: '#dc2626',
                });
              } catch (e) { }
              break;
            }
          } catch (statusError) {
            console.warn('CSV job status check failed', statusError);
            if (statusError?.response?.status === 404) {
              missingStatusResponses += 1;
              if (missingStatusResponses >= 10) {
                jobStatusUnavailable = true;
                setCsvUploadMessage(null);
                const unavailableMessage = 'Upload was accepted, but its processing status is unavailable. Please check the consent table before retrying.';
                setCsvUploadError(unavailableMessage);
                try {
                  await Swal.fire({
                    title: 'Upload Status Unavailable',
                    text: unavailableMessage,
                    icon: 'warning'
                  });
                } catch (alertError) {
                  console.error('Failed to show unavailable CSV status alert', alertError);
                }
                try {
                  await refreshAllConsentRecords();
                } catch (refreshError) {
                  console.error('Failed to refresh consents after unavailable CSV status', refreshError);
                }
                break;
              }
            } else {
              missingStatusResponses = 0;
            }
          }
        }
        if (!terminalStatusReceived && !jobStatusUnavailable) {
          setCsvUploadMessage('Still Processing');
          setCsvUploadError('The upload is still processing or its final status was not received. Refresh the consent table before retrying to avoid duplicate records.');
        }
        return;
      }
      if (res && res.data && res.data.success) {
        setCsvUploadMessage('Record consent complete: All records saved.');
        setCsvUploadError(null);
        // try { alert('CSV upload succeeded: ' + JSON.stringify({ inserted: res.data.inserted, errors: res.data.errors || [] })); } catch (e) {}
        try {
          const summary = [];
          if (res.data.inserted) summary.push(`${res.data.inserted} inserted`);
          if (res.data.parsed !== undefined) summary.push(`${res.data.parsed} parsed`);
          if (res.data.skipped) summary.push(`${res.data.skipped} skipped`);
          const txt = summary.length ? summary.join(' • ') : 'Upload succeeded';
          Swal.fire({ title: 'Upload succeeded', text: txt, icon: 'success' });
        } catch (e) { }
      } else {
        const data = res && res.data ? res.data : {};
        const parts = [];
        if (data.inserted) parts.push(`${data.inserted} inserted`);
        if (data.parsed !== undefined) parts.push(`${data.parsed} parsed`);
        if (data.skipped) parts.push(`${data.skipped} skipped`);
        const genSummary = parts.length ? parts.join(' • ') : '';
        const errorsArr = Array.isArray(data.errors) ? data.errors : [];
        const errorLines = errorsArr.map((er, idx) => (er && er.row) ? `Row ${er.row}: ${er.error}` : `${idx + 1}. ${er && er.error ? er.error : JSON.stringify(er)}`);
        const preview = errorLines.length ? errorLines.slice(0, 8).join('\n') + (errorLines.length > 8 ? `\n...and ${errorLines.length - 8} more` : '') : (data.error || data.detail || 'Some records may not have been saved.');
        const serverMsg = preview;
        setCsvUploadError(serverMsg);
        setCsvUploadMessage(null);
        try {
          const escapeHtml = (s) => (s || '').toString().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          const html = `<div style="text-align:left;white-space:pre-wrap;font-family:monospace">${escapeHtml(genSummary ? genSummary + '\n\n' + preview : preview)}</div>`;
          const isDuplicateIssue = /duplicate/i.test(serverMsg);
          Swal.fire({
            title: isDuplicateIssue ? 'Duplicate CSV rows found' : (errorsArr.length ? 'Upload completed with errors' : 'Upload result'),
            html,
            icon: isDuplicateIssue ? 'warning' : (errorsArr.length ? 'warning' : 'info'),
            width: 700
          });
        } catch (e) { console.warn('Failed to show friendly upload response', e); }
      }
      try { await refreshAllConsentRecords(); } catch (e) { console.error('Failed fetching consents after upload', e); }
    } catch (err) {
      if (chunkUploadId) {
        try {
          await axios.delete(`${endpoint}/chunks/${chunkUploadId}`);
        } catch (cleanupError) {
          console.error('Failed cleaning incomplete CSV upload chunks', cleanupError);
        }
      }
      console.groupCollapsed('CSV upload failed: details');
      console.error('Error object:', err);
      if (err && err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response headers:', err.response.headers);
        console.error('Response data:', err.response.data);
      }
      console.error('Request config:', err?.config);
      console.groupEnd();
      const serverMsg = err?.response?.data?.detail || err?.response?.data?.error || err?.response?.data?.message || err?.message;
      setCsvUploadError(serverMsg || 'Upload failed.');
      setCsvUploadMessage(null);
      // try { alert('CSV upload failed: ' + (serverMsg || err?.message || 'See console for details')); } catch (e) {}
      try { Swal.fire({ text: 'CSV upload failed: ' + (serverMsg || err?.message || 'See console for details'), icon: 'error' }); } catch (e) { }
    } finally {
      setCsvTransferActive(false);
      setCsvLiveStats(null);
      try { if (csvFileInputRef && csvFileInputRef.current) csvFileInputRef.current.value = null; } catch (e) { }
      setPendingCSVFile(null);
      setShowCSVConfirm(false);
      setTimeout(() => { setCsvUploadMessage(null); setCsvUploadError(null); }, 8000);
    }
  };

  const fetchConsentsRequestId = useRef(0);

  const fetchConsents = useCallback(async (options = {}) => {
    const currentRequestId = ++fetchConsentsRequestId.current;
    const requestStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    try {
      const tenantId = Number(user?.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
      if (!tenantId) {
        setConsents([]);
        setServerStats({});
        setServerTotal(0);
        return;
      }
      const params = {
        tenant_id: tenantId,
        dashboard: true,
        page: options.page || currentPage,
        page_size: pageSize,
      };
      if (!options.unfiltered) {
        if (roleFilter) params.role = roleFilter;
        if (statusFilter === 'auto-declined') params.auto_declined = true;
        else if (statusFilter === 'expired') params.expired_only = true;
        else if (statusFilter === 'expiring-soon') {
          params.expiring_soon = true;
          params.expiring_days = 7;
        }
        else if (statusFilter) params.dashboard_status = statusFilter;
        if (typeFilter) params.dashboard_type = typeFilter;
        if (debouncedSearchQuery) params.search = debouncedSearchQuery;
        if (expiringFilter) {
          params.expiring_soon = true;
          params.expiring_days = expiringDays || 7;
        }
        if (dateFilter) {
          const now = new Date();
          const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const dateKey = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          if (dateFilter === 'today') {
            params.date_from = dateKey(end);
            params.date_to = params.date_from;
          } else if (dateFilter === '7days' || dateFilter === '30days') {
            params.date_window_days = dateFilter === '7days' ? 7 : 30;
          }
        }
      }
      const cacheKey = createSessionCacheKey('userpanel-consents', user, params);
      const cachedData = readSessionCache(cacheKey);
      if (cachedData && currentRequestId === fetchConsentsRequestId.current) {
        setConsents(cachedData.consents || []);
        setServerStats(cachedData.stats || {});
        setServerTotal(Number(cachedData.total ?? 0));
      }
      const res = await axios.get(`${API_BASE}/user/consent`, { params, skipLoading: true });
      // setServerStats(res.data?.stats || {});
      // setServerTotal(Number(res.data?.total ?? 0));
      if (currentRequestId !== fetchConsentsRequestId.current) return;

      const hasActiveFilters = !options.unfiltered && !!(typeFilter || statusFilter || roleFilter || dateFilter || expiringFilter || debouncedSearchQuery);
      const chosenStats = (hasActiveFilters && res.data?.filtered_stats) ? res.data.filtered_stats : (res.data?.stats || res.data?.filtered_stats || {});
      setServerStats(chosenStats);
      setServerTotal(Number(res.data?.total ?? chosenStats?.total ?? 0));
      const rows = res.data?.consents || [];
      if (rows && rows.length > 0) {
        const fromRecords = {};
        rows.forEach(r => {
          if (r.type && r.template_status) {
            const k1 = String(r.type).trim().toLowerCase().replace(/[\s-]+/g, '_');
            const k2 = String(r.type).trim().toLowerCase().replace(/[\s_]+/g, ' ');
            const st = String(r.template_status).trim().toLowerCase();
            fromRecords[k1] = st;
            fromRecords[k2] = st;
          }
        });
        if (Object.keys(fromRecords).length > 0) {
          setTemplateStatusMap(prev => ({ ...prev, ...fromRecords }));
        }
      }
      // helper: determine normalized role for a record
      const roleOfRecord = (r) => {
        const entityVal = String((r.entity || '')).toLowerCase();
        const fields = [r.role, r.user_role, r.type, r.consent_type, r.category, r.template, r.entity];
        const v = String((fields.filter(Boolean).join(' ') || '')).toLowerCase();
        const employeeKeys = ['employee', 'staff', 'member', 'employeeid', 'emp'];
        const vendorKeys = ['vendor', 'supplier', 'vendorid', 'vendor_name', 'vendorcompany'];
        const partnerKeys = ['partner', 'partners', 'partnerid', 'affiliate'];
        const customerKeys = ['customer', 'client', 'customerid', 'buyer', 'consumer'];
        if (entityVal) {
          if (employeeKeys.some(k => entityVal.includes(k))) return 'employee';
          if (vendorKeys.some(k => entityVal.includes(k))) return 'vendor';
          if (partnerKeys.some(k => entityVal.includes(k))) return 'partner';
          if (customerKeys.some(k => entityVal.includes(k))) return 'customer';
        }
        if (employeeKeys.some(k => v.includes(k))) return 'employee';
        if (vendorKeys.some(k => v.includes(k))) return 'vendor';
        if (partnerKeys.some(k => v.includes(k))) return 'partner';
        if (customerKeys.some(k => v.includes(k))) return 'customer';
        return null;
      };

      const mapped = (rows || []).map(r => {
        let recordData = r.data && typeof r.data === 'object' ? r.data : {};
        if (typeof r.data === 'string') {
          try {
            recordData = JSON.parse(r.data);
          } catch {
            const metadataEnd = r.data.indexOf('\nsuspended_at:');
            if (metadataEnd > -1) {
              try { recordData = JSON.parse(r.data.slice(0, metadataEnd)); } catch { recordData = {}; }
            }
          }
        }
        if (!recordData || typeof recordData !== 'object' || Array.isArray(recordData)) recordData = {};
        const reminderCount = Number(r.reminder_count !== undefined && r.reminder_count !== null ? r.reminder_count : (r.remind_count !== undefined && r.remind_count !== null ? r.remind_count : (recordData.reminder_count || 0))) || 0;
        const lastReminderAt = r.last_reminder_at || recordData.last_reminder_at || null;
        const reminderCycleStart = r.reminder_cycle_start || recordData.reminder_cycle_start || null;
        const lastEmailSentAt = r.last_email_sent_at || recordData.last_email_sent_at || null;
        return {
          ...r,
          reminder_count: reminderCount,
          last_reminder_at: lastReminderAt,
          reminder_cycle_start: reminderCycleStart,
          last_email_sent_at: lastEmailSentAt,
          sub_consent_name: r.sub_consent_name || recordData.sub_consent_name || recordData.sub_category || '',
          sub_consent_version: r.sub_consent_version || recordData.sub_consent_version || '',
          version: r.version || recordData.version || recordData.consent_version || '',
          address: r.address || r.Address || r.user_address || r.postal_address || r.locality || r.location || '',
          createdAt: r.createdAt || r.created_at || r.created || null,
          expiry: (r.expiry !== undefined && r.expiry !== null) ? r.expiry : (r.expiry_days !== undefined && r.expiry_days !== null) ? r.expiry_days : (r.expiryDays !== undefined && r.expiryDays !== null) ? r.expiryDays : (r.expiry_date !== undefined && r.expiry_date !== null) ? r.expiry_date : (r.expires_at !== undefined && r.expires_at !== null) ? r.expires_at : null,
          preferredLanguages: r.preferred_languages || r.preferredLanguages || r.languages || r.language || '',
          parentName: r.parent_name || r.parentName || null,
          parentEmail: r.parent_email || r.parentEmail || null,
          parentPhone: r.parent_phone || r.parentPhone || null,
          isMinor: (() => {
            try {
              if (r.parent_name || r.parentName || r.parent_email || r.parentEmail || r.parent_phone || r.parentPhone) return true;
              const dobVal = r.dob || r.date_of_birth || null;
              if (!dobVal) return false;
              const b = new Date(dobVal);
              if (Number.isNaN(b.getTime())) return false;
              const today = new Date();
              let age = today.getFullYear() - b.getFullYear();
              const m = today.getMonth() - b.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
              return age < 18;
            } catch (e) {
              return false;
            }
          })(),
          normRole: roleOfRecord(r),
        };
      });

      setConsents(mapped);
      const dataShownAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      setConsentDataTimingMs(Math.round(dataShownAt - requestStartedAt));
      if (!firstConsentDataShown.current) {
        firstConsentDataShown.current = true;
        setConsentPageReadyMs(Math.round(dataShownAt - userPanelOpenedAt.current));
      }
      writeSessionCache(cacheKey, {
        consents: mapped,
        total: Number(res.data?.total ?? chosenStats?.total ?? 0),
        stats: chosenStats,
      });
    } catch (e) {
      console.warn('Failed to fetch consents', e);
    }
    // }, [user]);
  }, [consentCacheTenantId, expiringFilter, expiringDays, roleFilter, statusFilter, typeFilter, dateFilter, debouncedSearchQuery, currentPage, pageSize]);

  // Re-fetch consents whenever fetchConsents reference updates with active filters
  useEffect(() => {
    try {
      fetchConsents();
    } catch (e) {
      console.warn('Failed to refresh consents after filter change', e);
    }
    // }, [fetchConsents, expiringFilter, expiringDays, roleFilter, statusFilter, tab]);
  }, [fetchConsents]);
  // Listen for consentResponse events (from SSE or postMessage) and refresh consents
  useEffect(() => {
    const handler = (e) => {
      try {
        const payload = e?.detail || (e?.data ? (typeof e.data === 'string' ? JSON.parse(e.data) : e.data) : null) || e;
        if (!payload) return;
        if (payload.tenant_id && consentCacheTenantId && String(payload.tenant_id) !== String(consentCacheTenantId)) return;
        fetchConsents();
      } catch (err) {
        // ignore
      }
    };
    const wm = (msg) => {
      try {
        const payload = msg && msg.data ? msg.data : null;
        if (payload) {
          const ev = new CustomEvent('consentResponse', { detail: payload });
          document.dispatchEvent(ev);
        }
      } catch (e) { }
    };
    document.addEventListener('consentResponse', handler);
    document.addEventListener('consent:updated', handler);
    // When a consent is created elsewhere in the app, refresh list immediately
    const handlerCreated = (e) => {
      try {
        const payload = e?.detail || (e?.data ? (typeof e.data === 'string' ? JSON.parse(e.data) : e.data) : null) || e;
        if (!payload) return;
        if (payload.tenant_id && consentCacheTenantId && String(payload.tenant_id) !== String(consentCacheTenantId)) return;
        try { clearAllSessionCache(); } catch (err) { }
        if (payload.id) {
          setConsents(prev => [payload, ...(prev || []).filter(c => c && c.id !== payload.id)]);
        }
        fetchConsents({ page: 1 });
      } catch (err) { }
    };
    window.addEventListener('consent:created', handlerCreated);
    window.addEventListener('message', wm);
    return () => {
      try { document.removeEventListener('consentResponse', handler); } catch (e) { }
      try { document.removeEventListener('consent:updated', handler); } catch (e) { }
      try { window.removeEventListener('consent:created', handlerCreated); } catch (e) { }
      try { window.removeEventListener('message', wm); } catch (e) { }
    };
  }, [fetchConsents, consentCacheTenantId]);

  // Auto-refresh consents periodically so UI reflects server state (e.g., reminders/auto-declines)
  useEffect(() => {
    let id = null;
    try {
      id = setInterval(() => {
        try {
          if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
            fetchConsents();
          }
        } catch (e) { }
      }, 60000); // 60s background sync when tab is active
    } catch (e) { }
    return () => { try { clearInterval(id); } catch (e) { } };
  }, [fetchConsents]);


  const ensurePrivacyAttachment = async () => {
    const b64ToFile = (b64, filename) => {
      if (!b64) return null;
      try {
        const byteChars = atob(b64);
        const byteNumbers = new Array(byteChars.length);
        for (let j = 0; j < byteChars.length; j++) byteNumbers[j] = byteChars.charCodeAt(j);
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        try { return new File([blob], filename, { type: 'application/pdf' }); } catch (e) { blob.name = filename; return blob; }
      } catch (e) {
        return null;
      }
    };

    const parseCached = (raw, keyHint) => {
      if (!raw) return null;

      // 1) data URL
      try {
        if (typeof raw === 'string' && raw.startsWith && raw.startsWith('data:')) {
          const b64 = raw.split(',')[1] || '';
          const filename = keyHint ? `${keyHint}.pdf` : 'attachment.pdf';
          return b64ToFile(b64, filename);
        }
      } catch (e) { /* ignore */ }

      // 2) JSON object containing base64
      try {
        const obj = JSON.parse(raw);
        const filename = (obj && (obj.filename || obj.name)) ? (obj.filename || obj.name) : (keyHint ? `${keyHint}.pdf` : 'attachment.pdf');
        let b64 = obj && (obj.data || obj.content || obj.file || obj.pdf) ? (obj.data || obj.content || obj.file || obj.pdf) : null;
        if (!b64) {
          for (const k of Object.keys(obj || {})) {
            const v = obj[k];
            if (typeof v === 'string' && v.length > 200) {
              const s = v.trim();
              if (s.startsWith('data:')) {
                b64 = s.split(',')[1];
                break;
              }
              if (/^[A-Za-z0-9+/=\r\n]+$/.test(s)) {
                b64 = s.replace(/\s+/g, '');
                break;
              }
            }
          }
        }
        if (b64) return b64ToFile(b64, filename);
      } catch (e) { /* not JSON, continue */ }

      // 3) raw base64 string
      try {
        const maybe = (raw || '').toString().trim();
        if (maybe.length > 200 && /^[A-Za-z0-9+/=\r\n]+$/.test(maybe)) {
          const b64 = maybe.replace(/\s+/g, '');
          const filename = keyHint ? `${keyHint}.pdf` : 'attachment.pdf';
          return b64ToFile(b64, filename);
        }
      } catch (e) { /* ignore */ }

      return null;
    };

    try {
      console.debug('ensurePrivacyAttachment: start', { privacyModalOpen, privacyFormat, privacyModalConsent: !!privacyModalConsent });
      setPrivacyGenerating(true);
      if (privacyFormat !== 'attachment') return;
      if (!privacyModalConsent) return;
      if (privacyAttachments && privacyAttachments.length) return;
      const type = (privacyModalConsent.type || privacyModalConsent.consent_type || privacyModalConsent.template || '').toString();
      if (!type) return;
      let messageToUse = (privacyMessage || '').toString().trim();
      if (!messageToUse) {
        try {
          const saved = localStorage.getItem('last_saved_notification') || localStorage.getItem('selected_consent_for_notification');
          if (saved) {
            const obj = JSON.parse(saved);
            if (obj && obj.consent_type && obj.consent_type === type) messageToUse = obj.consent_message || '';
          }
        } catch (e) { /* ignore */ }
      }

      const norm = type.toString().trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/gi, '');
      const normAlt = type.toString().trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9_\-]/gi, '');
      const candidates = [`notification_pdf_${norm}`, `notification_pdf_${normAlt}`];

      // 1) try exact keys first
      for (const k of candidates) {
        try {
          const raw = localStorage.getItem(k);
          const f = parseCached(raw, k);
          if (f) {
            console.debug('ensurePrivacyAttachment: found cached by exact key', k);
            setPrivacyAttachments([f]);
            return;
          }
        } catch (e) { /* continue */ }
      }

      // 2) scan cached keys that include the normalized token and also gather multiple matches
      try {
        const matches = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key) continue;
          const lk = key.toString().toLowerCase();
          if (!lk.includes('notification_pdf')) continue;
          const raw = localStorage.getItem(key);
          // if the key name contains the type token prefer it
          if (lk.includes(norm) || (normAlt && lk.includes(normAlt))) {
            const f = parseCached(raw, key);
            if (f) {
              console.debug('ensurePrivacyAttachment: matched by key token', key);
              matches.push(f);
              continue;
            }
          }
          // if parsing yields a file and its filename contains the norm, include it
          try {
            const f2 = parseCached(raw, key);
            if (f2) {
              const fn = (f2.name || f2.filename || '').toString().toLowerCase();
              if (fn && (fn.includes(norm) || (normAlt && fn.includes(normAlt)))) {
                console.debug('ensurePrivacyAttachment: matched by stored filename', key, fn);
                matches.push(f2);
              }
            }
          } catch (e) { /* ignore */ }
        }
        if (matches.length) {
          setPrivacyAttachments(matches);
          return;
        }
      } catch (e) { console.warn('ensurePrivacyAttachment: scan failed', e); }

      // 3) as a last resort, attach any parsed cached PDF (useful when keys don't map exactly)
      try {
        const anyMatches = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key) continue;
          if (!key.toLowerCase().includes('notification_pdf')) continue;
          const raw = localStorage.getItem(key);
          const f = parseCached(raw, key);
          if (f) anyMatches.push(f);
        }
        if (anyMatches.length) {
          console.debug('ensurePrivacyAttachment: attaching first cached PDF from general scan');
          setPrivacyAttachments(anyMatches);
          return;
        }
      } catch (e) { /* ignore */ }

      // 4) Check server-side `privacy_notices` entries for this tenant and type
      try {
        const params = {};
        if (user && user.tenant_id) params.tenant_id = user.tenant_id;
        const listRes = await axios.get(`${API_BASE}/user/privacy-notices`, { params });
        const notices = (listRes && listRes.data && listRes.data.privacy_notices) ? listRes.data.privacy_notices : [];
        if (Array.isArray(notices) && notices.length) {
          const findMatch = (arr, token) => {
            const tk = (token || '').toString().trim().toLowerCase();
            for (const n of arr) {
              try {
                const ct = (n.consent_type || n.title || (n.card_data && n.card_data.label) || '').toString().toLowerCase();
                if (ct && ct === tk) return n;
              } catch (e) { }
            }
            for (const n of arr) {
              try {
                const ct = (n.consent_type || n.title || (n.card_data && n.card_data.label) || '').toString().toLowerCase();
                if (ct && ct.includes(tk)) return n;
              } catch (e) { }
            }
            return null;
          };
          const matched = findMatch(notices, type);
          if (matched && matched.pdf_url) {
            try {
              console.debug('ensurePrivacyAttachment: fetching server PDF', `${API_BASE}${matched.pdf_url}`);
              const pdfRes = await axios.get(`${API_BASE}${matched.pdf_url}`, { responseType: 'blob' });
              if (pdfRes && pdfRes.data) {
                const blob = pdfRes.data;
                const filename = (matched.title || type || 'attachment').replace(/[^a-z0-9_\-]/gi, '_') + '.pdf';
                try {
                  const file = new File([blob], filename, { type: 'application/pdf' });
                  setPrivacyAttachments([file]);
                } catch (e) {
                  blob.name = filename;
                  setPrivacyAttachments([blob]);
                }
                return;
              }
            } catch (e) {
              console.warn('Failed to fetch privacy_notice PDF from server', e);
            }
          }
        }
        /*

            rows.forEach(d => {
              const key = (d.consent_type || d.type || d.consent_type_name || '').toString().trim().toLowerCase().replace(/[\s-]+/g, '_');
              if (key && (d.parent_id == null || d.parent_id === 0) && !latestStatusMap.has(key)) {
                latestStatusMap.set(key, String(d.status || '').trim().toLowerCase());
              }
            });
            rows.forEach(d => {
              const key = (d.consent_type || d.type || d.consent_type_name || '').toString();
              const normKey = key.trim().toLowerCase().replace(/[\s-]+/g, '_');
              if (latestStatusMap.get(normKey) !== 'accepted') return;
              const status = String(d.status || '').trim().toLowerCase();
              if (status !== 'accepted') return;
              const msg = d.consent_message || d.message || d.description || '';
              if (key) {
                tenantTypes.add(normKey);
                addType(key, msg);
              }
            });
          }

              }
            } catch (e) {
              console.warn('Failed to fetch privacy_notice PDF from server', e);
            }
          }
        */
      } catch (e) {
        console.warn('Failed to list privacy_notices from server', e);
      }

      let res;
      if (messageToUse) {

        // ask server to generate PDF from provided message text
        const body = new URLSearchParams();
        body.append('consent_type', type);
        body.append('message', messageToUse);
        const tid = Number(user?.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
        if (tid) body.append('tenant_id', String(tid));
        res = await axios.post(`${API_BASE}/manager/consent/template_pdf_custom`, body, { responseType: 'blob', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, timeout: 20000 });
      } else {
        // try to use cached PDF generated by NotificationPage
        try {
          const norm = type.toString().trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/gi, '');
          const cached = localStorage.getItem(`notification_pdf_${norm}`);
          if (cached) {
            const obj = JSON.parse(cached);
            if (obj && obj.data) {
              const b64 = obj.data;
              const byteChars = atob(b64);
              const byteNumbers = new Array(byteChars.length);
              for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob(
                [byteArray],
                { type: 'application/pdf' },
              );
              const filename = obj.filename || `${norm}.pdf`;
              try {
                const file = new File([blob], filename, { type: 'application/pdf' });
                setPrivacyAttachments([file]);
              } catch (e) {
                blob.name = filename;
                setPrivacyAttachments([blob]);
              }
              console.debug('Loaded cached PDF for', type, filename);
              return;
            }
          }
        } catch (e) {
          console.warn('Failed to load cached generated PDF', e);
        }
        // fallback to server template lookup
        // const url = `${API_BASE}/manager/consent/template_pdf?consent_type=${encodeURIComponent(type)}`;
        const tid = Number(user?.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
        const url = `${API_BASE}/manager/consent/template_pdf?consent_type=${encodeURIComponent(type)}${tid ? `&tenant_id=${encodeURIComponent(tid)}` : ''}`;
        res = await axios.get(url, { responseType: 'blob', timeout: 20000 });
      }

      if (res && res.data) {
        const blob = res.data;
        const filename = `${type.replace(/[^a-z0-9_\-]/gi, '_')}.pdf`;
        try {
          const file = new File([blob], filename, { type: 'application/pdf' });
          setPrivacyAttachments([file]);
        } catch (e) {
          const f = blob;
          f.name = filename;
          setPrivacyAttachments([f]);
        }
      }
    } catch (e) {
      console.warn('Auto-generate PDF failed', e);
    } finally {
      try { setPrivacyGenerating(false); } catch (e) { }
    }
  };

  useEffect(() => {
    ensurePrivacyAttachment();
  }, [privacyModalOpen, privacyFormat, privacyModalConsent, privacyMessage]);

  // When an attachment is prepared while the privacy modal is open, open a preview automatically
  useEffect(() => {
    try {
      if (privacyModalOpen && privacyFormat === 'attachment' && privacyAttachments && privacyAttachments.length) {
        // preview the first attachment
        try { previewPrivacyAttachment(0); } catch (e) { console.warn('Auto-preview failed', e); }
      }
    } catch (e) { /* ignore */ }
  }, [privacyAttachments, privacyModalOpen, privacyFormat]);

  useEffect(() => {
    // if (initialTab) {
    //   setTab(initialTab);
    // }
    // Always default to 'consent' tab after login unless initialTab is explicitly set
    if (initialTab) {
      setTab(initialTab);
    } else {
      setTab('consent');
    }
  }, [initialTab]);


  useEffect(() => {
    let mounted = true;
    // (async () => {
    const refreshTypes = async () => {
      try {
        // We'll normalize types by lowercasing & trimming (norm) to dedupe
        const msgByNorm = {};
        const displayByNorm = {}; // norm -> display name (first-seen casing)

        // helper to add a display + msg for a raw type
        const addType = (raw, msg) => {
          if (!raw && raw !== 0) return;
          const s = (raw || '').toString();
          const norm = s.trim().toLowerCase();
          if (!norm) return;
          if (!displayByNorm[norm]) displayByNorm[norm] = s; // keep first-seen casing
          if (msg !== undefined && msg !== null) {
            // prefer manager-provided message; if already present, keep existing
            if (!msgByNorm[norm] || (msg && msg.length && !msgByNorm[norm])) msgByNorm[norm] = msg || '';
          } else {
            if (!msgByNorm[norm]) msgByNorm[norm] = msgByNorm[norm] || '';
          }
          // Fallback to built-in consent card content if available and no message yet
          try {
            if ((!msgByNorm[norm] || msgByNorm[norm] === '') && initialContent && initialContent[s]) {
              msgByNorm[norm] = initialContent[s];
            }
          } catch (e) {
            // ignore
          }
        };

        const tenantTypes = new Set();
        try {
          if (user && user.tenant_id) {
            const res = await axios.get(`${API_BASE}/manager/new-consents`, { params: { tenant_id: user.tenant_id }, skipLoading: true });
            const rows = res.data?.data || [];
            const latestStatusMap = new Map();
            rows.forEach(d => {
              const key = (d.consent_type || d.type || d.consent_type_name || '').toString().trim().toLowerCase().replace(/[\s-]+/g, '_');
              const cleanKey = (d.consent_type || d.type || d.consent_type_name || '').toString().trim().toLowerCase().replace(/[\s_]+/g, ' ');
              if (key && (d.parent_id == null || d.parent_id === 0) && !latestStatusMap.has(key)) {
                latestStatusMap.set(key, String(d.status || '').trim().toLowerCase());
                latestStatusMap.set(cleanKey, String(d.status || '').trim().toLowerCase());
              }
            });
            const statusObj = {};
            latestStatusMap.forEach((val, key) => { statusObj[key] = val; });
            setTemplateStatusMap(prev => ({ ...prev, ...statusObj }));
            rows.forEach(d => {
              const key = (d.consent_type || d.type || d.consent_type_name || '').toString();
              const normKey = key.trim().toLowerCase().replace(/[\s-]+/g, '_');
              // if (latestFallbackStatus.get(normKey) !== 'accepted') return;
              const status = String(d.status || '').trim().toLowerCase();
              // if (status !== 'accepted') return;

              const isActiveOrAccepted = ['accepted', 'active', 'approved', 'granted', 'agree', 'agreed', 'yes'].includes(status);
              const isNotDisabled = !['inactive', 'rejected', 'declined', 'deleted'].includes(latestStatusMap.get(normKey));
              if (!isActiveOrAccepted || !isNotDisabled) return;
              const msg = d.consent_message || d.message || d.description || '';
              if (key) {
                tenantTypes.add(key.toString().trim().toLowerCase());
                tenantTypes.add(normKey);
                addType(key, msg);
              }
            });
            if (false) rows.forEach(d => {
              const status = String(d.status || 'accepted').trim().toLowerCase();
              if (!['accepted', 'active', 'approved'].includes(status)) return;
              const key = (d.consent_type || d.type || d.consent_type_name || '').toString();
              const msg = d.consent_message || d.message || d.description || '';
              if (key) {
                tenantTypes.add(key.toString().trim().toLowerCase());
                addType(key, msg);
              }
            });
            //   } 
            // }catch (e){
            //     console.warn('Failed to load manager/new-consents', e);
            // }
          }
        } catch (e) {
          console.warn('manager/new-consents failed', e);
        }

        // Fallbacks: user/new_types and user/types — request tenant-scoped if tenant present
        try {
          // const a = await axios.get('${API_BASE}/user/consent/new_types');
          const params = (user && user.tenant_id) ? { params: { tenant_id: user.tenant_id }, skipLoading: true } : { skipLoading: true };
          const a = await axios.get(`${API_BASE}/user/consent/new_types`, params);
          const arr = a.data?.types || [];
          arr.forEach(t => addType(t, ''));
        } catch (e) {
          // ignore
        }
        try {
          // const b = await axios.get(`${API_BASE}/user/consent/types');
          const params = (user && user.tenant_id) ? { params: { tenant_id: user.tenant_id }, skipLoading: true } : { skipLoading: true };
          const b = await axios.get(`${API_BASE}/user/consent/types`, params);
          const arr2 = b.data?.types || [];
          arr2.forEach(t => addType(t, ''));
        } catch (e) {
          // ignore
        }

        // Ensure we only expose accepted types
        try {
          if (tenantTypes.size > 0) {
            Object.keys(displayByNorm).forEach(norm => {
              if (!tenantTypes.has(norm)) delete displayByNorm[norm];
            });
          } else {
            const allowedDefaults = (CONSENT_TYPES || []).map(c => (c.key || '').toString().trim().toLowerCase());
            Object.keys(displayByNorm).forEach(norm => {
              const keep = allowedDefaults.includes(norm);
              if (!keep) delete displayByNorm[norm];
              else if (!displayByNorm[norm]) displayByNorm[norm] = norm;
            });
          }
        } catch (e) {
          // ignore filtering errors
        }

        // Merge with any local window-provided remoteTypes/content (keep first-seen order)
        try {
          if (typeof window !== 'undefined') {
            // placeholder for future hooks
          }
        } catch (e) { }

        const ordered = Object.keys(displayByNorm).map(k => displayByNorm[k]);
        if (mounted) {
          setTypes(ordered);
          // store messages keyed by normalized form for reliable lookup
          setTypeMessages(msgByNorm);
        }
        // return ordered list and messages so callers can use them immediately
        return { ordered, msgByNorm };
      } catch (err) {
        console.warn('Failed to refresh types', err);
      }
    };

    // initial load
    refreshTypes();

    // expose refresh function for other handlers via ref-like property
    UserPanel._refreshTypes = refreshTypes;

    // load id-proof data types for the id proof dropdown (optional)
    (async () => {
      try {
        const tid = Number(user?.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
        const dres = await axios.get(`${API_BASE}/manager/data-types`, {
          params: tid ? { tenant_id: tid } : {},
          skipLoading: true
        });
        if (mounted && dres.data && Array.isArray(dres.data.data_types)) {
          setDataTypes(dres.data.data_types);
        }
      } catch (err) {
        console.warn('Failed to load data_types', err);
      }
    })();

    (async () => {
      try {
        const tid = Number(user?.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
        const configRes = await axios.get(`${API_BASE}/manager/consent-options`, {
          params: tid ? { tenant_id: tid } : {},
          skipLoading: true
        });
        if (mounted && configRes.data) setConsentOptions(configRes.data);
      } catch (err) {
        console.warn('Failed to load consent options config', err);
      }
    })();

    return () => { mounted = false };
  }, []);

  // Keep data types (created by the manager) in sync without a page reload
  useEffect(() => {
    const reloadDataTypes = async () => {
      try {
        const tid = Number(user?.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
        const dres = await axios.get(`${API_BASE}/manager/data-types`, {
          params: tid ? { tenant_id: tid } : {},
          skipLoading: true
        });
        if (dres.data && Array.isArray(dres.data.data_types)) setDataTypes(dres.data.data_types);
      } catch (err) {
        console.warn('Failed to reload data_types', err);
      }
    };
    window.addEventListener('dataTypesUpdated', reloadDataTypes);
    return () => window.removeEventListener('dataTypesUpdated', reloadDataTypes);
  }, [user?.tenant_id]);

  const configuredLocations = (consentOptions?.location || []).map(option => option.value || option.key).filter(Boolean);
  const configuredLanguages = (consentOptions?.language || []).map(option => ({
    value: option.value || option.key,
    label: option.label || option.value || option.key,
  }));
  const configuredDataTypeOptions = (consentOptions?.data_type_consent
    || consentOptions?.dataTypeConsent
    || consentOptions?.data_types
    || consentOptions?.dataTypes
    || []).map(option => {
      const key = typeof option === 'string' ? option : (option.key || option.value || option.name);
      const label = typeof option === 'string' ? option : (option.label || option.name || option.value || key);
      return key ? { key: String(key), value: String(key), label: String(label || key) } : null;
    }).filter(Boolean);
  const effectiveLocations = configuredLocations;
  // Data types created by the manager (Data Types page) come first, then the
  // configured defaults. De-duplicated case-insensitively.
  const managerDataTypeOptions = (Array.isArray(dataTypes) ? dataTypes : [])
    .map(dt => {
      const key = String((dt && (dt.key || dt.value || dt.name)) || '').trim();
      const label = String((dt && (dt.label || dt.name || dt.value || dt.key)) || '').trim();
      return key ? { key, value: key, label: label || key } : null;
    })
    .filter(Boolean);
  const normalizeDataTypeKey = (val) => {
    if (!val) return '';
    let k = String(val).trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    k = k.replace(/\d+$/, '');
    if (k.endsWith('card')) k = k.slice(0, -4);
    if (k === 'email' || k === 'emailaddress') return 'email';
    if (k === 'phone' || k === 'phonenumber' || k === 'mobile') return 'phone';
    if (k === 'voter' || k === 'voterid') return 'voterid';
    if (k === 'customer' || k === 'customerid') return 'customerid';
    if (k === 'drivinglicense' || k === 'dl') return 'drivinglicense';
    if (k === 'bankac' || k === 'bankaccount') return 'bankac';
    if (k === 'aadhaar' || k === 'aadhar' || k === 'adhar') return 'aadhaar';
    return k;
  };

  const effectiveDataTypeConsents = (() => {
    const seenKeys = new Set();
    const seenLabels = new Set();
    const result = [];
    const addOption = (opt) => {
      if (!opt) return;
      const rawKey = String(opt.key || opt.value || '').trim();
      const rawLabel = String(opt.label || opt.name || opt.value || rawKey).trim();
      if (!rawKey && !rawLabel) return;

      const normKey = normalizeDataTypeKey(rawKey) || normalizeDataTypeKey(rawLabel);
      const cleanLabel = rawLabel.trim().toLowerCase().replace(/[^a-z0-9]/g, '');

      if (normKey && seenKeys.has(normKey)) return;
      if (cleanLabel && seenLabels.has(cleanLabel)) return;

      if (normKey) seenKeys.add(normKey);
      if (cleanLabel) seenLabels.add(cleanLabel);

      result.push({
        key: opt.key || rawKey,
        value: opt.value || rawKey,
        label: rawLabel || opt.key || rawKey
      });
    };

    // First add manager data types (includes tenant custom types + standard defaults with keys)
    managerDataTypeOptions.forEach(addOption);
    // Then ensure all configured standard defaults are always present and never missing
    configuredDataTypeOptions.forEach(addOption);

    return result.length > 0 ? result : configuredDataTypeOptions;
  })();
  const languageCodeFor = (rawLanguage) => {
    const value = String(rawLanguage || '').trim().toLowerCase();
    if (!value) return '';
    const option = (consentOptions?.language || []).find(item => [item.key, item.value, item.label]
      .filter(Boolean)
      .some(candidate => String(candidate).trim().toLowerCase() === value));
    return normalizeLangCode(option ? String(option.value || option.key).trim() : value);
  };
  const effectiveLanguageOptions = (() => {
    const optionsByCode = new Map(configuredLanguages.map(option => [languageCodeFor(option.value), option]));
    for (const [name, code] of Object.entries(GLOBAL_LANGUAGE_MAP)) {
      const normalizedCode = languageCodeFor(code);
      if (tenantLanguageCodes.includes(normalizedCode) && !optionsByCode.has(normalizedCode)) {
        optionsByCode.set(normalizedCode, { value: normalizedCode, label: name });
      }
    }
    return tenantLanguageCodes.map(code => optionsByCode.get(code)).filter(Boolean);
  })();

  useEffect(() => {
    let mounted = true;
    setTenantLanguagesLoading(true);
    const loadTenantLanguages = async () => {
      const tenant_id = (user && (user.tenant_id || user.tenantId || user.tenant))
        ? (user.tenant_id || user.tenantId || user.tenant)
        : (Number(localStorage.getItem('tenant_id')) || null);
      if (!tenant_id) {
        if (mounted) {
          setTenantLanguageCodes([]);
          setTenantLanguagesLoading(false);
        }
        return;
      }
      try {
        const res = await axios.get(`${API_BASE}/manager/new-consents`, {
          params: { tenant_id, include_variants: true, limit: 500 },
          skipLoading: true,
        });
        const rows = res?.data?.data || [];
        const codes = new Set();
        for (const row of (Array.isArray(rows) ? rows : [])) {
          const status = String(row.status || 'active').trim().toLowerCase();
          if (['inactive', 'rejected'].includes(status)) continue;
          let meta = row.consent_metadata || row.data || {};
          try { if (typeof meta === 'string') meta = JSON.parse(meta); } catch (e) { meta = {}; }
          let messageMeta = {};
          try {
            const message = row.consent_message || row.message;
            messageMeta = typeof message === 'string' ? (JSON.parse(message) || {}) : (message || {});
          } catch (e) { messageMeta = {}; }
          const rawLanguage = messageMeta?.language_code || messageMeta?.languageCode || messageMeta?.language || messageMeta?.language_name ||
            meta?.language_code || meta?.languageCode || meta?.language || meta?.lang || meta?.language_name || meta?.languageName ||
            row.language_code || row.language_name || row.language || '';
          const languageCode = languageCodeFor(rawLanguage);
          if (languageCode) codes.add(languageCode);
        }
        try {
          const subRes = await axios.get(`${API_BASE}/manager/sub-category-consents`, {
            params: { tenant_id, language: '' },
            skipLoading: true,
          });
          const subRows = subRes?.data?.rows || [];
          for (const row of (Array.isArray(subRows) ? subRows : [])) {
            let meta = row.data || {};
            try { if (typeof meta === 'string') meta = JSON.parse(meta); } catch (e) { meta = {}; }
            let messageMeta = {};
            try { messageMeta = typeof row.message === 'string' ? (JSON.parse(row.message) || {}) : (row.message || {}); } catch (e) { messageMeta = {}; }
            const rawLanguage = messageMeta?.language_code || messageMeta?.languageCode || messageMeta?.language || messageMeta?.language_name ||
              meta?.language_code || meta?.language || meta?.lang || meta?.language_name || meta?.languageName || meta?.languageCode || '';
            const languageCode = languageCodeFor(rawLanguage);
            if (languageCode) codes.add(languageCode);
          }
        } catch (e) { /* new_consent languages are sufficient when unavailable */ }
        if (mounted) setTenantLanguageCodes(Array.from(codes).filter(Boolean));
      } catch (e) {
        console.warn('Failed to load tenant consent languages', e);
        if (mounted) setTenantLanguageCodes([]);
      } finally {
        if (mounted) setTenantLanguagesLoading(false);
      }
    };
    loadTenantLanguages();
    return () => { mounted = false; };
  }, [consentCacheTenantId, consentOptions]);

  useEffect(() => {
    if (!showCSVConfirm || tenantLanguagesLoading || !effectiveLanguageOptions.length) return;
    const currentLanguageExists = effectiveLanguageOptions.some(option => languageCodeFor(option.value) === languageCodeFor(selectedUploadLanguage));
    if (!currentLanguageExists) setSelectedUploadLanguage(effectiveLanguageOptions[0].value);
  }, [showCSVConfirm, tenantLanguagesLoading, effectiveLanguageOptions, selectedUploadLanguage]);

  useEffect(() => {
    if (isSlidingPageOpen && firstInputRef.current) {
      setTimeout(() => firstInputRef.current.focus(), 100);
    }
  }, [isSlidingPageOpen]);

  // useEffect(() => {
  //   fetchConsents();
  // }, [fetchConsents]);

  // useEffect(() => {
  //   if (tab === 'consent') {
  //     fetchConsents();
  //     // fetchCookieEvents();
  //   }
  // }, [tab, fetchConsents]);


  const openSlidingPage = () => {
    const today = new Date().toISOString();
    // ensure freshest types/messages are loaded before opening
    (async () => {
      try {
        if (UserPanel && typeof UserPanel._refreshTypes === 'function') {
          try { await UserPanel._refreshTypes(); } catch (e) { /* ignore */ }
        }
        setUserForm({
          username: user?.name || '',
          ageCategory: 'adult',
          email: '',
          phone: '',
          dob: '',
          address: '',
          createdAt: today,
          type: '',
          consent: '',
          entity: '',
          appName: '',
          userAction: '',
          data: '',
          expiry: '',
          status: '',
          parentName: '',
          parentEmail: '',
          parentPhone: '',
          _guardianType: 'parent',
          language: '',
        });
        setUseParentContact(false);
        setSlidingPageOpen(true);
        setEditingId(null);
        setHasSubmitted(false);
      } catch (e) { /* ignore */ }
    })();
  };

  const handlePrivacyNotice = (consent) => {
    // Prefill send modal with privacy notice template (fetch manager/new-consents)
    (async () => {
      try {
        setPrivacyModalConsent(consent);
        setPrivacyModalNotice(null);
        const recordedLanguage = consent.consent_language || consent.language_code || consent.language || consent.preferred_languages || '';
        const recordedLanguageValue = Array.isArray(recordedLanguage) ? recordedLanguage[0] : String(recordedLanguage).split(',')[0].trim();
        const recordedLanguageCode = languageCodeFor(recordedLanguageValue) || 'en';
        let template = '';
        // if (saved) {
        //   try {
        //     const obj = JSON.parse(saved);
        //     if (obj && obj.consent_type && (obj.consent_type === (consent.type || consent.consent_type || ''))) {
        //       template = obj.consent_message || '';
        //     }
        //   } catch (e) { /* ignore */ }
        // }
        // if (!template) {
        //   try {
        //     const res = await axios.get(`${API_BASE}/manager/new-consents`);
        //     const data = res.data?.data || [];
        //     const match = data.find(r => {
        //       const ct = r.consent_type || r.type || r.consent_type_name || '';
        //       return ct && ((ct === (consent.type || consent.consent_type)) || (ct.toString().toLowerCase() === (consent.type || consent.consent_type || '').toString().toLowerCase()));
        //     });
        //     if (match) template = match.consent_message || match.message || match.consent || '';
        //   } catch (e) {
        let selectedNotice = null;
        try {
          const params = {};
          if (user && user.tenant_id) params.tenant_id = user.tenant_id;
          const np = await axios.get(`${API_BASE}/user/privacy-notices`, { params });
          const notices = np.data?.privacy_notices || [];
          if (Array.isArray(notices) && notices.length) {
            const type = (consent.type || consent.consent_type || '').toString().trim().toLowerCase();
            const findMatch = (arr, token) => {
              const tk = (token || '').toString().trim().toLowerCase();
              if (!tk) return null;
              return arr.find(n => {
                const ct = ((n.consent_type || n.title || n.description || '') || '').toString().trim().toLowerCase();
                return ct && (ct === tk || ct.includes(tk));
              });
            };
            const typeMatches = notices.filter(n => findMatch([n], type));
            selectedNotice = typeMatches.find(n => {
              const noticeLanguage = n.language_code || n.language || n.card_data?.language || '';
              const noticeCode = languageCodeFor(noticeLanguage);
              return noticeCode === recordedLanguageCode;
            }) || typeMatches[0] || notices[0];
            if (selectedNotice) {
              setPrivacyModalNotice(selectedNotice);
              template = selectedNotice.content || selectedNotice.description || '';
              if (!template && selectedNotice.html) {
                template = selectedNotice.html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
              }
            }
          }
        } catch (e) {
          console.warn('Failed to load privacy notices from DB', e);
        }

        if (!template) {
          const saved = localStorage.getItem('selected_consent_for_notification');
          if (saved) {
            try {
              const obj = JSON.parse(saved);
              if (obj && obj.consent_type && (obj.consent_type === (consent.type || consent.consent_type || ''))) {
                template = obj.consent_message || '';
              }
            } catch (e) { /* ignore */ }
          }
          if (!template) {
            try {
              const res = await axios.get(`${API_BASE}/manager/new-consents`);
              const data = res.data?.data || [];
              const match = data.find(r => {
                const ct = r.consent_type || r.type || r.consent_type_name || '';
                return ct && ((ct === (consent.type || consent.consent_type)) || (ct.toString().toLowerCase() === (consent.type || consent.consent_type || '').toString().toLowerCase()));
              });
              if (match) template = match.consent_message || match.message || match.consent || '';
            } catch (e) {
              // ignore
            }
          }
        }
        const fallback = consent.consent || consent.message || '';
        // Prefer template, then stored consent text, then the consent `type` field
        const typeAsText = (consent.type || consent.consent_type || '').toString();
        setPrivacyMessage(stripHtml(template || fallback || typeAsText || ''));
        setPrivacyFormat('text');
        // when opening in text mode, clear any preloaded attachments
        setPrivacyAttachments([]);
        setPrivacyAutoTranslate(true);
        // Keep the language used while recording this consent; do not offer other languages here.
        setPrivacyTargetLanguage(recordedLanguageCode);
        // setPrivacyAttachments([]);
        // try to immediately load a cached PDF for this consent type so attachment shows without delay
        try {
          const type = (consent.type || consent.consent_type || consent.template || '').toString();
          const norm = (type || '').toString().trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/gi, '');
          if (norm) {
            const cached = localStorage.getItem(`notification_pdf_${norm}`);
            if (cached) {
              const obj = JSON.parse(cached);
              if (obj && obj.data) {
                const b64 = obj.data;
                const byteChars = atob(b64);
                const byteNumbers = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const filename = obj.filename || `${norm}.pdf`;
                try {
                  const file = new File([blob], filename, { type: 'application/pdf' });
                  setPrivacyAttachments([file]);
                } catch (e) {
                  blob.name = filename;
                  setPrivacyAttachments([blob]);
                }
                console.debug('Loaded cached PDF for', type, filename);
              }
            }
          }
        } catch (e) {
          console.warn('Failed to load cached PDF in handlePrivacyNotice', e);
        }
        // Try fetching privacy notices from backend and open matching PDF if available
        try {
          const np = await axios.get(`${API_BASE}/user/privacy-notices`, { params: { tenant_id: user?.tenant_id } });
          const list = np.data?.privacy_notices || [];
          if (list && list.length) {
            const t = (consent.type || consent.consent_type || '').toString().toLowerCase();
            let found = list.find(n => (n.title || '').toString().toLowerCase().includes(t));
            if (!found) found = list[0];
            if (found && found.id) {
              // fetch PDF from server and attach to modal instead of opening a new page
              try {
                const pdfRes = await axios.get(`${API_BASE}/user/privacy-notices/${found.id}/pdf`, { responseType: 'blob' });
                if (pdfRes && pdfRes.data) {
                  const blob = pdfRes.data;
                  // const type = (found.title || (consent.type || 'attachment')).replace(/[^a-z0-9_\-]/gi, '_')+'.pdf';
                  const filename = (consent && (consent.id || consent.ID)) ? `consent_${consent.id || consent.ID}.pdf` : (found.title || (consent.type || 'attachment')).replace(/[^a-z0-9_\-]/gi, '_') + '.pdf';
                  try {
                    const file = new File([blob], filename, { type: 'application/pdf' });
                    setPrivacyAttachments([file]);
                  } catch (e) {
                    blob.name = filename;
                    setPrivacyAttachments([blob]);
                  }
                  setPrivacyFormat('attachment');
                  setPrivacyModalOpen(true);
                  return;
                }
              } catch (e) {
                console.debug('Failed to fetch privacy notice PDF, falling back to modal', e);
              }
            }
          }
        } catch (e) {
          console.debug('No privacy notices available from backend', e);
        }
        setPrivacyModalOpen(true);
      } catch (e) {
        console.warn('Failed to prepare privacy send modal', e);
        // alert('Privacy notice not available');
        Swal.fire({ text: 'Privacy notice not available', icon: 'info' });
      }
    })();
  };

  const closeSlidingPage = () => {
    setSlidingPageOpen(false);
    setSaved(false);
    setEditingId(null);
  };

  const resolveLocalizedConsentText = (typeValue, selectedLanguage, candidateTypeList = consentTypesByLanguage, selectedSubId = null) => {
    const typeName = String(typeValue || '').trim();
    const lang = normalizeLangCode(selectedLanguage);
    if (!typeName) return '';

    const normalizedType = typeName.toLowerCase();
    if (selectedSubId !== null && selectedSubId !== undefined) {
      const subMatch = (subConsents || []).find(item => String(item?.id) === String(selectedSubId));
      if (subMatch) {
        const rawMessage = subMatch.message || subMatch.consent_message || '';
        const parsedMessage = parseConsentPayload(rawMessage);
        if (parsedMessage) {
          const messageLanguage = parsedMessage.language_code || parsedMessage.language;
          if (!messageLanguage || normalizeLangCode(messageLanguage) === lang) {
            const message = parsedMessage.consent_message || parsedMessage.content || parsedMessage.message;
            if (message) return String(message);
          }
        } else if (rawMessage) {
          return String(rawMessage);
        }
      }
    }

    const typeMatch = (candidateTypeList || []).find(item => {
      const value = item?.consent_type || item?.type || item?.name || item?.id || '';
      return String(value).trim().toLowerCase() === normalizedType;
    });
    if (typeMatch) {
      const typedMsg = typeMatch.consent_message || typeMatch.message || '';
      if (typedMsg) return typedMsg;
    }

    // const subMatch = (subConsents || []).find(item => {
    //   const idMatches = selectedSubId !== null && selectedSubId !== undefined
    //     ? String(item?.id) === String(selectedSubId)
    //     : false;
    //   if (!idMatches) return false;
    //   const raw = item?.message;
    //   if (typeof raw === 'string') {
    //     try {
    //       const parsed = JSON.parse(raw);
    //       if (parsed && parsed.kind === 'multilingual_consent' && parsed.language_code) {
    //         return String(parsed.language_code).trim().toLowerCase() === lang;
    //       }
    //     } catch (e) { }
    //   }
    //   return false;
    // });
    // if (subMatch) {
    //   try {
    //     const raw = subMatch.message;
    //     if (typeof raw === 'string') {
    //       const parsed = JSON.parse(raw);
    //       if (parsed && parsed.kind === 'multilingual_consent' && parsed.consent_message) {
    //         return parsed.consent_message;
    //       }
    //     }
    //   } catch (e) { }
    // }

    const exactTypeKey = normalizedType.replace(/\s+/g, '_');
    const candidateKeys = [normalizedType, exactTypeKey, normalizedType.replace(/_consent$/, ''), exactTypeKey.replace(/_consent$/, '')];
    for (const key of candidateKeys) {
      const msg = typeMessages?.[key];
      if (msg) return msg;
    }
    return typeMessages?.[normalizedType] || typeMessages?.[exactTypeKey] || '';
  };

  useEffect(() => {
    if (!isSlidingPageOpen || !userForm.language || !userForm.type || !consentTypesByLanguage.length) return;
    const localizedMessage = resolveLocalizedConsentText(
      userForm.type,
      userForm.language,
      consentTypesByLanguage,
      userForm.subCategory || null
    );
    if (localizedMessage && localizedMessage !== userForm.consent) {
      setUserForm(prev => ({ ...prev, consent: localizedMessage }));
    }
  }, [isSlidingPageOpen, userForm.language, userForm.type, userForm.subCategory, userForm.consent, consentTypesByLanguage]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setUserForm(prev => {
      // If type is being changed, auto-fill consent field
      if (name === 'type') {
        const selectedType = findConsentTypeOption(consentTypesByLanguage, value);
        const canonicalValue = selectedType ? (selectedType.consent_type || selectedType.type || selectedType.name || value) : value;
        const msg = resolveLocalizedConsentText(canonicalValue, prev.language || 'en', consentTypesByLanguage, null);
        // clear any previously selected subCategory when type changes
        return {
          ...prev,
          type: canonicalValue,
          consent_type: canonicalValue,
          consent: msg,
          consentId: selectedType?.consent_id || selectedType?.parent_consent_id || selectedType?.id || null,
          subCategory: '',
        };
      }
      // Keep the previously selected type when that type still exists in the
      // language-scoped list; otherwise clear it to force a valid choice.
      if (name === 'language') {
        const nextLanguage = value;
        const currentType = prev.type || prev.consent_type || '';
        const keepType = currentType && (consentTypesByLanguage || []).some(item => {
          const candidate = item?.consent_type || item?.type || item?.name || item?.id || '';
          return optionMatchesConsentType(candidate, currentType);
        });
        const nextType = keepType ? currentType : '';
        const nextConsent = nextType
          ? resolveLocalizedConsentText(nextType, nextLanguage, consentTypesByLanguage, prev.subCategory || null)
          : '';
        return {
          ...prev,
          language: nextLanguage,
          type: nextType,
          consent_type: nextType,
          consent: nextConsent,
          consentId: nextType ? (prev.consentId || null) : null,
          subCategory: '',
        };
      }
      if (name === 'location') {
        return { ...prev, location: value, locality: value };
      }
      return { ...prev, [name]: value };
    });
    // If subCategory was changed, update consent preview accordingly
    try {
      if (name === 'subCategory') {
        const selId = value;
        if (!selId) {
          const typeMsg = resolveLocalizedConsentText(userForm.type, userForm.language || 'en', consentTypesByLanguage);
          setUserForm(prev => ({ ...prev, subCategory: '', consent: typeMsg }));
        } else {
          const sel = (subConsents || []).find(s => String(s.id) === String(selId) || String(s.label) === String(selId));
          if (sel) {
            const message = resolveLocalizedConsentText(userForm.type, userForm.language || 'en', consentTypesByLanguage, sel.id);
            setUserForm(prev => ({ ...prev, subCategory: selId, consent: message || prev.consent }));
          } else {
            // attempt server lookup as fallback
            (async () => {
              try {
                const tenant_id = (user && (user.tenant_id || user.tenantId || user.tenant)) ? (user.tenant_id || user.tenantId || user.tenant) : (Number(localStorage.getItem('tenant_id')) || null);
                const params = {};
                if (tenant_id) params.tenant_id = tenant_id;
                const res = await axios.get(`${API_BASE}/manager/sub-category-consents`, { params });
                const rows = res.data && (res.data.rows || res.data) ? (res.data.rows || res.data) : [];
                const found = (rows || []).find(r => String(r.id) === String(selId) || String(r.name || r.sub_category) === String(selId));
                if (found) {
                  const rawMessage = found.message || found.consent_message || '';
                  const parsedMessage = parseConsentPayload(rawMessage);
                  const message = parsedMessage?.consent_message || parsedMessage?.content || rawMessage;
                  setUserForm(prev => ({ ...prev, subCategory: selId, consent: message || prev.consent || '' }));
                }
              } catch (ee) { /* ignore */ }
            })();
          }
        }
      }
    } catch (e) { console.warn('Error handling subCategory change', e); }
  };

  // Helpers for masking
  // const maskEmail = (email) => {
  //   if (!email) return '—';
  //   try {
  //     const parts = String(email).split('@');
  //     if (parts.length < 2) return email;
  //     const local = parts[0];
  //     const domain = parts.slice(1).join('@');
  //     if (local.length <= 2) return `${local[0]}****@${domain}`;
  //     const first = local[0];
  //     const last = local[local.length - 1];
  //     return `${first}****${last}@${domain}`;
  //   } catch (e) {
  //     return email;
  //   }
  // };

  // const maskPhone = (phone) => {
  //   if (!phone) return '—';
  //   const digits = String(phone).replace(/\D/g, '');
  //   const len = digits.length;
  //   if (len <= 4) return phone;
  //   const start = Math.floor((len - 4) / 2);
  //   const masked = digits.slice(0, start) + '****' + digits.slice(start + 4);
  //   return masked;
  // };
  const maskEmail = (email) => maskEmailUtil(email);
  const maskPhone = (phone) => maskPhoneUtil(phone);

  const getRevokedParts = (rec) => {
    const s = (rec?.status || '').toString();
    if (!s) return [];
    if (s.toLowerCase().includes('all revoked')) return ['all'];
    const parts = [];
    if (s.toLowerCase().includes('email revoked')) parts.push('email');
    if (s.toLowerCase().includes('whatsapp revoked') || s.toLowerCase().includes('whatsapp')) parts.push('whatsapp');
    if (s.toLowerCase().includes('d.o.b revoked') || s.toLowerCase().includes('d.o.b')) parts.push('dob');
    return parts;
  };

  const isRevoked = (rec, part) => {
    if (!rec) return false;
    const parts = getRevokedParts(rec) || [];
    if (parts.includes('all')) return true;
    if (part === 'email') return parts.includes('email');
    if (part === 'phone' || part === 'whatsapp') return parts.includes('whatsapp') || parts.includes('phone');
    if (part === 'dob') return parts.includes('dob');
    return false;
  };

  const validatePhone = (phone) => {
    if (!phone) return false;
    const digits = String(phone).replace(/\D/g, '');
    return /^[6-9]\d{9}$/.test(digits);
  };

  const renderStatusIcon = (status) => {
    const s = (status || '').toString().toLowerCase();
    let color = '#f59e0b'; // default yellow for pending
    let title = status || 'Pending';
    if (s.includes('revok')) {
      color = '#dc2626';
      title = 'Revoked';
    } else if (s.includes('active') || s.includes('approved') || s.includes('accepted')) {
      color = '#16a34a';
      title = status || 'Active';
    } else if (s.includes('suspend') || s.includes('reconsent') || s.includes('supersed')) {
      color = '#063ed9ff';
      title = 'Reconsent';
    } else if (s.includes('ended') || s.includes('expired')) {
      color = '#7c3aed';
      title = 'Expired';
    } else if (s.includes('declin') || s.includes('reject')) {
      // treat decline/reject as declined (red)
      color = '#dc2626';
      title = 'Declined';
    } else if (s.includes('pending') || !s) {
      color = '#f59e0b';
      title = status || 'Pending';
    } else {
      // fallback gray
      color = '#6b7280';
      title = status;
    }
    return (
      <span title={title} style={{ color, fontSize: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="8" />
        </svg>
      </span>
    );
  };

  // Normalize various backend status strings into a small set of display labels
  const normalizeStatusLabel = (status) => {
    const s = (status || '').toString().toLowerCase();
    if (!s || s.trim() === '') return 'Pending';
    if (s.includes('revok')) return 'Revoked';
    if (s.includes('accept') || s.includes('approved') || s.includes('active')) return 'Accepted';
    if (s.includes('expir') || s.includes('ended')) return 'Expired';
    // Map any decline/reject variant to 'Declined'
    if (s.includes('declin')) return 'Declined';
    if (s.includes('reject')) return 'Declined';
    if (s.includes('suspend') || s.includes('reconsent') || s.includes('supersed')) return 'Reconsent';
    // fallback: return capitalized original short form
    const first = String(status).split(/\s|_|-/)[0] || status;
    return String(first).charAt(0).toUpperCase() + String(first).slice(1);
  };

  const handleReconsentStatusClick = (rec) => {
    const recId = rec?.id != null ? String(rec.id) : '';
    if (recId && reconsentSubmittedIds.has(recId)) {
      Swal.fire({
        icon: 'info',
        title: 'Reconsent already submitted',
        text: 'This consent record already has a reconsent submission recorded.',
      });
      return;
    }

    if (isTemplateUnderProcess(rec)) {
      Swal.fire({
        icon: 'info',
        title: 'Consent Version Under Process',
        text: `The consent template for "${getConsentTypeLabel(rec)}" is currently Under Process and awaiting manager acceptance. Re-consent is only permitted after acceptance.`,
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    let reason = 'Consent template version was updated in database. A new version of the consent notice is active.';
    let suspendedAt = rec.updated_at || rec.created_at || '';
    if (rec.data && typeof rec.data === 'string') {
      const reasonMatch = rec.data.match(/suspended_reason:\s*(.+)/i);
      const atMatch = rec.data.match(/suspended_at:\s*(.+)/i);
      if (reasonMatch && reasonMatch[1]) {
        const r = reasonMatch[1].trim();
        if (r === 'consent_version_updated_new_consent_recorded') {
          reason = 'Consent version was updated in database. New consent notice recorded for the active version.';
        } else {
          reason = r;
        }
      }
      if (atMatch && atMatch[1]) {
        suspendedAt = atMatch[1].trim();
      }
    }

    Swal.fire({
      title: '<div style="font-size:18px;font-weight:700;color:#92400e;">Reconsent Required</div>',
      html: `
        <div style="text-align:left;font-size:14px;color:#334155;line-height:1.6;">
          <p style="margin-bottom:8px;"><strong>Reason for Suspension:</strong></p>
          <div style="background:#fffbeb;border:1px solid #fde68a;padding:10px 14px;border-radius:8px;color:#92400e;margin-bottom:12px;font-size:13.5px;">
            ${reason}
          </div>
          <p style="margin-bottom:4px;"><strong>Consent Type:</strong> ${getConsentTypeLabel(rec) || rec.type || 'N/A'}</p>
          <p style="margin-bottom:4px;"><strong>Recorded Version:</strong> ${rec.version || 'Older Version'}</p>
          ${suspendedAt ? `<p style="margin-bottom:4px;"><strong>Suspended At:</strong> ${formatDateTime(suspendedAt)}</p>` : ''}
        </div>
      `,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: '🔁 Reconsent',
      cancelButtonText: 'Close',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#64748b',
      customClass: { popup: 'swal-popup-custom' }
    }).then(async (result) => {
      if (result.isConfirmed) {
        const consentType = rec.type || rec.consent_type || '';
        let activeTemplateMsg = '';

        try {
          const res = await axios.get(`${API_BASE}/manager/new-consents`, { params: { tenant_id: user?.tenant_id } });
          const data = res.data?.data || [];
          const match = data.find(r => {
            const ct = r.consent_type || r.type || r.consent_type_name || '';
            return ct && ct.toString().toLowerCase() === consentType.toString().toLowerCase();
          });
          if (match) {
            activeTemplateMsg = match.consent_message || match.message || match.consent || '';
            const mStatus = String(match.status || '').toLowerCase();
            if (mStatus === 'under_process') {
              Swal.fire({
                icon: 'warning',
                title: 'Cannot Proceed With Re-consent',
                text: `The consent version for "${consentType}" is currently Under Process and awaiting manager acceptance.`,
                confirmButtonColor: '#2563eb'
              });
              return;
            }
          }
        } catch (e) {
          console.warn('Failed to fetch active template for reconsent', e);
        }

        if (!activeTemplateMsg) {
          const normType = consentType.toString().trim().toLowerCase();
          activeTemplateMsg = typeMessages[normType] || decodeHtmlEntities(rec.consent || rec.data || '');
        }

        const parseIdProofType = (v) => {
          if (!v) return [];
          if (Array.isArray(v)) return v.map(s => String(s).trim()).filter(Boolean);
          if (typeof v === 'string') {
            const sep = v.includes('|') ? '|' : ',';
            return v.split(sep).map(s => s.trim()).filter(Boolean);
          }
          return [String(v).trim()].filter(Boolean);
        };

        const parseMultiValue = (v) => {
          if (!v) return [];
          if (Array.isArray(v)) return v.map(s => String(s).trim()).filter(Boolean);
          if (typeof v === 'string') {
            const trimmed = v.trim();
            if (!trimmed) return [];
            const sep = trimmed.includes('|') ? '|' : ',';
            return trimmed.split(sep).map(s => s.trim()).filter(Boolean);
          }
          return [String(v).trim()].filter(Boolean);
        };

        const parseDataTypeConsent = (v) => {
          if (!v) return [];
          if (typeof v === 'string') {
            const trimmed = v.trim();
            if (!trimmed) return [];
            if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
              try {
                const parsed = JSON.parse(trimmed);
                return parseMultiValue(parsed);
              } catch (e) {
                return parseMultiValue(trimmed);
              }
            }
            return parseMultiValue(trimmed);
          }
          return parseMultiValue(v);
        };

        const parseLangs = (l) => {
          if (!l) return [];
          if (Array.isArray(l)) return l.map(s => String(s).trim()).filter(Boolean);
          if (typeof l === 'string') return l.split(',').map(s => s.trim()).filter(Boolean);
          return [String(l).trim()].filter(Boolean);
        };

        setUserForm({
          username: rec.username || rec.Name || user?.name || '',
          ageCategory: (rec.parent_name || rec.parentName || (getAgeFromDOB(rec.dob) !== null && getAgeFromDOB(rec.dob) < 18)) ? 'minor' : 'adult',
          email: rec.email || user?.email || '',
          phone: rec.phone || '',
          dob: rec.dob || '',
          address: rec.address || rec.Address || rec.user_address || rec.postal_address || rec.street_address || rec.current_address || '',
          createdAt: new Date().toISOString(),
          type: consentType,
          consent: activeTemplateMsg,
          entity: rec.entity || rec.group || rec.user_group || '',
          appName: rec.appName || rec.app_name || '',
          userAction: rec.userAction || rec.user_action || '',
          data: rec.data || '',
          expiry: rec.expiry || rec.expiry_date || '',
          status: '',
          parentName: rec.parent_name || rec.parentName || '',
          parentEmail: rec.parent_email || rec.parentEmail || '',
          parentPhone: rec.parent_phone || rec.parentPhone || '',
          idProofType: parseIdProofType(rec.id_proof_type || rec.idProofType || ''),
          idProofFileName: rec.id_proof_file || rec.idProofFileName || '',
          dataTypeConsent: parseDataTypeConsent(rec.data_type_consent || rec.dataTypeConsent || rec.Data_Type_consent || rec.data_type_consent_value || ''),
          location: rec.location || rec.locality || rec.city || rec.user_location || '',
          locality: rec.location || rec.locality || rec.city || rec.user_location || '',
          department: rec.department || rec.dept || rec.department_name || '',
          subCategory: rec.sub_category || rec.subCategory || '',
          preferredLanguages: parseLangs(rec.preferred_languages || rec.languages || rec.language || rec.consent_language || rec.language_code || ''),
        });

        const hasParent = !!(
          rec.parent_name || rec.parentName ||
          rec.parent_email || rec.parentEmail ||
          rec.parent_phone || rec.parentPhone
        );
        setUseParentContact(hasParent);
        setEditingId(null);
        setHasSubmitted(false);
        setPendingReconsentId(recId || null);
        setSlidingPageOpen(true);

        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'info',
          title: 'Consent form ready with active version template. Click "Submit Consent" to re-consent.',
          showConfirmButton: false,
          timer: 4000
        });
      }
    });
  };

  const getEmailResponseStatusFromRecord = (rec) => {
    if (!rec) return '';
    // prefer explicit email-response fields if present
    if (rec.email_response) return String(rec.email_response);
    if (rec.email_response_status) return String(rec.email_response_status);
    if (rec.user_response_status) return String(rec.user_response_status);
    // fallback to status text if it mentions accepted/declined via email
    if (rec.status && typeof rec.status === 'string') return rec.status;
    // fallback empty
    return '';
  };

  // Parse a server-supplied datetime value as UTC when it lacks an explicit
  // timezone. Many backend rows emit naive ISO strings like "2026-04-20T10:30:00"
  // (no trailing 'Z' and no +HH:MM offset). Browsers treat such strings as
  // *local* time which causes incorrect display on production where the server
  // is UTC but the viewer is in a different timezone. By appending 'Z' to
  // naive ISO strings we force them to be interpreted as UTC, after which
  // Intl.DateTimeFormat with timeZone:'Asia/Kolkata' converts to IST correctly.
  const parseAsUTC = (v) => {
    if (v === null || v === undefined || v === '') return null;
    try {
      if (v instanceof Date) return v;
      if (typeof v === 'number') return new Date(v);
      const s = String(v).trim();
      // ISO-like with date+time but no zone designator -> treat as UTC
      // matches: "YYYY-MM-DDTHH:MM[:SS[.fff]]" without Z / +HH:MM / -HH:MM at end
      const isoNaive = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
      if (isoNaive.test(s)) {
        // Normalize space separator to 'T' and append 'Z'
        return new Date(s.replace(' ', 'T') + 'Z');
      }
      return new Date(s);
    } catch (e) {
      return new Date(v);
    }
  };

  const formatDateTime = (v) => {
    if (!v && v !== 0) return '—';
    try {
      const d = parseAsUTC(v) || new Date(v);
      if (!d || Number.isNaN(d.getTime())) return String(v);
      const dateStr = new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(d);
      const timeStr = new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour12: true,
        hour: '2-digit',
        minute: '2-digit'
      }).format(d);
      return `${dateStr}, ${timeStr}`;
    } catch (e) { return String(v); }
  };

  const formatDateTimeCell = (v, fallback = null) => {
    if (!v && v !== 0) return '—';
    try {
      let d = parseAsUTC(v) || new Date(v);
      if (!d || Number.isNaN(d.getTime())) return String(v);

      // Check if time is 00:00:00 UTC (from date-only strings or midnight default)
      const isMidnightUTC = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
      if (isMidnightUTC && fallback) {
        const fbDate = parseAsUTC(fallback) || new Date(fallback);
        if (fbDate && !Number.isNaN(fbDate.getTime()) && !(fbDate.getUTCHours() === 0 && fbDate.getUTCMinutes() === 0)) {
          d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), fbDate.getUTCHours(), fbDate.getUTCMinutes(), fbDate.getUTCSeconds()));
        }
      }

      const dateStr = new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(d);

      const stillMidnight = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
      const timeStr = stillMidnight
        ? ''
        : new Intl.DateTimeFormat('en-IN', {
          timeZone: 'Asia/Kolkata',
          hour12: true,
          hour: '2-digit',
          minute: '2-digit'
        }).format(d);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.3 }}>
          <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 12.5 }}>{dateStr}</div>
          {timeStr ? <div style={{ fontSize: 11.5, color: '#64748b' }}>{timeStr}</div> : null}
        </div>
      );
    } catch (e) {
      return String(v);
    }
  };

  const getReminderSlots = (rec) => {
    // Return { count, first, second, third } where values are date strings or '—'
    try {
      let recordData = rec?.data && typeof rec.data === 'object' ? rec.data : null;
      if (!recordData && typeof rec?.data === 'string') {
        try { recordData = JSON.parse(rec.data); } catch (e) { }
      }
      let rc = Number(rec?.reminder_count !== undefined && rec?.reminder_count !== null ? rec.reminder_count : (rec?.remind_count !== undefined && rec?.remind_count !== null ? rec.remind_count : (recordData?.reminder_count || 0))) || 0;
      rc = Math.min(3, rc);
      let baseDate = rec?.reminder_cycle_start || rec?.last_email_sent_at || rec?.createdAt || rec?.created_at || (recordData?.reminder_cycle_start || recordData?.last_email_sent_at) || null;
      let lastReminder = rec?.last_reminder_at || (recordData?.last_reminder_at) || null;

      const tryFmt = (d) => {
        if (!d) return '—';
        try {
          const dt = parseAsUTC(d) || new Date(d);
          return (dt && !Number.isNaN(dt.getTime())) ? formatDateTime(dt) : '—';
        } catch (e) {
          return '—';
        }
      };

      if (baseDate) {
        const base = parseAsUTC(baseDate) || new Date(baseDate);
        if (base && !Number.isNaN(base.getTime())) {
          // Reminders in system run on 3-day intervals (day 3, day 6, day 9)
          const slot1 = new Date(base.getTime() + 3 * 24 * 60 * 60 * 1000);
          const slot2 = new Date(base.getTime() + 6 * 24 * 60 * 60 * 1000);
          const slot3 = new Date(base.getTime() + 9 * 24 * 60 * 60 * 1000);

          return {
            count: rc,
            first: rc >= 1 ? (rc === 1 && lastReminder ? tryFmt(lastReminder) : tryFmt(slot1)) : '—',
            second: rc >= 2 ? (rc === 2 && lastReminder ? tryFmt(lastReminder) : tryFmt(slot2)) : '—',
            third: rc >= 3 ? (rc === 3 && lastReminder ? tryFmt(lastReminder) : tryFmt(slot3)) : '—'
          };
        }
      }

      // fallback: use explicit timestamps if available
      if (rc === 0) return { count: 0, first: '—', second: '—', third: '—' };
      if (rc === 1) return { count: 1, first: tryFmt(lastReminder || baseDate), second: '—', third: '—' };
      if (rc === 2) return { count: 2, first: tryFmt(baseDate), second: tryFmt(lastReminder), third: '—' };
      return { count: rc, first: tryFmt(baseDate), second: '—', third: tryFmt(lastReminder) };
    } catch (e) {
      return { count: 0, first: '—', second: '—', third: '—' };
    }
  };

  const getResponseDateFromRecord = (rec) => {
    if (!rec) return '—';
    const candidates = [
      rec.response_at,
      rec.email_response_at,
      rec.response_time,
      rec.user_responded_at,
      rec.user_action_at
    ];
    for (const v of candidates) {
      if (!v && v !== 0) continue;
      try {
        const d = parseAsUTC(v) || new Date(v);
        if (d && !Number.isNaN(d.getTime())) return formatDateTimeCell(d);
      } catch (e) { }
      // fallback to any string
      if (typeof v === 'string' && v.trim()) return v;
    }
    return '—';
  };

  // Detect whether a record represents a declined/rejected response
  const isDeclinedRecord = (rec) => {
    if (!rec) return false;
    const fields = [rec.status, rec.email_response, rec.email_response_status, rec.user_response_status, rec.userAction, rec.user_action];
    for (const f of fields) {
      if (!f) continue;
      const s = String(f).toLowerCase();
      if (s.includes('declin') || s.includes('reject')) return true;
    }
    return false;
  };

  // Detect whether a record was auto-rejected due to reminder cycle (3+ reminders)
  const isAutoRejectedRecord = (rec) => {
    try {
      if (!rec) return false;
      if (rec.is_auto_declined === true) return true;
      const s = String(rec.status || '').toLowerCase();
      // If accepted, active, approved, revoked or cancelled, not auto-declined
      if (s.includes('accept') || s.includes('approved') || s.includes('active') || s.includes('revok') || s.includes('cancel')) {
        return false;
      }
      // Explicit auto-declined indicators
      if (s.includes('auto-declined') || s.includes('auto_declined') || s.includes('auto-decline') || s.includes('auto_reject')) return true;
      if (rec.data && typeof rec.data === 'string' && (rec.data.includes('auto_declined') || rec.data.includes('auto_declined_reason'))) return true;

      // 3 or more reminders sent without acceptance = auto-declined
      const rc = Number(rec.reminder_count !== undefined && rec.reminder_count !== null ? rec.reminder_count : (rec.remind_count || 0));
      if (!Number.isNaN(rc) && rc >= 3) return true;
      if (s.includes('reject') && rc >= 1) return true;
      return false;
    } catch (e) {
      return false;
    }
  };

  const handleAutoDeclinedClick = (rec) => {
    if (!rec) return;
    const rc = Number(rec.reminder_count !== undefined && rec.reminder_count !== null ? rec.reminder_count : (rec.remind_count || 0));
    const effectiveRc = Math.min(Math.max(rc, 1), 3);
    const slots = getReminderSlots(rec || {});
    const consentType = getConsentTypeLabel(rec) || rec.type || 'Consent Notice';
    const recipient = rec.username || rec.Name || rec.email || 'Data Subject';

    let customReason = rec.auto_declined_reason || '';
    if (!customReason && rec.data && typeof rec.data === 'string') {
      const m = rec.data.match(/auto_declined_reason\s*:\s*([^\n\r]+)/i);
      if (m && m[1]) customReason = m[1].trim();
    }
    const reasonText = customReason || `Consent request was automatically declined by the system policy. A total of 3 reminder notifications were sent to the data subject, but no response was received within the required timeframe.`;

    Swal.fire({
      title: '<div style="font-size:18px;font-weight:700;color:#991b1b;display:flex;align-items:center;justify-content:center;gap:8px;"><span>⚠️</span><span>Consent Auto-Declined</span></div>',
      html: `
        <div style="text-align:left;font-size:13.5px;color:#334155;line-height:1.6;">
          <div style="background:#fef2f2;border:1px solid #fecaca;padding:12px 14px;border-radius:8px;color:#991b1b;margin-bottom:14px;">
            <div style="font-weight:700;font-size:13px;margin-bottom:4px;">Decline Reason:</div>
            <div style="font-size:13px;color:#7f1d1d;">${reasonText}</div>
          </div>

          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 14px;display:flex;flex-direction:column;gap:7px;font-size:13px;">
            <div><strong>Consent Type:</strong> <span style="color:#2563eb;font-weight:600;">${consentType}</span></div>
            <div><strong>Recipient:</strong> <span>${recipient}</span> ${rec.email ? `<span style="color:#64748b;">(${maskEmail(rec.email)})</span>` : ''}</div>
            <div><strong>Reminder Cycle:</strong> <span style="font-weight:600;color:#b45309;">${effectiveRc} reminders sent (Maximum 3 reached)</span></div>
            <div style="background:#fff;border:1px solid #e2e8f0;border-radius:6px;padding:8px 10px;margin-top:2px;">
              <div style="font-size:11.5px;font-weight:600;color:#64748b;margin-bottom:3px;">Reminder Schedule History:</div>
              <div style="font-size:12px;color:#334155;display:flex;justify-content:space-between;flex-wrap:wrap;gap:6px;">
                <span><strong>1st:</strong> ${slots.first || 'Sent'}</span>
                <span><strong>2nd:</strong> ${slots.second || 'Sent'}</span>
                <span><strong>3rd:</strong> ${slots.third || 'Sent'}</span>
              </div>
            </div>
          </div>

          <div style="margin-top:12px;font-size:12px;color:#64748b;text-align:center;">
            To re-engage this user, you may initiate a fresh consent request.
          </div>
        </div>
      `,
      icon: 'warning',
      confirmButtonText: 'OK',
      confirmButtonColor: '#991b1b',
      customClass: { popup: 'swal-popup-custom' }
    });
  };

  const isAcceptedRecord = (rec) => {
    if (!rec) return false;
    const fields = [rec.status, rec.email_response, rec.email_response_status, rec.user_response_status, rec.userAction, rec.user_action];
    for (const f of fields) {
      if (!f) continue;
      const s = String(f).toLowerCase();
      if (/inactive|unaccept|not accept|not active|not approv|not grant|not agree/.test(s)) continue;
      if (s.includes('accept') || s.includes('approved') || s.includes('active')) return true;
    }
    return false;
  };

  const isPendingRecord = (rec) => {
    if (!rec) return false;
    return !isAcceptedRecord(rec) && !isDeclinedRecord(rec) && !isAutoRejectedRecord(rec);
  };

  const shouldAllowResend = (rec) => {
    return rec && !isAutoRejectedRecord(rec) && !isDeclinedRecord(rec) && !isAcceptedRecord(rec);
  };

  const renderEmailResponseIcon = (statusText) => {
    const s = (statusText || '').toString().toLowerCase();
    let color = '#f59e0b';
    let title = 'Email: Pending';
    if (s.includes('accept') || s.includes('accepted')) { color = '#16a34a'; title = 'Email: Accepted'; }
    else if (s.includes('declin') || s.includes('declined')) { color = '#dc2626'; title = 'Email: Declined'; }
    else if (s.includes('pending') || !s) { color = '#f59e0b'; title = 'Email: Pending'; }
    return (
      <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="4" width="20" height="14" rx="2" fill={color} />
          <path d="M22 6l-10 7L2 6" fill="#fff" opacity="0.9" />
        </svg>
      </span>
    );
  };

  const renderIdProofIcons = (rec) => {
    if (!rec) return null;
    const raw = rec.id_proof_type || rec.idProofType || rec.id_proof || rec.idProof || rec.idProofFileName || '';
    let items = [];
    if (Array.isArray(raw)) items = raw;
    else if (typeof raw === 'string' && raw.trim()) items = raw.split(/,\s*/).filter(Boolean);
    // if (items.length === 0) return null;
    const sortedItems = sortIdProofValues(items);
    if (sortedItems.length === 0) return null;

    // If only one id_proof_type was provided, show its name; if multiple, show a generic message
    // if (items.length === 1){
    //   const it = items[0];


    if (sortedItems.length === 1) {
      const it = sortedItems[0];
      return (
        <div title={it} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: '#eef2ff', borderRadius: 8, color: '#1e3a8a', fontSize: 12 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2L2 7v6c0 5 5 9 10 9s10-4 10-9V7l-10-5z" fill="#2563eb" /></svg>
          <div style={{ whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{it}</div>
        </div>
      );
    }

    return (
      // <div title= {`${items.length} ID proofs`} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'4px 8px',background:'#f3f4f6',borderRadius:8,fontSize:12,color:'#374151'}}>
      <div title={`${sortedItems.length} ID proofs`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: '#f3f4f6', borderRadius: 8, fontSize: 12, color: '#374151' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2L2 7v6c0 5 5 9 10 9s10-4 10-9V7l-10-5z" fill="#7c3aed" /></svg>
        <div>Multiple ID proofs</div>
      </div>
    );
  };

  const renderIdProofCountIcon = (rec) => {
    if (!rec) return null;
    const raw = rec.id_proof_type || rec.idProofType || rec.id_proof || rec.idProof || rec.idProofFileName || '';
    let items = [];
    if (Array.isArray(raw)) items = raw;
    else if (typeof raw === 'string' && raw.trim()) items = raw.split(/,\s*/).filter(Boolean);
    const count = items.length;
    if (count === 0) return null;
    if (count === 1) {
      return (
        <span title="Single ID proof" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#06b6d4" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7v6c0 5 5 9 10 9s10-4 10-9V7l-10-5z" />
            <rect x="8" y="9" width="8" height="6" rx="1" fill="#fff" />
          </svg>
        </span>
      );
    }
    return (
      <span title={`${count} ID proofs`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#7c3aed" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 7h12v10H4z" opacity="0.95" />
          <path d="M8 3h12v10H8z" opacity="0.95" />
        </svg>
        {/* <div style={{background:'#f3f4f6',borderRadius:8,padding:'2px 6px',fontSize:12,color:'#111'}}>{count}</div> */}
      </span>
    );
  };

  const computeExpiryDays = (rec) => {
    if (!rec) return '';
    // If user declined/rejected, do not show a misleading '0' — treat as unknown
    try { if (isDeclinedRecord(rec)) return ''; } catch (e) { }
    const now = new Date();
    const msPerDay = 1000 * 60 * 60 * 24;

    // If expiry is provided (could be number of days, e.g. 45 or "45 days" or ISO date string)
    if (rec.expiry !== undefined && rec.expiry !== null && rec.expiry !== '') {
      let expiryNum = NaN;
      if (typeof rec.expiry === 'number') {
        expiryNum = rec.expiry;
      } else if (typeof rec.expiry === 'string') {
        const cleaned = rec.expiry.trim();
        // Check if ISO date string (YYYY-MM-DD...)
        if (/^\d{4}-\d{2}-\d{2}/.test(cleaned)) {
          try {
            const expDate = parseAsUTC(cleaned) || new Date(cleaned);
            if (expDate && !Number.isNaN(expDate.getTime())) {
              return Math.floor((expDate - now) / msPerDay);
            }
          } catch (e) { }
        }
        const match = cleaned.match(/\d+/);
        if (match) {
          expiryNum = parseInt(match[0], 10);
        }
      }

      if (!Number.isNaN(expiryNum) && expiryNum > 0) {
        const createdRaw = rec.createdAt || rec.created_at || null;
        if (createdRaw) {
          try {
            const created = parseAsUTC(createdRaw) || new Date(createdRaw);
            if (created && !Number.isNaN(created.getTime())) {
              const diff = Math.floor((now - created) / msPerDay);
              const remaining = expiryNum - diff;
              return remaining;
            }
          } catch (e) {
            return expiryNum;
          }
        }
        return expiryNum;
      }
    }

    // If createdAt is available but no explicit expiry, default to 45 days total
    const createdRaw = rec.createdAt || rec.created_at || null;
    if (createdRaw) {
      try {
        const created = parseAsUTC(createdRaw) || new Date(createdRaw);
        if (created && !Number.isNaN(created.getTime())) {
          const diff = Math.floor((now - created) / msPerDay);
          const remaining = 45 - diff;
          return remaining;
        }
      } catch (e) {
        return '';
      }
    }

    return '';
  };

  function formatExpiryValue(val) {
    if (val === '' || val === null || val === undefined) return '—';
    const n = Number(val);
    if (Number.isNaN(n)) return '—';
    if (n > 1) return `${n} days`;
    if (n === 1) return `1 day`;
    if (n === 0) return `Today`;
    const ago = Math.abs(n);
    if (ago === 1) return `1 day ago`;
    return `${ago} days ago`;
  }

  function renderExpiryCell(rec) {
    if (!rec) return '—';
    try {
      const st = String(rec.status || rec.consent_status || rec.state || '').toLowerCase();
      if (st.includes('suspend') || st.includes('reconsent') || st.includes('supersed')) {
        return (
          <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 6, background: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '12.5px', border: '1px solid #fde68a' }}>
            Version Update
          </span>
        );
      }
    } catch (e) { }
    // If this record was auto-rejected after reminder cycle, show explicit message
    try { if (isAutoRejectedRecord(rec)) return 'No response'; } catch (e) { }
    const raw = computeExpiryDays(rec);
    // keep raw display when unknown
    if (raw === '' || raw === null || raw === undefined) return '—';
    const n = Number(raw);
    if (Number.isNaN(n)) return '—';

    let style = { display: 'inline-block', padding: '6px 10px', borderRadius: 8, minWidth: 80, textAlign: 'center', fontWeight: 700 };
    // expired -> darker red background for visibility
    if (n < 0) {
      style = { ...style, background: '#fecaca', color: '#7f1d1d', border: '1px solid rgba(127,29,29,0.08)' };
    } else if (n <= 7) {
      // expiring within 7 days -> darker amber
      style = { ...style, background: '#fed7aa', color: '#78350f', border: '1px solid rgba(120,53,15,0.06)' };
    } else {
      style = { ...style, background: 'transparent', color: '#0f172a' };
    }

    return (
      <div style={style}>
        {formatExpiryValue(n)}
      </div>
    );
  }

  const getAgeFromDOB = (dob) => {
    if (!dob) return null;
    try {
      const parts = String(dob).split('-');
      if (parts.length === 3) {
        const y = parseInt(parts[0], 10);
        // An incomplete year being typed in date input (e.g. 0002, 0020, 0201) is < 1900
        if (isNaN(y) || y < 1900) return null;
      }
      const b = new Date(dob);
      if (isNaN(b.getTime())) return null;
      const today = new Date();
      let age = today.getFullYear() - b.getFullYear();
      const m = today.getMonth() - b.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < b.getDate())) {
        age--;
      }
      return age;
    } catch (e) {
      return null;
    }
  };

  const handleAgeCategoryChange = (category) => {
    if (category === 'adult') {
      if (userForm.dob) {
        const age = getAgeFromDOB(userForm.dob);
        if (age !== null && age < 18) {
          Swal.fire({
            title: 'Age Alert',
            text: 'The entered date of birth indicates age under 18 (Minor). Adult cannot have a minor date of birth. Please clear the date of birth or enter an adult date of birth.',
            icon: 'warning',
            confirmButtonColor: '#2563eb'
          });
          setUserForm(prev => ({
            ...prev,
            ageCategory: 'adult',
            dob: '',
            parentName: '',
            parentEmail: '',
            parentPhone: '',
            _guardianType: 'parent'
          }));
          return;
        }
      }
      setUserForm(prev => ({
        ...prev,
        ageCategory: 'adult',
        parentName: '',
        parentEmail: '',
        parentPhone: '',
        _guardianType: 'parent'
      }));
    } else if (category === 'minor') {
      if (userForm.dob) {
        const age = getAgeFromDOB(userForm.dob);
        if (age !== null && age >= 18) {
          Swal.fire({
            title: 'Age Alert',
            text: 'The entered date of birth indicates age 18 or above (Adult). Minor cannot have an adult date of birth. Please enter a minor date of birth (under 18).',
            icon: 'warning',
            confirmButtonColor: '#2563eb'
          });
          setUserForm(prev => ({
            ...prev,
            ageCategory: 'minor',
            dob: '',
            _guardianType: prev._guardianType || 'parent'
          }));
          return;
        }
      }
      setUserForm(prev => ({
        ...prev,
        ageCategory: 'minor',
        _guardianType: prev._guardianType || 'parent'
      }));
    }
  };

  const handleDOBChange = (e) => {
    const { value } = e.target;
    if (!value) {
      setUserForm(prev => ({ ...prev, dob: '' }));
      return;
    }

    const currentCategory = userForm.ageCategory || 'adult';

    // Parse year to detect partial / in-progress typing in HTML5 date input (e.g. 0002, 0020, 0201)
    const parts = String(value).split('-');
    const year = parts.length === 3 ? parseInt(parts[0], 10) : NaN;
    const isYearComplete = !isNaN(year) && year >= 1900;

    // While year is incomplete (less than 4 digits / < 1900), allow user to freely type without popups or resets
    if (!isYearComplete) {
      setUserForm(prev => ({ ...prev, dob: value }));
      return;
    }

    const todayStr = new Date().toISOString().slice(0, 10);
    if (value > todayStr) {
      Swal.fire({ text: 'Date of birth cannot be in the future', icon: 'warning' });
      setUserForm(prev => ({ ...prev, dob: '' }));
      return;
    }

    const age = getAgeFromDOB(value);

    // If Adult is selected and user inputs a minor's DOB:
    if (currentCategory === 'adult' && age !== null && age < 18) {
      Swal.fire({
        title: 'Age Restriction',
        text: 'The entered date of birth indicates an age under 18 (Minor). Please select "Minor" option or enter an adult date of birth.',
        icon: 'warning',
        confirmButtonColor: '#2563eb'
      });
      setUserForm(prev => ({ ...prev, dob: '' }));
      return;
    }

    // If Minor is selected and user inputs an adult's DOB:
    if (currentCategory === 'minor' && age !== null && age >= 18) {
      Swal.fire({
        title: 'Age Restriction',
        text: 'The entered date of birth indicates an age of 18 or above (Adult). Please select "Adult" option or enter a minor date of birth (under 18).',
        icon: 'warning',
        confirmButtonColor: '#2563eb'
      });
      setUserForm(prev => ({ ...prev, dob: '' }));
      return;
    }

    setUserForm(prev => {
      const next = { ...prev, dob: value };
      if (currentCategory === 'minor' || (age !== null && age < 18)) {
        if (!next._guardianType) {
          next._guardianType = 'parent';
        }
      } else {
        next.parentName = '';
        next.parentEmail = '';
        next.parentPhone = '';
        next._guardianType = 'parent';
      }
      return next;
    });
  };

  const handleParentChange = (e) => {
    const { name, value } = e.target;
    setUserForm(prev => {
      const updated = { ...prev, [name]: value };
      // Keep email and phone synced with parent contact for minors
      if (name === 'parentEmail') {
        updated.email = value;
      }
      if (name === 'parentPhone') {
        updated.phone = value;
      }
      return updated;
    });
  };

  const closeParentModal = () => setParentModalOpen(false);

  const saveParentInfo = () => {
    setParentModalOpen(false);
  };

  // Action handlers
  // const handleWhatsApp = (consent) => {
  const handleWhatsApp = async (consent) => {
    const message = `Consent Details:\nName: ${consent.username || consent.Name || ''}\nEmail: ${consent.email}\nType: ${consent.type}\nConsent: ${consent.consent}`;
    // const encodedMessage = encodeURIComponent(message);
    // window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    const phone = (consent.phone || consent.mobile || consent.whatsapp || '').toString().trim();

    // If no phone number on this record, fall back to the old wa.me behaviour
    // so the user can still open WhatsApp Web and pick a contact manually.
    if (!phone) {
      const encodedMessage = encodeURIComponent(message);
      window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
      return;
    }

    // Otherwise send through the backend (Twilio WhatsApp Business API).
    // This avoids the "log in to WhatsApp Web" prompt entirely — the message
    // is delivered server-side and the recipient receives it on their phone.
    try {
      const res = await axios.post(`${API_BASE}/user/send-whatsapp`, {
        phone,
        body: message,
        consent_id: consent.id,
      });
      const data = res?.data || {};
      if (data.sent) {
        Swal.fire({ title: '✅ WhatsApp Sent', text: `Message delivered to: ${data.to}`, icon: 'success' });
      } else {
        // Show the diagnostic the backend returned so QA knows exactly what to fix
        // (e.g. "recipient must join the Twilio sandbox first").
        Swal.fire({ title: '⚠️ WhatsApp Not Sent', text: (data.detail || 'Unknown error'), icon: 'warning' });
      }
      // Refresh consent list so the UI shows the current DB status (not a stale cached value)
      try { fetchConsents(); } catch (e) { }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Request failed';
      Swal.fire({ title: '❌ WhatsApp Send Failed', text: detail, icon: 'error' });
    }
  };

  const showTooltip = (e, text) => {
    const x = e.clientX + 12;
    const y = e.clientY + 12;
    setTooltip({ visible: true, text: text || '', x, y });
  };

  const moveTooltip = (e) => {
    if (!tooltip.visible) return;
    setTooltip(t => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }));
  };

  const hideTooltip = () => setTooltip({ visible: false, text: '', x: 0, y: 0 });
  const handleEdit = async (consent) => {

    let source = consent;
    try {
      if (consent?.id) {
        const res = await axios.get(`${API_BASE}/user/consent/${consent.id}`);
        source = res.data?.consent || res.data || consent;
      }
    } catch (err) {
      console.warn('Failed to fetch consent details for editing, using provided record', err);
    }

    // id_proof_type parse karo — comma ya pipe separated string ko array mein
    const parseIdProofType = (v) => {
      if (!v) return [];
      if (Array.isArray(v)) return v.map(s => String(s).trim()).filter(Boolean);
      if (typeof v === 'string') {
        // pipe separator bhi handle karo
        const sep = v.includes('|') ? '|' : ',';
        return v.split(sep).map(s => s.trim()).filter(Boolean);
      }
      return [String(v).trim()].filter(Boolean);
    };

    const parseMultiValue = (v) => {
      if (!v) return [];
      if (Array.isArray(v)) return v.map(s => String(s).trim()).filter(Boolean);
      if (typeof v === 'string') {
        const trimmed = v.trim();
        if (!trimmed) return [];
        const sep = trimmed.includes('|') ? '|' : ',';
        return trimmed.split(sep).map(s => s.trim()).filter(Boolean);
      }
      return [String(v).trim()].filter(Boolean);
    };

    const parseDataTypeConsent = (v) => {
      if (!v) return [];
      if (typeof v === 'string') {
        const trimmed = v.trim();
        if (!trimmed) return [];
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            return parseMultiValue(parsed);
          } catch (e) {
            return parseMultiValue(trimmed);
          }
        }
        return parseMultiValue(trimmed);
      }
      return parseMultiValue(v);
    };

    // preferred languages parse karo
    const parseLangs = (l) => {
      if (!l) return [];
      if (Array.isArray(l)) return l.map(s => String(s).trim()).filter(Boolean);
      if (typeof l === 'string') return l.split(',').map(s => s.trim()).filter(Boolean);
      return [String(l).trim()].filter(Boolean);
    };

    const pickFirstNonEmpty = (...values) => {
      for (const value of values) {
        if (value === null || value === undefined) continue;
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed) return trimmed;
          continue;
        }
        if (Array.isArray(value)) {
          const first = value.map(v => String(v ?? '').trim()).find(Boolean);
          if (first) return first;
          continue;
        }
        const str = String(value).trim();
        if (str) return str;
      }
      return '';
    };

    const readLanguageValue = (value) => {
      if (!value) return '';
      if (Array.isArray(value)) {
        return String(value[0] || '').trim();
      }
      if (typeof value === 'string') {
        const tokens = value.split(/[|,]/).map(s => s.trim()).filter(Boolean);
        return tokens[0] || '';
      }
      return String(value).trim();
    };

    const parseRecordData = (value) => {
      if (value && typeof value === 'object') return value;
      if (typeof value !== 'string') return {};
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (e) {
        return {};
      }
    };
    const recordData = parseRecordData(source.data || consent.data);
    const legacyData = [recordData];
    ['user_data', 'consent_data', 'record', 'fields'].forEach((key) => {
      const nested = parseRecordData(recordData[key]);
      if (Object.keys(nested).length) legacyData.push(nested);
    });
    const pickRecordValue = (...keys) => pickFirstNonEmpty(
      ...[source, consent, ...legacyData].flatMap(record => keys.map(key => record?.[key]))
    );
    const idTypes = parseIdProofType(pickRecordValue(
      'id_proof_type', 'idProofType', 'id_proof_types', 'idProofTypes', 'id_proof'
    ));
    const resolvedAddress = pickRecordValue(
      'address', 'Address', 'user_address', 'postal_address', 'street_address',
      'address_line', 'current_address', 'local_address', 'address_details'
    );
    const resolvedLocation = pickRecordValue(
      'location', 'locality', 'user_location', 'city', 'city_name', 'area', 'address_location'
    );
    const resolvedDepartment = pickRecordValue(
      'department', 'departments', 'dept', 'department_name', 'work_department'
    );
    const resolvedEntity = pickFirstNonEmpty(
      source.entity,
      source.group,
      source.user_group,
      source.entity_name,
      source.role
    );
    const resolvedLanguage = readLanguageValue(
      source.consent_language || source.language_code || source.language || source.preferred_languages || source.preferredLanguages || source.languages || ''
    );
    const resolvedSubCategory = pickRecordValue(
      'sub_consent_id', 'subConsentId', 'parent_sub_consent_id', 'parentSubConsentId'
    );
    const resolvedSubCategoryLabel = pickRecordValue(
      'sub_consent_name', 'subConsentName', 'sub_category', 'subCategory'
    );

    setUserForm({
      username: source.username || source.Name || consent.username || consent.Name || '',
      ageCategory: (source.parent_name || source.parentName || consent.parent_name || consent.parentName || (getAgeFromDOB(source.dob || consent.dob) !== null && getAgeFromDOB(source.dob || consent.dob) < 18)) ? 'minor' : 'adult',
      email: source.email || consent.email || '',
      phone: source.phone || consent.phone || '',
      dob: source.dob || consent.dob || '',
      address: resolvedAddress || consent.address || consent.Address || consent.user_address || consent.postal_address || '',
      createdAt: source.createdAt || source.created_at || consent.createdAt || consent.created_at || new Date().toISOString(),
      type: source.type || source.consent_type || consent.type || consent.consent_type || '',
      entity: resolvedEntity || consent.entity || '',
      consent: decodeHtmlEntities(source.consent || source.consent_message || consent.consent || consent.data || ''),
      appName: source.appName || source.app_name || consent.appName || consent.app_name || '',
      userAction: source.userAction || source.user_action || consent.userAction || consent.user_action || '',
      data: source.data || consent.data || '',
      expiry: source.expiry || source.expiry_date || consent.expiry || consent.expiry_date || '',
      status: source.status || consent.status || '',
      parentName: source.parent_name || source.parentName || consent.parent_name || consent.parentName || '',
      parentEmail: source.parent_email || source.parentEmail || consent.parent_email || consent.parentEmail || '',
      parentPhone: source.parent_phone || source.parentPhone || consent.parent_phone || consent.parentPhone || '',
      idProofType: idTypes,
      idProofFileName: source.id_proof_file || source.idProofFileName || consent.id_proof_file || consent.idProofFileName || '',
      dataTypeConsent: parseDataTypeConsent(
        pickRecordValue('data_type_consent', 'dataTypeConsent', 'Data_Type_consent', 'data_type_consent_value', 'data_types', 'dataTypes')
      ),
      location: resolvedLocation || consent.location || consent.locality || '',
      department: resolvedDepartment || consent.department || '',
      subCategory: resolvedSubCategory || resolvedSubCategoryLabel || '',
      subCategoryLabel: resolvedSubCategoryLabel || '',
      language: resolvedLanguage || readLanguageValue(consent.consent_language || consent.language || consent.preferred_languages || ''),
      preferredLanguages: parseLangs(
        source.preferred_languages || source.languages || source.language || consent.preferred_languages || consent.languages || consent.language || ''
      ),
    });


    if (source.id_proof_file || consent.id_proof_file) {
      setIdProofFile(null);
    }

    // parent contact check
    const hasParent = !!(
      source.parent_name || source.parentName || consent.parent_name || consent.parentName ||
      source.parent_email || source.parentEmail || consent.parent_email || consent.parentEmail ||
      source.parent_phone || source.parentPhone || consent.parent_phone || consent.parentPhone
    );
    setUseParentContact(hasParent);

    setEditingId(consent.id);
    setHasSubmitted(false);
    setSlidingPageOpen(true);
  };

  // const handleDelete = (consent) => {
  //   (async () => {
  //     try {
  //       setMailLoading(prev => ({ ...prev, [consent.id]: true }));
  //       await new Promise(r => setTimeout(r, 2000));

  const handleMail = async (consent) => {
    // Check if consent has already reached 3 reminders or is auto-declined
    const currentRc = Number(consent.reminder_count !== undefined && consent.reminder_count !== null ? consent.reminder_count : (consent.remind_count || 0));
    if (currentRc >= 3 || isAutoRejectedRecord(consent)) {
      Swal.fire({
        title: '<div style="font-size:18px;font-weight:700;color:#991b1b;display:flex;align-items:center;justify-content:center;gap:8px;"><span>⚠️</span><span>Maximum Reminders Reached</span></div>',
        html: `
          <div style="font-size:13.5px;color:#334155;line-height:1.6;text-align:left;">
            <p>A maximum of <strong>3 reminder notifications</strong> have already been sent to this recipient.</p>
            <div style="background:#fef2f2;border:1px solid #fecaca;padding:10px 12px;border-radius:6px;color:#991b1b;margin-top:8px;">
              This consent has been automatically declined by system policy. No further reminder emails can be sent.
            </div>
          </div>
        `,
        icon: 'warning',
        confirmButtonColor: '#991b1b',
        confirmButtonText: 'OK'
      });
      return;
    }

    setMailLoading(prev => ({ ...prev, [consent.id]: true }));
    try {
      // friendly UI delay so user sees the loading state
      await new Promise(r => setTimeout(r, 2000));

      // Determine message/template to send: localStorage -> manager templates -> consent.consent
      let template = '';
      try {
        const saved = localStorage.getItem('selected_consent_for_notification');
        if (saved) {
          const obj = JSON.parse(saved);
          if (obj && obj.consent_type && (obj.consent_type === (consent.type || consent.consent_type || ''))) {
            template = obj.consent_message || '';
          }
        }
      } catch (e) { /* ignore */ }

      if (!template) {
        try {
          const tRes = await axios.get(`${API_BASE}/manager/new-consents`);
          const data = tRes.data?.data || [];
          const match = data.find(r => {
            const ct = r.consent_type || r.type || r.consent_type_name || '';
            return ct && ((ct === (consent.type || consent.consent_type)) || (ct.toString().toLowerCase() === (consent.type || consent.consent_type || '').toString().toLowerCase()));
          });
          if (match) template = match.consent_message || match.message || match.consent || '';
        } catch (e) {
          // ignore
        }
      }

      const fallback = consent.consent || consent.message || '';
      let messageToSend = template || fallback || '';

      // determine target language from consent's preferred languages
      let target = '';
      const prefLangs = (consent.consent_language || consent.preferred_languages || consent.preferredLanguages || consent.languages || consent.language || '');
      if (Array.isArray(prefLangs) && prefLangs.length) target = prefLangs[0];
      else if (typeof prefLangs === 'string' && prefLangs.trim()) target = prefLangs.split(',')[0].trim();

      // perform client-side translation if a non-English target is present
      if (target) {
        const code = languageCodeFor(target);
        if (code && code !== 'en') {
          try {
            const tr = await axios.post('https://libretranslate.de/translate', {
              q: messageToSend,
              source: 'en',
              target: code,
              format: 'text'
            }, { headers: { 'Content-Type': 'application/json' }, timeout: 15000 });
            const translated = tr.data?.translatedText || tr.data?.translated_text || tr.data?.result || null;
            if (translated) messageToSend = translated;
          } catch (te) {
            console.warn('Translation failed, sending original message', te);
          }
        }
      }

      const fd = new FormData();
      fd.append('format', 'text');
      fd.append('include_response_links', 'true');
      if (target) fd.append('target_language', target);

      const res = await axios.post(`${API_BASE}/user/consent/${consent.id}/resend`, fd);
      if (res.data && (res.data.success || res.status === 200)) {
        const nowIso = new Date().toISOString();
        const serverCount = res.data?.reminder_count !== undefined ? Number(res.data.reminder_count) : (currentRc + 1);
        const newCount = Math.min(3, Math.max(serverCount, 1));
        const willBeAutoDeclined = newCount >= 3 || res.data?.is_auto_declined === true;
        const newStatus = willBeAutoDeclined ? (res.data?.status || 'Auto-declined') : consent.status;

        if (willBeAutoDeclined) {
          Swal.fire({
            title: '<div style="font-size:18px;font-weight:700;color:#991b1b;display:flex;align-items:center;justify-content:center;gap:8px;"><span>⚠️</span><span>Reminder Sent & Auto-Declined</span></div>',
            html: `
              <div style="font-size:13.5px;color:#334155;line-height:1.6;text-align:left;">
                <p>Reminder email <strong>#${newCount}</strong> has been sent successfully.</p>
                <div style="background:#fef2f2;border:1px solid #fecaca;padding:10px 12px;border-radius:6px;color:#991b1b;margin-top:8px;">
                  <strong>Policy Applied:</strong> The maximum limit of 3 reminders has been reached without recipient response. This consent has now been automatically marked as <strong>Auto-declined</strong>.
                </div>
              </div>
            `,
            icon: 'warning',
            confirmButtonColor: '#991b1b',
            confirmButtonText: 'OK'
          });
        } else {
          Swal.fire({ text: `Notification reminder #${newCount} sent successfully`, icon: 'success' });
        }

        // update local consents state immediately with the count from server
        setConsents(prev => (prev || []).map(p => p.id === consent.id ? ({
          ...p,
          reminder_count: newCount,
          status: willBeAutoDeclined ? newStatus : p.status,
          is_auto_declined: willBeAutoDeclined ? true : p.is_auto_declined,
          reminder_cycle_start: p.reminder_cycle_start || nowIso,
          last_email_sent_at: nowIso,
          last_reminder_at: nowIso
        }) : p));
        // also update selectedConsent if open
        if (selectedConsent && selectedConsent.id === consent.id) {
          setSelectedConsent(prev => ({
            ...prev,
            reminder_count: newCount,
            status: willBeAutoDeclined ? newStatus : prev.status,
            is_auto_declined: willBeAutoDeclined ? true : prev.is_auto_declined,
            reminder_cycle_start: prev.reminder_cycle_start || nowIso,
            last_email_sent_at: nowIso,
            last_reminder_at: nowIso
          }));
        }
        if (detailsConsent && detailsConsent.id === consent.id) {
          setDetailsConsent(prev => ({
            ...prev,
            reminder_count: newCount,
            status: willBeAutoDeclined ? newStatus : prev.status,
            is_auto_declined: willBeAutoDeclined ? true : prev.is_auto_declined,
            reminder_cycle_start: prev?.reminder_cycle_start || nowIso,
            last_email_sent_at: nowIso,
            last_reminder_at: nowIso
          }));
        }
        // Invalidate session cache so page reload always reads updated database state instead of stale count: 0
        try {
          clearAllSessionCache();
        } catch (e) { }
        try { await sendAuditLog('email_user', consent.email || consent.responder_email || null, `Sent notification for consent ${consent.id}`); } catch (e) { }
      } else {
        Swal.fire({ text: 'Failed to send notification email', icon: 'error' });
      }
    } catch (err) {
      console.error('Resend email error', err);
      // const msg = err?.response?.data?.detail || err?.message || 'Error sending notification';
      const msg = extractErrorMsg(err) || 'Error sending notification';
      Swal.fire({ text: 'Error sending notification: ' + msg, icon: 'error' });
    } finally {
      setMailLoading(prev => ({ ...prev, [consent.id]: false }));
    }
  };

  const handleRevoke = (consent) => {
    // Safety: require a valid consent object with an id before opening the revoke drawer.
    if (!consent || !consent.id) {
      console.warn('[UserPanel] handleRevoke called without a valid consent:', consent);
      try { console.trace && console.trace(); } catch (e) { }
      return;
    }

    // Prevent duplicate revoke requests for the same consent id
    try {
      if (revokeRequestedIds && revokeRequestedIds.has(String(consent.id))) {
        Swal.fire({
          text: `A revoke request has already been raised for this consent (ID: ${consent.id}). You cannot request it again.`,
          icon: 'info'
        });
        return;
      }
    } catch (e) { /* ignore */ }

    setSelectedConsent(consent);
    // setRevokeForm({ reason: '', comments: '', attachments: [] });
    // setRevokeOptions({ email: false, whatsapp: false, dob: false, all: false });  
    setRevokeForm({ reason: '', comments: '', attachments: [] });
    setRevokeOptions({ email: false, whatsapp: false, dob: false, all: false });
    // helpful trace for debugging unexpected opens during QA
    try { console.debug('[UserPanel] Opening revoke drawer for consent id=', consent.id); } catch (e) { }
    setRevokeModalOpen(true);
  };

  // toggle revoke option helpers (email, whatsapp, dob, all)
  const toggleRevokeOption = (opt) => {
    setRevokeOptions(prev => {
      // if user clicked 'all', toggle all on/off
      if (opt === 'all') {
        const nextAll = !prev.all;
        return { email: nextAll, whatsapp: nextAll, dob: nextAll, all: nextAll };
      }
      // do not allow selecting an option that is already revoked on the selectedConsent
      if (!selectedConsent) return prev;
      if (opt === 'email' && !selectedConsent.email) {
        // alert('You have already revoked this permission.');
        Swal.fire({ text: 'You have already revoked this permission.', icon: 'info' });
        return prev;
      }
      if (opt === 'whatsapp' && !selectedConsent.phone) {
        Swal.fire({ text: 'You have already revoked this permission.', icon: 'info' });
        return prev;
      }
      if (opt === 'dob' && !selectedConsent.dob) {
        // alert('You have already revoked this permission.');
        Swal.fire({ text: 'You have already revoked this permission.', icon: 'info' });
        return prev;
      }
      // when selecting a single option, ensure only that option is active (unless turning it off)
      const willEnable = !prev[opt];
      if (willEnable) {
        return { email: false, whatsapp: false, dob: false, all: false, [opt]: true };
      }
      // if disabling, just turn it off
      return { ...prev, [opt]: false, all: false };
    });
  };

  const openComments = (consent) => {
    // setCommentsRecord(consent);
    // setCommentsOpen(true);
    (async () => {
      try {
        // try fetching fresh details from server by id
        if (consent?.id) {
          const res = await axios.get(`${API_BASE}/user/consent/${consent.id}`);
          const record = res.data?.consent || res.data || consent;
          setCommentsRecord(record);
        } else {
          setCommentsRecord(consent);
        }
      } catch (err) {
        console.warn('Failed to fetch consent details, using provided record', err);
        setCommentsRecord(consent);
      } finally {
        setCommentsOpen(true);
      }
    })();
  };

  const normalizeRecord = (r) => {
    if (!r) return r;
    const typeVal = r.type || r.consent_type || r.template || r.consent_type_name || r.type_name || r.template_name || '';
    let expiryVal = (r.expiry !== undefined && r.expiry !== null) ? r.expiry : (r.expiry_days !== undefined && r.expiry_days !== null) ? r.expiry_days : (r.expiryDays !== undefined && r.expiryDays !== null) ? r.expiryDays : (r.expiry_date || r.expires_at || null);
    // format numeric expiry as days
    if (typeof expiryVal === 'number' || (/^\d+$/.test(String(expiryVal)))) {
      expiryVal = String(expiryVal) + ' days';
    }
    // if ISO datetime, shorten to date
    if (typeof expiryVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(expiryVal)) expiryVal = expiryVal.slice(0, 10);
    const createdAt = r.createdAt || r.created_at || r.created || null;
    const parentName = r.parent_name || r.parentName || null;
    const parentEmail = r.parent_email || r.parentEmail || null;
    const parentPhone = r.parent_phone || r.parentPhone || null;
    const isMinor = (() => {
      try {
        if (r.parent_name || r.parentName || r.parent_email || r.parentEmail || r.parent_phone || r.parentPhone) return true;
        const dob = r.dob || r.date_of_birth || null;
        if (!dob) return false;
        const b = new Date(dob);
        if (Number.isNaN(b.getTime())) return false;
        const today = new Date();
        let age = today.getFullYear() - b.getFullYear();
        const m = today.getMonth() - b.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
        return age < 18;
      } catch (e) { return false; }
    })();
    let recordData = r.data && typeof r.data === 'object' ? r.data : null;
    if (!recordData && typeof r.data === 'string') {
      try { recordData = JSON.parse(r.data); } catch (e) { }
    }
    const reminderCount = Number(r.reminder_count !== undefined && r.reminder_count !== null ? r.reminder_count : (r.remind_count !== undefined && r.remind_count !== null ? r.remind_count : (recordData?.reminder_count || 0))) || 0;
    const lastReminderAt = r.last_reminder_at || recordData?.last_reminder_at || null;
    const reminderCycleStart = r.reminder_cycle_start || recordData?.reminder_cycle_start || null;
    const lastEmailSentAt = r.last_email_sent_at || recordData?.last_email_sent_at || null;

    const rawId = r.id || r.consent_id || r.consentId;
    const encryptedId = r.encrypted_id || r.public_id || (rawId ? (String(rawId).startsWith('CNS-') ? String(rawId) : `CNS-${String(rawId).padStart(8, '0')}`) : null);

    return {
      ...r,
      encrypted_id: encryptedId,
      public_id: r.public_id || encryptedId,
      type: typeVal,
      expiry: expiryVal,
      createdAt,
      reminder_count: reminderCount,
      last_reminder_at: lastReminderAt,
      reminder_cycle_start: reminderCycleStart,
      last_email_sent_at: lastEmailSentAt,
      parentName,
      parentEmail,
      parentPhone,
      isMinor,
      preferredLanguages: r.preferred_languages || r.preferredLanguages || r.languages || r.language || '',
      consent_language: r.consent_language || r.language_code || r.language || ''
    };
  };

  const openDetails = async (consent) => {
    try {
      if (consent?.id) {
        const res = await axios.get(`${API_BASE}/user/consent/${consent.id}`);
        const recordRaw = res.data?.consent || res.data || consent;
        const record = normalizeRecord(recordRaw);
        setDetailsConsent(record);

      } else {
        // setDetailsConsent(consent);
        setDetailsConsent(normalizeRecord(consent));
        // try { localStorage.removeItem('selected_consent_for_notification'); } catch (e) {}
      }
    } catch (err) {
      console.warn('Failed to fetch consent details, using provided record', err);
      setDetailsConsent(consent);
    } finally {
      // ensure sliding drawer (edit form) is closed when opening details modal
      setSlidingPageOpen(false);
      setEditingId(null);
      setIsDetailsOpen(true);

      // start polling for email response status until it becomes accepted/declined
      try {
        if (consent?.id) {
          if (detailsPollRef.current) clearInterval(detailsPollRef.current);
          const poll = setInterval(async () => {
            try {
              const r = await axios.get(`${API_BASE}/user/consent/${consent.id}`, { skipLoading: true });
              const fresh = r.data?.consent || r.data || consent;
              setDetailsConsent(fresh);
              const st = getEmailResponseStatusFromRecord(fresh) || '';
              if (st && !st.toString().toLowerCase().includes('pending')) {
                clearInterval(poll);
                detailsPollRef.current = null;
              }
            } catch (e) {
              // ignore polling errors
            }
          }, 5000);
          detailsPollRef.current = poll;
        }
      } catch (e) {

      }
    }
  };

  const closeComments = () => {
    setCommentsOpen(false);
    setCommentsRecord(null);
  };


  const closeDetails = () => {
    setIsDetailsOpen(false);
    setDetailsConsent(null);
    if (detailsPollRef.current) {
      clearInterval(detailsPollRef.current);
      detailsPollRef.current = null;
    }
  };

  // cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (detailsPollRef.current) {
        clearInterval(detailsPollRef.current);
        detailsPollRef.current = null;
      }
    };
  }, []);

  const closeRevokeModal = () => {
    setRevokeModalOpen(false);
    setSelectedConsent(null);
    setRevokeForm({
      reason: '',
      comments: '',
      attachments: [],
    });
  };

  const handleRevokeFormChange = (e) => {
    const { name, value } = e.target;
    setRevokeForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setRevokeForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const handlePrivacyAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowed = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
    const accepted = [];
    const rejected = [];
    files.forEach(f => {
      const ext = (f.name || '').split('.').pop().toLowerCase();
      if (allowed.includes(ext)) accepted.push(f); else rejected.push(f.name || f);
    });
    // if (rejected.length) alert('These files were not accepted: ' + rejected.join(', ') + '\nAllowed: PDF, DOC, DOCX, JPG, PNG');
    if (rejected.length) Swal.fire({ text: 'These files were not accepted: ' + rejected.join(', ') + '\nAllowed: PDF, DOC, DOCX, JPG, PNG', icon: 'warning' });
    if (accepted.length) {
      setPrivacyAttachments(prev => ([...prev, ...accepted]));
    }
    // clear input so same file can be re-selected if removed
    if (privacyAttachmentRef && privacyAttachmentRef.current) privacyAttachmentRef.current.value = null;
  };

  const removePrivacyAttachment = (index) => {
    setPrivacyAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const removeAttachment = (index) => {
    setRevokeForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleRevokeSubmit = async (e) => {
    e.preventDefault();

    if (!revokeForm.reason) {
      // alert('Please provide a reason for revocation');
      Swal.fire({ text: 'Please provide a reason for revocation', icon: 'warning' });
      return;
    }

    // determine selected revoke fields, only include fields that are present
    const selectedFields = [];
    if (revokeOptions.all) {
      // if 'all' chosen, ensure at least one present field will be revoked
      const present = [];
      if (selectedConsent?.email) present.push('email');
      // if (selectedConsent?.phone) present.push('whatsapp');
      if (selectedConsent?.phone) present.push('phone');
      if (selectedConsent?.dob) present.push('dob');
      if (present.length === 0) {
        // alert('No data available to revoke for this record');
        Swal.fire({ text: 'No data available to revoke for this record', icon: 'info' });
        return;
      }
      selectedFields.push('all');
    } else {
      if (revokeOptions.email && selectedConsent?.email) selectedFields.push('email');
      // if (revokeOptions.whatsapp && selectedConsent?.phone) selectedFields.push('whatsapp');
      if (revokeOptions.whatsapp && selectedConsent?.phone) selectedFields.push('phone');
      if (revokeOptions.dob && selectedConsent?.dob) selectedFields.push('dob');
    }
    if (selectedFields.length === 0) {
      // alert('Please select at least one available field to revoke (Email / WhatsApp / D.O.B / All)');
      Swal.fire({ text: 'Please select at least one available field to revoke (Email / WhatsApp / D.O.B / All)', icon: 'warning' });
      return;
    }

    try {
      const tenantIdValue = user?.tenant_id || selectedConsent?.tenant_id || parseInt(localStorage.getItem('tenant_id') || '0', 10) || null;
      const payload = {
        tenant_id: tenantIdValue ?? '',
        full_name: selectedConsent?.username || selectedConsent?.Name || selectedConsent?.full_name || '',
        fullName: selectedConsent?.username || selectedConsent?.Name || selectedConsent?.full_name || '',
        email: selectedConsent?.email || selectedConsent?.responder_email || '',
        contact_phone: selectedConsent?.phone || '',
        contactPhone: selectedConsent?.phone || '',
        request_type: 'erasure',
        requestType: 'erasure',
        request_details: `Consent revoke request from User Panel.\nConsent ID: ${selectedConsent?.id}\nConsent Type: ${selectedConsent?.type || ''}\nRevoked fields: ${selectedFields.join(', ')}\nReason: ${revokeForm.reason}\nComments: ${revokeForm.comments || ''}`,
        requestDetails: `Consent revoke request from User Panel.\nConsent ID: ${selectedConsent?.id}\nConsent Type: ${selectedConsent?.type || ''}\nRevoked fields: ${selectedFields.join(', ')}\nReason: ${revokeForm.reason}\nComments: ${revokeForm.comments || ''}`,
        request_reason: revokeForm.reason || '',
        requestReason: revokeForm.reason || '',
        personal_data_involved: selectedFields.join(', '),
        personalDataInvolved: selectedFields.join(', '),
        declaration_name: selectedConsent?.username || selectedConsent?.Name || selectedConsent?.email || 'User',
        declarationName: selectedConsent?.username || selectedConsent?.Name || selectedConsent?.email || 'User',
        declaration_date: new Date().toISOString().split('T')[0],
        declarationDate: new Date().toISOString().split('T')[0],
        send_email: 'true',
        source: 'user_panel_revoke',
        public_id: selectedConsent?.public_id || selectedConsent?.publicId || '',
      };

      const selectedDrivePreference = (() => {
        try {
          return (window && window.localStorage && window.localStorage.getItem('consent_selected_drive')) || '';
        } catch (e) {
          return '';
        }
      })();
      if (selectedDrivePreference) {
        payload.drive_type = selectedDrivePreference;
      }

      const apiKey = process.env.REACT_APP_MANAGER_API_KEY || localStorage.getItem('manager_api_key') || '';
      const apiToken = process.env.REACT_APP_MANAGER_API_TOKEN || localStorage.getItem('manager_api_token') || '';
      const headers = {};
      if (apiKey) headers['X-API-KEY'] = apiKey;
      if (apiToken) headers['X-API-TOKEN'] = apiToken;
      const driveAccessToken = localStorage.getItem('drive_access_token') || '';
      if (driveAccessToken) headers['X-Google-Drive-Token'] = driveAccessToken;
      if (selectedDrivePreference) headers['X-Drive-Type'] = selectedDrivePreference;

      const hasAttachments = revokeForm.attachments?.length > 0;
      const requestBody = hasAttachments ? new FormData() : payload;
      if (hasAttachments) {
        Object.entries(payload).forEach(([key, value]) => {
          requestBody.append(key, value);
        });
        revokeForm.attachments.forEach((file) => requestBody.append('attachments', file, file.name));
      }

      const res = await axios.post(`${API_BASE}/manager/dsar`, requestBody, { headers });
      // if (res?data && (res.data.success === undefined || res.data.success)){
      if (res?.data?.success === true && res.data.id != null) {
        const revokeBody = new FormData();
        revokeBody.append('consent_id', String(selectedConsent.id));
        revokeBody.append('reason', revokeForm.reason);
        revokeBody.append('comments', revokeForm.comments || '');
        revokeBody.append('revoked_fields', JSON.stringify(selectedFields));
        if (tenantIdValue != null) revokeBody.append('tenant_id', String(tenantIdValue));
        if (selectedDrivePreference) revokeBody.append('drive_type', selectedDrivePreference);
        revokeForm.attachments.forEach((file) => {
          revokeBody.append('attachments', file, file.name);
        });

        try {
          const revokeRes = await axios.post(`${API_BASE}/user/consent/revoke`, revokeBody, { headers });
          if (revokeRes?.data?.success !== true) {
            throw new Error(revokeRes?.data?.detail || 'Consent revocation was not confirmed');
          }
        } catch (revokeErr) {
          if (selectedConsent?.id !== undefined && selectedConsent?.id !== null) {
            setRevokeRequestedIds(prev => {
              const next = new Set(prev);
              next.add(String(selectedConsent.id));
              persistRevokeRequestedIds(next);
              return next;
            });
          }
          const revokeMsg = extractErrorMsg(revokeErr) || revokeErr?.message || 'Consent revocation failed';
          Swal.fire({
            text: `DSAR ticket #${res.data.id} was created, but the consent could not be revoked: ${revokeMsg}`,
            icon: 'error'
          });
          return;
        }

        try {
          if (selectedConsent?.id !== undefined && selectedConsent?.id !== null) {
            setRevokeRequestedIds(prev => {
              const next = new Set(prev);
              next.add(String(selectedConsent.id));
              persistRevokeRequestedIds(next);
              return next;
            });
          }
        } catch (e) { /* ignore */ }

        setConsents(prev => prev.map(consent => (
          String(consent.id) === String(selectedConsent.id)
            ? { ...consent, status: selectedFields.includes('all') ? 'REVOKED' : 'PARTIALLY_REVOKED' }
            : consent
        )));

        Swal.fire({
          text: 'Revoke request submitted successfully. It has been added to the DSAR Dashboard and a confirmation email has been sent.',
          icon: 'success'
        });
        closeRevokeModal();
      } else {
        Swal.fire({ text: 'Failed to submit revoke request: ' + (res?.data?.error || res?.data?.detail || 'The server did not confirm DSAR ticket creation.'), icon: 'error' });
      }
    } catch (err) {
      console.error('Revoke request error', err);
      const msg = extractErrorMsg(err) || 'Error submitting revoke request';
      Swal.fire({ text: 'Error submitting revoke request: ' + msg, icon: 'error' });
    }
  };

  // handle create or update submit
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (submitting) {
      Swal.fire({ text: 'Submission in progress — please wait.', icon: 'info' });
      return;
    }
    if (hasSubmitted && !editingId) {
      Swal.fire({ text: 'This form has already been submitted.', icon: 'info' });
      return;
    }
    // Check if minor (under 18)
    const isMinor = userForm.ageCategory === 'minor';

    if (userForm.dob) {
      const parts = String(userForm.dob).split('-');
      const year = parts.length === 3 ? parseInt(parts[0], 10) : NaN;
      if (isNaN(year) || year < 1900) {
        Swal.fire({
          title: 'Invalid Date of Birth',
          text: 'Please enter a complete and valid 4-digit year for Date of Birth.',
          icon: 'warning',
          confirmButtonColor: '#2563eb'
        });
        return;
      }
    }

    const age = getAgeFromDOB(userForm.dob);

    if (!isMinor && userForm.dob) {
      if (age !== null && age < 18) {
        Swal.fire({
          title: 'Invalid Date of Birth',
          text: 'Selected category is Adult, but entered date of birth indicates age under 18 (Minor). Please select "Minor" or enter an adult date of birth.',
          icon: 'warning',
          confirmButtonColor: '#2563eb'
        });
        return;
      }
    }

    // Under 18 parent / guardian validation
    if (isMinor) {
      if (!userForm.dob) {
        Swal.fire({ text: 'Date of birth is required for Minor', icon: 'warning' });
        return;
      }
      if (age !== null && age >= 18) {
        Swal.fire({
          title: 'Invalid Date of Birth',
          text: 'Selected category is Minor, but entered date of birth indicates age 18 or above (Adult). Please select "Adult" or enter a minor date of birth.',
          icon: 'warning',
          confirmButtonColor: '#2563eb'
        });
        return;
      }
      if (!userForm._guardianType) {
        Swal.fire({
          title: 'Selection Required',
          text: 'User is under 18 — please select Relationship: Parent or Guardian',
          icon: 'warning',
        });
        return;
      }
      if (!userForm.parentName || !userForm.parentName.trim()) {
        Swal.fire({ text: `User is under 18 — ${userForm._guardianType === 'guardian' ? 'Guardian' : 'Parent'} name is required`, icon: 'warning' });
        return;
      }
      if (!userForm.parentEmail || !userForm.parentEmail.trim()) {
        Swal.fire({ text: `User is under 18 — ${userForm._guardianType === 'guardian' ? 'Guardian' : 'Parent'} email is required`, icon: 'warning' });
        return;
      }
      const parentEmailRegex = /^[^\s@]+@[^\s@]+\.(com|in|org|net|edu|gov|co\.in|io|info|biz|[a-z]{2,})$/i;
      if (!parentEmailRegex.test(userForm.parentEmail.trim())) {
        Swal.fire({
          title: 'Invalid Parent/Guardian Email',
          text: 'Please enter a valid email address for parent/guardian (e.g. name@example.com)',
          icon: 'error',
        });
        return;
      }
      if (!userForm.parentPhone || !validatePhone(userForm.parentPhone)) {
        Swal.fire({ text: 'Parent/guardian phone must be 10 digits and start with 6-9', icon: 'warning' });
        return;
      }
    }

    const effectiveEmail = (isMinor ? (userForm.parentEmail || '').trim() : (userForm.email || '').trim());
    if (!effectiveEmail) {
      Swal.fire({ text: isMinor ? 'Parent/guardian email is required' : 'Email is required', icon: 'warning' });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.(com|in|org|net|edu|gov|co\.in|io|info|biz|[a-z]{2,})$/i;
    if (!emailRegex.test(effectiveEmail)) {
      Swal.fire({
        title: 'Invalid Email',
        text: 'Please enter a complete, valid email address (e.g. name@example.com)',
        icon: 'error',
      });
      return;
    }

    if (!userForm.username) {
      Swal.fire({ text: 'Name is required', icon: 'warning' });
      return;
    }
    if (isMinor && !userForm.dob) {
      Swal.fire({ text: 'Date of birth is required for Minor', icon: 'warning' });
      return;
    }
    const effectivePhone = (isMinor ? (userForm.parentPhone || '').trim() : (userForm.phone || '').trim());
    if (!effectivePhone) {
      Swal.fire({ text: isMinor ? 'Parent/guardian phone number is required' : 'Phone number is required', icon: 'warning' });
      return;
    }
    if (!validatePhone(effectivePhone)) {
      Swal.fire({ text: isMinor ? 'Parent phone must be 10 digits and start with 6-9' : 'Phone must be 10 digits and start with 6-9', icon: 'warning' });
      return;
    }

    (async () => {
      try {
        if (pendingReconsentId && isTemplateUnderProcess({ type: userForm.type, consent_type: userForm.type })) {
          setSubmitting(false);
          Swal.fire({
            icon: 'warning',
            title: 'Cannot Submit Re-consent',
            text: `The consent template for "${userForm.type}" is currently Under Process and awaiting manager acceptance.`,
            confirmButtonColor: '#2563eb'
          });
          return;
        }
        setSubmitting(true);
        const payload = {
          action: userForm.action,
          createdAt: (userForm.createdAt && String(userForm.createdAt).length > 10) ? userForm.createdAt : new Date().toISOString(),
          created_at: (userForm.createdAt && String(userForm.createdAt).length > 10) ? userForm.createdAt : new Date().toISOString(),
          type: userForm.type,
          consent_type: userForm.type || userForm.consent_type || '',
          consentType: userForm.type || userForm.consent_type || '',
          entity: userForm.entity || null,
          username: userForm.username,
          email: effectiveEmail,
          phone: effectivePhone,
          dob: userForm.dob,
          age_category: userForm.ageCategory || (isMinor ? 'minor' : 'adult'),
          ageCategory: userForm.ageCategory || (isMinor ? 'minor' : 'adult'),
          address: userForm.address || null,
          location: userForm.location || null,
          locality: userForm.location || null,
          department: userForm.department || null,
          consent: userForm.consent,
          appName: userForm.appName,
          userAction: userForm.userAction,
          data: userForm.data,
          expiry: (userForm.expiry === '' || userForm.expiry === null || typeof userForm.expiry === 'undefined') ? null : (Number.isNaN(Number(userForm.expiry)) ? null : Number(userForm.expiry)),
          status: pendingReconsentId ? 'pending' : userForm.status,
          pendingReconsentId: pendingReconsentId || undefined,
          is_reconsent: pendingReconsentId ? true : undefined,
          reconsent: pendingReconsentId ? true : undefined,
          parent_name: isMinor ? (userForm.parentName || null) : null,
          parent_email: isMinor ? (userForm.parentEmail || null) : null,
          parent_phone: isMinor ? (userForm.parentPhone || null) : null,
          parentName: isMinor ? (userForm.parentName || null) : null,
          parentEmail: isMinor ? (userForm.parentEmail || null) : null,
          parentPhone: isMinor ? (userForm.parentPhone || null) : null,
          guardian_type: isMinor ? (userForm._guardianType || null) : null,
          sub_category: userForm.subCategory || undefined,
          consent_id: userForm.consentId || undefined,
          parent_consent_id: userForm.consentId || undefined,
          sub_consent_id: userForm.subCategory || undefined,
          consent_language: userForm.language || 'en',
          language: userForm.language || 'en',
          preferred_languages: userForm.language || 'en',
          data_type_consent: Array.isArray(userForm.dataTypeConsent) ? userForm.dataTypeConsent.filter(Boolean).join(',') : (userForm.dataTypeConsent || ''),
          dataTypeConsent: Array.isArray(userForm.dataTypeConsent) ? userForm.dataTypeConsent.filter(Boolean).join(',') : (userForm.dataTypeConsent || ''),
          Data_Type_consent: Array.isArray(userForm.dataTypeConsent) ? userForm.dataTypeConsent.filter(Boolean).join(',') : (userForm.dataTypeConsent || ''),
        };
        if (user?.tenant_id) payload.tenant_id = user.tenant_id;
        try {
          const _ver = await getVersion(user?.tenant_id, userForm.type);
          if (_ver) payload.version = _ver;
        } catch (e) {
          // ignore version fetch errors
        }

        let res;
        // if (editingId) {
        //   res = await axios.put(`http://localhost:8000/user/consent/${editingId}`, payload);
        // } else {
        //   res = await axios.post('http://localhost:8000/user/consent', payload);

        // ID proof type / file removed from consent form; normal POST is used
        if (!editingId && idProofFile) {
          const fd = new FormData();
          Object.keys(payload).forEach(k => { if (payload[k] !== undefined && payload[k] !== null) fd.append(k, payload[k]); });
          fd.append('consent_text', payload.consent || '');
          // ask server to suppress immediate notify so frontend can send one combined email with attachment
          // fd.append('suppress_notify', '1'); // Allow backend notification
          if (userForm.dataTypeConsent) {
            const v = Array.isArray(userForm.dataTypeConsent) ? userForm.dataTypeConsent.filter(Boolean).join(',') : String(userForm.dataTypeConsent);
            fd.append('data_type_consent', v);
            fd.append('dataTypeConsent', v);
            fd.append('Data_Type_consent', v);
          }
          // if (userForm.preferredLanguages && Array.isArray(userForm.preferredLanguages) && userForm.preferredLanguages.length) {
          //   fd.append('preferred_languages', userForm.preferredLanguages.join(','));
          // }
          if (idProofFile) {
            if (Array.isArray(idProofFile)) {
              idProofFile.forEach(f => fd.append('id_proof', f, f.name));
            } else {
              fd.append('id_proof', idProofFile, idProofFile.name);
            }
          }
          if (userForm.language) {
            fd.append('language', userForm.language);
            fd.append('preferred_languages', userForm.language);
            fd.append('preferredLanguages', userForm.language);
          }
          const selectedDrivePreference = (() => {
            try {
              return (window && window.localStorage && window.localStorage.getItem('consent_selected_drive')) || '';
            } catch (e) {
              return '';
            }
          })();
          if (selectedDrivePreference) {
            fd.append('drive_type', selectedDrivePreference);
          }
          // const uploadHeaders = { 'Content-Type': 'multipart/form-data' };
          const uploadHeaders = {};
          if (selectedDrivePreference) {
            uploadHeaders['X-Drive-Type'] = selectedDrivePreference;
          }
          const driveAccessToken = localStorage.getItem('drive_access_token') || '';
          if (driveAccessToken) uploadHeaders['X-Google-Drive-Token'] = driveAccessToken;
          res = await axios.post(`${API_BASE}/user/consent/with_id`, fd, { headers: uploadHeaders });
        } else {
          if (editingId) {
            res = await axios.put(`${API_BASE}/user/consent/${editingId}`, payload);
          } else {
            payload.suppress_notify = false;
            res = await axios.post(`${API_BASE}/user/consent`, payload);
          }
        }

        if (res.data && res.data.success) {
          const newId = res.data.id || res.data.consent_id || editingId;
          const entry = {
            id: newId,
            ...payload,
            createdAt: payload.createdAt || payload.created_at || new Date().toISOString(),
            created_at: payload.created_at || payload.createdAt || new Date().toISOString(),
            status: payload.status || 'Pending',
            reminder_count: 0,
            isMinor: Boolean(payload.isMinor || payload.is_minor || payload.parent_name || payload.parentName),
          };

          // Stack order: Place new consent immediately at the top of the stack (LIFO) in Overall Consents
          if (!editingId) {
            setConsents(prev => [entry, ...(prev || []).filter(c => c && c.id !== newId)]);
          } else {
            setConsents(prev => (prev || []).map(c => (c && c.id === newId ? { ...c, ...entry } : c)));
          }

          try {
            clearAllSessionCache();
          } catch (e) { }

          try {
            window.dispatchEvent(new CustomEvent('consent:created', { detail: entry }));
          } catch (e) {
            console.warn('Failed to dispatch consent event', e);
          }
          Swal.fire({
            icon: 'success',
            title: 'Consent Recorded Successfully',
            text: 'Consent saved to database. Email processing in background.',
            timer: 2000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          });
          setSaved(true);
          setTimeout(() => setSaved(false), 1500);
          setTab('consent');
          closeSlidingPage();
          setHasSubmitted(true);
          if (pendingReconsentId) {
            setReconsentSubmittedIds(prev => {
              const next = new Set(prev);
              next.add(String(pendingReconsentId));
              persistReconsentSubmittedIds(next);
              return next;
            });
          }
          setPendingReconsentId(null);
          if (!editingId) {
            setRoleFilter(null);
            setStatusFilter(null);
            setExpiringFilter(false);
            setTypeFilter(null);
            setDateFilter('');
            setSearchQuery('');
            setDebouncedSearchQuery('');
            setDedupeMode(false);
          }
          setCurrentPage(1);
          await fetchConsents({ page: 1, unfiltered: !editingId });
          if (!editingId) {
            try {
              await sendAuditLog(
                'create_consent',
                payload.email || null,
                JSON.stringify({
                  consent_id: entry.id,
                  consent_type: payload.type,
                  status: payload.status,
                }),
              );
            } catch (e) {
              console.error('Failed to log consent creation', e);
            }
          }
          // Persist the consent template for this tenant so the message
          // appears next time in the Type dropdown (best-effort, silent failure)
          try {
            if (user && user.tenant_id) {
              axios.post(`${API_BASE}/manager/new-consent`, {
                tenant_id: user.tenant_id,
                consent_type: payload.type,
                consent_message: payload.consent
              }).catch(err => console.warn('Failed to persist template to manager/new-consent', err));
            }
          } catch (e) {
            console.warn('Error while attempting to persist template', e);
          }
        } else {
          Swal.fire({ text: 'Failed to save consent: ' + (res.data?.detail || JSON.stringify(res.data)), icon: 'error' });
        }
      } catch (err) {
        console.error('Save error', err);
        const msg = extractErrorMsg(err) || 'Error saving consent';
        const isDuplicate = /duplicate consent/i.test(msg);
        Swal.fire({
          title: isDuplicate ? 'Duplicate Consent' : 'Error saving consent',
          text: isDuplicate ? msg.replace(/^Duplicate consent:\s*/i, '') : msg,
          icon: isDuplicate ? 'warning' : 'error',
        });
      }
      finally {
        setSubmitting(false);
      }
    })();
  };

  // prepare pagination and filtered data
  const GUIDE_STEPS = [
    { title: 'Record Consent', body: 'Click this + button to open the form and record a brand new consent for a user.', target: '.tour-target-new-consent-btn' },
    { title: 'Search consents', body: 'Search records by name, email, phone, or consent type.', target: '.tour-target-consent-search' },
    { title: 'Filter by status', body: 'Filter the table to show only Accepted, Declined, Rejected, Revoked, or Pending consents.', target: '.tour-target-status-filter' },
    { title: 'Bulk CSV upload', body: 'Click here to download a sample CSV template and upload multiple consent records at once.', target: '.tour-target-csv-upload-btn' },
    { title: 'User Logs', body: 'View activity logs for all users from this button.', target: '.tour-target-user-logs-btn' },
    { title: 'Back', body: 'Go back to the previous page.', target: '.tour-target-back-btn' },
    { title: 'Legend', body: 'Click here to see what each status color and icon in the table means.', target: '.tour-target-legend-btn' },
    { title: 'Pagination controls', body: 'Change how many rows show per page, and move between pages using Prev/Next.', target: '.tour-target-pagination-bar' },
    { title: 'Bulk actions', body: 'After selecting one or more rows using the checkboxes, use these buttons to send a bulk email, WhatsApp message, or privacy notice.', target: '.tour-target-bulk-actions' },
    { title: 'Consents table', body: 'Each row shows one consent record with its status, expiry, and available actions.', target: '.consent-table-modern' },
    { title: 'Edit consent', body: "Click the pencil icon to edit this consent record's details.", target: '.modern-icon-btn.file-icon-btn' },
    { title: 'More options', body: 'Click the ⋮ icon to send this consent via WhatsApp, Email, or Privacy Notice.', target: '.modern-icon-btn.more-icon-btn' },
    { title: 'Revoke consent', body: 'Click this icon to raise a revoke (erasure) request for this consent — it will appear on the DSAR Dashboard.', target: '.modern-icon-btn.revoke-icon-btn' },
    { title: 'View details', body: 'Click this icon next to a name to see the full details of that consent record.', target: '.tour-target-details-btn' },
    { title: 'View full consent text', body: 'Click this icon in the Consent column to read the complete consent message.', target: '.tour-target-view-consent-btn' },
  ];

  // If dedupeMode is set (overview-driven click), deduplicate records by identity+type first
  // const baseConsents = (() => {
  //   if (!dedupeMode) return consents || [];
  //   const seen = new Set();
  //   const out = [];
  //   for (const r of (consents || [])) {
  //     try {
  //       const email = (r.email || '').toString().trim().toLowerCase();
  //       const phone = (r.phone || '').toString().replace(/\s|\-|\+/g, '');
  //       const username = (r.username || r.name || '').toString().trim().toLowerCase();
  //       const uid = (r.user_id || r.userId || '').toString();
  //       const identity = email || phone || username || uid || 'anon';
  //       const type = (r.type || r.consent_type || r.category || '').toString().trim().toLowerCase();
  //       const key = `${identity}||${type}`;
  //       if (seen.has(key)) continue;
  //       seen.add(key);
  //       out.push(r);
  //     } catch (e) { /* ignore row errors */ }
  //   }
  //   return out;
  // })();

  const baseConsents = consents || [];


  const filteredConsents = baseConsents;
  const sortedFilteredConsents = sortConsentRecords(filteredConsents);
  const totalItems = serverTotal ?? sortedFilteredConsents.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  if (currentPage > totalPages) setCurrentPage(totalPages);

  const visibleConsents = sortedFilteredConsents;

  // Selection helpers (operate on the currently visible page)
  const toggleSelect = (id) => {
    // Prevent selecting auto-declined, declined, or accepted rows
    try {
      const rec = (visibleConsents || []).find(c => c && c.id === id) || (consents || []).find(c => c && c.id === id);
      if (rec) {
        if (isAutoRejectedRecord(rec) || isDeclinedRecord(rec) || isAcceptedRecord(rec)) return;
      }
    } catch (e) { }
    setSelectedIds(prev => {
      try {
        if (!prev || !Array.isArray(prev)) return [id];
        if (prev.includes(id)) return prev.filter(x => x !== id);
        return [...prev, id];
      } catch (e) { return [id]; }
    });
  };
  const selectAllVisible = (checked) => {
    if (checked) {
      const ids = (visibleConsents || []).filter(c => c && !isAutoRejectedRecord(c) && !isDeclinedRecord(c) && !isAcceptedRecord(c)).map(c => c.id).filter(Boolean);
      setSelectedIds(ids);
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkMail = async () => {
    if (!selectedIds || !selectedIds.length) return;
    const rows = (consents || []).filter(x => selectedIds.includes(x.id));
    const allowedRows = rows.filter(r => shouldAllowResend(r));
    const skippedRows = rows.filter(r => !shouldAllowResend(r));
    if (allowedRows.length === 0) {
      Swal.fire({ text: 'No selected records are eligible for bulk email. Only pending records can be resent.', icon: 'warning' });
      return;
    }

    setBulkLoading(true);
    Swal.fire({
      title: 'Sending reminders...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });
    const results = { ok: 0, failed: 0, errors: [] };
    // for (const id of selectedIds) {
    for (const consent of allowedRows) {
      try {
        // const consent = (consents || []).find(x => x.id === id);
        const id = consent.id;
        const fd = new FormData();
        fd.append('format', 'text');
        fd.append('include_response_links', 'true');
        const prefLangs = (consent.consent_language || consent.preferred_languages || consent.preferredLanguages || consent.languages || consent.language || '');
        let target = '';
        if (Array.isArray(prefLangs) && prefLangs.length) target = prefLangs[0];
        else if (typeof prefLangs === 'string' && prefLangs.trim()) target = prefLangs.split(',')[0].trim();
        if (target) fd.append('target_language', target);
        const res = await axios.post(`${API_BASE}/user/consent/${id}/resend`, fd);
        if (res && (res.data?.success || res.status === 200)) {
          results.ok++;
          try {
            const now = new Date().toISOString();
            const newCount = res.data?.reminder_count !== undefined ? Number(res.data.reminder_count) : (Number(consent.reminder_count || 0) + 1);
            setConsents(prev => (prev || []).map(c => c && c.id === id ? ({ ...c, reminder_count: newCount, reminder_cycle_start: c.reminder_cycle_start || now, last_email_sent_at: now, last_reminder_at: now }) : c));
          } catch (e) { }
        } else results.failed++;
      } catch (err) {
        results.failed++;
        results.errors.push({ id: consent.id, msg: err?.message || (err?.response?.data?.detail || 'Error') });
      }
    }
    // Invalidate session cache after bulk email so page reload always reads updated database state
    try {
      clearAllSessionCache();
    } catch (e) { }
    setBulkLoading(false);
    setSelectedIds([]);
    Swal.close();
    let message = `${results.ok} succeeded, ${results.failed} failed`;
    if (skippedRows.length) {
      message += `<br/>${skippedRows.length} skipped because they were not pending.`;
    }
    if (results.errors.length) {
      message += '<br/>' + results.errors.map(e => `ID ${e.id}: ${e.msg}`).join('<br/>');
    }
    Swal.fire({
      title: 'Bulk Email Results',
      // html: `${results.ok} succeeded, ${results.failed} failed${results.errors.length ? '<br/>' + results.errors.map(e => `ID ${e.id}: ${e.msg}`).join('<br/>') : ''}`,
      html: message,
      icon: results.failed ? 'warning' : 'success'
    });
  };

  // const handleBulkWhatsApp = () => {
  const handleBulkWhatsApp = async () => {
    if (!selectedIds || !selectedIds.length) return;
    // const rows = (consents || []).filter(x => selectedIds.includes(x.id));
    // const lines = rows.map(r => `Name: ${r.username || r.Name || ''} | Email: ${r.email || ''} | Phone: ${r.phone || ''}`);
    // const message = `Privacy/Consent Notices:\n\n` + lines.join('\n');
    // const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    // window.open(url, '_blank');
    setBulkLoading(true);
    // Swal.fire({ title: 'Sending reminders...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    // try {
    //   const rows = (consents || []).filter(x => selectedIds.includes(x.id));
    //   const lines = rows.map(r => `Name : ${r.username || r.Name || ''} | Email: ${r.email || ''} | Phone: ${r.phone || ''}`);
    //   const message = `Privacy/Consent Notices:\n\n` + lines.join('\n');
    //   const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    //   window.open(url, '_blank');



    Swal.fire({ title: 'Sending WhatsApp messages...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    const rows = (consents || []).filter(x => selectedIds.includes(x.id));
    const results = { ok: 0, failed: 0, skipped: 0, errors: [] };

    try {
      for (const r of rows) {
        const phone = (r.phone || r.mobile || r.whatsapp || '').toString().trim();
        if (!phone) {
          results.skipped += 1;
          results.errors.push({ id: r.id, msg: 'No phone number on record' });
          continue;
        }
        const message =
          `Consent Details:\n` +
          `Name: ${r.username || r.Name || ''}\n` +
          `Email: ${r.email || ''}\n` +
          `Type: ${r.type || ''}\n` +
          `Consent: ${r.consent || ''}`;
        try {
          const res = await axios.post(`${API_BASE}/user/send-whatsapp`, {
            phone,
            body: message,
            consent_id: r.id,
          });
          const data = res?.data || {};
          if (data.sent) {
            results.ok += 1;
          } else {
            results.failed += 1;
            results.errors.push({ id: r.id, msg: data.detail || 'Twilio rejected' });
          }
        } catch (err) {
          results.failed += 1;
          const detail = err?.response?.data?.detail || err?.message || 'Request failed';
          results.errors.push({ id: r.id, msg: detail });
        }
      }
    } finally {
      //   setTimeout(() => {
      //     setBulkLoading(false);
      //     Swal.close();
      //   }, 700);
      // }
      setBulkLoading(false);
      Swal.close();
    }

    const errHtml = results.errors.length
      ? '<br/><br/><div style="text-align:left;max-height:200px;overflow:auto;font-size:12px">' +
      results.errors.map(e => `ID ${e.id}: ${e.msg}`).join('<br/>') +
      '</div>'
      : '';
    Swal.fire({
      title: 'Bulk WhatsApp Results',
      html: `✅ ${results.ok} sent &nbsp; ⚠️ ${results.failed} failed &nbsp; ⏭️ ${results.skipped} skipped${errHtml}`,
      icon: results.failed || results.skipped ? 'warning' : 'success',
    });
  };

  const handleBulkPrivacy = () => {
    if (!selectedIds || !selectedIds.length) return;
    // const first = (consents || []).find(x => x.id === selectedIds[0]);
    // if (first) {
    //   handleBulkPrivacy(first);

    setBulkLoading(true);
    Swal.fire({ title: 'Preparing privacy notice...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      // open privacy modal prefilled for the first selected consent so user can send a notice to multiple via server later
      const first = (consents || []).find(x => x.id === selectedIds[0]);
      if (first) {
        handlePrivacyNotice(first);
      }
    } finally {
      setTimeout(() => {
        setBulkLoading(false);
        Swal.close();
      }, 400);
    }
  };

  const handleBulkReconsent = async () => {
    if (bulkLoading) return;
    if (!selectedIds || !selectedIds.length) return;
    const rows = (consents || []).filter(x => selectedIds.includes(x.id));
    if (!rows.length) return;

    const underProcessRows = rows.filter(r => isTemplateUnderProcess(r));
    if (underProcessRows.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Send Reconsent',
        text: 'One or more selected records belong to a consent version that is currently Under Process. Please wait until the manager accepts the new version.',
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Bulk Reconsent',
      html: `Are you sure you want to send active version reconsent for the <strong>${rows.length}</strong> selected record(s)?<br/><br/>New consent records with unique IDs will be created and saved in the table.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '🔁 Yes, Send Reconsent',
      confirmButtonColor: '#2563eb',
      cancelButtonText: 'Cancel'
    });
    if (!confirm.isConfirmed) return;

    setBulkLoading(true);
    Swal.fire({
      title: 'Processing Reconsent...',
      text: 'Creating new consent records with active version templates...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    let newConsentsRes = null;
    try {
      const res = await axios.get(`${API_BASE}/manager/new-consents`, { params: { tenant_id: user?.tenant_id } });
      newConsentsRes = res.data?.data || [];
    } catch (e) {
      console.warn('Failed to load manager/new-consents for bulk reconsent', e);
    }

    const results = { ok: 0, failed: 0, errors: [] };
    const today = new Date().toISOString();

    for (const rec of rows) {
      try {
        const consentType = rec.type || rec.consent_type || '';
        let activeTemplateMsg = '';
        let versionStr = null;
        try {
          versionStr = await getVersion(user?.tenant_id, consentType);
        } catch (e) {
          console.warn(`Failed to load active version for consent type ${consentType}`, e);
        }

        if (newConsentsRes && Array.isArray(newConsentsRes)) {
          const match = newConsentsRes.find(r => {
            const ct = r.consent_type || r.type || r.consent_type_name || '';
            return ct && ct.toString().toLowerCase() === consentType.toString().toLowerCase();
          });
          if (match) {
            activeTemplateMsg = match.consent_message || match.message || match.consent || '';
          }
        }

        if (!activeTemplateMsg) {
          const normType = consentType.toString().trim().toLowerCase();
          activeTemplateMsg = typeMessages[normType] || decodeHtmlEntities(rec.consent || rec.data || '');
        }

        const payload = {
          username: rec.username || rec.Name || rec.name || user?.name || '',
          email: rec.email || user?.email || '',
          phone: rec.phone || '',
          dob: rec.dob || rec.date_of_birth || rec.dateOfBirth || '',
          address: rec.address || rec.Address || rec.user_address || rec.postal_address || '',
          createdAt: today,
          created_at: today,
          type: consentType,
          consent_type: consentType,
          consent: activeTemplateMsg,
          entity: rec.entity || '',
          appName: rec.appName || rec.app_name || '',
          userAction: rec.userAction || rec.user_action || '',
          data: rec.data || '',
          expiry: (rec.expiry === '' || rec.expiry === null || typeof rec.expiry === 'undefined') ? null : (Number.isNaN(Number(rec.expiry)) ? null : Number(rec.expiry)),
          status: 'pending',
          parent_name: rec.parent_name || rec.parentName || null,
          parent_email: rec.parent_email || rec.parentEmail || null,
          parent_phone: rec.parent_phone || rec.parentPhone || null,
          id_proof_type: rec.id_proof_type || rec.idProofType || null,
          id_proof_file: rec.id_proof_file || rec.idProofFileName || null,
          data_type_consent: rec.data_type_consent || rec.dataTypeConsent || '',
          dataTypeConsent: rec.data_type_consent || rec.dataTypeConsent || '',
          Data_Type_consent: rec.data_type_consent || rec.dataTypeConsent || '',
          location: (rec.location || rec.locality) || null,
          department: rec.department || null,
          sub_category: rec.sub_category || rec.subCategory || null,
          preferred_languages: rec.preferred_languages || rec.languages || null,
          suppress_notify: false,
          is_reconsent: true,
          reconsent: true,
          pendingReconsentId: rec.id
        };

        if (user?.tenant_id) payload.tenant_id = user.tenant_id;
        if (versionStr) payload.version = versionStr;

        const postRes = await axios.post(`${API_BASE}/user/consent`, payload);
        if (postRes.data && (postRes.data.success || postRes.data.id)) {
          const newId = postRes.data.id;
          if (newId) {
            try {
              await axios.post(`${API_BASE}/user/consent/${newId}/resend`, { message: activeTemplateMsg, format: 'text' });
            } catch (e) {
              console.warn(`Failed to send reconsent notification for new consent ID ${newId}`, e);
            }
          }
          results.ok++;
        } else {
          results.failed++;
          results.errors.push({ id: rec.id, msg: postRes.data?.detail || 'Failed to create new consent record' });
        }
      } catch (err) {
        results.failed++;
        results.errors.push({ id: rec.id, msg: err?.response?.data?.detail || err?.message || 'Error processing reconsent' });
      }
    }

    setBulkLoading(false);
    setSelectedIds([]);
    setStatusFilter(null);
    setCurrentPage(1);
    Swal.close();

    await fetchConsents();

    let message = `Successfully created and saved ${results.ok} new version consent record(s).`;
    if (results.failed > 0) {
      message += `<br/>${results.failed} failed.`;
    }
    Swal.fire({
      title: 'Bulk Reconsent Complete',
      html: message,
      icon: results.failed === 0 ? 'success' : 'warning'
    });
  };

  // Helper: determine the best display label for a consent's type (consent_type preferred)
  const getConsentTypeLabel = (rec) => {
    if (!rec) return 'N/A';
    try {
      // return (
      //   rec.consent_type || rec.type || rec.tempalte || rec.title || (rec.card_data && (rec.card_data.label || rec.card_data.title)) 'N/A'
      // );
      const rawValue = rec.consent_type || rec.type || rec.template || rec.title || (rec.card_data && (rec.card_data.label || rec.card_data.title)) || 'N/A';
      const language = normalizeLangCode(rec.consent_language || rec.language || rec.preferred_languages || rec.preferredLanguages || 'en');
      const localizedLabel = localizedConsentTypeLabels[language]?.[normalizeConsentTypeKey(rawValue)];
      if (localizedLabel) return localizedLabel;
      const translatedLabel = getTranslatedConsentTypeLabel(rawValue, language, rec);
      if (translatedLabel) return translatedLabel;
      return formatDisplayLabel(rawValue);
    } catch (e) { return 'N/A'; }
  };

  /**
   * Helper to determine data subject category (Child, Parent, Adult) based on record data:
   * - Child (Red): DOB age < 18, parent contact info present, or role/type/name/category contains "child" or "minor" or "kis"
   * - Parent (Blue): role/type/category contains "parent" or "guardian", or explicitly marked as parent
   * - Adult (Yellow): Default or age >= 18 or role/type/category contains "adult"
   */
  const getDataSubjectCategory = (c) => {
    if (!c) return 'adult';

    const nameStr = String(c.username || c.Name || c.name || '').toLowerCase();
    const roleStr = String(c.role || c.user_role || c.role_name || c.userRole || c.subject_type || c.data_subject_type || c.category || c.type || c.consent_type || '').toLowerCase();
    const subTypeStr = String(c.sub_type || c.kis_type || c.entity || '').toLowerCase();

    // 1. Explicit Child indicators
    if (
      c.is_child ||
      c.is_minor ||
      c.isMinor ||
      roleStr.includes('child') ||
      roleStr.includes('minor') ||
      roleStr.includes('kis') ||
      nameStr.includes('child') ||
      subTypeStr.includes('child')
    ) {
      return 'child';
    }

    // 2. Explicit Parent indicators
    if (
      c.is_parent ||
      roleStr.includes('parent') ||
      roleStr.includes('guardian') ||
      nameStr.includes('parent') ||
      subTypeStr.includes('parent')
    ) {
      return 'parent';
    }

    // 3. DOB check for minor (< 18 years old)
    const dobVal = c.dob || c.date_of_birth || c.dateOfBirth || c.birth_date;
    if (dobVal) {
      try {
        const birth = new Date(dobVal);
        if (!isNaN(birth.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - birth.getFullYear();
          const m = today.getMonth() - birth.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
          if (age < 18) return 'child';
        }
      } catch (e) { }
    }

    // 4. Parent contact details present indicates a child record needing parent consent
    if (
      (c.parentName && String(c.parentName).trim()) ||
      (c.parent_name && String(c.parent_name).trim()) ||
      (c.parentEmail && String(c.parentEmail).trim()) ||
      (c.parent_email && String(c.parent_email).trim()) ||
      (c.parentPhone && String(c.parentPhone).trim()) ||
      (c.parent_phone && String(c.parent_phone).trim())
    ) {
      return 'child';
    }

    // 5. Default is Adult
    return 'adult';
  };

  const renderDataSubjectIcon = (c) => {
    const category = getDataSubjectCategory(c);
    if (category === 'child') {
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#ef4444"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0 }}
          title="Child (Red)"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      );
    }
    if (category === 'parent') {
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#2563eb"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0 }}
          title="Parent (Blue)"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      );
    }
    // Adult (Yellow / Orange)
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#f59e0b"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}
        title="Adult (Yellow)"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    );
  };

  // Modern UI Helper & Stats Computations
  // const totalConsentsCount = consents ? consents.length : 0;
  // const acceptedCount = consents ? consents.filter(c => isAcceptedRecord(c)).length : 0;
  // const pendingCount = consents ? consents.filter(c => isPendingRecord(c)).length : 0;
  // const declinedCount = consents ? consents.filter(c => isDeclinedRecord(c) || isAutoRejectedRecord(c)).length : 0;
  // const expiringSoonCount = consents ? consents.filter(c => {


  const isRevokedRecord = (rec) => {
    if (!rec) return false;
    const s = String(rec?.status || '').toLowerCase();
    return s.includes('revok') || s.includes('withdrawn') || s.includes('cancel');
  };
  const isReconsentRecord = (rec) => {
    if (!rec) return false;
    const s = String(rec?.status || '').toLowerCase();
    return s.includes('reconsent') || s.includes('suspend') || s.includes('supersed');
  };
  const isExpiredRecord = (rec) => {
    if (!rec) return false;
    try {
      const dl = computeExpiryDays(rec);
      const n = (dl === '' || dl === null) ? NaN : Number(dl);
      return isAcceptedStatusForExpiry(rec) && !isNaN(n) && n < 0;
    } catch (e) { return false; }
  };
  const handleExpiryCardFilter = (nextFilter) => {
    const isActive = statusFilter === nextFilter;
    setExpiringFilter(false);
    setStatusFilter(isActive ? null : nextFilter);
    setRoleFilter(null);
    setTypeFilter(null);
    setDateFilter('');
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setCurrentPage(1);
  };
  const isAcceptedStatusForExpiry = (rec) => {
    const status = String(rec?.status || '').toLowerCase();
    if (/delet|revok|withdrawn|cancel|declin|reject|reconsent|suspend|supersed|expir|ended|inactive|in-active|deactiv|unaccept|nonaccept|not accept|not active|not approv|not grant|not agree|disagree/.test(status)) {
      return false;
    }
    return /accept|active|approv|grant|agree/.test(status) || status === 'yes';
  };

  const totalConsentsCount = serverStats ? Number(serverStats.total ?? 0) : (serverTotal ?? (consents ? consents.length : 0));
  const acceptedCount = serverStats ? Number(serverStats.accepted ?? serverStats.active ?? 0) : (consents ? consents.filter(c => isAcceptedRecord(c)).length : 0);
  const pendingCount = serverStats ? Number(serverStats.pending ?? 0) : (consents ? consents.filter(c => isPendingRecord(c)).length : 0);
  const declinedCount = serverStats ? Number(serverStats.declined ?? serverStats.rejected ?? 0) : (consents ? consents.filter(c => isDeclinedRecord(c) || isAutoRejectedRecord(c)).length : 0);
  const revokedCount = serverStats ? Number(serverStats.revoked ?? 0) : (consents ? consents.filter(c => isRevokedRecord(c)).length : 0);
  const reconsentCount = serverStats ? Number(serverStats.reconsent ?? 0) : (consents ? consents.filter(c => isReconsentRecord(c)).length : 0);
  const expiredCount = serverStats ? Number(serverStats.expired ?? 0) : (consents ? consents.filter(c => isExpiredRecord(c)).length : 0);
  const expiringSoonCount = serverStats ? Number(serverStats.expiring_soon ?? serverStats.expiring ?? 0) : (consents ? consents.filter(c => {
    try {
      const dl = computeExpiryDays(c);
      const n = (dl === '' || dl === null) ? NaN : Number(dl);
      return isAcceptedStatusForExpiry(c) && !isNaN(n) && n > 0 && n <= 7;
    } catch (e) { return false; }
  }).length : 0);

  const getTypeBadgeStyle = (typeLabel) => {
    const label = String(typeLabel || '').toLowerCase();
    if (label.includes('purpose')) return { bg: '#e0f2fe', color: '#0369a1', border: '#bae6fd' };
    if (label.includes('general')) return { bg: '#f3e8ff', color: '#6b21a8', border: '#e9d5ff' };
    if (label.includes('mohit') || label.includes('custom')) return { bg: '#ffedd5', color: '#c2410c', border: '#fed7aa' };
    if (label.includes('marketing')) return { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' };
    if (label.includes('website') || label.includes('cookie')) return { bg: '#ffe4e6', color: '#be123c', border: '#fecdd3' };
    return { bg: '#f1f5f9', color: '#334155', border: '#e2e8f0' };
  };

  const getStatusBadgeConfig = (status, rowAutoRejected, rowDeclined) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('revok')) {
      return { bg: '#fee2e2', color: '#991b1b', dotColor: '#dc2626', label: 'Revoked' };
    }
    if (rowAutoRejected) {
      return { bg: '#ffe4e6', color: '#991b1b', dotColor: '#dc2626', label: 'Auto-declined' };
    }
    if (rowDeclined) {
      return { bg: '#fee2e2', color: '#991b1b', dotColor: '#dc2626', label: 'Declined' };
    }
    if (s.includes('accept') || s === 'active' || s === 'approved') {
      return { bg: '#dcfce7', color: '#15803d', dotColor: '#16a34a', label: 'Accepted' };
    }
    if (s.includes('pending')) {
      return { bg: '#ffedd5', color: '#c2410c', dotColor: '#f59e0b', label: 'Pending' };
    }
    if (s.includes('expired') || s.includes('ended')) {
      return { bg: '#f3e8ff', color: '#6b21a8', dotColor: '#7c3aed', label: 'Expired' };
    }
    if (s.includes('suspend') || s.includes('reconsent') || s.includes('supersed')) {
      return { bg: 'rgb(238, 64, 93)', color: 'rgb(5, 15, 7)', dotColor: 'rgb(31, 17, 231)', label: 'Reconsent' };
    }
    return { bg: '#f1f5f9', color: '#334155', dotColor: '#94a3b8', label: normalizeStatusLabel(status) };
  };

  const handleExportConsents = () => {
    if (!consents || !consents.length) {
      Swal.fire({ text: 'No consents available to export', icon: 'info' });
      return;
    }
    const headers = ['ID', 'Data Subject', 'Email', 'Type', 'Data Type', 'Consent', 'Status', 'Expiry', 'Created At'];
    const rows = (filteredConsents || consents).map(c => {
      const rawDt = c.data_type_consent || c.dataTypeConsent || c.Data_Type_consent || '';
      let formattedDt = '';
      if (Array.isArray(rawDt)) {
        formattedDt = rawDt.filter(Boolean).join(', ');
      } else if (rawDt) {
        try {
          const parsed = JSON.parse(rawDt);
          if (Array.isArray(parsed)) {
            formattedDt = parsed.filter(Boolean).join(', ');
          } else {
            formattedDt = String(rawDt);
          }
        } catch (_) {
          formattedDt = String(rawDt);
        }
      }
      return [
        c.id || '',
        `"${(c.username || c.Name || '').replace(/"/g, '""')}"`,
        `"${(c.email || '').replace(/"/g, '""')}"`,
        `"${(getConsentTypeLabel(c) || '').replace(/"/g, '""')}"`,
        `"${formattedDt.replace(/"/g, '""')}"`,
        `"${(c.consent || '').replace(/[\r\n]+/g, ' ').replace(/"/g, '""')}"`,
        `"${(c.status || '').replace(/"/g, '""')}"`,
        `"${(computeExpiryDays(c) || '').replace(/"/g, '""')}"`,
        `"${(c.createdAt || c.created_at || '').replace(/"/g, '""')}"`
      ];
    });
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consents_export_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="user-panel">
      {tab !== 'consent' && (
        <div className="page-header">
          <div className="header-container" style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 12 : 20,
            width: '100%',
          }}>
            <div className="header-left" style={{ width: isMobile ? '100%' : 'auto' }}>
              <h2 className="page-title" style={{ wordBreak: 'break-word' }}>
                👋 Welcome, <span>{getWelcomeName(user)}</span>
              </h2>
            </div>
            <div className="header-center" style={{ flex: isMobile ? '0' : '1' }}></div>
            <div className="header-right" style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
              gap: isMobile ? 10 : 12,
              width: isMobile ? '100%' : 'auto',
            }}>
              <button
                className="icon-btn primary"
                onClick={openSlidingPage}
                title="Record Consent"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                +
              </button>
              <input
                ref={searchInputRef}
                className="search-input"
                placeholder="Search name / email / phone / type"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: isMobile ? '100%' : 280,
                  minWidth: 0,
                  marginLeft: isMobile ? 0 : 8,
                }}
              />
              <select
                className="status-select"
                value={statusFilter || ''}
                onChange={e => setStatusFilter(e.target.value || null)}
                style={{
                  height: 34,
                  borderRadius: 6,
                  border: '1.5px solid #9bb1ff',
                  padding: '0 8px',
                  marginLeft: isMobile ? 0 : 8,
                  width: isMobile ? '100%' : 180,
                  minWidth: isMobile ? '100%' : 140,
                }}
              >
                <option value="">All</option>
                <option value="accepted">Accept</option>
                <option value="declined">Decline</option>
                <option value="rejected">Reject</option>
                <option value="revoked">Revoked</option>
                <option value="reconsent">Reconsent</option>
                <option value="pending">Pending</option>
              </select>
              <input
                ref={csvFileInputRef}
                type="file"
                accept=".csv,.json,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleCSVInputChange}
              />
              <button
                className="icon-btn"
                onClick={handleExportConsents}
                title="Export Consents CSV"
                style={{
                  width: isMobile ? '100%' : 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 6,
                  background: '#f8fafc',
                  border: '1.5px solid #cbd5e1',
                  color: '#334155',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                📥 Export CSV
              </button>
              <button
                className="icon-btn upload"
                onClick={() => setShowSampleCSV(true)}
                title="Upload CSV / Excel"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                ⬆
              </button>
              <button
                className="icon-btn tour-target-user-logs-btn"
                onClick={() => { try { if (onMenu) onMenu('user-logs'); else window.location.href = '/user/logs'; } catch (e) { window.location.href = '/user/logs'; } }}
                title="User Logs"
                style={{ marginLeft: isMobile ? 0 : 8, width: isMobile ? '100%' : 'auto' }}
              >
                📋
              </button>
              <button
                className="back-btn"
                onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = '/')}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modals & Popups */}
      <input
        ref={csvFileInputRef}
        type="file"
        accept=".csv,.json,.xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleCSVInputChange}
      />
      {csvUploadMessage && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 3000, background: '#22c55e', color: '#fff', padding: '14px 28px', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.12)', fontWeight: 600, fontSize: 16 }}>
          {csvUploadMessage}
        </div>
      )}
      {csvUploadError && (
        <div
          style={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 3000,
            background: '#ef4444',
            color: '#fff',
            padding: '12px 18px',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            fontWeight: 600,
            fontSize: 13.5,
            maxWidth: 420,
            maxHeight: 120,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {typeof csvUploadError === 'string' && csvUploadError.length > 120
              ? `${csvUploadError.slice(0, 120)}...`
              : csvUploadError}
          </span>
          <button
            type="button"
            onClick={() => setCsvUploadError(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: 16,
              cursor: 'pointer',
              padding: '0 4px',
              lineHeight: 1,
            }}
            title="Dismiss notification"
          >
            ✕
          </button>
        </div>
      )}
      {/* Sample CSV and Confirmation Modals */}
      <SampleCSVModal visible={showSampleCSV} onClose={() => setShowSampleCSV(false)} downloadSampleCSV={downloadSampleCSV} csvFileInputRef={csvFileInputRef} modalHeaderOffset={modalHeaderOffset} />
      <UploadConfirmModal
        visible={showCSVConfirm}
        onClose={() => { setShowCSVConfirm(false); setPendingCSVFile(null); }}
        file={pendingCSVFile}
        typesList={(types && types.length) ? types.slice().sort((a, b) => a.toString().localeCompare(b.toString(), undefined, { sensitivity: 'base' })) : CONSENT_TYPES.map(t => t.key).slice().sort((a, b) => a.toString().localeCompare(b.toString(), undefined, { sensitivity: 'base' }))}
        initialMessages={{ ...typeMessages }}
        onSubmitUpload={({ file, consent_type, consent_message, consent_id, sub_consent_id, version, language }) => performCSVUpload({ file, consent_type, consent_message, consent_id, sub_consent_id, version, language })}
        modalHeaderOffset={modalHeaderOffset}
      />

      {tab === 'dashboard' && (
        <div className="user-dashboard">
          <div className="user-info-card">
            <h3>Your Profile</h3>
            <div>
              <strong>Email:</strong> {maskEmail(user?.email)}
            </div>
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, color: '#64748b', fontWeight: 700 }}>Points balance</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{Number(user?.points ?? 0)}</div>
              </div>
              <div style={{ width: 52, height: 52, borderRadius: 16, background: 'linear-gradient(135deg, #fef3c7, #fbbf24)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🏅</div>
            </div>
          </div>
          <div className="user-stats-card">
            <h3>Dashboard</h3>
            <p>Quick stats and recent activity will appear here.</p>
          </div>
        </div>
      )}

      {tab === 'consent' && (
        <div className="consent-dashboard-container">
          {/* 1. Welcome Banner Header */}
          <div className="consent-welcome-banner">
            <div className="consent-welcome-left">
              <h3>👋 Welcome, {getWelcomeName(user)}</h3>
            </div>
            <div className="consent-welcome-right">
              <button
                // className="btn-filter-action"
                className="btn-filter-action tour-target-back-btn"
                type="button"
                onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = '/')}
                style={{ height: 38, padding: '0 16px', borderRadius: 8, background: '#ffffff', border: '1px solid #cbd5e1', color: '#1e293b', fontWeight: 600, boxShadow: '0 1px 2px rgba(0,0,0,0.05)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                Back
              </button>
              <button
                type="button"
                onClick={() => setGuideOpen(true)}
                style={{ height: 38, padding: '0 16px', borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 8 }}
              >
                📖 Page Guide
              </button>

            </div>
          </div>

          {/* 2. 5 Summary Metric Cards Grid */}
          <div className="consent-stats-grid">
            {/* Total Consents */}
            <div className="consent-stat-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter(null)}>
              <div className="stat-header">
                <div className="stat-icon-wrapper total">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="stat-title-area">
                  <div className="stat-label">Total Consents</div>
                </div>
              </div>
              <div className="stat-body">
                <div className="stat-value" title={Number(totalConsentsCount || 0).toLocaleString()}>{formatCompactNumber(totalConsentsCount)}</div>
                <div className="stat-sublabel">All time</div>
              </div>
              <svg className="stat-wave-svg" viewBox="0 0 200 40" preserveAspectRatio="none">
                <path d="M0,25 C50,38 100,5 150,28 C175,38 190,15 200,20 L200,40 L0,40 Z" fill="rgba(37,99,235,0.08)" />
                <path d="M0,25 C50,38 100,5 150,28 C175,38 190,15 200,20" fill="none" stroke="#3b82f6" strokeWidth="2" />
              </svg>
            </div>

            {/* Accepted */}
            <div className="consent-stat-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter(statusFilter === 'accepted' ? null : 'accepted')}>
              <div className="stat-header">
                <div className="stat-icon-wrapper accepted">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div className="stat-title-area">
                  <div className="stat-label">Accepted</div>
                </div>
              </div>
              <div className="stat-body">
                <div className="stat-value" title={Number(acceptedCount || 0).toLocaleString()}>{formatCompactNumber(acceptedCount)}</div>
              </div>
              <svg className="stat-wave-svg" viewBox="0 0 200 40" preserveAspectRatio="none">
                <path d="M0,30 C40,10 90,35 140,12 C170,2 185,25 200,18 L200,40 L0,40 Z" fill="rgba(22,163,74,0.08)" />
                <path d="M0,30 C40,10 90,35 140,12 C170,2 185,25 200,18" fill="none" stroke="#22c55e" strokeWidth="2" />
              </svg>
            </div>

            {/* Pending */}
            <div className="consent-stat-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}>
              <div className="stat-header">
                <div className="stat-icon-wrapper pending">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <div className="stat-title-area">
                  <div className="stat-label">Pending</div>
                </div>
              </div>
              <div className="stat-body">
                <div className="stat-value" title={Number(pendingCount || 0).toLocaleString()}>{formatCompactNumber(pendingCount)}</div>
              </div>
              <svg className="stat-wave-svg" viewBox="0 0 200 40" preserveAspectRatio="none">
                <path d="M0,20 C60,35 110,10 160,30 C180,38 190,22 200,25 L200,40 L0,40 Z" fill="rgba(234,88,12,0.08)" />
                <path d="M0,20 C60,35 110,10 160,30 C180,38 190,22 200,25" fill="none" stroke="#f97316" strokeWidth="2" />
              </svg>
            </div>

            {/* Declined */}
            <div className="consent-stat-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter(statusFilter === 'declined' ? null : 'declined')}>
              <div className="stat-header">
                <div className="stat-icon-wrapper declined">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </div>
                <div className="stat-title-area">
                  <div className="stat-label">Declined</div>
                </div>
              </div>
              <div className="stat-body">
                <div className="stat-value" title={Number(declinedCount || 0).toLocaleString()}>{formatCompactNumber(declinedCount)}</div>
              </div>
              <svg className="stat-wave-svg" viewBox="0 0 200 40" preserveAspectRatio="none">
                <path d="M0,28 C45,15 95,35 145,20 C170,12 185,28 200,22 L200,40 L0,40 Z" fill="rgba(220,38,38,0.08)" />
                <path d="M0,28 C45,15 95,35 145,20 C170,12 185,28 200,22" fill="none" stroke="#ef4444" strokeWidth="2" />
              </svg>
            </div>

            {/* Revoked */}
            <div className="consent-stat-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter(statusFilter === 'revoked' ? null : 'revoked')}>
              <div className="stat-header">
                <div className="stat-icon-wrapper revoked">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 2.64-6.36L3 8"></path>
                    <path d="M3 3v5h5"></path>
                  </svg>
                </div>
                <div className="stat-title-area">
                  <div className="stat-label">Revoked</div>
                </div>
              </div>
              <div className="stat-body">
                <div className="stat-value" title={Number(revokedCount || 0).toLocaleString()}>{formatCompactNumber(revokedCount)}</div>
                <div className="stat-sublabel">All time</div>
              </div>
              <svg className="stat-wave-svg" viewBox="0 0 200 40" preserveAspectRatio="none">
                <path d="M0,25 C40,15 90,30 140,15 C170,8 185,22 200,18 L200,40 L0,40 Z" fill="rgba(100,116,139,0.08)" />
                <path d="M0,25 C40,15 90,30 140,15 C170,8 185,22 200,18" fill="none" stroke="#64748b" strokeWidth="2" />
              </svg>
            </div>

            {/* Reconsent */}
            <div className="consent-stat-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter(statusFilter === 'reconsent' ? null : 'reconsent')}>
              <div className="stat-header">
                <div className="stat-icon-wrapper reconsent">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 7h-9"></path>
                    <path d="M14 3l-4 4 4 4"></path>
                    <path d="M4 17h9"></path>
                    <path d="M10 13l4 4-4 4"></path>
                  </svg>
                </div>
                <div className="stat-title-area">
                  <div className="stat-label">Reconsent</div>
                </div>
              </div>
              <div className="stat-body">
                <div className="stat-value" title={Number(reconsentCount || 0).toLocaleString()}>{formatCompactNumber(reconsentCount)}</div>
                <div className="stat-sublabel">All time</div>
              </div>
              <svg className="stat-wave-svg" viewBox="0 0 200 40" preserveAspectRatio="none">
                <path d="M0,28 C50,12 100,32 150,18 C175,10 188,25 200,20 L200,40 L0,40 Z" fill="rgba(245,158,11,0.08)" />
                <path d="M0,28 C50,12 100,32 150,18 C175,10 188,25 200,20" fill="none" stroke="#f59e0b" strokeWidth="2" />
              </svg>
            </div>

            {/* Expired */}
            <div className="consent-stat-card" style={{ cursor: 'pointer' }} onClick={() => handleExpiryCardFilter('expired')}>

            {/* <div className="consent-stat-card" style={{ cursor: 'pointer' }} onClick={() => { setExpiringFilter(false); setStatusFilter(statusFilter === 'expired' ? null : 'expired'); }}> */}
              <div className="stat-header">
                <div className="stat-icon-wrapper expired">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 6v6l4 2"></path>
                  </svg>
                </div>
                <div className="stat-title-area">
                  <div className="stat-label">Expired</div>
                </div>
              </div>
              <div className="stat-body">
                <div className="stat-value" title={Number(expiredCount || 0).toLocaleString()}>{formatCompactNumber(expiredCount)}</div>
                <div className="stat-sublabel">All time</div>
              </div>
              <svg className="stat-wave-svg" viewBox="0 0 200 40" preserveAspectRatio="none">
                <path d="M0,22 C45,30 95,10 145,24 C170,30 185,16 200,18 L200,40 L0,40 Z" fill="rgba(100,116,139,0.08)" />
                <path d="M0,22 C45,30 95,10 145,24 C170,30 185,16 200,18" fill="none" stroke="#64748b" strokeWidth="2" />
              </svg>
            </div>

            {/* Expiring Soon */}
            {/* <div className="consent-stat-card" style={{ cursor: 'pointer'}} onClick = {() => setExpiringFilter(prev => !prev)}> */}
            <div className="consent-stat-card" style={{ cursor: 'pointer' }} onClick={() => {
              handleExpiryCardFilter('expiring-soon');
            }}>
              <div className="stat-header">
                <div className="stat-icon-wrapper expiring">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
                <div className="stat-title-area">
                  <div className="stat-label">Expiring Soon</div>
                </div>
              </div>
              <div className="stat-body">
                <div className="stat-value" title={Number(expiringSoonCount || 0).toLocaleString()}>{formatCompactNumber(expiringSoonCount)}</div>
                <div className="stat-sublabel">Within 7 days</div>
              </div>
              <svg className="stat-wave-svg" viewBox="0 0 200 40" preserveAspectRatio="none">
                <path d="M0,22 C55,32 105,12 155,25 C175,32 188,18 200,20 L200,40 L0,40 Z" fill="rgba(147,51,234,0.08)" />
                <path d="M0,22 C55,32 105,12 155,25 C175,32 188,18 200,20" fill="none" stroke="#a855f7" strokeWidth="2" />
              </svg>
            </div>
          </div>

          {/* 3. Filter Bar */}
          <div className="consent-filter-bar">
            {/* <div className="consent-search-box"> */}
            <div className="consent-search-box tour-target-consent-search">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by name, email, phone or type..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            <select
              // className="consent-filter-select"
              className="consent-filter-select tour-target-status-filter"
              value={typeFilter || ''}
              onChange={e => setTypeFilter(e.target.value || null)}
            >
              <option value="">All Types</option>
              {(types && types.length > 0 ? types : CONSENT_TYPES.map(t => t.key)).map(t => (
                <option key={t} value={t}>{formatDisplayLabel(t)}</option>
              ))}
            </select>

            <select
              className="consent-filter-select"
              value={statusFilter || ''}
              onChange={e => {
                const nextStatus = e.target.value || null;
                setExpiringFilter(false);
                setStatusFilter(nextStatus);
                if (nextStatus === 'expiring-soon') setExpiringDays(7);
              }}
            >
              <option value="">All Status</option>
              <option value="accepted">Accepted</option>
              <option value="pending">Pending</option>
              <option value="declined">Declined</option>
              <option value="auto-declined">Auto-declined</option>
              <option value="reconsent">Reconsent</option>
              <option value="revoked">Revoked</option>
              <option value="expired">Expired</option>
              <option value="expiring-soon">Expiring Soon</option>
            </select>

            <select
              className="consent-filter-select"
              value={dateFilter || ''}
              onChange={e => setDateFilter(e.target.value || '')}
            >
              <option value="">All Time</option>
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
            </select>

            {/* <button className="btn-new-consent" type="button" onClick={openSlidingPage}> */}
            <button className="btn-new-consent tour-target-new-consent-btn" type="button" onClick={openSlidingPage}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> New Consent
            </button>

            <button
              // className="btn-filter-action"
              className="btn-filter-action tour-target-csv-upload-btn"
              type="button"
              title="Upload CSV"
              onClick={() => setShowSampleCSV(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              CSV Upload
            </button>

            <button
              className="btn-filter-action tour-target-user-logs-btn"
              type="button"
              title="User Logs"
              onClick={() => { try { if (onMenu) onMenu('user-logs'); else window.location.href = '/user/logs'; } catch (e) { window.location.href = '/user/logs'; } }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                <line x1="9" y1="12" x2="15" y2="12"></line>
                <line x1="9" y1="16" x2="15" y2="16"></line>
              </svg>
              User Logs
            </button>
          </div>

          {/* 4. Overall Consents Table Card */}
          <div className="consent-table-card">
            <div className="table-card-header">
              <div>
                <h3 className="table-card-title">Overall Consents</h3>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }} aria-live="polite">
                  {consentPageReadyMs !== null && <>Page ready: {consentPageReadyMs} ms</>}
                  {consentPageReadyMs !== null && consentDataTimingMs !== null && ' · '}
                  {consentDataTimingMs !== null && <>Data shown: {consentDataTimingMs} ms</>}
                </div>
              </div>

              <div className="table-card-controls">
                <button className="tour-target-legend-btn" type="button" onClick={() => setShowLegend(s => !s)} style={{ background: 'transparent', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                  {showLegend ? 'Hide legend ▲' : 'Show legend ▼'}
                </button>

                <div className="table-info-text">
                  Showing {totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalItems)} of {totalItems}
                </div>

                <select
                  className="consent-filter-select"
                  style={{ height: 34, minWidth: 110, fontSize: 13 }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>

            {showLegend && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(15, 23, 42, 0.5)',
                  backdropFilter: 'blur(3px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999,
                  padding: 16
                }}
                onClick={() => setShowLegend(false)}
              >
                <div
                  style={{
                    background: '#ffffff',
                    borderRadius: 12,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    width: '100%',
                    maxWidth: 640,
                    padding: '20px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Legend</div>
                    <button
                      type="button"
                      onClick={() => setShowLegend(false)}
                      style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      Close ✕
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px 24px', fontSize: 13.5, color: '#334155', fontWeight: 500, paddingTop: 4 }}>
                    {/* 1. Active */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                      <span>Active</span>
                    </div>

                    {/* 2. Pending */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />
                      <span>Pending</span>
                    </div>

                    {/* 3. Revoked */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
                      <span>Revoked</span>
                    </div>

                    {/* 4. Child */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <span>Child</span>
                    </div>

                    {/* 5. Parent */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <span>Parent</span>
                    </div>

                    {/* 6. Adult */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <span>Adult</span>
                    </div>

                    {/* 7. Record Consent */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 140 }}>
                      <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#4f46e5', color: '#ffffff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>+</span>
                      <span>Record Consent</span>
                    </div>

                    {/* 8. CSV Upload */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                      <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 14 }}>↑</span>
                      <span>CSV Upload</span>
                    </div>

                    {/* 9. Privacy Notice */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 140 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                      </svg>
                      <span>Privacy Notice</span>
                    </div>

                    {/* 10. Revoke */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                      <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#ef4444', color: '#ffffff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>✕</span>
                      <span>Revoke</span>
                    </div>

                    {/* 11. Email Response */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 140 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b" stroke="none">
                        <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                        <path d="M22 6l-10 7L2 6" stroke="#ffffff" strokeWidth="2"></path>
                      </svg>
                      <span>Email Response</span>
                    </div>

                    {/* 12. Details */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      <span>Details</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedIds && selectedIds.length > 0 && (() => {
              const selectedRows = (consents || []).filter(c => selectedIds.includes(c.id));
              const hasUnderProcessSelected = selectedRows.some(c => isReconsentRecord(c) && isTemplateUnderProcess(c));

              return (
                <div className="tour-target-bulk-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', gap: 12 }}>
                  <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{selectedIds.length} selected</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" onClick={() => handleBulkMail()} disabled={bulkLoading} style={{ padding: '6px 12px', borderRadius: 6 }}>📧 Send Email</button>
                    <button className="btn" onClick={() => handleBulkWhatsApp()} disabled={bulkLoading} style={{ padding: '6px 12px', borderRadius: 6 }}>💬 Send WhatsApp</button>
                    <button className="btn" onClick={() => handleBulkPrivacy()} disabled={bulkLoading} style={{ padding: '6px 12px', borderRadius: 6 }}>📄 Send Privacy Notice</button>
                    <button
                      className="btn"
                      onClick={() => {
                        if (hasUnderProcessSelected) {
                          Swal.fire({
                            icon: 'warning',
                            title: 'Cannot Send Reconsent',
                            text: 'One or more selected records have consent templates that are Under Process and awaiting manager acceptance.',
                            confirmButtonColor: '#2563eb'
                          });
                          return;
                        }
                        handleBulkReconsent();
                      }}
                      disabled={bulkLoading || hasUnderProcessSelected}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        background: hasUnderProcessSelected ? '#f1f5f9' : '#fef3c7',
                        color: hasUnderProcessSelected ? '#94a3b8' : '#92400e',
                        border: `1px solid ${hasUnderProcessSelected ? '#e2e8f0' : '#fde68a'}`,
                        fontWeight: 600,
                        cursor: hasUnderProcessSelected ? 'not-allowed' : 'pointer',
                        opacity: hasUnderProcessSelected ? 0.6 : 1
                      }}
                      title={hasUnderProcessSelected ? 'Selected consent version is Under Process and cannot be re-consented yet' : 'Send Reconsent'}
                    >
                      🔁 Send Reconsent
                    </button>
                    <button className="btn" onClick={() => setSelectedIds([])} style={{ padding: '6px 12px', borderRadius: 6 }}>Clear</button>
                  </div>
                </div>
              );
            })()}

            <div style={{ overflowX: 'auto', minHeight: moreMenuOpenId ? '120px' : 'auto' }}>
              <table className="consent-table-modern">
                <thead>
                  <tr>
                    <th style={{ width: 40, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        aria-label="Select all visible"
                        checked={selectedIds && visibleConsents && visibleConsents.length > 0 && selectedIds.length === visibleConsents.length}
                        onChange={(e) => selectAllVisible && selectAllVisible(e.target.checked)}
                      />
                    </th>
                    <th style={{ width: 44, textAlign: 'center' }}>#</th>
                    <th style={{ textAlign: 'center', width: 95 }}>Actions</th>
                    <th style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>Created At</th>
                    <th style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>Type</th>
                    <th style={{ textAlign: 'left', minWidth: 160 }}>Name</th>
                    <th style={{ textAlign: 'left', minWidth: 160 }}>Consent / Purpose</th>
                    <th style={{ textAlign: 'center', minWidth: 130 }}>Status</th>
                    <th style={{ textAlign: 'center', width: 90 }}>Reminder</th>
                    <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Expiry (In Days)</th>
                  </tr>
                </thead>
                <tbody>
                  {consents.length === 0 ? (
                    <tr>
                      <td colSpan="10" style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                        No consent records found
                      </td>
                    </tr>
                  ) : (
                    visibleConsents.map((c, index) => {
                      const openUpward = index >= 2 && index >= visibleConsents.length - 2;
                      const hasExplicitReconsent = /reconsent|suspend|supersed/i.test(String(c.status || ''));
                      const rowDeclined = !hasExplicitReconsent && isDeclinedRecord(c);
                      const rowAutoRejected = !hasExplicitReconsent && isAutoRejectedRecord(c);
                      const rowAccepted = isAcceptedRecord(c);
                      const typeStyle = getTypeBadgeStyle(getConsentTypeLabel(c));
                      const statusConfig = getStatusBadgeConfig(c.status, rowAutoRejected, rowDeclined);
                      const isReconsentRow = (statusConfig.label === 'Reconsent' || String(c.status || '').toLowerCase().includes('suspend') || String(c.status || '').toLowerCase().includes('reconsent') || String(c.status || '').toLowerCase().includes('supersed'));
                      const isUnderProcess = isReconsentRow && isTemplateUnderProcess(c);
                      const reconsentAlreadySubmitted = Boolean(isReconsentRow && c?.id != null && reconsentSubmittedIds.has(String(c.id)));
                      const statusBadgeLabel = reconsentAlreadySubmitted ? 'Reconsent Sent' : (isUnderProcess ? 'Reconsent (Under Process)' : statusConfig.label);

                      return (
                        <tr key={c.id}>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              aria-label="Select row"
                              checked={selectedIds && selectedIds.includes && selectedIds.includes(c.id)}
                              disabled={rowAutoRejected || rowDeclined}
                              onChange={(e) => { e.stopPropagation && e.stopPropagation(); if (!(rowAutoRejected || rowDeclined)) toggleSelect(c.id); }}
                            />
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 500, color: '#64748b' }}>
                            {(currentPage - 1) * pageSize + index + 1}
                          </td>

                          {/* Actions */}
                          <td style={{ textAlign: 'center' }}>
                            {isReconsentRow ? (
                              isUnderProcess ? (
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  disabled={true}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    Swal.fire({
                                      icon: 'warning',
                                      title: 'Cannot Proceed With Re-consent',
                                      text: `The consent template for "${getConsentTypeLabel(c)}" is currently Under Process and awaiting manager acceptance. Re-consent is only permitted after acceptance.`,
                                      confirmButtonColor: '#2563eb'
                                    });
                                  }}
                                  style={{
                                    padding: '4px 10px',
                                    borderRadius: 6,
                                    background: '#f1f5f9',
                                    color: '#94a3b8',
                                    border: '1px solid #e2e8f0',
                                    fontWeight: 600,
                                    fontSize: 12,
                                    cursor: 'not-allowed',
                                    opacity: 0.6,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4
                                  }}
                                  title="Consent version is Under Process and awaiting manager acceptance"
                                >
                                  🔁 Reconsent
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  disabled={reconsentAlreadySubmitted}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!reconsentAlreadySubmitted) {
                                      handleReconsentStatusClick(c);
                                    }
                                  }}
                                  style={{
                                    padding: '4px 10px',
                                    borderRadius: 6,
                                    background: reconsentAlreadySubmitted ? '#f1f5f9' : '#fef3c7',
                                    color: reconsentAlreadySubmitted ? '#94a3b8' : '#92400e',
                                    border: `1px solid ${reconsentAlreadySubmitted ? '#e2e8f0' : '#fde68a'}`,
                                    fontWeight: 600,
                                    fontSize: 12,
                                    cursor: reconsentAlreadySubmitted ? 'not-allowed' : 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4
                                  }}
                                  title={reconsentAlreadySubmitted ? 'Reconsent already submitted' : 'Click to re-consent'}
                                >
                                  🔁 Reconsent
                                </button>
                              )
                            ) : rowAutoRejected ? (
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={(e) => { e.stopPropagation(); handleAutoDeclinedClick(c); }}
                                style={{
                                  padding: '4px 10px',
                                  borderRadius: 6,
                                  background: '#fff1f2',
                                  color: '#991b1b',
                                  border: '1px solid #fecdd3',
                                  fontWeight: 600,
                                  fontSize: 12,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4
                                }}
                                title="Click to view reason why this consent was auto-declined"
                              >
                                <span>Auto-declined</span>
                                <span style={{ fontSize: 11, background: '#fee2e2', borderRadius: '50%', width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>ℹ</span>
                              </button>
                            ) : rowDeclined ? (
                              <span style={{ color: '#b91c1c', fontWeight: 'bold', fontSize: '13px' }}>Declined</span>
                            ) : (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative' }}>
                                <button
                                  className="modern-icon-btn file-icon-btn"
                                  title={rowAccepted ? 'Accepted records cannot be edited' : 'Edit'}
                                  disabled={rowAccepted}
                                  onClick={(e) => { e.stopPropagation(); if (!rowAccepted) handleEdit(c); }}
                                  style={{ padding: 5, background: '#f1f5f9', border: 'none', borderRadius: 6, opacity: rowAccepted ? 0.45 : 1, cursor: rowAccepted ? 'not-allowed' : 'pointer' }}
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="2">
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
                                    <path d="M20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                                  </svg>
                                </button>

                                <button
                                  className="modern-icon-btn more-icon-btn"
                                  title="More options"
                                  onClick={(e) => { e.stopPropagation(); setMoreMenuOpenId(prev => prev === c.id ? null : c.id); }}
                                  style={{ padding: 5, background: '#f1f5f9', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#334155">
                                    <circle cx="12" cy="5" r="2" />
                                    <circle cx="12" cy="12" r="2" />
                                    <circle cx="12" cy="19" r="2" />
                                  </svg>
                                </button>

                                <button
                                  className="modern-icon-btn revoke-icon-btn"
                                  title={revokeRequestedIds.has(String(c.id)) ? 'Revoke request already raised' : 'Revoke'}
                                  disabled={revokeRequestedIds.has(String(c.id))}
                                  onClick={(e) => { e.stopPropagation(); handleRevoke(c); }}
                                  style={{ padding: 5, background: '#fee2e2', border: 'none', borderRadius: 6, opacity: revokeRequestedIds.has(String(c.id)) ? 0.4 : 1, cursor: revokeRequestedIds.has(String(c.id)) ? 'not-allowed' : 'pointer' }}
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="15" y1="9" x2="9" y2="15"></line>
                                    <line x1="9" y1="9" x2="15" y2="15"></line>
                                  </svg>
                                </button>

                                {moreMenuOpenId === c.id && (
                                  <div style={{ position: 'absolute', [openUpward ? 'bottom' : 'top']: 34, right: 0, background: '#ffffff', border: '1px solid #cbd5e1', boxShadow: '0 12px 28px -5px rgba(0,0,0,0.18), 0 8px 10px -6px rgba(0,0,0,0.1)', padding: '6px', borderRadius: 8, zIndex: 1200, display: 'flex', flexDirection: 'row', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                                    <button
                                      title="Send WhatsApp"
                                      onClick={(e) => { e.stopPropagation(); handleMoreAction('whatsapp', c); }}
                                      style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, cursor: 'pointer', width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                    >
                                      <svg width="16" height="16" fill="#15803d" viewBox="0 0 16 16">
                                        <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                                      </svg>
                                    </button>
                                    <button
                                      title={rowAccepted ? 'Accepted records cannot receive reminder emails' : (rowAutoRejected ? 'Record is auto-declined (3 reminders reached)' : (rowDeclined ? 'Declined records cannot receive reminder emails' : 'Send Email'))}
                                      onClick={(e) => { e.stopPropagation(); if (!rowAccepted && !rowAutoRejected && !rowDeclined) handleMoreAction('mail', c); }}
                                      disabled={rowAccepted || rowAutoRejected || rowDeclined}
                                      style={{ background: (rowAccepted || rowAutoRejected || rowDeclined) ? '#f8fafc' : '#eff6ff', color: (rowAccepted || rowAutoRejected || rowDeclined) ? '#94a3b8' : '#1d4ed8', border: (rowAccepted || rowAutoRejected || rowDeclined) ? '1px solid #e2e8f0' : '1px solid #bfdbfe', borderRadius: 6, cursor: (rowAccepted || rowAutoRejected || rowDeclined) ? 'not-allowed' : 'pointer', width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, opacity: (rowAccepted || rowAutoRejected || rowDeclined) ? 0.5 : 1 }}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={(rowAccepted || rowAutoRejected || rowDeclined) ? '#94a3b8' : '#1d4ed8'} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                                        <path d="M22 6l-10 7L2 6"></path>
                                      </svg>
                                    </button>
                                    <button
                                      title="Send Privacy Notice"
                                      onClick={(e) => { e.stopPropagation(); handleMoreAction('privacy', c); }}
                                      style={{ background: '#f3e8ff', color: '#6b21a8', border: '1px solid #e9d5ff', borderRadius: 6, cursor: 'pointer', width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b21a8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                        <line x1="16" y1="13" x2="8" y2="13"></line>
                                        <line x1="16" y1="17" x2="8" y2="17"></line>
                                        <polyline points="10 9 9 9 8 9"></polyline>
                                      </svg>
                                    </button>
                                    <button
                                      title="View Consent Activity Logs"
                                      onClick={(e) => { e.stopPropagation(); handleMoreAction('logs', c); }}
                                      style={{ background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', borderRadius: 6, cursor: 'pointer', width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                                        <line x1="9" y1="12" x2="15" y2="12"></line>
                                        <line x1="9" y1="16" x2="15" y2="16"></line>
                                      </svg>
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>

                          {/* Created At */}
                          <td style={{ fontSize: 12.5, color: '#64748b', whiteSpace: 'nowrap' }}>
                            {formatDateTimeCell(c.createdAt || c.created_at || c.created || null, c.last_email_sent_at || c.reminder_cycle_start || c.response_at || null)}
                          </td>

                          {/* Type pastel badge */}
                          <td style={{ maxWidth: 180, minWidth: 120 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: '100%', minWidth: 0 }}>
                              <span
                                title={getConsentTypeLabel(c)}
                                className="type-badge-pastel"
                                style={{
                                  background: typeStyle.bg,
                                  color: typeStyle.color,
                                  border: `1px solid ${typeStyle.border}`,
                                  maxWidth: 120,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: '1 1 auto',
                                  minWidth: 0
                                }}
                              >
                                {getConsentTypeLabel(c)}
                              </span>
                              <button
                                type="button"
                                title={`View full consent type: ${getConsentTypeLabel(c)}`}
                                onClick={(e) => { e.stopPropagation(); setDetailsConsent(normalizeRecord(c)); setDetailsCopied(false); setConsentScrollPct(0); setIsDetailsOpen(true); openDetails(c); }}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, color: '#475569', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                            </div>
                          </td>

                          {/* Name Cell: Role icon + Name + Quick action icons (Eye details) + DOB + Email */}
                          <td style={{ textAlign: 'left' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                {renderDataSubjectIcon(c)}

                                <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 13, whiteSpace: 'nowrap' }}>
                                  {String(c.username || c.Name || c.name || 'N/A').replace(/\s+/g, ' ').trim()}
                                </span>

                                {(c.isMinor || getDataSubjectCategory(c) === 'child') ? (
                                  <span style={{ fontSize: 10, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', padding: '1px 6px', borderRadius: 9999, fontWeight: 600 }}>
                                    Minor
                                  </span>
                                ) : (
                                  <span style={{ fontSize: 10, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', padding: '1px 6px', borderRadius: 9999, fontWeight: 500 }}>
                                    Adult
                                  </span>
                                )}

                                <button
                                  type="button"
                                  className="tour-target-details-btn"
                                  title="View User Details"
                                  onClick={(e) => { e.stopPropagation(); setDetailsConsent(normalizeRecord(c)); setDetailsCopied(false); setConsentScrollPct(0); setIsDetailsOpen(true); openDetails(c); }}
                                  style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', padding: '2px 4px', color: '#475569', display: 'inline-flex', alignItems: 'center' }}
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                </button>
                              </div>

                              {(c.dob || c.date_of_birth || c.dateOfBirth) && (
                                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, paddingLeft: 22, whiteSpace: 'nowrap' }}>
                                  DOB: {maskDOB(c.dob || c.date_of_birth || c.dateOfBirth)}
                                </div>
                              )}
                              {(c.email || c.responder_email || c.user_email) && (
                                <div style={{ fontSize: 11, color: '#64748b', paddingLeft: 22, maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={maskEmail(c.email || c.responder_email || c.user_email)}>
                                  {maskEmail(c.email || c.responder_email || c.user_email)}
                                </div>
                              )}
                              {(c.phone || c.mobile || c.phone_number || c.user_phone) && (
                                <div style={{ fontSize: 11, color: '#64748b', paddingLeft: 22, maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={maskPhone(c.phone || c.mobile || c.phone_number || c.user_phone)}>
                                  {maskPhone(c.phone || c.mobile || c.phone_number || c.user_phone)}
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Consent / Purpose */}
                          <td style={{ textAlign: 'left' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                              </svg>
                              <span
                                title="Click to view full consent message"
                                onClick={() => openComments(c)}
                                style={{
                                  fontWeight: 500,
                                  color: '#334155',
                                  fontSize: 13,
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {getConsent3Words(c.consent || c.data)}
                              </span>
                              <button
                                className="tour-target-view-consent-btn"
                                type="button"
                                title="View full consent"
                                onClick={(e) => { e.stopPropagation(); openComments(c); }}
                                style={{
                                  background: '#eff6ff',
                                  border: '1px solid #bfdbfe',
                                  borderRadius: 5,
                                  cursor: 'pointer',
                                  padding: '2px 5px',
                                  color: '#2563eb',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                            </div>
                          </td>

                          {/* Status with dot */}
                          <td style={{ textAlign: 'center' }}>
                            {isUnderProcess && !reconsentAlreadySubmitted ? (
                              <span
                                className="status-badge-dot"
                                style={{
                                  background: '#fef3c7',
                                  color: '#92400e',
                                  border: '1px solid #fde68a',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  lineHeight: 1.25
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  Swal.fire({
                                    icon: 'info',
                                    title: 'Consent Version Under Process',
                                    text: `The consent template for "${getConsentTypeLabel(c)}" is currently Under Process and awaiting manager acceptance. Re-consent will be enabled once it is Accepted and locked.`,
                                    confirmButtonColor: '#2563eb'
                                  });
                                }}
                                title="Consent version is Under Process and awaiting manager acceptance"
                              >
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600, fontSize: 12 }}>
                                  <span className="dot" style={{ background: '#f59e0b', width: 6, height: 6, borderRadius: '50%', flexShrink: 0 }} />
                                  <span>Reconsent</span>
                                </div>
                                <span style={{ fontSize: '10.5px', fontWeight: 600, color: '#b45309', opacity: 0.95 }}>
                                  (Under Process)
                                </span>
                              </span>
                            ) : (
                              <span
                                className="status-badge-dot"
                                style={{
                                  background: statusConfig.bg,
                                  color: statusConfig.color,
                                  border: undefined,
                                  cursor: (rowAutoRejected || statusConfig.label === 'Auto-declined') ? 'pointer' : (reconsentAlreadySubmitted ? 'default' : ((statusConfig.label === 'Reconsent' || String(c.status || '').toLowerCase().includes('suspend') || String(c.status || '').toLowerCase().includes('reconsent')) ? 'pointer' : 'default'))
                                }}
                                onClick={(e) => {
                                  const s = String(c.status || '').toLowerCase();
                                  if (rowAutoRejected || statusConfig.label === 'Auto-declined') {
                                    e.stopPropagation();
                                    handleAutoDeclinedClick(c);
                                    return;
                                  }
                                  if (reconsentAlreadySubmitted) {
                                    e.stopPropagation();
                                    return;
                                  }
                                  if (statusConfig.label === 'Reconsent' || s.includes('suspend') || s.includes('reconsent') || s.includes('supersed')) {
                                    e.stopPropagation();
                                    handleReconsentStatusClick(c);
                                  }
                                }}
                                title={(rowAutoRejected || statusConfig.label === 'Auto-declined') ? 'Click to view auto-decline reason' : (reconsentAlreadySubmitted ? 'Reconsent already submitted' : (statusConfig.label === 'Reconsent' ? 'Click to view reason' : statusConfig.label))}
                              >
                                <span className="dot" style={{ background: statusConfig.dotColor }} />
                                {statusBadgeLabel}
                              </span>
                            )}
                          </td>

                          {/* Reminder */}
                          <td style={{ textAlign: 'center', color: '#64748b', fontSize: 12.5 }}>
                            {(() => {
                              try {
                                const st = (c.status || c.consent_status || c.state || '') || '';
                                const v = String(st).toLowerCase();
                                const hasAccept = (v && (v.includes('accept') || v === 'active' || v === 'approved'));
                                const hasDecline = (v && v.includes('declin'));
                                const hasReject = (v && v.includes('reject'));
                                const hasRevoked = (v && v.includes('revok'));
                                const hasReconsent = (statusConfig.label === 'Reconsent' || (v && (v.includes('suspend') || v.includes('reconsent') || v.includes('supersed'))));
                                const isAutoDec = rowAutoRejected || (statusConfig && statusConfig.label === 'Auto-declined') || (v && (v.includes('auto') || v.includes('auto_decline') || v.includes('auto-decline') || v.includes('auto_reject')));
                                if (hasAccept || hasDecline || hasReject || hasRevoked || hasReconsent || isAutoDec) return '—';
                                const baselineStr = c.reminder_cycle_start || c.last_email_sent_at || c.createdAt || c.created_at || null;
                                if (!baselineStr) return '—';
                                const d = new Date(baselineStr);
                                if (isNaN(d.getTime())) return '—';
                                let days = Math.floor((Date.now() - d.getTime()) / 86400000);
                                days = Math.max(0, days);
                                const display = Math.min(days, 2);
                                return `In ${display} day${display === 1 ? '' : 's'}`;
                              } catch (e) { return '—'; }
                            })()}
                          </td>

                          {/* Expiry */}
                          <td style={{ fontWeight: 500, color: '#334155' }}>
                            {renderExpiryCell(c)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* 5. Pagination Footer */}
            <div className="table-pagination-footer tour-target-pagination-bar">
              <div style={{ fontSize: 13.5, color: '#64748b', fontWeight: 500 }}>
                Showing {totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
              </div>

              <div className="pagination-pages">
                <button
                  className="page-btn"
                  type="button"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  ‹ Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum = i + 1;
                  if (totalPages > 5) {
                    if (currentPage > 3 && currentPage < totalPages - 1) {
                      pageNum = currentPage - 2 + i;
                    } else if (currentPage >= totalPages - 1) {
                      pageNum = totalPages - 4 + i;
                    }
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                      type="button"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  className="page-btn"
                  type="button"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Next ›
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consent Details Modal */}
      {isDetailsOpen && detailsConsent && (() => {
        const encId = detailsConsent.encrypted_id || detailsConsent.public_id || (detailsConsent.id ? `CNS-${String(detailsConsent.id).padStart(8, '0')}` : '—');
        const isAutoDec = isAutoRejectedRecord(detailsConsent);
        const isDec = !isAutoDec && isDeclinedRecord(detailsConsent);
        const isAcc = !isAutoDec && !isDec && isAcceptedRecord(detailsConsent);

        const statusLabel = isAutoDec ? 'Auto-Declined' : isDec ? 'Declined' : isAcc ? 'Accepted' : 'Pending';
        const statusConfig = isAutoDec || isDec ? {
          bg: '#fef2f2',
          border: '#fecaca',
          text: '#991b1b',
          dot: '#dc2626'
        } : isAcc ? {
          bg: '#f0fdf4',
          border: '#bbf7d0',
          text: '#166534',
          dot: '#16a34a'
        } : {
          bg: '#fffbeb',
          border: '#fde68a',
          text: '#b45309',
          dot: '#d97706'
        };

        const personName = String(detailsConsent.username || detailsConsent.Name || detailsConsent.name || 'User').replace(/\s+/g, ' ').trim();
        const getInitials = (str) => {
          if (!str) return 'U';
          const parts = str.split(/\s+/).filter(Boolean);
          if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
          return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        };

        const slots = getReminderSlots(detailsConsent || {});
        const reminderCount = Number(detailsConsent?.reminder_count !== undefined && detailsConsent?.reminder_count !== null ? detailsConsent.reminder_count : (detailsConsent?.remind_count !== undefined && detailsConsent?.remind_count !== null ? detailsConsent.remind_count : (slots?.count || 0)));

        const consentTypeLabel = getConsentTypeLabel(detailsConsent) || detailsConsent.type || detailsConsent.consent_type || 'Data Setup';

        // Created at parsing
        const createdRaw = detailsConsent.createdAt || detailsConsent.created_at || null;
        let createdDateStr = '—';
        let createdTimeStr = '—';
        if (createdRaw) {
          try {
            const dt = parseAsUTC(createdRaw) || new Date(createdRaw);
            if (dt && !Number.isNaN(dt.getTime())) {
              createdDateStr = new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }).format(dt);
              createdTimeStr = new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).format(dt);
            }
          } catch (e) {
            createdDateStr = String(createdRaw);
          }
        }

        // Expiry calculation
        const expiryDaysRaw = computeExpiryDays(detailsConsent);
        let expiryMain = '—';
        if (expiryDaysRaw !== '' && expiryDaysRaw !== null && expiryDaysRaw !== undefined && !Number.isNaN(Number(expiryDaysRaw))) {
          const n = Number(expiryDaysRaw);
          expiryMain = formatExpiryValue(n);
        } else if (isAutoDec) {
          expiryMain = 'No response';
        }

        // Data types formatted consistently
        const rawDataTypes = detailsConsent.data_type_consent || detailsConsent.dataTypeConsent || detailsConsent.Data_Type_consent;
        let dataTypesList = [];
        let dataTypesFull = 'Personal data';
        if (rawDataTypes) {
          const list = (Array.isArray(rawDataTypes) ? rawDataTypes : String(rawDataTypes).split(',')).map(s => s.trim()).filter(Boolean);
          if (list.length > 0) {
            dataTypesList = list.map(val => {
              const match = effectiveDataTypeConsents.find(o =>
                o.value === val || o.key === val || o.label === val ||
                (typeof normalizeDataTypeKey === 'function' && normalizeDataTypeKey(o.key || o.value) === normalizeDataTypeKey(val))
              );
              const name = match && match.label ? match.label : String(val);
              return name
                .replace(/[_-]+/g, ' ')
                .split(' ')
                .filter(Boolean)
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');
            });
            dataTypesFull = dataTypesList.join(', ');
          }
        }

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.55)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: 20
            }}
            onClick={() => { setIsDetailsOpen(false); setDetailsCopied(false); setConsentScrollPct(0); }}
          >
            <div
              style={{
                background: '#ffffff',
                borderRadius: 14,
                boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
                width: '100%',
                maxWidth: 820,
                maxHeight: '92vh',
                overflowY: 'auto',
                padding: '24px 28px',
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                border: '1px solid #e2e8f0'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: '#eff6ff', border: '1.5px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                    </svg>
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>Consent Details</h3>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>ID:</span>
                      <span style={{ fontWeight: 600, color: '#0f172a', fontFamily: 'monospace' }}>{encId}</span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Status Badge with dot */}
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '5px 12px',
                    borderRadius: 9999,
                    background: statusConfig.bg,
                    border: `1px solid ${statusConfig.border}`,
                    color: statusConfig.text,
                    fontSize: 12.5,
                    fontWeight: 600
                  }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusConfig.dot }} />
                    <span>{statusLabel}</span>
                  </div>

                  {/* Close button */}
                  <button
                    type="button"
                    onClick={() => { setIsDetailsOpen(false); setDetailsCopied(false); setConsentScrollPct(0); }}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      color: '#64748b',
                      cursor: 'pointer',
                      fontSize: 15,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease'
                    }}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Auto-declined Warning Banner if applicable */}
              {isAutoDec && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>⚠️</span>
                    <div style={{ fontSize: 13, color: '#991b1b', lineHeight: 1.5 }}>
                      <strong>Auto-Declined by System Policy:</strong> 3 reminder notifications were sent to the data subject with no response within the designated period.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => handleAutoDeclinedClick(detailsConsent)}
                    style={{ background: '#991b1b', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    View Details
                  </button>
                </div>
              )}

              {/* Section 1: User Information */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                  </svg>
                  <span>User information</span>
                </div>

                <div style={{
                  background: '#ffffff',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: 12,
                  padding: '18px 20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 22,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                }}>
                  {/* Avatar with Initials */}
                  <div style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: '#eff6ff',
                    border: '1.5px solid #bfdbfe',
                    color: '#1d4ed8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    flexShrink: 0
                  }}>
                    {getInitials(personName)}
                  </div>

                  {/* 3 Columns matching Image 2 layout (without ID Proof Type) */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px 24px',
                    flex: 1,
                    fontSize: 13
                  }}>
                    {/* Column 1: Name, D.O.B, Phone */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div>
                        <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>Name</div>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>{personName}</span>
                          {(() => {
                            const cat = getDataSubjectCategory(detailsConsent);
                            const styles = {
                              child: { bg: '#fee2e2', text: '#dc2626' },
                              parent: { bg: '#dbeafe', text: '#1d4ed8' },
                              adult: { bg: '#fef3c7', text: '#d97706' }
                            }[cat] || { bg: '#f1f5f9', text: '#475569' };
                            return (
                              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: styles.bg, color: styles.text, textTransform: 'capitalize' }}>
                                {cat}
                              </span>
                            );
                          })()}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>D.O.B</div>
                        <div
                          style={{ fontWeight: 600, color: '#0f172a', marginTop: 2 }}
                          title={maskDOB(detailsConsent.dob || detailsConsent.date_of_birth)}
                        >
                          {(() => {
                            const rawDob = detailsConsent.dob || detailsConsent.date_of_birth;
                            if (!rawDob) return '—';
                            try {
                              const d = new Date(rawDob);
                              if (!Number.isNaN(d.getTime())) {
                                return maskDOB(new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }).format(d));
                              }
                            } catch (e) { }
                            return maskDOB(rawDob);
                          })()}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>Phone</div>
                        <div
                          style={{ fontWeight: 600, color: '#0f172a', marginTop: 2 }}
                          title={!isRevoked(detailsConsent, 'phone') && (detailsConsent.phone || detailsConsent.mobile) ? maskPhone(detailsConsent.phone || detailsConsent.mobile) : '—'}
                        >
                          {!isRevoked(detailsConsent, 'phone') && (detailsConsent.phone || detailsConsent.mobile) ? maskPhone(detailsConsent.phone || detailsConsent.mobile) : '—'}
                        </div>
                      </div>
                    </div>

                    {/* Column 2: Language, Email, Response at */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div>
                        <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>Language</div>
                        <div style={{ fontWeight: 600, color: '#0f172a', marginTop: 2 }}>
                          {getLanguageDisplayName(detailsConsent.preferredLanguages || detailsConsent.language || detailsConsent.preferred_languages || detailsConsent.consent_language || 'en')}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>Email</div>
                        <div
                          style={{ fontWeight: 600, color: '#0f172a', marginTop: 2, wordBreak: 'break-all' }}
                          title={!isRevoked(detailsConsent, 'email') && detailsConsent.email ? maskEmail(detailsConsent.email) : '—'}
                        >
                          {!isRevoked(detailsConsent, 'email') && detailsConsent.email ? maskEmail(detailsConsent.email) : '—'}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>Response at</div>
                        <div style={{ fontWeight: 600, color: '#0f172a', marginTop: 2 }}>
                          {getResponseDateFromRecord(detailsConsent)}
                        </div>
                      </div>
                    </div>

                    {/* Column 3: Email response (and Guardian if applicable) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div>
                        <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>Email response</div>
                        <div style={{ fontWeight: 700, color: statusConfig.text, marginTop: 2 }}>
                          {statusLabel}
                        </div>
                      </div>

                      {(detailsConsent.parent_name || detailsConsent.parentName || detailsConsent.parent_email || detailsConsent.parentEmail) && (
                        <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Parent / Guardian</div>
                            <div style={{ fontWeight: 600, color: '#0f172a', marginTop: 1 }}>{detailsConsent.parent_name || detailsConsent.parentName || '—'}</div>
                          </div>
                          {(detailsConsent.parent_email || detailsConsent.parentEmail) && (
                            <div>
                              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Parent Email</div>
                              <div style={{ fontWeight: 600, color: '#0f172a', marginTop: 1, wordBreak: 'break-all' }}>
                                {maskEmail(detailsConsent.parent_email || detailsConsent.parentEmail)}
                              </div>
                            </div>
                          )}
                          {(detailsConsent.parent_phone || detailsConsent.parentPhone) && (
                            <div>
                              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Parent Phone</div>
                              <div style={{ fontWeight: 600, color: '#0f172a', marginTop: 1 }}>
                                {maskPhone(detailsConsent.parent_phone || detailsConsent.parentPhone)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Consent Summary */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                  </svg>
                  <span>Consent summary</span>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 12
                }}>
                  {/* 1. Reminders Card */}
                  <div style={{
                    background: '#ffffff',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 12,
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 105,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 12, fontWeight: 600 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                      </svg>
                      <span>Reminders</span>
                    </div>

                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '4px 0 8px 0' }}>
                      {reminderCount} sent
                    </div>

                    {/* 3-Step Timeline Track */}
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <div
                        title={`1st: ${slots.first}`}
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: reminderCount >= 1 ? '#2563eb' : '#f1f5f9',
                          color: reminderCount >= 1 ? '#ffffff' : '#94a3b8',
                          border: `1.5px solid ${reminderCount >= 1 ? '#2563eb' : '#cbd5e1'}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 700
                        }}
                      >1</div>
                      <div style={{ flex: 1, height: 2, background: reminderCount >= 2 ? '#2563eb' : '#e2e8f0' }} />
                      <div
                        title={`2nd: ${slots.second}`}
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: reminderCount >= 2 ? '#2563eb' : '#f1f5f9',
                          color: reminderCount >= 2 ? '#ffffff' : '#94a3b8',
                          border: `1.5px solid ${reminderCount >= 2 ? '#2563eb' : '#cbd5e1'}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 700
                        }}
                      >2</div>
                      <div style={{ flex: 1, height: 2, background: reminderCount >= 3 ? '#2563eb' : '#e2e8f0' }} />
                      <div
                        title={`3rd: ${slots.third}`}
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: reminderCount >= 3 ? (isAutoDec ? '#dc2626' : '#2563eb') : '#f1f5f9',
                          color: reminderCount >= 3 ? '#ffffff' : '#94a3b8',
                          border: `1.5px solid ${reminderCount >= 3 ? (isAutoDec ? '#dc2626' : '#2563eb') : '#cbd5e1'}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 700
                        }}
                      >3</div>
                    </div>
                  </div>

                  {/* 2. Consent Type Card */}
                  <div style={{
                    background: '#ffffff',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 12,
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 105,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 12, fontWeight: 600 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                      </svg>
                      <span>Consent type</span>
                    </div>

                    <div style={{ fontSize: 15, fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: '4px 0' }} title={consentTypeLabel}>
                      {consentTypeLabel}
                    </div>

                    {/* <div style={{ fontSize: 12, color: '#64748b' }}>
                      {isAutoDec ? 'System auto-declined' : 'Active template'}
                    </div> */}
                  </div>

                  {/* 3. Data Type Card */}
                  <div style={{
                    background: '#ffffff',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 12,
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 105,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 12, fontWeight: 600 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
                        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
                        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
                      </svg>
                      <span>Data type</span>
                    </div>

                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#0f172a',
                        margin: '4px 0',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                      title={dataTypesFull}
                    >
                      {dataTypesFull}
                    </div>

                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {dataTypesList.length > 0 ? `${dataTypesList.length} ${dataTypesList.length === 1 ? 'type' : 'types'} selected` : 'Standard identifiers'}
                    </div>
                  </div>

                  {/* 4. Version Card */}
                  <div style={{
                    background: '#ffffff',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 12,
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 105,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 12, fontWeight: 600 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="4" y1="9" x2="20" y2="9"></line>
                        <line x1="4" y1="15" x2="20" y2="15"></line>
                        <line x1="10" y1="3" x2="8" y2="21"></line>
                        <line x1="16" y1="3" x2="14" y2="21"></line>
                      </svg>
                      <span>Version</span>
                    </div>

                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '4px 0' }}>
                      {detailsConsent.version || detailsConsent.consent_version || '1.0.2'}
                    </div>

                    {/* <div style={{ fontSize: 12, color: '#64748b' }}>
                      Latest
                    </div> */}
                  </div>

                  {/* 5. Created At Card */}
                  <div style={{
                    background: '#ffffff',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 12,
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 105,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 12, fontWeight: 600 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <span>Created at</span>
                    </div>

                    <div style={{ margin: '4px 0' }}>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a' }}>
                        {createdDateStr}
                      </div>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>
                        {createdTimeStr}
                      </div>
                    </div>

                    {/* <div style={{ fontSize: 12, color: '#64748b' }}>
                      Timestamp
                    </div> */}
                  </div>

                  {/* 6. Expiry Card */}
                  <div style={{
                    background: '#ffffff',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 12,
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 105,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 12, fontWeight: 600 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      <span>Expiry</span>
                    </div>

                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '4px 0' }}>
                      {expiryMain}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3: Full Consent / Data */}
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
                  Full consent
                </div>

                <div style={{
                  background: '#ffffff',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: 12,
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                }}>
                  {/* Top Header of Consent Text */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 16px',
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Consent text</span>

                  </div>

                  {/* Scrollable Content */}
                  <div
                    onScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                      const total = scrollHeight - clientHeight;
                      if (total > 0) {
                        const pct = Math.min(100, Math.max(0, Math.round((scrollTop / total) * 100)));
                        setConsentScrollPct(pct);
                      } else {
                        setConsentScrollPct(100);
                      }
                    }}
                    style={{
                      maxHeight: 220,
                      overflowY: 'auto',
                      padding: '16px 18px',
                      fontSize: 13.5,
                      color: '#334155',
                      lineHeight: 1.7,
                      fontFamily: 'inherit',
                      background: '#ffffff',
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#cbd5e1 transparent'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: (detailsConsent.consent || detailsConsent.data || detailsConsent.description || '').trim() ||
                        ('HCL Consent Receipt<br/>-----------------------------------<br/>Consent Ref: ' + encId + '<br/>Email(s): ' + (maskEmail(detailsConsent.email) || 'N/A') + '<br/>Version: 1.0.0<br/>Data Retention Period: 7 Years<br/>Mandatory Purposes:<br/> - Identity Verification<br/> - Account Administration')
                    }}
                  />

                  {/* Footer with scroll reading progress
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 16px',
                    background: '#f8fafc',
                    borderTop: '1px solid #e2e8f0',
                    fontSize: 12,
                    color: '#64748b'
                  }}> */}
                  {/* </div> */}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Hover tooltip */}
      {tooltip.visible && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            padding: '6px 8px',
            borderRadius: 6,
            fontSize: 12,
            pointerEvents: 'none',
            zIndex: 2000,
            transform: 'translate(0, 0)'
          }}
        >{tooltip.text}</div>
      )}

      {/* Create Consent Drawer */}
      <div
        className={`drawer-overlay ${isSlidingPageOpen ? 'open' : ''}`}
        onClick={closeSlidingPage}
      />

      <aside className={`drawer ${isSlidingPageOpen ? 'open' : ''}`}>
        {/* <div className="drawer-content">
          <h3> User Details Form </h3> */}
        <div className="drawer-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: 0 }}>Record Consent</h3>
            {/* <div style={{fontSize:12,color:'#f00a44ff'}}>Capture consent details — include parent info for minors</div> */}
          </div>
          <button className="drawer-close" onClick={closeSlidingPage}>
            ✖
          </button>
        </div>

        <div className="drawer-form">
          {saved && (
            <div className="success-message">
              User details saved successfully!
            </div>
          )}

          <label>
            <span>Name <span className="required">*</span></span>
            <input
              ref={firstInputRef}
              type="text"
              name="username"
              value={userForm.username}
              onChange={handleFormChange}
              placeholder="Enter Name"
              required
            />
          </label>

          {/* Adult / Minor Category Radio Buttons */}
          <div style={{ marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
              Category <span className="required">*</span>
            </span>
            <div style={{ display: 'flex', gap: 24, padding: '9px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', margin: 0 }}>
                <input
                  type="radio"
                  name="ageCategory"
                  value="adult"
                  checked={userForm.ageCategory === 'adult' || !userForm.ageCategory}
                  onChange={() => handleAgeCategoryChange('adult')}
                  style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                />
                <span style={{ fontSize: 13.5, fontWeight: 600, color: (userForm.ageCategory === 'adult' || !userForm.ageCategory) ? '#2563eb' : '#475569' }}>
                  Adult
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', margin: 0 }}>
                <input
                  type="radio"
                  name="ageCategory"
                  value="minor"
                  checked={userForm.ageCategory === 'minor'}
                  onChange={() => handleAgeCategoryChange('minor')}
                  style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                />
                <span style={{ fontSize: 13.5, fontWeight: 600, color: userForm.ageCategory === 'minor' ? '#2563eb' : '#475569' }}>
                  Minor (Under 18)
                </span>
              </label>
            </div>
          </div>

          <label>
            <span>
              D.O.B {userForm.ageCategory === 'minor' ? <span className="required">*</span> : <span style={{ fontSize: 11.5, color: '#64748b', fontWeight: 'normal' }}>(Optional)</span>}
            </span>
            <input
              type="date"
              name="dob"
              min="1900-01-01"
              max={new Date().toISOString().slice(0, 10)}
              value={userForm.dob || ''}
              onChange={handleDOBChange}
              required={userForm.ageCategory === 'minor'}
            />
          </label>

          {/* If under 18 / Minor: Show ONLY Parent / Guardian details (hide separate Email & Phone below) */}
          {(userForm.ageCategory === 'minor') ? (
            <div style={{
              margin: '14px 0 16px 0',
              padding: '16px 18px',
              borderRadius: 10,
              background: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              borderLeft: '4px solid #2563eb',
              boxShadow: '0 2px 8px rgba(15,23,42,0.04)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>Parent / Guardian Information</span>
                  <span style={{ fontSize: 11, background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 9999, fontWeight: 600 }}>
                    Minor (Under 18)
                  </span>
                </div>
              </div>
              <p style={{ margin: '0 0 12px 0', fontSize: 12.5, color: '#64748b' }}>
                Minor selected. Parent or Guardian details and date of birth are required.
              </p>

              {/* Relationship selector */}
              <div style={{ marginBottom: 14, padding: '10px 12px', background: '#ffffff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
                  Relationship <span style={{ color: '#dc2626' }}>*</span>
                </div>
                <div style={{ display: 'flex', gap: 24 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="radio"
                      name="_guardianTypeRadio"
                      checked={userForm._guardianType === 'parent' || !userForm._guardianType}
                      onChange={() => setUserForm(prev => ({ ...prev, _guardianType: 'parent' }))}
                      style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1e293b' }}>Parent</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="radio"
                      name="_guardianTypeRadio"
                      checked={userForm._guardianType === 'guardian'}
                      onChange={() => setUserForm(prev => ({ ...prev, _guardianType: 'guardian' }))}
                      style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1e293b' }}>Guardian</span>
                  </label>
                </div>
              </div>

              {/* Parent Name */}
              <label style={{ display: 'block', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
                  {userForm._guardianType === 'guardian' ? 'Guardian' : 'Parent'} Name <span style={{ color: '#dc2626' }}>*</span>
                </span>
                <input
                  type="text"
                  name="parentName"
                  value={userForm.parentName || ''}
                  onChange={handleParentChange}
                  placeholder={`Enter ${userForm._guardianType === 'guardian' ? 'guardian' : 'parent'} name`}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 7, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: 13.5 }}
                  required
                />
              </label>

              {/* Parent Email */}
              <label style={{ display: 'block', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
                  {userForm._guardianType === 'guardian' ? 'Guardian' : 'Parent'} Email <span style={{ color: '#dc2626' }}>*</span>
                </span>
                <input
                  type="email"
                  name="parentEmail"
                  value={userForm.parentEmail || ''}
                  onChange={handleParentChange}
                  placeholder={`Enter ${userForm._guardianType === 'guardian' ? 'guardian' : 'parent'} email`}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 7, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: 13.5 }}
                  required
                />
              </label>

              {/* Parent Phone */}
              <label style={{ display: 'block', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
                  {userForm._guardianType === 'guardian' ? 'Guardian' : 'Parent'} Phone <span style={{ color: '#dc2626' }}>*</span>
                </span>
                <input
                  type="tel"
                  name="parentPhone"
                  value={userForm.parentPhone || ''}
                  onChange={handleParentChange}
                  placeholder="10-digit mobile number"
                  pattern="[6-9][0-9]{9}"
                  maxLength={10}
                  title="10 digits, starts with 6-9"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 7, border: '1px solid #cbd5e1', boxSizing: 'border-box', fontSize: 13.5 }}
                  required
                />
              </label>
            </div>
          ) : (
            /* For 18+ (Above 18), show regular Email and Phone fields */
            <>
              <label>
                <span>Email <span className="required">*</span></span>
                <input
                  type="email"
                  name="email"
                  value={userForm.email || ''}
                  onChange={handleFormChange}
                  placeholder="Enter email address"
                  required
                />
              </label>

              <label>
                <span>Phone Number <span className="required">*</span></span>
                <input
                  type="tel"
                  name="phone"
                  value={userForm.phone || ''}
                  pattern="[6-9][0-9]{9}"
                  maxLength={10}
                  title="10 digits, starts with 6-9"
                  onChange={handleFormChange}
                  placeholder="Enter phone number"
                  required
                />
              </label>
            </>
          )}


          <label>
            <span>Address <span className="required">*</span></span>
            <textarea
              name="address"
              value={userForm.address}
              onChange={handleFormChange}
              rows="3"
              placeholder="Enter address"
              required
            />
          </label>

          <label>
            <span>Location<span className="required">*</span></span>
            <select name="location" value={userForm.location || ''} onChange={handleFormChange}>
              <option value="">-- Select Location --</option>
              {userForm.location && !effectiveLocations.some(loc => String(loc) === String(userForm.location)) && (
                <option value={userForm.location}>{userForm.location}</option>
              )}
              {effectiveLocations.slice().sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </label>

          <label>
            <span>Department<span className="required">*</span></span>
            <select name="department" value={userForm.department || ''} onChange={handleFormChange}>
              <option value="">-- Select Department --</option>
              {userForm.department && !departments.some(d => String(d.name) === String(userForm.department)) && (
                <option value={userForm.department}>{userForm.department}</option>
              )}
              {departments && departments.length ? departments.map(d => (
                <option key={d.id || d.name} value={d.name}>{d.name}</option>
              )) : (
                <option value="">No departments</option>
              )}
            </select>
          </label>
          <label>
            <span>Group <span className="required">*</span></span>
            <select name="entity" value={userForm.entity || ''} onChange={handleFormChange} required>
              {/* <select
              name="entity"
              value={
                (userForm.entity === 'Vendor' || userForm.entity === 'vendor' || userForm.entity === 'Vendor & Supplier')
                  ? 'Vendor & Supplier'
                  : (userForm.entity === 'Partners' || userForm.entity === 'partners' || userForm.entity === 'Partner' || userForm.entity === 'partner')
                    ? 'Partner'
                    : (userForm.entity || '')
              }
              onChange={handleFormChange}
              required
            > */}
              <option value="">-- Select Group --</option>
              <option value="Employee">Employee</option>
              <option value="Vendor & Supplier">Vendor & Supplier</option>
              <option value="Partner">Partner</option>
              <option value="Customer">Customer</option>
            </select>
          </label>

          <label>
            <span>Language <span className="required">*</span></span>
            <select name="language" value={userForm.language || ''} onChange={handleFormChange} required>
              <option value="">{tenantLanguagesLoading ? 'Loading tenant languages…' : '-- Select Language --'}</option>
              {effectiveLanguageOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              {!tenantLanguagesLoading && !effectiveLanguageOptions.length && <option value="" disabled>No consent languages configured for this tenant</option>}
            </select>
          </label>


          <label>
            <span>Consent Type <span className="required">*</span></span>
            <select name="type" value={userForm.type} onChange={handleFormChange} disabled={loadingLanguageTypes}>
              <option value="">-- Select Consent Type --</option>
              {loadingLanguageTypes ? (
                <option value="">Loading…</option>
              ) : (consentTypesByLanguage && consentTypesByLanguage.length > 0 ? (
                // consentTypesByLanguage.map(t => <option key={t.consent_type || t.type || t.id} value={t.consent_type || t.type}>{getTranslatedConsentTypeLabel(t.consent_type || t.type || t.name, userForm.language || 'en',)}</option>)
                consentTypesByLanguage.map(t => {
                  const typeValue = t.consent_type || t.type || t.name || t.id;
                  const typeLabel = t.label || t.title || t.localized_label || t.localizedLabel || typeValue;
                  // return <option key={typeValue} value={typeValue}>{getTranslatedConsentTypeLabel(typeValue, userForm.language || 'en',  {...t, label: typeLabel })}</option>;
                  return <option key={typeValue} value={typeValue}>{getConsentTypeOptionLabel({ ...t, label: typeLabel }, userForm.language || 'en')}</option>;
                })
              ) : (
                <option value="" disabled>No consent types available for this language</option>
              ))}
            </select>
            {loadingLanguageTypes && <span style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Loading language-specific types…</span>}
          </label>

          {showSubConsent && (
            <label>
              <span>Sub Consent</span>
              <select name="subCategory" value={userForm.subCategory || ''} onChange={handleFormChange} required disabled={loadingSubConsents}>
                <option value="">-- Select Sub Consent --</option>
                {loadingSubConsents ? (
                  <option value="">Loading…</option>
                ) : (subConsents && subConsents.length ? subConsents.map(s => (
                  <option key={s.id} value={s.id}>{getTranslatedSubConsentLabel(s, userForm.language || 'en')}</option>
                )) : (
                  <option value="">No sub-consents</option>
                ))}
                {userForm.subCategory && !subConsents.some(s => String(s.id) === String(userForm.subCategory)) && (
                  <option value={userForm.subCategory}>{userForm.subCategoryLabel || userForm.subCategory}</option>
                )}
              </select>
            </label>
          )}

          <label>
            <span>Consent <span className="required">*</span></span>
            {userForm.consent ? (
              <div style={{ background: '#fff', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', minHeight: 80, maxHeight: 240, overflowY: 'auto', marginBottom: 8 }}>
                <div dangerouslySetInnerHTML={{ __html: sanitize(userForm.consent) }} />
              </div>
            ) : null}
            <input type="hidden" name="consent" value={userForm.consent || ''} />
          </label>

          <label>
            <span>Data Type Consent <span className="required">*</span></span>
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  value={''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) return;
                    setUserForm(prev => {
                      const cur = Array.isArray(prev.dataTypeConsent) ? [...prev.dataTypeConsent] : [];
                      if (!cur.includes(val)) cur.push(val);
                      return { ...prev, dataTypeConsent: cur };
                    });
                    try { e.target.selectedIndex = 0; } catch (ex) { }
                  }}
                  style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #e2e8f0' }}
                  aria-label="Add Data Type Consent"
                >
                  <option value="">(Select a data type to add)</option>
                  {effectiveDataTypeConsents.filter(opt => {
                    if (!Array.isArray(userForm.dataTypeConsent)) return true;
                    const optNorm = normalizeDataTypeKey(opt.key || opt.value) || normalizeDataTypeKey(opt.label);
                    return !userForm.dataTypeConsent.some(val => {
                      if (val === opt.value || val === opt.key || (opt.label && val === opt.label)) return true;
                      const valNorm = normalizeDataTypeKey(val);
                      return valNorm && valNorm === optNorm;
                    });
                  }).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label || opt.name || opt.value}</option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: '#64748b' }}>Select one or more</div>
              </div>

              {userForm.dataTypeConsent && userForm.dataTypeConsent.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  {userForm.dataTypeConsent.map((value, idx) => {
                    const match = effectiveDataTypeConsents.find(o => o.value === value || o.key === value || o.label === value || (typeof normalizeDataTypeKey === 'function' && normalizeDataTypeKey(o.key || o.value) === normalizeDataTypeKey(value)));
                    const displayLabel = match ? (match.label || match.name || match.value) : value;
                    return (
                      <div key={value + idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eff6ff', color: '#1e293b', padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, border: '1px solid #bfdbfe' }}>
                        <span>{displayLabel}</span>
                        <button type="button" onClick={() => setUserForm(prev => ({ ...prev, dataTypeConsent: (Array.isArray(prev.dataTypeConsent) ? prev.dataTypeConsent.filter(x => x !== value) : []).filter(Boolean) }))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#475569', fontSize: 14, padding: 0, display: 'flex', alignItems: 'center', marginLeft: 2 }} aria-label={`Remove ${displayLabel}`}>
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </label>

          <div style={{
            display: 'flex',
            gap: 12,
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid #e2e8f0',
            justifyContent: 'flex-end',
            alignItems: 'center',
            position: 'sticky',
            bottom: 0,
            background: '#fff',
            zIndex: 10,
            padding: '16px 0 8px 0',
          }}>
            <button
              type="button"
              onClick={closeSlidingPage}
              style={{
                padding: '10px 24px',
                borderRadius: 8,
                border: '1.5px solid #e2e8f0',
                background: '#f8fafc',
                color: '#475569',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s',
                minWidth: 100,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = '#f8fafc';
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            >
              ✕ Cancel
            </button>

            <button
              className="tour-target-drawer-submit-btn"
              type="button"
              onClick={handleFormSubmit}
              disabled={submitting || (hasSubmitted && !editingId)}
              style={{
                padding: '10px 28px',
                borderRadius: 8,
                border: 'none',
                background: (submitting || (hasSubmitted && !editingId))
                  ? '#93c5fd'
                  : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: (submitting || (hasSubmitted && !editingId)) ? 'not-allowed' : 'pointer',
                opacity: (submitting || (hasSubmitted && !editingId)) ? 0.7 : 1,
                transition: 'all 0.15s',
                minWidth: 120,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
              onMouseEnter={e => {
                if (!submitting && !(hasSubmitted && !editingId)) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37,99,235,0.35)';
                }
              }}
              onMouseLeave={e => {
                if (!submitting && !(hasSubmitted && !editingId)) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {submitting ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="9" fill="none" stroke="#fff" strokeWidth="2" strokeOpacity="0.3" />
                    <path d="M12 3a9 9 0 0 1 9 9" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round">
                      <animateTransform attributeName="transform" type="rotate"
                        from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                    </path>
                  </svg>
                  Submitting...
                </>
              ) : (hasSubmitted && !editingId) ? (
                <>✓ Submitted</>
              ) : editingId ? (
                <>✎ Update</>
              ) : (
                <>✓ Submit</>
              )}
            </button>
          </div>

        </div>
      </aside>

      {/* Comments Modal */}
      {isCommentsOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={closeComments}
          style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, zIndex: 1200 }}
        >
          <div
            className="modal"
            onClick={e => e.stopPropagation()}
            // style={{background:'#fff',borderRadius:10,width:'100%', maxWiddth:920, boxShadow:'0 12px 48px rgba(2,6,23,0.4)',overflow:'hidden',display:'flex',flexDirection:'column'}}
            style={{
              background: '#fff',
              borderRadius: 10,
              width: '100%',
              maxWidth: 760,
              boxShadow: 'rgba(2, 6, 23, 0.4) 0px 12px 48px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: 'auto',
              maxHeight: '80vh',
              margin: '0 12px'
            }}
          >
            {/* <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',borderBottom:'1px solid #eef2ff'}}> */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eef2ff' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Consent Details</div>
                <div style={{ fontSize: 13, color: '#374151', marginTop: 6, display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{commentsRecord?.createdAt ? `Created: ${commentsRecord.createdAt}` : ''}</div>
                  {commentsRecord?.id && (
                    <div style={{ fontSize: 12, color: '#0369a1', background: '#e0f2fe', padding: '1px 8px', borderRadius: 6, fontWeight: 700, fontFamily: 'monospace', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span>🔒 Ref:</span>
                      <span>{commentsRecord.encrypted_id || commentsRecord.public_id || `CNS-${String(commentsRecord.id).padStart(8, '0')}`}</span>
                    </div>
                  )}
                  {(commentsRecord?.username || commentsRecord?.Name) && <div style={{ fontSize: 12, color: '#6b7280' }}>Name: {commentsRecord.username || commentsRecord.Name}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {commentsRecord?.status && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {renderStatusIcon(commentsRecord.status)}
                    <div style={{ fontSize: 12, color: '#7281a3ff', background: '#eef2ff', padding: '4px 8px', borderRadius: 8 }}>{commentsRecord.status}</div>
                  </div>
                )}
                <button onClick={closeComments} aria-label="Close" style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer' }}>✖</button>
              </div>
            </div>

            {/* Auto-declined Banner */}
            {isAutoRejectedRecord(commentsRecord) && (
              <div style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#991b1b' }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  <span><strong>Auto-Declined:</strong> System policy auto-declined this consent after 3 reminder cycles without response.</span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => handleAutoDeclinedClick(commentsRecord)}
                  style={{ background: '#991b1b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  View Reason
                </button>
              </div>
            )}
            {/* <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:20,padding:20,maxHeight:'60vh',overflow:'auto'}}  */}
            {/* <div> */}
            {/* <div style ={{gridTemplateColumns:'1fr 320px',gap:20,padding:16,maxHeight:'calc(80vh - 72px)',overflowY:'auto'}}> */}
            <div style={{ gridTemplateColumns: '1fr 320px', gap: 20, padding: 16, maxHeight: 'calc(80vh - 72px)', overflowY: 'auto' }}>
              <div style={{ overflowY: 'auto', paddingRight: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Full Consent</div>
                {/* <div style={{background:'#f8fafc',padding:14,borderRadius:8,whiteSpace:'pre-wrap',lineHeight:1.5,overflowWrap:'break-word'}}> */}
                <div style={{ padding: 12, borderRadius: 8, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {/* {commentsRecord?.consent || commentsRecord?.data || '—'} */}
                  {(commentsRecord?.consent || commentsRecord?.data) ? (
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: sanitize(commentsRecord?.consent || commentsRecord?.data) }} />
                  ) : (
                    '—'
                  )}
                </div>

              </div>
              {/* <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:12}}>'column',gap:12}}>
                <button type ="button" className="btn" onClick={closeComments}>Close</button> */}

            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {isDetailsOpen && detailsConsent && (
        <div className="modal-overlay" style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={closeDetails}>
          {/* <div className="modal" style={{background:'#ffffff',padding:20,borderRadius:10,maxWidth:760,width:'96%',boxShadow:'0 8px 32px rgba(15,23,42,0.12)'}} onClick={e => e.stopPropagation()}> */}
          <div className="modal" style={{ background: '#ffffff', padding: 20, borderRadius: 10, maxWidth: 760, width: '96%', boxShadow: '0 8px 32px rgba(15,23,42,0.12)', maxHeight: '80vh', overflowY: 'auto', boxSizing: 'border-box' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Consent Details</div>
                <div style={{ fontSize: 12, color: '#0369a1', marginTop: 4, fontFamily: 'monospace', fontWeight: 600 }}>
                  {detailsConsent.id ? `🔒 Ref: ${detailsConsent.encrypted_id || detailsConsent.public_id || `CNS-${String(detailsConsent.id).padStart(8, '0')}`}` : ''}
                </div>
              </div>
              <button onClick={closeDetails} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer' }}>✖</button>
            </div>
            {/* <div style ={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:14,alignItems:'start'}}> */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, alignItems: 'start', maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' }}>
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Name</div>
                    <div style={{ fontWeight: 600 }}>{detailsConsent.username || detailsConsent.Name || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Language</div>
                    <div style={{ fontWeight: 600 }}>{getLanguageDisplayName(detailsConsent.preferredLanguages || detailsConsent.preferred_languages || detailsConsent.languages || detailsConsent.language || 'en')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>D.O.B</div>
                    {/* <div>{detailsConsent.dob || '-'}</div> */}
                    <div>{(!isRevoked(detailsConsent, 'dob') && detailsConsent.dob) ? maskDOB(detailsConsent.dob) : '—'}</div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Email</div>
                    {/* <div>{detailsConsent.email ? (isRevoked(detailsConsent, 'email') : '—'}</div> */}
                    <div>{!isRevoked(detailsConsent, 'email') && detailsConsent.email ? maskEmail(detailsConsent.email) : '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Phone</div>
                    {/* <div>{detailsConsent.phone ? maskPhone(detailsConsent.phone) : '—'}</div> */}
                    <div>{!isRevoked(detailsConsent, 'phone') && detailsConsent.phone ? maskPhone(detailsConsent.phone) : '—'}</div>
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Full Consent </div>
                  <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, whiteSpace: 'pre-wrap', lineHeight: 1.5, color: '#0f172a', border: '1px solid #eef2ff' }}>

                    {((detailsConsent.consent || detailsConsent.data) && sanitize(detailsConsent.consent || detailsConsent.data)) ? (
                      <div dangerouslySetInnerHTML={{ __html: sanitize(detailsConsent.consent || detailsConsent.data) }} />
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Email Response</div>
                    <div style={{ fontWeight: 600 }}>{(() => {
                      const isAuto = isAutoRejectedRecord(detailsConsent);
                      const isD = !isAuto && isDeclinedRecord(detailsConsent);
                      const isA = !isAuto && !isD && isAcceptedRecord(detailsConsent);
                      return isAuto ? 'Auto-Declined' : isD ? 'Declined' : isA ? 'Accepted' : 'Pending';
                    })()}</div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Response At</div>
                    <div style={{ fontWeight: 600 }}>{getResponseDateFromRecord(detailsConsent)}</div>
                  </div>

                  {/* Reminder details: count + per-slot timestamps (1st,2nd,3rd) */}
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Reminders</div>
                    {(function renderReminderSlots() {
                      const slots = getReminderSlots(detailsConsent || {});
                      return (
                        <div style={{ fontSize: 13, color: '#0f172a' }}>
                          <div style={{ fontWeight: 700 }}>Count: {slots.count}</div>
                          <div style={{ marginTop: 6 }}>
                            <div>1st: {slots.first}</div>
                            <div>2nd: {slots.second}</div>
                            <div>3rd: {slots.third}</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Type</div>
                    {/* <div style={{fontWeight:700,color:'#0b74ff'}}>{detailsConsent.type || detailsConsent.consent_type || detailsConsent.template'—'}</div> */}
                    <div style={{ fontWeight: 700, color: '#0b74ff', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', width: '100%', display: 'block' }}>{getConsentTypeLabel(detailsConsent) || '—'}</div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Created At</div>
                    <div>{formatDateTimeCell(detailsConsent.createdAt || detailsConsent.created_at || detailsConsent.created || null, detailsConsent.last_email_sent_at || detailsConsent.reminder_cycle_start || detailsConsent.response_at || null)}</div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Expiry</div>
                    <div>{detailsConsent.expiry || detailsConsent.expiry_date || detailsConsent.expires_at || detailsConsent.expiryDate || '—'}</div>
                  </div>

                  {/* {detailsConsent.parentName && (} */}

                  {((detailsConsent.isMinor) && (detailsConsent.parentName || detailsConsent.parentEmail || detailsConsent.parentPhone)) && (
                    <div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>Parent / Guardian</div>
                      {/* <div>{detailsConsent.parentName} - {detailsConsent.parentEmail || detailsConsent.parentPhone || ' '}</div> */}
                      <div>{detailsConsent.parentName || '—'}{(detailsConsent.parentEmail || detailsConsent.parentPhone) ? ` — ${detailsConsent.parentEmail || detailsConsent.parentPhone}` : ''}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <button onClick={closeDetails} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e6eef8', background: '#fff', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}


      {/* Revoke Consent Modal */}
      <div
        className={`drawer-overlay ${isRevokeModalOpen ? 'open' : ''}`}
        onClick={closeRevokeModal}
      />

      <aside className={`drawer ${isRevokeModalOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h3>🚫 Revoke Consent</h3>
          <button className="drawer-close" onClick={closeRevokeModal}>
            ✖
          </button>
        </div>
        <div className="drawer-form">
          {selectedConsent && (
            <div className="consent-info-card">
              <h4>📋 Consent Details</h4>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Name:</span>
                  <span className="info-value">
                    {selectedConsent.username || selectedConsent.Name || ''}
                    {selectedConsent.parentName && (
                      <div style={{ fontSize: '0.9em', color: '#555' }}>Parent: {selectedConsent.parentName}</div>
                    )}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{maskEmail(selectedConsent.email)}</span>
                </div>
                {(selectedConsent.phone || selectedConsent.mobile) && (
                  <div className="info-item">
                    <span className="info-label">Phone:</span>
                    <span className="info-value">{maskPhone(selectedConsent.phone || selectedConsent.mobile)}</span>
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">Type:</span>
                  <span className="info-value">{selectedConsent.type}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Consent:</span>
                  <span className="info-value"></span>
                  {/* <span className="info-value">{selectedConsent.consent}</span> */}
                  <div dangerouslySetInnerHTML={{ __html: sanitize(selectedConsent.consent || '') }} />

                </div>
              </div>
            </div>
          )}

          {/* revoke field options */}
          <div style={{ marginBottom: 12, padding: 8, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fff' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Revoke Options</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => toggleRevokeOption('email')}
                title={selectedConsent?.email ? 'Revoke Email' : 'Email already revoked'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: 8, borderRadius: 8, border: revokeOptions.email ? '1px solid #2563eb' : '1px solid #e5e7eb', background: revokeOptions.email ? '#eef2ff' : '#fff', cursor: selectedConsent?.email ? 'pointer' : 'not-allowed' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" stroke="#92400e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
                <span style={{ fontSize: 13 }}>Email</span>
              </button>

              <button
                type="button"
                onClick={() => toggleRevokeOption('whatsapp')}
                title={selectedConsent?.phone ? 'Revoke WhatsApp' : 'WhatsApp already revoked'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: 8, borderRadius: 8, border: revokeOptions.whatsapp ? '1px solid #dc2626' : '1px solid #e5e7eb', background: revokeOptions.whatsapp ? '#fff1f2' : '#fff', cursor: selectedConsent?.phone ? 'pointer' : 'not-allowed' }}
              >
                <svg width="16" height="16" fill="#25D366" viewBox="0 0 16 16">
                  <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                </svg>
                <span style={{ fontSize: 13 }}>WhatsApp</span>
              </button>

              <button
                type="button"
                onClick={() => toggleRevokeOption('dob')}
                title={selectedConsent?.dob ? 'Revoke D.O.B' : 'D.O.B already revoked'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: 8, borderRadius: 8, border: revokeOptions.dob ? '1px solid #2563eb' : '1px solid #e5e7eb', background: revokeOptions.dob ? '#eef2ff' : '#fff', cursor: selectedConsent?.dob ? 'pointer' : 'not-allowed' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" stroke="#1e3a8a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                <span style={{ fontSize: 13 }}>D.O.B</span>
              </button>

              <button
                type="button"
                onClick={() => toggleRevokeOption('all')}
                title={'Revoke All'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: 8, borderRadius: 8, border: revokeOptions.all ? '1px solid #ef4444' : '1px solid #e5e7eb', background: revokeOptions.all ? '#fee2e2' : '#fff', cursor: 'pointer' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" stroke="#991b1b" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"><path d="M12 2v6" /><path d="M5 12h14" /><path d="M6 20h12" /></svg>
                <span style={{ fontSize: 13 }}>All</span>
              </button>
            </div>
          </div>
          <label className="form-field">
            <div className="form-label">
              <span>Reason for Revocation</span>
              <span className="required">*</span>
            </div>

            <select
              name="reason"
              value={revokeForm.reason}
              onChange={handleRevokeFormChange}
              required
            >
              <option value="">Select a reason</option>
              <option value="user_request">User Request</option>
              <option value="data_breach">Data Breach</option>
              <option value="policy_violation">Policy Violation</option>
              <option value="expired">Consent Expired</option>
              <option value="other">Other</option>
            </select>
          </label>


          <label>
            <span>Comments</span>
            <textarea
              name="comments"
              value={revokeForm.comments}
              onChange={handleRevokeFormChange}
              rows="4"
              placeholder="Add any additional comments or details about the revocation..."
            />
          </label>


          {/* Attachments Section */}
          <div className="form-field" style={{ marginTop: 16 }}>
            <div className="form-label" style={{ marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>📎 Attachments (Optional)</span>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              hidden
            />

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justify: 'center',
                gap: 6,
                padding: '16px 20px',
                borderRadius: 12,
                border: '2px dashed #cbd5e1',
                background: '#f8fafc',
                color: '#3b82f6',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.background = '#eff6ff';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.background = '#f8fafc';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: '#dbeafe',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justify: 'center',
                fontSize: 18,
              }}>
                📎
              </div>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: '#1e40af' }}>Click to Upload Attachments</span>
              <span style={{ fontSize: 11.5, color: '#64748b' }}>Select documents, images, or receipts to support this request</span>
            </button>
          </div>

          {revokeForm.attachments.length > 0 && (
            <div style={{ marginTop: 14, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>📁 Attached Files ({revokeForm.attachments.length}):</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {revokeForm.attachments.map((file, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justify: 'space-between',
                    padding: '9px 12px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    fontSize: 13,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                      <span style={{ fontSize: 16 }}>📄</span>
                      <span style={{ fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.name}
                      </span>
                      <span style={{ fontSize: 11, color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      title="Remove file"
                      style={{
                        background: '#fee2e2',
                        border: '1px solid #fecaca',
                        color: '#dc2626',
                        borderRadius: 6,
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justify: 'center',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                        marginLeft: 10,
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#ffffff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justify: 'flex-end',
            gap: 12,
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid #e2e8f0',
          }}>
            <button
              type="button"
              onClick={closeRevokeModal}
              style={{
                padding: '10px 20px',
                borderRadius: 10,
                border: '1.5px solid #cbd5e1',
                background: '#ffffff',
                color: '#475569',
                fontSize: 13.5,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#94a3b8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRevokeSubmit}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 22px',
                borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#ffffff',
                fontSize: 13.5,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(220, 38, 38, 0.35)',
                transition: 'all 0.20s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.45)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(220, 38, 38, 0.35)'; }}
            >
              <span style={{ fontSize: 15 }}>🚫</span>
              <span>Revoke Consent</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Details Drawer */}
      <div className={`drawer-overlay ${isDetailsOpen ? 'open' : ''}`} onClick={closeDetails} />
      {/* Privacy Send Modal */}
      {privacyModalOpen && privacyModalConsent && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1400 }} onClick={() => setPrivacyModalOpen(false)}>
          <div style={{ width: 680, maxWidth: '94vw', margin: '6vh auto', background: '#fff', borderRadius: 8, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eef2f7' }}>
              <h3 style={{ margin: 0 }}>Send Privacy Notice — {privacyModalConsent.username || privacyModalConsent.Name || ''}</h3>
              <button onClick={() => setPrivacyModalOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: '#374151', marginBottom: 6 }}>Message & Attachment</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Message will be sent as the email body; a PDF attachment will be included if available or generated.</div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: '#374151', marginBottom: 6 }}>Message</div>
                <textarea
                  value={privacyMessage}
                  onChange={(e) => setPrivacyMessage(e.target.value)}
                  rows={8}
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 8,
                    border: '1px solid #e6eef8',
                    boxSizing: 'border-box',
                    minHeight: 120,
                    maxHeight: '36vh',
                    overflowY: 'auto',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>

                  <input type="checkbox" checked={privacyAutoTranslate} disabled />
                  <span style={{ fontSize: 13 }}>Use consent form language</span>
                </label>
                <div style={{ marginLeft: 'auto' }}>
                  <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
                    {effectiveLanguageOptions.find(l => l.value === privacyTargetLanguage)?.label || privacyTargetLanguage || 'English'}
                  </span>
                </div>
              </div>

              {privacyFormat === 'attachment' && (
                <div style={{ marginBottom: 12 }}>
                  {privacyGenerating && (
                    <div style={{ marginBottom: 8, fontSize: 13, color: '#374151' }}>Generating attachment...</div>
                  )}
                  <input
                    ref={privacyAttachmentRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                    multiple
                    onChange={handlePrivacyAttachmentChange}
                    style={{ display: 'none' }}
                  />
                  {/* <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button type="button" className="btn" onClick={() => privacyAttachmentRef.current && privacyAttachmentRef.current.click()}>Add Attachments</button>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{privacyAttachments.length} file(s) selected</div>
                  </div> */}
                  {privacyAttachments.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      {privacyAttachments.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', border: '1px solid #eef2f7', borderRadius: 6, marginBottom: 6 }}>
                          <div style={{ flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>({(f.size / 1024).toFixed(1)} KB)</div>
                          <button type="button" onClick={() => removePrivacyAttachment(i)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>✖</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn" onClick={() => setPrivacyModalOpen(false)} style={{ border: '1px solid #e6eef8', background: '#fff', padding: '8px 12px' }}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!privacyModalConsent) return;
                    setPrivacyLoading(true);
                    try {
                      // determine target language: explicit selection > consent's preference
                      let target = privacyTargetLanguage || '';
                      if (!target && privacyAutoTranslate) {
                        const p = (privacyModalConsent.preferred_languages || privacyModalConsent.preferredLanguages || privacyModalConsent.languages || privacyModalConsent.language || '');
                        if (Array.isArray(p) && p.length) target = p[0];
                        else if (typeof p === 'string' && p.trim()) target = p.split(',')[0].trim();
                      }

                      let messageToSend = privacyMessage;
                      // attempt client-side translation for supported languages
                      if (privacyAutoTranslate && target) {
                        const code = languageCodeFor(target);
                        if (code && code !== 'en') {
                          try {
                            const tRes = await axios.post('https://libretranslate.de/translate', {
                              q: privacyMessage,
                              source: 'en',
                              target: code,
                              format: 'text'
                            }, { headers: { 'Content-Type': 'application/json' }, timeout: 15000 });
                            const translated = tRes.data?.translatedText || tRes.data?.translated_text || tRes.data?.result || null;
                            if (translated) messageToSend = translated;
                          } catch (te) {
                            console.warn('Translation failed, sending English', te);
                          }
                        }
                      }

                      // Ensure attachments exist: try cached server privacy notices or generate PDF from message
                      try {
                        if (!privacyAttachments || !privacyAttachments.length) {
                          // try server-side privacy-notices first
                          try {
                            const params = {};
                            if (user && user.tenant_id) params.tenant_id = user.tenant_id;
                            const listRes = await axios.get(`${API_BASE}/user/privacy-notices`, { params });
                            const notices = (listRes && listRes.data && listRes.data.privacy_notices) ? listRes.data.privacy_notices : [];
                            const type = (privacyModalConsent.type || privacyModalConsent.consent_type || privacyModalConsent.template || '').toString();
                            const findMatch = (arr, token) => {
                              const tk = (token || '').toString().trim().toLowerCase();
                              for (const n of arr) {
                                try {
                                  const ct = (n.consent_type || n.title || (n.card_data && n.card_data.label) || '').toString().toLowerCase();
                                  if (ct && ct === tk) return n;
                                } catch (e) { }
                              }
                              for (const n of arr) {
                                try {
                                  const ct = (n.consent_type || n.title || (n.card_data && n.card_data.label) || '').toString().toLowerCase();
                                  if (ct && ct.includes(tk)) return n;
                                } catch (e) { }
                              }
                              return null;
                            };
                            const matched = findMatch(notices, type);
                            if (matched && matched.id) {
                              try {
                                await axios.post(`${API_BASE}/manager/privacy-notices/${matched.id}/generate_pdf`);
                                const pdfRes = await axios.get(`${API_BASE}/user/privacy-notices/${matched.id}/pdf`, { responseType: 'blob' });
                                if (pdfRes && pdfRes.data) {
                                  const blob = pdfRes.data;
                                  // const filename = (matched.title || type || 'attachment').replace(/[^a-z0-9_\-]/gi, '_') + '.pdf';
                                  const filename = (privacyModalConsent && (privacyModalConsent.id || privacyModalConsent.ID)) ? `consent_${privacyModalConsent.id || privacyModalConsent.ID}.pdf` : (matched.title || type || 'attachment').replace(/[^a-z0-9_\-]/gi, '_') + '.pdf';
                                  try {
                                    const file = new File([blob], filename, { type: 'application/pdf' });
                                    setPrivacyAttachments([file]);
                                  } catch (e) {
                                    blob.name = filename;
                                    setPrivacyAttachments([blob]);
                                  }
                                }
                              } catch (e) {
                                console.warn('Server-side generate/fetch failed', e);
                              }
                            }
                          } catch (e) {
                            // fallback: try to generate PDF from the message itself
                            try {
                              const body = new URLSearchParams();
                              body.append('consent_type', (privacyModalConsent.type || privacyModalConsent.consent_type || '').toString());
                              body.append('message', messageToSend);
                              const tid = Number(user?.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
                              if (tid) body.append('tenant_id', String(tid));
                              const gen = await axios.post(`${API_BASE}/manager/consent/template_pdf_custom`, body, { responseType: 'blob', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, timeout: 20000 });
                              if (gen && gen.data) {
                                const blob = gen.data;
                                const filename = (privacyModalConsent && (privacyModalConsent.id || privacyModalConsent.ID)) ? `consent_${privacyModalConsent.id || privacyModalConsent.ID}.pdf` : `${(privacyModalConsent.type || 'attachment').replace(/[^a-z0-9_\-]/gi, '_')}.pdf`;
                                try {
                                  const file = new File([blob], filename, { type: 'application/pdf' });
                                  setPrivacyAttachments([file]);
                                } catch (e) {
                                  blob.name = filename;
                                  setPrivacyAttachments([blob]);
                                }
                              }
                            } catch (e2) {
                              console.warn('PDF generation fallback failed', e2);
                            }
                          }
                        }
                      } catch (attachErr) {
                        console.warn('Attachment preparation failed', attachErr);
                      }

                      // Prepare and send: always include message as email body; attach PDF if available
                      // if (privacyAttachments && privacyAttachments.length) {
                      //   const fd = new FormData();
                      //   fd.append('message', messageToSend);
                      //   fd.append('translate', 'false');
                      //   if (target) fd.append('target_language', target);
                      const fd = new FormData();
                      fd.append('message', messageToSend);
                      fd.append('translate', 'false');
                      if (target) fd.append('target_language', target);
                      fd.append('include_response_links', 'false');
                      if (privacyModalNotice && privacyModalNotice.id) {
                        fd.append('privacy_notice_id', privacyModalNotice.id);
                      }
                      if (privacyAttachments && privacyAttachments.length) {
                        privacyAttachments.forEach((f) => fd.append('attachments', f, f.name));
                      }
                      // await axios.post(`${API_BASE}/user/consent/${privacyModalConsent.id}/resend`, fd);
                      const selectedDrivePreference = (() => {
                        try {
                          return (window && window.localStorage && window.localStorage.getItem('consent_selected_drive')) || '';
                        } catch (e) {
                          return '';
                        }
                      })();
                      if (selectedDrivePreference) {
                        fd.append('drive_type', selectedDrivePreference);
                      }
                      const uploadHeaders = {};
                      if (selectedDrivePreference) {
                        uploadHeaders['X-Drive-Type'] = selectedDrivePreference;
                      }
                      await axios.post(`${API_BASE}/user/consent/${privacyModalConsent.id}/resend`, fd, { headers: uploadHeaders });

                      // After successful send, persist a record in privacy_notices only when this is not already an existing notice
                      if (!privacyModalNotice) {
                        try {
                          const savePayload = {
                            tenant_id: user?.tenant_id || null,
                            title: (privacyModalConsent.type || privacyModalConsent.consent_type || privacyModalConsent.template || '').toString(),
                            content: messageToSend,
                            html: messageToSend,
                            card_data: (privacyAttachments && privacyAttachments.length) ? { format: 'attachment', filename: privacyAttachments[0].name, language: target || null } : { format: 'text', language: target || null },
                            created_by: user?.id || null
                          };
                          try {
                            if (savePayload.tenant_id) {
                              await axios.post(`${API_BASE}/manager/privacy-notices`, savePayload);
                            } else {
                              console.warn('Skipping saving privacy notice: tenant_id missing', savePayload);
                            }
                          } catch (e) {
                            console.warn('Failed to save privacy notice to DB', e);
                          }
                        } catch (e) {
                          console.warn('Error while saving privacy notice metadata', e);
                        }
                      }

                      Swal.fire({ text: 'Notification sent', icon: 'success' });
                      setPrivacyModalOpen(false);
                    } catch (err) {
                      console.error('Privacy send error', err);
                      Swal.fire({ text: 'Failed to send notification', icon: 'error' });
                    } finally {
                      setPrivacyLoading(false);
                    }
                  }}
                  disabled={privacyLoading || privacyGenerating}
                  style={{ backgroundColor: '#2563eb', color: '#fff', border: 'none', padding: '8px 14px', cursor: (privacyLoading || privacyGenerating) ? 'progress' : 'pointer' }}
                >
                  {privacyLoading ? 'Sending...' : (privacyGenerating ? 'Generating...' : 'Send')}
                </button>
              </div>
              {privacyStatusMessage && (
                <div style={{ marginTop: 8, color: '#1f2937', background: '#fff7ed', border: '1px solid #ffedd5', padding: '8px 12px', borderRadius: 6 }}>
                  {privacyStatusMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <GuideModal
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        steps={GUIDE_STEPS}
      />


    </div>
  );
}

export default UserPanel;
